// src/encounter/encounter-shell-page.tsx
// Container — resolves the encounter (fetch-or-create) and gates between the
// pre-encounter rail (status Initial → before Start) and the encounter step shell
// (Draft/in-progress). Renders the real HPI template + patient and persists the shared
// encounter QuestionnaireResponse FHIR-native (QB create/upsert, PROD-1159/PROD-1354c).
// Source gate: pre_encounter_widget.dart:278-288.
import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback, lazy, Suspense } from 'react';
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
// PROD-1972 — the ambient accept path. `decideAccept` is the policy (see accept-rules.ts for
// why a model-supplied ICD code is only ever a lookup hint); `resolveDiagnosisCode` is the
// Master lookup it needs.
import { decideAccept, needsModelCodeLookup, type ConceptRef } from './ambient/accept-rules';
import { useAmbientDecisionPort } from './services/use-ambient-decisions';
import { useAmbientAlertAcknowledgementPort } from './services/use-ambient-alert-acknowledgements';
import { useAmbientTranscriptPort } from './services/use-ambient-transcript';
import { useAmbientAnalysisPort } from './services/use-ambient-analysis';
import { useAmbientRecordingsPort } from './services/use-ambient-recordings';
import { RecordingConsentHost } from './ambient/RecordingConsentHost';
import { AmbientConsentReporter } from './ambient/ambient-consent-reporter';
import { AmbientStartCard, AmbientStartWithEncounterCard } from './ambient/ambient-start-card';
import { AmbientWindow } from './ambient/ambient-window';
import { ambientIsLive, ambientPlacement, shouldAutoOpen } from './ambient/ambient-overlay';
import type { RecordingConsentStatus } from './ambient/recording-consent-types';
import { clockOf } from './intelligence/rail-words';
import { resolveDiagnosisCode } from './ambient/resolve-diagnosis-code';
import { readTemplatePin, templateDrift, type TemplateDrift, type TemplatePin } from './lib/template-pin';
import {
  orphanedAnswersNoteSection,
  partitionSavedResponse,
  preservedItems,
  type OrphanedAnswer,
} from './lib/orphaned-answers';
import { TemplateDriftNotice } from './template-drift-notice';
import { TemplateLoadNotice } from './template-load-notice';
import { SignedSaveRefusedNotice } from './signed-save-refused-notice';
import { NoteConflictNotice } from './note-conflict-notice';
import { isSignedNoteRefusal, riskChangedElsewhereSentence, riskChangedSignReason, SIGNED_NOT_SAVED, signedRefusalSentence } from './lib/signed-refusal';
import { useAmbientRecoveries } from './ambient/use-ambient-recoveries';
import { toOrderSeed } from './ambient/to-order-seed';
import type { Proposal } from './ambient/ambient-types';
import { findDuplicateOrder, toPlanItems } from './lib/plan-items';
import { createQrSaveWaiters } from './lib/qr-save-waiters';
import { applyPlacement, responseCarries, type FindingPlacement, type FindingSection } from './ambient/finding-placement';
import type { FindingAcceptResult, FindingsHost } from './ambient/use-finding-suggestions';
import {
  buildDictatedVitalsRequest,
  recordedVitalsChanged,
  type AcceptedVital,
  type DictatedVitalsAcceptResult,
} from './lib/dictated-vitals-request';
import type { PatientVital } from '@/types/vitals-api';
import { clinicianRoleFor } from './ambient/accept-verb';
import { useRole } from '@/auth/use-role';
import { ALLIED_PLAN_STEP, NURSING_PLAN_STEP, type PaStepKey } from './lib/encounter-steps';
import { resolveSteps, toClinicalRole, type ShellRole, type SuppressibleStepKey } from './lib/encounter-blocks';
import { useEncounterTemplate } from './services/use-encounter-template';
import { useAmbientNarrativeHost } from './ambient/use-ambient-narrative-host';
import { useAmbientSinkHost } from './ambient/use-ambient-sink-host';
import {
  useEncounterByAppointment,
  useCreateEncounter,
  ENCOUNTER_STATUS_INITIAL,
  ENCOUNTER_STATUS_DRAFT,
} from './services/use-encounter';
import { isSupersededSave } from './lib/superseded-save';
import {
  readSignLockRefusal,
  useSaveEncounterQuestionnaire,
  type SignLockRefusal,
} from './services/use-save-encounter-questionnaire';
import { useEncounterQuestionnaireResponse } from './services/use-encounter-questionnaire-response';
import { readVersionConflict } from './lib/answer-set';
import { isDevHost } from '@/config/dev-host';
import type { InsertedTemplatesConfig } from './inserted-templates/use-inserted-templates';
import type { QuestionnaireResponseResource } from '@/types/questionnaire-response-save-api';
import { toQuestionnaireResponseResource } from './lib/to-questionnaire-response-resource';
import { fromQuestionnaireResponseResource } from './lib/from-questionnaire-response-resource';
import { noteDeltasFromResource } from './lib/note-delta-extension';
import { buildQrItemMeta } from './lib/qr-item-meta';
import { isQuestionnaireStep, stepHasContent } from './lib/questionnaire-groups';
import { questionnaireHasItemCode } from './lib/questionnaire-item-code';
import { NO_EXCLUSIONS, withoutPatientExcluded } from './lib/patient-context';
import { REQUIRED_ITEMS_TOAST_MS, requiredItemsWarning, unansweredRequiredItemGaps } from './lib/required-items';
import { SIGN_BLOCKED } from './lib/sign-blocked';
import { useRiskBridge } from './risk/services/use-risk-bridge';
import { managedRiskLines, riskStepAnswersOf, withoutRiskAnswers } from './risk/lib/risk-qr-bridge';
import { steadiFollowUpFor } from './risk/lib/steadi-follow-up';
import { hiddenStepsOf, stepTitlesOf } from './lib/cps-layout';
import { useCurrentStep } from './lib/use-current-step';
import { useDirtyGeneration } from './lib/use-dirty-generation';
import { usePatientSummary } from './services/use-patient-summary';
import { usePatientDetail } from './services/use-patient-detail';
import { useEncounterVitals } from './services/use-encounter-vitals';
import { useVitalsHistory, useBmiHistory } from './services/use-vitals-history';
import {
  toGrowthSeries,
  toWeightForLength,
  isGrowthChartAge,
  growthSexFrom,
} from './lib/to-growth-series';
import { GrowthChartPrintDialog } from './growth-chart-print-dialog';
import { useSaveVitals } from './services/use-save-vitals';
import {
  buildVitalsRequest,
  buildVitalsRequestFromCodes,
  buildMarkReviewedRequest,
  isVitalsReviewed,
  type VitalsFormValue,
  type LmpFlags,
  type VitalsRequest,
} from './lib/to-vitals-request';
import { buildVisualAcuityRequest, VISUAL_ACUITY_LOINC } from './lib/to-visual-acuity-request';
import { fromVisualAcuityJson, emptyVisualAcuity, isVisualAcuityEmpty, type VisualAcuityData } from './lib/visual-acuity';
import { VisualAcuityDialog } from './visual-acuity-dialog';
import { VisualAcuityForm } from './visual-acuity-form';
import { VitalsEditDialog } from './vitals-edit-dialog';
import { VitalsHistorySlot } from './vitals-history/vitals-history-slot';
import { useSubmittedQuestionnaire } from './services/use-submitted-questionnaire';
import { useEncounterAssessment } from './services/use-assessment';
import { useSaveAssessment } from './services/use-save-assessment';
import { useRemoveInvoiceLine } from './services/use-remove-invoice-line';
import {
  AP_DIAGNOSIS_ENTITY,
  findWrittenDiagnosis,
  type AcceptResult,
  type RetractPlan,
  type RetractTarget,
} from './ambient/written-entity';
import { useMedicationSearchTiered } from './services/use-medication-search-tiered';
import { useSignLock, useCreateSignLock } from './services/use-sign-lock';
import {
  deriveSignLockState,
  isProviderSigned,
  signedUnderLegacyNoteWording,
  signLockSignedAt,
  toSignLockRequest,
} from './lib/to-sign-lock';
import { toApNoteSections } from './lib/to-ap-note';
import { useWrapUp, useSaveWrapUp } from './services/use-wrap-up';
import { buildWrapUpFollowUpRequest, buildWrapUpInstructionsRequest, followUpFromWrapUp } from './lib/to-wrap-up-request';
import { showServerError, showWarning } from '@/shared/lib/toast-helpers';
import { medicationPlanRoute, medicationRecordToSeed, patientMedicationForOrder, type MedicationPlanIntent } from './lib/medication-record-to-seed';
import { appendPieceToLine, type RailNoteLanding, type RailNotePieceHost } from './lib/rail-note-piece';
import { railLandingLinkId, railLandingState, type RailLandingFacts } from './lib/rail-note-landing';
import { getMedicationDetails } from './services/use-save-medication';
import { ApMedStopHost } from './ap-med-stop-host';
import type { FollowUpUnit } from '@/types/wrap-up-api';
import { useEncounterClinicalDetails } from '@/billing/services/use-clinical-note';
import { toPostVisitSummary } from './lib/to-post-visit-summary';
import { toLabPrintExtras } from './lib/lab-print-extras';
import { useLabRequisitions } from './services/use-lab-requisitions';
import { usePVSPrint } from '@/patients/services/use-post-visit-summary';
import { patientAssessmentToCanvas, assessmentOrderStatus } from './lib/from-patient-assessment';
import { toPatientAssessment, type DxCard, type PlanOrder } from './lib/to-patient-assessment';
import {
  appendLineToNote,
  appendLineToNoteDelta,
  noteDeltaHasContent,
  noteHasLine,
  removeLineFromNotePair,
} from './lib/append-to-assessment-note';
import { addArticleWithOnSet, type EducationArticle } from './lib/to-education-article';
import { ensureOnePrimary } from './lib/dx-primary';
import {
  setOrderPayload,
  removeOrderPayload,
  setDxCoding as setDxCodingEntry,
  removeDxCoding,
  isSyntheticId,
  type OrderPayloadMap,
  type DxCodingMap,
  type CodedOrderPayload,
} from './lib/order-payload';
import { medicationResultToPlanOrder, replaceOrderKeepingLinks } from './lib/to-plan-order';
import type { MedicationDose } from './lib/to-medication-order';
import { toCanvasOrderTypes } from './lib/order-types';
import { toOrdersSummaryGroups } from './lib/to-orders-summary';
import { toVaccinationRequests, type VaccineAdministrationRequestView } from './lib/to-vaccination-requests';
import { toMedicationAdminRequests } from './lib/to-medication-admin-requests';
import { VaccinationAdminDialog } from './vaccination-admin-dialog';
import type { VaccinationDraft } from './vaccination-admin-editor';
import { findVaccinationEntry, applyVaccinationAdministration } from './lib/apply-vaccination-administration';
import { vaccinationEntryToInitial, vaccinationEntryToValueSet } from './lib/from-vaccination-entry';
import { toSubmittedForm } from './lib/to-submitted-questionnaire';
import { toTemplateAxes } from './lib/template-axes';
import { resolveTemplateSpecialty } from './lib/booked-specialty';
import { resolveTemplateVisitType, type TemplateVisitType } from './lib/booked-visit-type';
import { toGenderId } from './lib/to-gender-id';
import { fhirToDsQuestionnaire } from './lib/fhir-to-ds-questionnaire';
import { parseReadoutValuesKey, readoutValuesKey, toReadoutLines } from './lib/readout-lines';
import { formatNoteStamp } from './lib/visit-time';
import { responseToNote, groupNoteByStep } from './generated-note';
import { vitalsNoteSection, patientSummaryNoteSection } from './lib/to-note-preamble';
import { patientSummaryToIpsSections, unavailableSummarySections } from './lib/to-ips-sections';
import { toRailOccupation, toRailEducation } from './lib/to-rail-social';
import { usePrescriptionOrders, toRichActiveMedications } from './services/use-prescription-orders';
import { toApActiveMedications } from './lib/to-ap-active-medications';
import { patientVitalsToRow, visualAcuityReading } from './lib/to-vitals-row';
import { toChartRead, type ChartReads } from './lib/to-chart-snapshot';
import { buildEncounterFacts } from './lib/cps-rule';
import { resolveVitalsPatientType } from './lib/to-vitals-patient-type';
import { physicianReadsVitalsOnly, vitalsBlockActions, vitalsReadState } from './structured-note/objective-vitals-model';
import { AP_NARRATIVE_EDITORS } from './structured-note/note-phrases';
import { genderOf, type ChartLaunchContext } from './lib/chart-population';
import { visitQuestionnaire } from './lib/visit-questionnaire';
import { useLeaveGuard } from './services/use-leave-guard';
import { confirmNoteViewChange, registerNoteFlush, useInlineDraftFlush, useSignAfterFlush, useUnsavedNote } from './services/unsaved-note';
import { startActionFor, locationModality, visitTypeFor } from './lib/reason-for-visit';
import { resolveSubjectId } from './lib/resolve-subject';
import { resolvePatientState } from './lib/appointment-patient';
import {
  isArrivedStatus,
  appointmentStartBlockReason,
  isChiefComplaintLocked,
} from './lib/is-arrived';
import { resolveAppointmentStatus } from './lib/appointment-status-precedence';
import { visitHeaderFacts } from './lib/visit-header-facts';
import { EncounterShellView, type EncounterFrameV2 } from './encounter-shell-view';
// PROD-2743 · frame v2 (DARK behind a per-browser switch; OFF by default).
import { sentReferralHandoffs } from './lib/to-referral-handoff';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import { parseQuestionnaireIds, patientReportedSection, withPatientReportedSection } from './lib/patient-reported';
import { PATIENT_REPORTED_OFF, type PatientReportedState } from './services/use-patient-reported';
import { PatientReportedReader } from './patient-reported-reader';
import { PatientReportedAnswersDialog } from './patient-reported-answers-dialog';
import { useHomePath } from '@/shared/lib/home-path';
import {
  alliedPlanProps,
  nursingPlanProps,
  useNoNonPrescriberPlanDraft,
  useNonPrescriberPlanDraft,
} from './use-non-prescriber-plan-draft';
import { DEFAULT_NOTE_MODE, sectionStepsFor, type NoteMode } from './lib/soap-ribbon';
import { alliedSoapLayout } from './lib/allied-soap';
import { EncounterWorkspaceTabStrip } from './pre-encounter-context-strip';
import { EncounterDocumentsPanel } from './encounter-documents-panel';
import { QuietLoading } from '@/shared/ui/components/quiet-loading';
import { CentrePanel } from './centre-panel';
import { ClinicalEditTab } from './clinical-edit-tab';
import { type AmbientScribeBlockState, type OrbState } from '@scripsteam/scrips-signal-ds';
import { IntelligenceRail, IntelligenceStrip } from './intelligence-rail';
import { intelligencePhase, RAIL_WORDS, TAB_SCREEN } from './intelligence/rail-words';
import { routeFor } from './intelligence/chip-routes';
import { IntelligenceRailFooter } from './intelligence/rail-footer';
import type { AskRequest } from './intelligence/ask-words';
import { nextAskSeq } from './intelligence/sent-asks';
import { R } from './intelligence/rail-classes';
import { RecordingHeaderControl } from './recording-header-control';
import type { AmbientRecordingControl } from './ambient/AmbientScribePanel';
import { recordingControlAtRest } from './ambient/recording-control-at-rest';
import type { ExplainRequest } from './intelligence/explain-panel';
import type { ExplainSystem } from './intelligence/use-explain';
import { IntelligenceDxSuggestions } from './intelligence-dx-suggestions';
import { ambientStatusWord } from './lib/ambient-status-word';
import { orbStateForAmbient } from './lib/orb-state';
import { SignNoteConfirmDialog } from './sign-note-confirm-dialog';
import { DiscardNoteConfirmDialog } from './discard-note-confirm-dialog';
import { PreEncounterTabStrip } from './pre-encounter-context-strip';
import { useMalaffiSso } from './services/use-malaffi-sso';
import { useL360HistoryReviewEvent } from './services/use-l360-history-review-event';
import { useL360LifestyleIntakeEvent } from './services/use-l360-lifestyle-intake-event';
import { useL360CareModelInputEvent } from './services/use-l360-care-model-input-event';
import { useEncounterInvoice } from './services/use-encounter-invoice';
import {
  useEPrescriptionEnabled,
  useEPrescriptionPreview,
  useSubmitEPrescription,
  ePrescriptionId,
} from './services/use-eprescription';
import { toInvoiceSection } from './lib/to-invoice-section';
import { useAddRemoveService, useUpdateServiceDiscount } from '@/billing/services/use-add-remove-service';
import { usePrintInvoice, useAddManagementDiscount } from '@/billing/services/use-invoice-actions';
import { InvoiceAddServiceDialog } from './invoice-add-service-dialog';
import { InvoiceManagementDiscountDialog } from './invoice-management-discount-dialog';
import { InvoiceEditServiceDialog } from './invoice-edit-service-dialog';
import { EPrescriptionPreviewDialog } from './eprescription-preview-dialog';
import { canSeeWrapUpInvoice } from './lib/wrap-up-invoice-gate';
import { EncounterDrawer } from './encounter-drawer';
import { IntelligenceCards } from './intelligence/intelligence-cards';
import { CatalogIntelligenceRail } from './intelligence/catalog-rail';
import { SignedAttestation } from './note-column/signed-attestation';
import type { NoteSectionKey } from './generated-note-body';
import { toNoteComplaints } from './note-column/to-note-complaints';
import { noteOrderDetail } from './note-column/to-note-order-detail';
import { DocumentsChartTab } from '@/patients/components/chart/documents-chart-tab';
import { EncounterChartPanel } from './encounter-chart-panel';
import { WorkspaceTimeline } from './workspace-timeline';
import { EncounterTerrainTab } from './terrain/encounter-terrain-tab';
import { EncounterCarePlanTab } from './care-plan-tab/encounter-care-plan-tab';
import { ENCOUNTER_TOP_TAB_PARAM, type EncounterTopTab } from './pre-encounter-context-strip';
import { CHART_INNER_PARAM, CHART_TAB_PARAM } from '@/patients/lib/patient-edit-tabs';
import { PreEncounterRailView } from './pre-encounter-rail-view';
import { DeviceTrendsSlot } from './device-trends/device-trends-slot';
// [PROD-2643] Antonin's cards ship in @scripsteam/signal (~1.5 MB): loaded only when the pre-visit draws one. The nurse's whole
// pre-visit (her reconciliation card included) is loaded by EncounterWorkflow (nurse-previsit-template.tsx).
const PrevisitDiagnosticsBlock = lazy(() => import('./previsit-diagnostics/previsit-diagnostics-block').then((m) => ({ default: m.PrevisitDiagnosticsBlock })));
const NurseNotesBlock = lazy(() => import('./nurse-notes-block').then((m) => ({ default: m.NurseNotesBlock })));
import { derivedCompletedSteps, stepHoldsAnswer, stepsNeedingAttention } from './lib/step-completion';
import { effectivePasses, PASSABLE_STEPS, useStepPasses, type StepPass } from './lib/step-pass';
import { setCrumbName } from '@/app/crumb-name';
// The Connect / Leave banner for a Virtual visit. Same component the pre-encounter rail mounts —
// Flutter draws its videoStartWidget on both surfaces, so the two must not be separate lookalikes.
import { PatientSummaryRail } from './patient-summary-rail';
import { ChartColumnStrip } from './chart-column-v2';
import { FrameV2PatientIdentity } from './frame-v2-patient-identity';
import { VisitStatusChip } from './visit-status-chip';
import { useAppointmentStatus } from './services/use-appointment-status';
import { useEncounterAppointmentRealtime } from './realtime/use-encounter-appointment-realtime';
import { useBundles } from './services/use-bundles';
import { useDeleteBundle } from './services/use-delete-bundle';
import { importBundleIntoCanvas, withBundleConditionIds } from './lib/import-bundle';
import { EncounterSearchDrawer } from './encounter-search-drawer';
import { orderSetsForDrawer } from './order-sets/order-sets-rail-model';
import { dictatedDoseForPick } from './lib/dictated-dose-for-pick';
import { consumeSeed, enqueueSeed } from './lib/pending-seeds';
import type { ApSearchCategory } from '@scripsteam/scrips-signal-ds';
import { AddToFavouritesDialog } from './add-to-favourites-dialog';
import { Button, GrowthChart, ModalYesNo, NoteFooter, UndoSnackbar } from '@scripsteam/scrips-signal-ds';
import { GeneratedNoteBody } from './generated-note-body';
import type { QuestionnaireResponse } from '@scripsteam/scrips-signal-ds';
// PROD-2136 / Q2 — the block is a Signal DS component now; this page supplies its props
// through `resolveChiefComplaint`, and never mounts it.
import type { ChiefComplaintHpiBlockProps } from '@scripsteam/scrips-signal-ds';
// PROD-2577 part B — the PE vitals block is a Signal DS component now (`layout: 'pills'`);
// this page supplies its props through `resolveVitals`, and never mounts `EncounterStepVitals`.
import type { VitalsBlockProps } from '@scripsteam/scrips-signal-ds';
import { vitalsRowToBlockItems } from './lib/to-vitals-block-items';
import type {
  PatientHeaderData,
  PatientHeaderDemographics,
  CodeSearchResult,
  MedicationResult,
  OrderType as DsOrderType,
} from '@scripsteam/scrips-signal-ds';
import type { AssessmentPlanViewProps } from './assessment-plan-view';
import type { VisitComplaintState } from './ap-visit-complaint';
import type { WrapUpViewProps } from './wrap-up-view';
import type { WrapUpProblemsProps } from './wrap-up-problems';
import { usePatientConditions } from '@/patients/services/use-patient-conditions';
import { CONDITION_STATUS, type PatientCondition } from '@/types/patient-chart-api';
import { useSaveCondition } from './services/use-save-condition';
import { buildConditionRequest } from './lib/to-condition-request';
import {
  toPatientProblems,
  matchProblem,
  buildUnresolveValue,
  todayLocalYmd,
  isTodayLocal,
} from './lib/patient-problems';
import type { EncounterStatus } from '@/types/encounter-api';
import { detailToHeader } from './lib/to-patient-header';
import { toContacts } from './lib/to-patient-contacts';
import type { EncounterPatientNav } from './lib/to-patient-header';
import { EncounterTelevisit } from './encounter-televisit';
import { televisitProviderId, isVirtualLocation } from './lib/to-televisit';
import { videoJoinReason } from './lib/video-join-window';
import { useVideoJoinWindow } from './use-video-join-window';
import { VideoJoinNotice } from './video-join-notice';
import { env } from '@/config/env';
import { canApplyOrderSets, useOrderSetApply } from './order-sets/use-order-set-apply';
import { NeedsDxSignPrompt } from './order-sets/needs-dx-sign-prompt';
import { useAuthStore } from '@/auth/auth-store';
import { MacroEditDialog } from '@/settings/components/macro/macro-edit-dialog';
import type { MacroData } from '@/types/settings-api';
import { useNoteMacros, type NoteMacroScope } from './services/use-note-macros';
import { dedupeMacros, type NoteMacro } from './lib/smart-phrase';
// PROD-1944. Lazy on purpose. Importing the panel eagerly pulls the whole ambient tree — the
// block, every proposal card, the DS components under them — into this module's graph, and
// this page is already one of the heaviest in the app. It measurably slowed the test suite
// (several encounter tests sit near the 5s timeout and were tipped over it), which is a fair
// proxy for what it does to a real first paint. The panel is only reachable in the Note tab,
// and only when configured, so there is no reason for it to be in the initial chunk.
// PROD-3195. The import is a function so the page can START it when the encounter opens (see the
// prefetch effect below) instead of when the panel first renders. `lazy` and the prefetch share ONE
// module promise. A failed load is forgotten, so the next render's `lazy` retry starts a fresh one.
let ambientPanelModule: Promise<typeof import('./ambient/AmbientScribePanel')> | undefined;
const loadAmbientScribePanel = () => {
  ambientPanelModule ??= import('./ambient/AmbientScribePanel').catch((error: unknown) => {
    ambientPanelModule = undefined;
    throw error;
  });
  return ambientPanelModule;
};
const AmbientScribePanel = lazy(() => loadAmbientScribePanel().then((m) => ({ default: m.AmbientScribePanel })));

// Lazy for the same reason the ambient panel is: importing the PVS dialog eagerly pulls
// `@/patients/services/use-post-visit-summary` into this module, and this page's test harnesses mock
// that path without exporting `usePostVisitSummary` — six suites failed on the eager import. Lazy
// keeps the module out of the page's import graph until the popup is actually opened, so the
// harnesses keep guarding what they guard instead of growing six new mock lines.
const PostVisitSummaryDialog = lazy(() =>
  import('@/patients/components/chart/post-visit-summary-dialog').then((m) => ({
    default: m.PostVisitSummaryDialog,
  })),
);
import {
  useChiefComplaintSuggestions,
  usePatientComplaint,
  useSaveChiefComplaint,
  fetchChiefComplaintSuggestions,
} from './services/use-chief-complaint';
import {
  complaintsToSelected,
  buildPatientComplaint,
  type PatientComplaintModel,
} from './lib/to-chief-complaint';
import { complaintRefs } from './complaint-map/system-order-for';
import { phraseSuggester } from './complaint-map/phrases-first';
import { alliedDisciplineOf, planSpecialityOf } from './lib/allied-discipline';
// PROD-2136 — the chief-complaint HPI block: per-complaint attribute chips saved into the
// complaint document's `patientHpi` through the SAME CreateChiefComplaint replace-all.
import {
  applyComplaintHpi,
  complaintHpiNoteCategories,
  findComplaintByDisplay,
  hasChiefComplaintHpiBlock,
  toggleHpiSelection,
  withChiefComplaintHpiNote,
  type ComplaintHpiGroup,
  type ComplaintHpiOption,
  clearHpiGroup,
  // The DTO -> presentation mapping for the DS block. Lives in the lib beside the document
  // it maps, so the page and its tests share one definition.
  toChiefComplaintHpiView,
  chiefComplaintPickerPlaceholder,
} from './lib/chief-complaint-hpi';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { MessageColleagueAction } from '@/staff-chat/components/message-colleague-action';
import { isStaffChatPatientOn } from '@/staff-chat/lib/staff-chat-switch';
import { PreviousNotesLoader } from './previous-notes-loader';
import type { PreviousNoteView } from './lib/to-previous-notes';
import { ClinicalNotePanel } from '@/billing/components/claim-review/clinical-note-panel';


export function EncounterShellPage() {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { isNurse, canLock, user, allowedOrderTypes, roleLabel, clinicalRole, mayOrder, mayStopOrSuspend } =
    useRole();
  const role: 'doctor' | 'nurse' = isNurse ? 'nurse' : 'doctor';
  // PROD-2040. The encounter shell's role prop, WIDENED. `role` above is deliberately left alone:
  // it is what the pre-encounter rail and the search drawer take, and those surfaces have no
  // allied-health behaviour in this change (an allied-health account keeps resolving to 'doctor'
  // there, exactly as it did before). Only the shell — which owns the step nav and so is the one
  // surface whose flow changes — is handed the widened value. (The ambient panel no longer takes
  // `role` either — see `ambientRole` below, PROD-2603.)
  const encounterRole: ShellRole = clinicalRole === 'allied-health' ? 'allied-health' : role;
  // PROD-2603. The AMBIENT panel's role, widened the same way. The panel used to take `role`, so an
  // allied-health account (persona 20 — `tenantRoleId` is the TenantMasterId, see `auth/roles.ts`
  // `isAlliedHealth`) arrived as 'doctor' and was offered every prescribing verb: "Find in
  // catalogue…" on a dictated medication, a vaccination order, lab and imaging orders. `acceptVerb`
  // withholds those for 'allied-health'. A nurse still resolves to 'nurse' — `clinicalRoleFor`
  // checks nurse first — so the nurse rule is untouched.
  // PROD-2669: only a physician (`mayOrder`, the Scrips.Common allowlist) resolves to 'doctor' and
  // is offered order verbs; every other persona keeps only the diagnosis verbs.
  const ambientRole = clinicianRoleFor(clinicalRole, mayOrder === true);
  // PROD-2946. Every MANUAL way into the Add-Order drawer and the Add-to-Favourites dialog (the
  // section chrome on HPI · Risk · ROS · PE, the A/P chrome, the Docs step's sparkle) is handed its
  // handler only on the physician allowlist. The drawer is also the way to a diagnosis, so a nurse
  // or allied-health user handed it could add one. With no handler the chrome draws no icon. The
  // server gates stay the authority; this stops the UI offering what the server will refuse.
  const ordersOffered = mayOrder === true;
  // PROD-1944. The ambient WebSocket is authenticated and cannot carry an Authorization
  // header, so the token is passed to the panel and ridden on the query string.
  const accessToken = useAuthStore((s) => s.accessToken);
  // PROD-3195. Whether sign-in is still being restored. Only then does the ambient slot show a loading
  // placeholder without a token: a session that ended or failed to refresh must not show one forever.
  const authPending = useAuthStore((s) => s.status === 'unknown');
  // PROD-3195. Fetch the ambient panel's chunk as soon as the encounter opens, so it is already here when
  // the token and the template let the slot render, and a clinician who presses Start is not waiting on
  // a download. Errors are ignored on purpose: `lazy` retries and reports on render. Kept above every
  // early return of this component (rules of hooks).
  useEffect(() => {
    if (env.ambientBaseUrl) void loadAmbientScribePanel().catch(() => undefined);
  }, []);
  // Signed-in user - the growth-chart print sheet needs the practice (letterhead) and practitioner.
  const authUser = useAuthStore((s) => s.user);

  const nav = (location.state as EncounterPatientNav | null) ?? null;
  // Tap-time nav identity (shallow: name + age/sex/MRN) — instant, but ABSENT on reload/deep-link.
  // Enriched below from the live patient detail once it loads (PROD-1329 patient card).
  const navDemographics: PatientHeaderDemographics | undefined = nav?.demographics;

  // ── PROD-2743 · frame v2 (DARK) ─────────────────────────────────────────────────────────────
  // Read ONCE per mount: the switch is per-browser (`?ef=frameV2` / `?ef=-frameV2`, see
  // shared/lib/encounter-flags.ts) and a mid-encounter flip must not swap the frame under a
  // clinician's cursor. The route is keyed on the appointment (app/router.tsx), so a new patient is
  // a new mount and re-reads it. ON by default for the encounter since 19 Oct (DEFAULT_ON in encounter-flags.ts;
  // `?ef=-frameV2` turns it off in this browser). OFF → every branch below keeps the step-by-step frame's render.
  const [frameV2On] = useState(() => isEncounterFlagOn('frameV2'));
  // `?x=ambient-window` (ON by default on the dev hosts): the catalog's ambient (a Start listening card above the note, the session
  // in a floating window). Frame v2 only. `?x=-ambient-window` is today's panel in the rail with the header mic.
  const [ambientWindowSwitch] = useState(() => isEncounterFlagOn('ambientWindow'));
  // SAMER RULINGS, 4 Nov (rail lock §3): ambient is OUT of the Intelligence rail. With Antonin's catalog rail
  // (`?x=intel-rail-catalog`) the ambient session always lives in its window, never in the rail, whatever `ambientWindow` says.
  const [catalogRailAmbientOut] = useState(() => isEncounterFlagOn('intelRailCatalog'));
  // `?x=ambient-overlay` (ON by default on the dev hosts; Samer 4 Nov): the session is ONLY the floating window, never the rail;
  // the header mic stays; the window opens by itself on listening; a nurse has no ambient. OFF: `ambientWindow` as before.
  const [ambientOverlaySwitch] = useState(() => isEncounterFlagOn('ambientOverlay'));
  const ambientPlace = ambientPlacement({ windowSwitch: ambientWindowSwitch || catalogRailAmbientOut, overlaySwitch: ambientOverlaySwitch, frameV2: frameV2On, isNurse });
  const ambientWindowOn = ambientPlace.windowOn;
  // `?x=med-actions` (ON by default on the dev hosts): Continue · Refill · Change on a chart medication, into the A&P Plan; the
  // A&P Active Medications buttons wired to the same handlers. `?x=-med-actions` is today's rows and disabled buttons.
  const [medActionsOn] = useState(() => isEncounterFlagOn('medActions'));
  /** `medActions`: who may send a medication to the Plan, the same gate as a medication order (physician, medication allowed). */
  const mayOrderMedications = mayOrder === true && (allowedOrderTypes ?? []).includes('medication');

  // Resolve encounterId: fast-path nav → GET-by-appointment → create.
  const [encounterId, setEncounterId] = useState<string | null>(nav?.encounterId ?? null);
  const [encounterStatus, setEncounterStatus] = useState<string | null>(null);
  // Frame v2 only: also read while the id is known but its status is not (a visit opened with `nav.encounterId`): the status says
  // whether the encounter has started (the pre-visit's Start / Continue / Edit label, and whether Start still owes the
  // Initial → Draft upgrade), and the by-appointment read is the one that carries it. It no longer opens the note by itself
  // (28 Oct, see `started` below). OFF reads exactly as main.
  const encounterQuery = useEncounterByAppointment(appointmentId, !encounterId || (frameV2On && encounterStatus === null));
  // [PROD-1523] `?x=previous-notes`: a follow-up pre-visit lists the patient's earlier notes for the same reason (Flutter
  // previousNotes()). OFF by default: no request, the section stays hidden as before.
  const [previousNotesOn] = useState(() => isEncounterFlagOn('previousNotes'));
  // `?x=previsit-back` (ON on the dev hosts): the long page's header Back returns to the pre-visit (see the topBar below).
  const [previsitBackOn] = useState(() => isEncounterFlagOn('previsitBack'));
  // `?x=intel-always-open` (ON on the dev hosts): Scrips Intelligence follows the workspace tab (its phase reads the tab's name,
  // and the Explain chip is operable only where chip-routes.ts routes it). OFF: the rail reads the encounter's phase, as before.
  const [intelFollowsTab] = useState(() => isEncounterFlagOn('intelAlwaysOpen'));
  // [PROD-1523] Previous notes (`?x=previous-notes`): <PreviousNotesLoader> (mounted only when on) fills these rows.
  const [previousNotes, setPreviousNotes] = useState<PreviousNoteView[]>([]);
  // The earlier note opens read-only in the chart's note viewer (Notes tab, ClinicalNotePanel).
  const [viewingPreviousNote, setViewingPreviousNote] = useState<PreviousNoteView | null>(null);
  /* The appointment record. Serves TWO cells of the chrome, which is why it is no longer gated on
     `!nav?.appointmentStatus`: the status pill can fall back to router state on the tap-through
     path, but the visit strip's ROOM has no other source at all (nav carries none, and the list
     endpoints ship only a bare roomId), so gating the fetch on a missing status left the room
     permanently blank for anyone who arrived by tapping an appointment. */
  const appointmentStatusQuery = useAppointmentStatus(appointmentId);

  /* Flutter's `appointmentUpdateListener` (`encounter_screen.dart:259-271`): the encounter listens
     to the Scheduling hub for ITS OWN appointment. Without this, the backend's status transitions
     — the one CreateEncounter triggers on Start, and the one signing triggers — reached the server
     but never the screen, so the pill kept its pre-Start value until a reload (PROD-2028). */
  useEncounterAppointmentRealtime(appointmentId);

  /**
   * The appointment status EVERY gate on this screen reads — Start, the vitals editor, the
   * chief-complaint lock, and the status pill itself.
   *
   * THE FETCHED APPOINTMENT WINS; nav is only the first-paint fallback (PROD-2030). The rule and
   * the full story behind it live in `lib/appointment-status-precedence.ts`, where a unit test can
   * actually reach them — inline here, the ordering was unfalsifiable, because every page test
   * mocks the status query to `{ data: null }` and nav then wins under EITHER order.
   */
  const appointmentStatusCode = resolveAppointmentStatus(
    appointmentStatusQuery.data?.appointmentStatus,
    nav?.appointmentStatus,
  );
  const createEncounter = useCreateEncounter();
  const createFiredRef = useRef(false);
  const openCreateFiredRef = useRef(false);

  /**
   * The encounter's LIFECYCLE STATUS (`Initial` → `Draft` → `Completed`), tracked in state rather
   * than read off `encounterQuery`, because that query is disabled the moment `encounterId` is
   * known (`useEncounterByAppointment(appointmentId, !encounterId)`). After the open-phase create
   * resolves an id, the query's cached data is still the `null` that triggered the create — so it
   * can no longer answer "is this row still Initial?", which is exactly what Start needs.
   */

  // RESUME: adopt an existing encounter resolved for this appointment (the worklist passes
  // `nav.encounterId` directly; a by-appointment fetch may also resolve one), and remember the
  // status it came back with so Start knows whether an Initial→Draft upgrade is still owed.
  useEffect(() => {
    // Frame v2: the id came with the tap-through and the status did not: take it from the read when it is the same encounter.
    if (frameV2On && encounterId && encounterStatus === null && encounterQuery.data?.identifier === encounterId) {
      setEncounterStatus(encounterQuery.data.status ?? null);
      return;
    }
    if (encounterId) return;
    const fetched = encounterQuery.data?.identifier;
    if (fetched) {
      setEncounterId(fetched);
      setEncounterStatus(encounterQuery.data?.status ?? null);
    }
  }, [frameV2On, encounterId, encounterStatus, encounterQuery.data]);

  /**
   * OPEN PHASE — create the encounter as `Initial` when the screen opens and none exists.
   *
   * FLUTTER (`patient_encounter_bloc.dart:401-431`): the open-time
   * `FetchEncounterByAppointmentIdEvent` branches on the GET result, and a null (the 404) runs
   *
   *     createEncounter(automaticMoveToAppointment: false, status: Initial)
   *
   * — create, but do NOT move the view. A network capture on 2026-08-28 confirms it: opening a
   * pre-encounter fires GetEncounterByEncounterId 404 → CreateEncounter 201 while the Start button
   * is still showing, and the `negotiate?encounterId=…` and RetrieveEncounterTemplates that follow
   * both depend on the id it returns.
   *
   * WHY (Andrew, 2026-08-28): "its called when opening the screen as we need to know the vitals,
   * patient history items added in which encounter and we need the encounterId." Vitals and history
   * recorded on the pre-encounter must attach to an encounter, so the id has to exist BEFORE Start.
   * React previously created only on the Start click, so those writes had no encounter to belong to.
   *
   * THIS REVERSES the "create on the CLICK, not on open" note that PROD-1475 left here. It is not a
   * re-litigation of that decision by a parity pass — it is Andrew's explicit instruction today,
   * with the reason above. It also lands what PROD-1152 had already flagged as owed: the comment
   * below the pre-encounter gate said "lifecycle persist of Initial→Draft is a follow-up".
   *
   * GATED ON ARRIVED, where Flutter is unconditional. Flutter's 404 branch creates for ANY status,
   * Booked included; `encounter-shell-page.start-gate.test.tsx` (PROD-1473) is a standing decision
   * that a Booked appointment must fire no CreateEncounter. Andrew's rationale doesn't reach Booked
   * either — vitals and the chief complaint are both locked for it, so there is nothing to attach.
   * Keeping the gate honours PROD-1473; widening it to match Flutter exactly is a one-line change
   * if that guard is ever retired.
   *
   * Requires a RESOLVED null, not merely a falsy one: `isSuccess && data === null` distinguishes
   * "the server says there is no encounter" from "the GET hasn't answered yet". Firing on the
   * latter would create a duplicate on every open (PROD-1476).
   */
  useEffect(() => {
    if (encounterId || openCreateFiredRef.current) return;
    if (!encounterQuery.isSuccess || encounterQuery.data !== null) return;
    const ctx = nav?.createContext;
    if (!ctx) return; // deep-link with no context: nothing to build a create from (PROD-1475)
    if (!isArrivedStatus(appointmentStatusCode)) return;

    openCreateFiredRef.current = true;
    createEncounter.mutate(
      { ...ctx, status: ENCOUNTER_STATUS_INITIAL },
      {
        onSuccess: (id) => {
          setEncounterId(id);
          setEncounterStatus(ENCOUNTER_STATUS_INITIAL);
          // Deliberately NOT setStarted(true) — Flutter's `automaticMoveToAppointment: false`.
          // The practitioner stays on the pre-encounter; only Start moves the view.
        },
        onError: () => {
          // Never strand the screen on a failed open-create: the pre-encounter still renders and
          // Start still works (it creates its own row). Clearing the latch allows one retry on a
          // later resolve rather than locking the encounter out for the session. The error banner
          // is suppressed for this phase — the user asked for nothing yet, so a failure here is
          // not theirs to action; a genuine failure surfaces when they press Start.
          openCreateFiredRef.current = false;
          createEncounter.reset();
        },
      },
    );
    // `createEncounter` is a stable mutation object; including it would re-run this on every
    // mutation state change (including the one this effect causes).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    encounterId,
    encounterQuery.isSuccess,
    encounterQuery.data,
    nav?.createContext,
    appointmentStatusCode,
  ]);

  // ── Pre-encounter gate ─────────────────────────────────────────────────────
  // The rail shows until the user clicks Start/Continue (the `started` flag), regardless of
  // persisted status — see the showRail note below. `started` is the client-side transition fired
  // by the Start button (lifecycle persist of Initial→Draft is a follow-up — PROD-1152).
  // Prefer the resolved encounter's subject (survives reload / direct-nav where the
  // router nav state is absent); fall back to the tap-time nav context.
  // PROD-2804: a THIRD source, last. A deep link / refresh / bookmark to a visit that has no
  // encounter yet has neither of the two above (the encounter GET 404s, router state is gone), so
  // the page had no patient and rendered an EMPTY chart. The appointment this page already fetches
  // (GetById) names its patient — see `lib/appointment-patient.ts`. The tap-through path is
  // untouched: nav supplies the subject first, so this fallback is never reached there.
  const patientId = resolveSubjectId(
    encounterQuery.data?.subject,
    nav?.createContext?.subjectId ?? appointmentStatusQuery.data?.patientId,
  );
  // Where the patient stands — `failed` renders "Couldn't load this patient", never a blank chart.
  const patientState = resolvePatientState({
    patientId,
    encounterLoading: encounterQuery.isLoading,
    appointmentLoading: appointmentStatusQuery.isLoading,
  });
  const patientPending = patientState === 'pending';

  // ── Top tab (Encounter | Chart) — PROD-1986 ────────────────────────────────────────────────
  // Flutter runs Chart as an in-place tab of the SAME encounter screen (one TabController:
  // encounter_screen.dart:981-1004 strip → :1049 `case "Chart"` / :1459-1490 tabViewInitial), so
  // the selection must NOT navigate: routing to /patients/edit unmounted this page and tore down
  // the QR draft + autosave. The state lives in a search param on this route, which keeps it
  // deep-linkable and browser-back-friendly while the page stays mounted throughout.
  const [searchParams, setSearchParams] = useSearchParams();
  // Chart · Timeline · Documents are in-place tabs of this route (`?tab=chart` · `?tab=timeline` · `?tab=documents`); Encounter is no param.
  const tabParam = searchParams.get(ENCOUNTER_TOP_TAB_PARAM);
  // `?x=terrain` (Andrew, 4 Nov): Terrain is a tab beside Encounter. Read once per mount, like the other switches. OFF, a
  // `?tab=terrain` link falls back to the Encounter tab.
  const [terrainOn] = useState(() => isEncounterFlagOn('terrain'));
  const [carePlanTabOn] = useState(() => isEncounterFlagOn('carePlanTab'));
  const topTab: EncounterTopTab =
    tabParam === 'chart' || tabParam === 'timeline' || tabParam === 'documents'
      ? tabParam
      : tabParam === 'terrain' && terrainOn
        ? 'terrain'
        : 'encounter';
  const handleTopTabChange = useCallback(
    (next: EncounterTopTab) => {
      const params = new URLSearchParams(searchParams);
      if (next === 'chart' || next === 'timeline' || next === 'documents' || next === 'terrain') params.set(ENCOUNTER_TOP_TAB_PARAM, next);
      else params.delete(ENCOUNTER_TOP_TAB_PARAM);
      // Push (not replace) so Back leaves the chart and returns to the encounter — Flutter's own
      // strip is a straight tab toggle, and a replace here would make Back exit the encounter.
      setSearchParams(params, { replace: false });
    },
    [searchParams, setSearchParams],
  );
  // The Chart tab is offered only when the chart can actually be mounted (patient id resolved) —
  // the single prop that both gates the tab and drives it (anti-fiction: no dead tab).
  const onTopTabChange = patientId ? handleTopTabChange : undefined;

  // ── PROD-2743 · frame v2 (DARK) ─────────────────────────────────────────────────────────────
  // `frameV2On` is read near the top of the component (the encounter read there already needs it).
  // PROD-3045 N4 · frame v2: the note header and the chart card's visit line read room, start and
  // reason from sources every path loads, so the physician who opened the visit and a nurse who
  // arrives later by link see one header. The rule lives in `lib/visit-header-facts.ts`. OFF keeps
  // its own expressions below, unchanged.
  const visitFacts = visitHeaderFacts({
    appointment: appointmentStatusQuery.data,
    navStartISO: nav?.appointmentStart,
    navReason: nav?.createContext?.reasonForVisitName,
    encounterReason: encounterQuery.data?.reasonForVisitName,
  });
  // [PROD-2949] The non-prescriber plan switch (`?ef=planSave`), read once per mount like frameV2.
  const [planSaveOn] = useState(() => isEncounterFlagOn('planSave'));
  // [PROD-1395] `?x=referrals`: the physician's referral to a speciality in the A&P, and the allied-health Referral card. OFF by default.
  const [referralsOn] = useState(() => isEncounterFlagOn('referrals'));
  // `referralHandoff` (dev default): the physician's wrap-up shows each referral he sends as Antonin's hand-off card (sender side).
  const [referralHandoffOn] = useState(() => isEncounterFlagOn('referralHandoff'));
  // PROD-2743 · frame v2 only: the saved-phrase library for the structured note's narratives (`.` and
  // Macros), read by <NoteMacroLibrary> (mounted only when the switch is on, so the OFF page issues
  // no query), and the text a clinician chose to save as a new phrase.
  const [noteMacros, setNoteMacros] = useState<NoteMacro[]>([]);
  const [macroDraft, setMacroDraft] = useState<string | null>(null);
  // PROD-2824 / PROD-2947 Part 2 · piece phrases in the structured note's lines are ON inside frame v2 (no extra
  // switch; the libraries' phrases are piece phrases). OFF has no structured note, so nothing changes there.
  const phrasePiecesOn = frameV2On;
  // PROD-2961 (D1, option A) · frame v2 only: each note line's Delta with pieces, by linkId, saved as the
  // item's `cps-note-delta` extension beside its plain text and read back on load. OFF never fills it.
  const [noteDeltas, setNoteDeltas] = useState<Readonly<Record<string, string>>>({});
  // The Deltas belong to ONE encounter: a different encounter starts with none (Codex P1) — adjusted
  // during render on the change, never an effect.
  const [noteDeltasFor, setNoteDeltasFor] = useState(encounterId);
  if (noteDeltasFor !== encounterId) {
    setNoteDeltasFor(encounterId);
    setNoteDeltas({});
  }
  const handleNoteDeltaChange = useCallback((linkId: string, delta: string | undefined) => {
    setNoteDeltas((prev) => {
      // Idempotent: clearing an absent key (every plain keystroke on a line with no pieces) or writing
      // the same Delta keeps the same state, so nothing re-renders.
      if (delta ? prev[linkId] === delta : !(linkId in prev)) return prev;
      const next = { ...prev };
      if (delta) next[linkId] = delta;
      else delete next[linkId];
      return next;
    });
  }, []);
  // TS-3 · frame v2 only: the note is ONE continuous scroll — the only mode clinicians see (design desk
  // D-64). One section at a time stays behind the developer-only `noteSections` switch, read once with
  // frame v2 on, so OFF reads nothing.
  const [noteMode] = useState<NoteMode>(() =>
    frameV2On && isEncounterFlagOn('noteSections') ? 'sections' : DEFAULT_NOTE_MODE,
  );
  // Frame v2: the ambient panel's status, so the ONE live Orb follows it (design desk, #569).
  const [ambientStatus, setAmbientStatus] = useState<AmbientScribeBlockState>('idle');
  // Frame v2 (round 18r design): the ambient panel's recording control, drawn in the page header (mic, then
  // "Recording · mm:ss" with Stop). Null until the panel reports it (no ambient panel: no control).
  const [recordingControl, setRecordingControl] = useState<AmbientRecordingControl | null>(null);
  // `ambientWindow`: the window is open; the recording-consent check's status for the card's words.
  const [ambientWindowOpen, setAmbientWindowOpen] = useState(false);
  // `ambientOverlay`: the window opens by itself when listening starts (header mic, card, or Start encounter and listen), on the
  // edge into live only, so a Close while listening is kept.
  const ambientLive = ambientIsLive(recordingControl);
  const [ambientWasLive, setAmbientWasLive] = useState(false);
  if (ambientLive !== ambientWasLive) {
    setAmbientWasLive(ambientLive);
    if (shouldAutoOpen(ambientPlace.autoOpen, ambientWasLive, ambientLive)) setAmbientWindowOpen(true);
  }
  const [ambientConsent, setAmbientConsent] = useState<RecordingConsentStatus | undefined>(undefined);
  // B1 (Andrew, 3 Nov): "Start encounter and listen" on the pre-visit. Armed ONLY by that button; the normal Start paths disarm
  // it, so a refused start never turns into listening later. `listenAfterStartDone` makes the start fire once per press.
  const [listenAfterStart, setListenAfterStart] = useState(false);
  const listenAfterStartDone = useRef(false);
  // Frame v2 rail: the Explain request — Heart & vessels loads with the rail; the footer chip asks again.
  const [explainRequest, setExplainRequest] = useState<ExplainRequest>({ system: 'heart-vessels', seq: 0 });
  const askExplain = useCallback((system: ExplainSystem) => setExplainRequest((r) => ({ system, seq: r.seq + 1 })), []);
  // Ask (Samer, 4 Nov 2026): the clinician's free question, sent to the engine by the Ask card; a new question replaces it.
  // OFF by default (encounter flag `ask`, `?x=ask`) until Antonin's verdict on the Ask card (D-79).
  const [askOn] = useState(() => isEncounterFlagOn('ask'));
  const [askRequest, setAskRequest] = useState<AskRequest | null>(null);
  // Each Send takes a press number unique for the page session (sent-asks.ts), so a reopened page never skips a new Send.
  const askQuestion = useCallback((question: string) => setAskRequest({ question, seq: nextAskSeq() }), []);
  // `?x=intel-rail-catalog`: the rail is Antonin's catalog rail (catalog-rail.tsx). Read once; OFF = round 18r, unchanged.
  const [catalogRailOn] = useState(() => isEncounterFlagOn('intelRailCatalog'));
  // Frame v2: which panel, if any, is open IN the centre column (layout law — never over either
  // rail). The generated note and Clinical edits open from the Wrap-up action bar (chair ruling,
  // 30 Sep). Unused when the switch is off.
  const [v2Panel, setV2Panel] = useState<'note' | 'clinical-edit' | null>(null);
  // The workspace tab is the header's tab (Encounter · Chart · Documents), the SAME search param as the old strip.
  const workspaceTab: EncounterTopTab = topTab;
  // A centre panel belongs to the tab it was opened on. The tab also changes WITHOUT a click —
  // browser Back / Forward rewrite the search param — so the panel closes on ANY tab change, not
  // only in the click handler (Codex P2). Adjusting state during render on a value change is
  // React's documented alternative to an effect here.
  const [panelTab, setPanelTab] = useState(workspaceTab);
  if (panelTab !== workspaceTab) {
    setPanelTab(workspaceTab);
    setV2Panel(null);
  }
  const handleWorkspaceTabChange = useCallback(
    (next: EncounterTopTab) => {
      const params = new URLSearchParams(searchParams);
      if (next === 'encounter') params.delete(ENCOUNTER_TOP_TAB_PARAM);
      else params.set(ENCOUNTER_TOP_TAB_PARAM, next);
      // A tab change closes any centre panel: the tab the clinician picked is what they see.
      setV2Panel(null);
      // Pushed, as the Encounter | Chart strip does, so Back returns to the previous tab.
      setSearchParams(params, { replace: false });
    },
    [searchParams, setSearchParams],
  );
  // Frame v2 rail (round 18r): "Open the results list" opens the Chart tab ON its Labs tab (chartTab=labs; the legacy
  // `inner` key is cleared so it cannot pick another section). Pushed, so Back returns to the encounter.
  const handleOpenResults = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.set(ENCOUNTER_TOP_TAB_PARAM, 'chart');
    params.set(CHART_TAB_PARAM, 'labs');
    params.delete(CHART_INNER_PARAM);
    setV2Panel(null);
    setSearchParams(params, { replace: false });
  }, [searchParams, setSearchParams]);
  // Timeline tab body: the visit's care-pathway history (the appointment's workflow feed). Offered once there is an appointment to read for.
  // `?x=care-plan-tab` (dev default): the same tab is the patient's care plan (Plan board, Trajectory) instead.
  const timelineNode =
    carePlanTabOn && patientId ? (
      // Its own scroller (Andrew 4 Nov: "scrolling doesn't work"): a flex child of the frame's main slot, which does not scroll.
      <div data-slot="encounter-care-plan-tab" className="flex-1 min-w-0 min-h-0 overflow-y-auto">
        <EncounterCarePlanTab patientId={patientId} />
      </div>
    ) : appointmentId ? (
      <div data-slot="encounter-timeline-tab">
        <WorkspaceTimeline appointmentId={appointmentId} inline />
      </div>
    ) : undefined;
  // Terrain tab body (`?x=terrain`): the patient's body systems from the record, offered once the patient id resolves.
  const terrainNode =
    terrainOn && patientId ? (
      // Fills the column as the Documents panel does. The inner scroller carries the Documents panel's slot because the bridge
      // rule that widens the pre-encounter group to the full row (showcase-bridge.css, frozen) keys on that slot; without it
      // the group keeps the DS width max(969px, 0.7 × vw) and leaves an empty band at 1440.
      <div data-slot="encounter-terrain-tab" className="flex-1 min-w-0 min-h-0 flex flex-col">
        <div data-slot="encounter-documents-scroller" className="flex-1 min-w-0 min-h-0 overflow-y-auto scrollbar-hide">
          <EncounterTerrainTab patientId={patientId} />
        </div>
      </div>
    ) : undefined;
  // Documents tab body: this visit's documents (the Docs step's own list), offered once the encounter exists.
  const documentsNode =
    patientId && encounterId ? <EncounterDocumentsPanel patientId={patientId} encounterId={encounterId} /> : undefined;

  // Encounter template (TemplateStudio BFF). Axes: practitionerId (user.id) + age +
  // specialtyId (appointment provider's SpecialitySkillId) + gender (patient sex → MM
  // value-set GUID) — full 5-axis sourcing (PROD-1162). All optional: an absent axis is
  // unconstrained on the BFF. Gated on a resolved patient.
  // PROD-2701: router state carries the booked specialty only on the list tap-through. Opened any
  // other way (pasted link, new tab, bookmark) it is read off the appointment this page already
  // fetches (`appointmentStatusQuery`, GetById) — and the template request WAITS for that read.
  const templateSpecialty = resolveTemplateSpecialty(nav?.specialtyId, appointmentStatusQuery);
  // TS-4 (PROD-2820): the visit's type code (+ the phase-2 profile pointer), read the same way —
  // tap-time nav first, else the same GetById — and the request WAITS for that read too.
  // PROD-3031 (Andrew's review of #634): frame v2 only. With the switch OFF the template request, its wait on
  // GetById and the pick are exactly main's — no visit type, no profile id, no rank walk.
  const templateVisitType: TemplateVisitType = frameV2On ? resolveTemplateVisitType(nav, appointmentStatusQuery) : { pending: false };
  const axes = toTemplateAxes({ id: user?.id }, navDemographics, {
    specialtyId: templateSpecialty.specialtyId,
    genderId: toGenderId(navDemographics?.sex),
    visitType: templateVisitType.visitType,
    appointmentProfileId: templateVisitType.appointmentProfileId,
  });
  // The template hook is called AFTER vitals below, so the cps-rule fact context (PROD-1384) can
  // include bmi (sourced from the loaded vitals). The pick is reactive, so a later-arriving bmi
  // re-selects without refetching.

  const resolvedStatus = encounterQuery.data?.status as EncounterStatus | undefined;
  // Every visit lands on the pre-visit first, on every way in and in both frames: `started` is the client flag only the
  // Start / Continue press sets, so an already-Draft encounter opens on the pre-visit with Continue (Edit once Completed /
  // Signed) — see `showRail` below. REVERSAL (Ahmed, 28 Oct 2026): this drops Antonin's 19 Oct frame v2 rule ("the note
  // is open when the visit's encounter has already started", Draft / Completed / Signed skipped the pre-visit).
  const [started, setStarted] = useState(false);
  // `ambientWindow` B1: after "Start encounter and listen", once the encounter is open and the panel reports its control, start
  // listening ONCE with the panel's own start (its consent gate included). Until the panel offers start it waits; if the
  // consent blocks a start, nothing starts and the card says why. A refused Start never gets here (`started` stays false).
  useEffect(() => {
    if (!listenAfterStart || !started || !recordingControl || listenAfterStartDone.current) return;
    // No start offered yet: wait (consent still checking or not reported yet, or the panel not caught up with a granted consent).
    // Only a denied consent ends the wait without listening (#904 review).
    if (!recordingControl.start && ambientConsent !== 'denied') return;
    listenAfterStartDone.current = true;
    // At rest includes an encounter that already has a recording (restored transcript, `ready`): it listens again, asking
    // Keep both or Replace through the panel's own request, instead of silently not listening.
    if (recordingControlAtRest(recordingControl) && recordingControl.start) recordingControl.start();
  }, [listenAfterStart, started, recordingControl, ambientConsent]);
  // `medActions`: the A&P Active Medications "Discontinue" opens today's Stop confirm for the matching chart medication.
  const [apMedStop, setApMedStop] = useState<{ identifier: string; display: string } | null>(null);
  // Chart body — the shipped ChartTabBody via EncounterChartPanel, encounter-scoped like Flutter's
  // encounter mount (ChartMainView(encounterId: ...)). Only mounted while the Chart tab is active. Before Start the chart draws its own patient panel (the DS pre-encounter
  // screen has no rail on that tab); the started frame owns its left column on every tab, so the chart adds none.
  const chartNode = patientId ? (
    <EncounterChartPanel patientId={patientId} encounterId={encounterId} leftPanel={!started} />
  ) : undefined;
  // The long page: the section ribbon's one slide-in when the visit starts (Start). Derived in the render that flips
  // `started` (no flash of an unanimated bar); cleared when the animation ends.
  const [stepBarReveal, setStepBarReveal] = useState(false);
  const [seenStarted, setSeenStarted] = useState(false);
  if (started !== seenStarted) {
    setSeenStarted(started);
    setStepBarReveal(started && frameV2On);
  }
  // Gate ONLY on the transient client flag, mirroring Flutter's viewState (encounter_screen.dart:909):
  // viewState always starts at `initial` and flips to `appointment` ONLY on the Start press
  // (patient_encounter_bloc.dart:2555-2569). The pre-encounter view does NOT depend on persisted
  // status — an already-Draft/Completed encounter still LANDS on the rail first (the primary button
  // just reads Continue/Edit via startActionFor). Previously this also gated on `inProgress`
  // (Draft/Completed), which made every revisit of a started encounter skip the rail (live-smoke bug).
  const showRail = !started;

  // RESCHEDULE IS GONE FROM THIS SCREEN (Andrew, 2026-08-25: "reschedule remove it").
  //
  // This REVERSES the divergence register's "Pre-encounter Reschedule action" entry and PROD-1246,
  // which had us build a functional Reschedule on the reading that Flutter's is a dead stub worth
  // replacing. Flutter agrees with the removal: `showReschedule` is hardwired `false` and the button
  // carries no `onPressed` (pre_encounter_widget.dart:274, 467-471), so nothing renders there.
  // Removing it is a return to parity, not a new divergence — but PROD-1246 is written scope, so
  // the ticket and that register row need updating rather than just this code.
  //
  // Only the pre-encounter affordance is removed. `RescheduleAppointmentDialog` itself is untouched
  // and still serves the calendar, the appointment panel and the status manager.
  // Hide Reschedule once the patient has arrived/checked-in or the visit is fulfilled
  // (PROD-1256, Flutter `isArrived`). Status rides the same tap-time nav as the slot.

  // Ungated (was rail-only): the patient summary + detail now feed BOTH the pre-encounter rail AND
  // the in-encounter patient-summary rail (PROD-1329) — same patient, same practice scope the
  // pre-encounter rail already proved.
  const summaryQuery = usePatientSummary(patientId);
  // The "Nothing to record" pass: only on the long page (frame v2).
  const stepRulesActive = frameV2On;
  const stepPasses = useStepPasses(encounterId, stepRulesActive);
  // Prescription orders (PROD-1588): SIG / dispense / refills / ordered-for enrichment for the
  // pre-encounter Active Medications block — merged with the summary meds by medication code
  // (Flutter FetchEPrescriptionEvent parity; see use-prescription-orders.ts). Dormant until the
  // encounter id resolves, exactly when Flutter fires the event (bloc:417).
  const rxOrdersQuery = usePrescriptionOrders(patientId, encounterId);
  // B1 / B6 (PROD-2596, PROD-2533): persistence for the ambient panel's accept / decline / undo and
  // the chief-complaint accept, plus the decision record read back from the server. `undefined`
  // until the patient and encounter are both known, so the panel offers no decision control that
  // would post to nowhere. `userId` is the JWT `sub` — the same value the server stamps as DecidedBy.
  const ambientDecisions = useAmbientDecisionPort(patientId, encounterId, authUser?.userId);
  // PROD-2598: ambient clinical-alert acknowledgements, recorded as an audit record (never part of
  // the note, never a sign gate) and read back so a reload keeps them.
  const ambientAlertAcks = useAmbientAlertAcknowledgementPort(patientId, encounterId);
  // PROD-1994 / PROD-1995: the ambient transcript and every correction to it, saved server-side
  // (append-only, attributed by the server) and restored on return.
  const ambientTranscript = useAmbientTranscriptPort(patientId, encounterId);
  // PROD-3195: the saved analysis (note draft, suggestions, entities), so the suggestions come back after a reload.
  // Its own port: a saved analysis must not rebuild the transcript port and re-run what depends on it.
  const ambientAnalysis = useAmbientAnalysisPort(patientId, encounterId);
  // PROD-3181: register / list / delete / download the encounter's ambient recordings.
  const ambientRecordings = useAmbientRecordingsPort(patientId, encounterId);
  // Occupation/Education ride the patient detail, not the aggregate summary (PROD-1270).
  const detailQuery = usePatientDetail(patientId);
  // Tap-time nav identity first (unchanged). Without it (reload / deep link): the appointment's
  // participant name, and while the patient is still being resolved or read, say so — a bare
  // "Patient" over an unloaded chart is what PROD-2804 measured.
  // The appointment's name is used ONLY for the patient the page actually resolved: the encounter's
  // subject outranks the appointment, and a name from one record over the other's chart would label
  // clinical data with the wrong identity (Codex P1 on this change).
  const appointmentPatientName =
    appointmentStatusQuery.data?.patientId && appointmentStatusQuery.data.patientId === patientId
      ? appointmentStatusQuery.data.patientName
      : undefined;
  const navPatient: PatientHeaderData = nav?.patient ?? {
    name:
      appointmentPatientName ??
      (!appointmentId ? '—' : patientPending || detailQuery.isLoading ? 'Loading patient…' : 'Patient'),
  };
  // R6: the summary rail's loading / failed states. The rail's sections are all drawn from the ONE
  // aggregate read (GetPatientSummary), so its state is the sections' state. Pending includes the
  // patient lookup itself — a disabled read is not an empty one.
  const summaryLoading = patientPending || summaryQuery.isLoading;
  // A failed read with NOTHING to show. A failed background refetch over data already on screen
  // keeps that data (it was a successful read) rather than blanking the rail mid-visit.
  const summaryLoadError = !!patientId && summaryQuery.isError && summaryQuery.data === undefined;
  const retrySummary = useCallback(() => {
    void summaryQuery.refetch();
  }, [summaryQuery]);
  // Patient card (PROD-1329): enrich the shallow tap-time identity with the live detail — fills the
  // card on reload/deep-link (when nav is absent) and adds phone + language the nav never carried.
  // Detail fields override nav (the live record is the source of truth); the adapter omits absent
  // fields so the merge never blanks a value nav did supply.
  const detailHeader = detailToHeader(detailQuery.data);
  const patient: PatientHeaderData = { ...navPatient, ...detailHeader.patient };
  // The top bar's last crumb is the patient's name, also after a reload (no tap-time state then).
  useEffect(() => {
    setCrumbName(patient.name?.trim() || null);
    return () => setCrumbName(null);
  }, [patient.name]);
  const demographics: PatientHeaderDemographics | undefined =
    navDemographics || Object.keys(detailHeader.demographics).length
      ? { ...navDemographics, ...detailHeader.demographics }
      : undefined;

  // ── The generated note's HEADER STAMP — one object, both note hosts ────────────────────────
  // Flutter's `generatedNotesHeader()` is part of the note itself, so it renders wherever the note
  // renders: beside the started encounter AND beside the pre-encounter workflow (`sidePanel()`,
  // encounter_screen.dart:1657). Ours only reached the in-encounter drawer — the pre-encounter rail
  // passed no `patient`, so the same note lost its identity line before Start (Andrew, 2026-08-24).
  //
  // Hoisted rather than written inline at each host, for the reason the note INSET was hoisted the
  // same day: two literals for one thing is how the two surfaces drifted in the first place.
  const noteHeaderPatient = {
    name: patient.name,
    mrn: demographics?.mrn,
    // Flutter's identity line is `patient.displayInfo()`, which carries the AGE after the DOB —
    // "DOB: Jul 4, 1998 28y" in Andrew's screenshot. The DS has no age field on the note header
    // (it prefixes "DoB: " and prints this string verbatim), so the age rides the dob value, using
    // the same `ageDisplay ?? age` precedence the patient card uses.
    dob: [demographics?.dob, demographics?.ageDisplay ?? (demographics?.age != null ? `${demographics.age}y` : undefined)]
      .filter(Boolean)
      .join(' ') || undefined,
    // FORMATTED, not the raw ISO. The DS prints this string verbatim, so passing
    // `nav.appointmentStart` put "2026-08-21T10:50:00" on a clinical note's header — caught by
    // rendering the story, and it predates PROD-2011. Flutter's line is "location • date • time"
    // (generated_notes_widget.dart:127-134, `scDateFormat` + `scTimeFormatSingleLetter`), and
    // `visitType` below carries the location.
    // The `??` is the DEEP-LINK cure, not defensive noise: `nav` is router state and exists only
    // when the clinician tapped through from Today/Calendar, so on a reload or a shared link this
    // was undefined and the visit line rendered as the bare location with no date or time at all.
    // `appointmentStatusQuery` already fetches the appointment for the status pill and the room;
    // its payload carries the start, so this needs no second request. Nav still WINS when present
    // — it is the same value with no round trip (the hook's own header states that precedence).
    date: formatNoteStamp(nav?.appointmentStart ?? appointmentStatusQuery.data?.start),
    visitType:
      encounterQuery.data?.locationName ?? nav?.createContext?.locationName ?? undefined,
    // "Created by <avatar> <name>" — Flutter credits the appointment's PROVIDER, not the signed-in
    // user (`generated_notes_widget.dart:135-161`). Absent until the appointment loads, and the DS
    // omits the line entirely rather than printing an empty credit.
    createdBy: appointmentStatusQuery.data?.provider?.name
      ? {
          name: appointmentStatusQuery.data.provider.name,
          avatar: appointmentStatusQuery.data.provider.photo ?? undefined,
        }
      : undefined,
  };

  // ── Malaffi HIE SSO (Flutter encounter_screen.dart:182-217 + ui_data.dart:127-137) ──────────
  // The encounter's third tab, present only for an org whose `resourceBasedIntegration
  // .integrationType` is "Malaffi". It has NO content pane — activating it opens the SSO
  // initiator in an 800x600 popup (`?q1={MRN}&q2={user id}`) and the strip stays on Encounter.
  // The ORG FLAG ALONE decides visibility (Flutter parity); the MRN/user id are resolved at click
  // time, so a slow patient-detail fetch never hides a tab Flutter would show.
  //
  // MRN: prefer the RAW `patientMrn` off the patient detail — the closest analogue of Flutter's
  // `appointment.patient.mrn`. `demographics.mrn` has been through `cleanMrn` (a DISPLAY
  // normalizer that strips a leading "MRN:" label), and an identifier handed to an external HIE
  // must be the record value, not a display string; it stays the fallback for the deep-link /
  // pre-detail window where only tap-time nav has loaded.
  //
  // q2 is `user.id`, NOT `practitionerId`: Flutter calls `providerId()` with its default
  // `forceProviderIdOnly: false` (utils.dart:1232-1249), which returns LoginUserData.id. This is
  // deliberately NOT the A/P `providerId` below (which prefers practitionerId — a different
  // identity for a different purpose).
  const openMalaffi = useMalaffiSso({
    integrationType: user?.resourceBasedIntegration?.integrationType,
    mrn: detailQuery.data?.patientMrn ?? demographics?.mrn,
    providerId: user?.id,
  });
  // A/P BUNDLES (PROD-1839) — the provider's saved A&P templates for the rail's Bundles sub-tab.
  // `user?.id` for the SAME reason as the Malaffi providerId directly above: Flutter's `getBundle()`
  // calls the bare `providerId()`, whose default returns LoginUserData.id, never `practitionerId`.
  // Passing the A/P `providerId` below (which prefers practitionerId) returns an empty list for a
  // provider who demonstrably has bundles in Flutter — which is exactly how this surfaced.
  const bundlesQuery = useBundles(user?.id);
  const deleteBundle = useDeleteBundle();

  // The two SHARED header surfaces (Andrew's screenshots 2026-08-08). Hosted HERE, not per step,
  // because the Add-Order icon exists on every encounter tab except Wrap-up — one drawer, seven
  // tabs. Wrap-up passes no handler below, so its cluster icons stay disabled.
  const [addOrderOpen, setAddOrderOpen] = useState(false);
  const [favouritesOpen, setFavouritesOpen] = useState(false);
  /**
   * CREATE BUNDLE (flow 1). Flutter opens the SAME drawer with `isBundle: true` and accumulates
   * picks into `createBundleData` — a buffer entirely separate from the encounter's A&P
   * (encounter_mobile_screen.dart:180-215). This is that buffer: nothing here touches the canvas,
   * which is why authoring a bundle mid-visit cannot disturb the visit.
   */
  /**
   * Picks from the Add-Order drawer (and ambient's order verbs), handed to the A/P view to open its
   * per-type setup dialog. The view reads the HEAD (`pendingSeed`) and consumes it when its dialog
   * opens.
   *
   * A QUEUE ONLY WITH `frameV2` ON (Codex P2, 1 Oct). With the switch on the A/P HOLDS a pick while
   * the plan has no diagnosis (`onHoldOrder`), so a seed can now wait here. One slot meant a second
   * pick silently replaced the first: pick A, pick B, add a diagnosis, and only B's form opened —
   * the discarded-order defect again, one click later. So ON appends and the view opens them in
   * turn. OFF keeps the single slot exactly: a new pick replaces, a consume empties.
   */
  const [pendingSeeds, setPendingSeeds] = useState<
    {
      item: unknown;
      category: DsOrderType;
      /** Dictated dose fields for the A/P dialog to open with (see `searchSeed.dictatedDose`). */
      dictatedDose?: MedicationDose;
    }[]
  >([]);
  const pendingSeed = pendingSeeds[0] ?? null;
  const setPendingSeed = useCallback(
    (seed: (typeof pendingSeeds)[number] | null) =>
      setPendingSeeds((prev) => (seed === null ? [] : enqueueSeed(prev, seed, frameV2On))),
    [frameV2On],
  );
  /**
   * The A/P's "I opened (or discarded) this one". OFF: empty the slot, exactly as before. ON: remove
   * THAT seed only if it is still the head, so StrictMode's replayed mount effect cannot also take
   * the order queued behind it (`pending-seeds.ts`).
   */
  const consumePendingSeed = frameV2On
    ? () => {
        const consumed = pendingSeed;
        setPendingSeeds((prev) => consumeSeed(prev, consumed));
      }
    : () => setPendingSeed(null);
  /**
   * Pre-fill for the Add-Order drawer when the ambient accept path opens a search (PROD-1972).
   * Separate from `pendingSeed`, which seeds the A/P per-type DIALOG — different surface, and
   * conflating them would open the wrong one.
   */
  const [searchSeed, setSearchSeed] = useState<{
    category: ApSearchCategory;
    query: string;
    /**
     * The dictated sig, waiting for the clinician to pick a coded record.
     *
     * It rides on the SEED rather than on `pendingSeed` because the search sits between the tick
     * and the dialog: the drug's identity is not known until the pick, and the dictated dose has to
     * survive that gap. Without this, "paracetamol 500 mg three times a day" reached a blank form —
     * the search consumed the name and nothing carried the rest.
     */
    dictatedDose?: MedicationDose;
  } | null>(
    null,
  );
  /** Same permission DATA the A/P rail uses: diagnosis first, then the allowed order types. */
  const searchCategories: ApSearchCategory[] = [
    'diagnosis',
    ...((allowedOrderTypes ?? []) as unknown as ApSearchCategory[]),
  ];

  // Vitals feed the PRE-ENCOUNTER surface only (regression cured 2026-07-23: the step
  // shell renders no vitals strip — E2 step frames carry none). Still fetched whenever
  // patient + encounter resolve: the template-fact bmi below needs it in-encounter too.
  const vitalsQuery = useEncounterVitals(patientId, encounterId);
  // Context-aware patientType (age/sex → newborn head-circ / female LMP) for the
  // pre-encounter strip. Flutter ui_data.dart emptyVitalUiList.
  const vitalsPatientType = resolveVitalsPatientType(demographics?.sex, demographics?.age);
  // What the A/P narrative's CHART pieces quote (latest vitals, the allergy list): the SAME
  // summary + encounter-vitals reads the chart rail and the vitals strip draw — no extra request —
  // with their load state, so a failed or pending read is never quoted as an empty list (R6).
  const noteChartReads = useMemo<ChartReads>(
    () => ({
      patientId: patientId ?? undefined,
      encounterId: encounterId ?? undefined,
      summary: toChartRead(summaryQuery),
      vitals: toChartRead(vitalsQuery),
      // R17: a chart piece's Refresh is a FRESH read of both queries for THIS patient + encounter.
      refetch: async () => {
        const [summary, vitals] = await Promise.all([
          patientId ? summaryQuery.refetch() : Promise.resolve(summaryQuery),
          patientId && encounterId ? vitalsQuery.refetch() : Promise.resolve(vitalsQuery),
        ]);
        return {
          patientId: patientId ?? undefined,
          encounterId: encounterId ?? undefined,
          summary: toChartRead(summary),
          vitals: toChartRead(vitals),
        };
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the read results, not the query objects
    [patientId, encounterId, summaryQuery.data, summaryQuery.isError, vitalsQuery.data, vitalsQuery.isError],
  );

  // Encounter template (TemplateStudio BFF) — axes built above (line ~164). The cps-rule fact
  // context (PROD-1384) resolves WHICH matched template applies: bmi (from the loaded vitals),
  // age, and sex. bmi lands once vitalsQuery resolves; the hook's pick is reactive, so the
  // template re-selects then without a refetch (an absent bmi → conditional unmatched → the
  // always-default stays the fallback, i.e. no regression).
  const bmiValue = Number(patientVitalsToRow(vitalsQuery.data).bmi?.value);
  const templateFacts = useMemo(
    () =>
      buildEncounterFacts({
        age: demographics?.age,
        gender: demographics?.sex,
        bmi: Number.isFinite(bmiValue) ? bmiValue : undefined,
        // Specialty fact (PROD-1496): lets a specialty-conditioned template out-rank the general
        // default in the pick. Same value as the specialty AXIS — tap-time nav, else the fetched
        // appointment's booked specialty (PROD-2701); absent only when neither has one.
        specialtyId: templateSpecialty.specialtyId,
      }),
    [demographics?.age, demographics?.sex, bmiValue, templateSpecialty.specialtyId],
  );
  // QR reload (PROD-1354c): the latest persisted QR for this encounter. `undefined` = not
  // settled; `null` = settled, none saved yet (a fresh encounter).
  const savedQrQuery = useEncounterQuestionnaireResponse(encounterId);
  // PINNED TEMPLATE (PROD-2608). A started note names the template it was started on (the
  // server-stamped pin, PROD-2537); it renders THAT template, never a fresh pick from the axes. The
  // pin comes from the saved note, or — for a note first saved in this session — from the save's
  // own response (`sessionPin`, set in handleSave), so a bmi that lands after the first save cannot
  // re-pick the template under a note that is already pinned.
  const [sessionPin, setSessionPin] = useState<TemplatePin | null>(null);
  const savedPin = useMemo(() => readTemplatePin(savedQrQuery.data), [savedQrQuery.data]);
  const notePin = savedPin ?? sessionPin;
  const {
    data: templateData,
    isLoading,
    isError,
    retry: retryTemplate,
    failedBefore: templateFailedBefore,
    retrying: retryingTemplate,
  } = useEncounterTemplate(
    axes,
    templateFacts,
    !!patientId,
    {
      pinnedTemplateId: notePin?.templateId ?? null,
      // Until the saved note settles, whether it is pinned is unknown — pick nothing yet.
      awaitingPin: !!encounterId && savedQrQuery.data === undefined && !savedQrQuery.isError,
      // Compare only a pin the stored note carries; a pin this session just stamped matches by birth.
      verifyPin: !!savedPin,
      // PROD-2701: hold the axes request while the deep-link specialty is looked up.
      awaitingSpecialty: templateSpecialty.pending,
      // TS-4: and while the visit type / profile id is looked up off the same read.
      awaitingVisitType: templateVisitType.pending,
      // TS-4's rank-aware pick (cps-selection-rank) is frame v2 only (PROD-3031); OFF picks as main does.
      rankAware: frameV2On,
    },
  );
  // Where the form on screen stands relative to the template the note was started on. `pending`
  // while the current-pin read is in flight — hydration waits for it (see the hydrate effect).
  const drift: TemplateDrift | 'pending' =
    templateData?.source === 'pinned-unavailable'
      ? 'unavailable'
      : templateData?.source === 'pinned' && savedPin
        ? templateData.currentPin === undefined
          ? 'pending'
          : templateDrift(savedPin, templateData.currentPin)
        : 'none';
  // AUTHOR-REMOVED STEPS (PROD template studio widen): the active template's `cps-layout` root
  // extension carries a `hiddenSteps` array naming which of hpi/risk/ros/pe the author removed.
  // Computed here (not only where `stepTitles` is read below) because the INITIAL STEP useState
  // right after this also needs it — `resolveSteps` has to see the same hidden set the nav will,
  // or the initial render could land on a step with no visible tab. `hiddenStepsOf` never throws:
  // no template / malformed extension / unknown members all yield `[]`, i.e. nothing hidden.
  // MEMOISED on the questionnaire, not recomputed per render: `hiddenStepsOf` returns a FRESH
  // array every call, and this value is a dependency of both the nav below and the reconciling
  // effect — an unstable identity would re-run the effect on every render forever.
  const templateHiddenSteps = useMemo(
    () => hiddenStepsOf(templateData?.questionnaire),
    [templateData?.questionnaire],
  );
  // [PROD-2949] The nurse's plan step (D-66) is hidden through the same `hiddenSteps` while
  // `?ef=planSave` is off. NURSES ONLY: every other role passes the template's own memoised array,
  // the very same reference as before, so the physician's and allied-health flows are byte-identical.
  const hiddenSteps = useMemo<readonly SuppressibleStepKey[]>(
    () => (encounterRole === 'nurse' && !planSaveOn ? [...templateHiddenSteps, NURSING_PLAN_STEP] : templateHiddenSteps),
    [encounterRole, planSaveOn, templateHiddenSteps],
  );
  // Studio-authored template → DS shape. [PROD-3376] Declared above the nav, which reads it (allied
  // health's PE step is content-gated). Was beside the gender-scoped copy below, so the Risk bridge can read the rendered template before any handler that needs it is built.
  // ONE conversion, memoised — the FHIR→DS mapper is the page's hot spot. `fhirToDsQuestionnaire`
  // rebuilds the whole item tree AND runs `applyLayoutSpans`, which JSON.parses the `cps-layout`
  // document TWICE (layoutPlacementMap + layoutSectionMap each call `parseLayout`) before a
  // recursive `stamp()` walk. It used to run UNMEMOISED at two call sites — here and at the
  // gender-filtered `dsQuestionnaire` below — so every keystroke in the encounter paid four
  // JSON.parses of the layout document plus two full tree rebuilds, and the fresh object identity
  // it returned invalidated the DS renderer's internal `computed`/`serialise` memos on top.
  // The template is the only input, so it is converted once per template here and SHARED.
  const dsTemplateQuestionnaire = useMemo(
    () => (templateData?.questionnaire ? fhirToDsQuestionnaire(templateData.questionnaire) : undefined),
    [templateData?.questionnaire],
  );
  // Pediatric GrowthChart (PROD-1324, Flutter parity under PROD-2044): the longitudinal
  // value-for-age graph. Fetch the per-LOINC vitals history only for a paediatric patient (so we
  // don't fan out 4 history calls on every adult encounter), map to the DS GrowthSeries by the
  // patient's RAW birth date, and build the chart node for the shell's growthChart slot. Distinct
  // from the current-encounter vitalsQuery above.
  //
  // THE GATE IS FLUTTER'S: age <= 19 (`growth_chart.dart:1218`). It used to be age < 2 because the
  // old DS chart only drew the WHO 0-24mo domain, so no growth chart rendered for anyone aged 2-19.
  // The DS now ships all three domains and derives the domain links from `ageYears` itself, so this
  // only decides whether to FETCH. Sex stays required: the DS renders nothing without one rather
  // than defaulting to one reference set.
  //
  // The mapper gets `detailHeader.dobIso` - the untouched ISO birth date - NOT `demographics.dob`,
  // which is the "2 Jan 2025" display string. `Date.parse` happens to accept that string in V8, but
  // the mapper's contract is ISO and the header mapper's own comment says to use the raw value for
  // age math.
  const growthSex = growthSexFrom(demographics?.sex);
  const growthAgeYears = demographics?.age;
  const showGrowthChart = isGrowthChartAge(growthAgeYears) && !!growthSex;
  const vitalsHistory = useVitalsHistory(patientId, showGrowthChart);
  const [growthPrintOpen, setGrowthPrintOpen] = useState(false);
  const growthSeries = useMemo(
    () => toGrowthSeries(vitalsHistory.byCode, detailHeader.dobIso),
    [vitalsHistory.byCode, detailHeader.dobIso],
  );
  // Weight-for-length pairs for the "L to W" / "S to W" charts. Paired HERE, not in the DS, because
  // this is where the encounter ids and timestamps live - Flutter pairs the two lists by ordinal
  // (`growth_chart.dart:504-518`), which plots a weight from one visit against a length from
  // another whenever the lists differ in size, and they routinely do.
  const growthWeightForLength = useMemo(
    () => toWeightForLength(vitalsHistory.byCode),
    [vitalsHistory.byCode],
  );
  // The print icon appears only when there IS a practice to print a letterhead for, mirroring
  // Flutter's `practiceData != null` gate (`growth_chart.dart:1318`). The practice name and address
  // are fetched by the dialog itself, on open - a `useQuery` added to this page would be the only
  // live query in the 13 mocked encounter-shell-page suites and fail them with "No QueryClient set".
  const canPrintGrowthChart = !!authUser?.practiceId && !!authUser?.id;
  const growthChartProps = {
    series: growthSeries,
    weightForLength: growthWeightForLength,
    ageYears: growthAgeYears,
    sex: growthSex,
  };
  // Built ONCE and rendered in BOTH return branches below: the page returns early for the
  // pre-encounter (`if (showRail)`), and that is the surface the growth chart is most used from.
  const growthPrintDialog = canPrintGrowthChart ? (
    <GrowthChartPrintDialog
      open={growthPrintOpen}
      onClose={() => setGrowthPrintOpen(false)}
      practitionerId={authUser?.id}
      practiceId={authUser?.practiceId}
      patient={{
        fullName: patient.name,
        mrn: detailQuery.data?.patientMrn ?? demographics?.mrn,
        genderName:
          demographics?.sex === 'F' ? 'Female' : demographics?.sex === 'M' ? 'Male' : undefined,
        // The display age string, verbatim - Flutter prints `patient.age` as-is ("1y 7m 26d").
        age: demographics?.ageDisplay,
      }}
      chart={growthChartProps}
    />
  ) : null;
  // The chart node waits for the RAW birth date as well: until the patient detail lands there is
  // no dob to derive ages from, and rendering then would flash "No readings on this chart" over a
  // history that is still being fetched. The FETCH above starts on age+sex alone, so nothing is lost.
  const growthChartNode = showGrowthChart && detailHeader.dobIso ? (
    <GrowthChart
      {...growthChartProps}
      onPrint={canPrintGrowthChart ? () => setGrowthPrintOpen(true) : undefined}
    />
  ) : undefined;
  // BMI sparkline series (Figma 613:2118 BMI tile trend, parity loop 2) — ONE extra call,
  // gated on the encounter actually having a BMI reading (no fetch on vital-less encounters);
  // cache-shared with the pediatric growth fetch via the same queryKey. Both vitals surfaces
  // (pre-encounter rail + in-encounter band) consume the same mapped row.
  const bmiHistory = useBmiHistory(patientId, !!patientVitalsToRow(vitalsQuery.data).bmi);
  const vitalsRow = patientVitalsToRow(vitalsQuery.data, { bmiHistory });
  // Submitted-questionnaire ("Special Form") section, by appointment (PROD-1251).
  const submittedFormQuery = useSubmittedQuestionnaire(showRail ? appointmentId : undefined);

  // `PaStepKey` = the DS `EncounterStepKey` union WIDENED by the PA-local step keys
  // (lib/encounter-steps.ts, PROD-2040).
  //
  // THE INITIAL STEP IS THE ROLE'S FIRST STEP, not the literal 'hpi' (PROD-2040 follow-up). Gating
  // HPI/ROS/PE to the medical team removes them from the allied-health NAV, but a hardcoded 'hpi'
  // initial step would still mount the HPI questionnaire section in the centre on load — with no tab
  // highlighted, because 'hpi' is not in that role's resolved nav. The gate has to be the same DATA
  // in both places or it is cosmetic. Measured to be behaviour-IDENTICAL for the existing roles:
  // `resolveSteps` starts at 'hpi' for physician AND nurse (asserted in encounter-blocks.test.ts and
  // in the initial-step case in encounter-blocks.role-gating.test.ts).
  /*
   * THE NAV THE INITIAL STEP IS COMPUTED AGAINST ARRIVES LATER THAN THE INITIAL STEP.
   *
   * This used to be a plain `useState` whose lazy initialiser called `resolveSteps` itself. That
   * initialiser runs ONCE, on the first render, and on that render `templateData` is still in
   * flight — so `hiddenStepsOf(undefined)` is `[]`, the nav resolves as if nothing were hidden,
   * and the step initialised to `'hpi'` whatever the template said. Nothing recomputed it when
   * the template landed: the encounter sat on HPI with no HPI tab to highlight and no HPI content
   * to render. Measured on a template hiding HPI and Risk (Ahmed, 2026-09-18).
   *
   * The reconcile now lives in `useCurrentStep`, with the reasoning — and the test that fails if
   * the effect is deleted — beside it.
   */
  // [PROD-3376] Allied health gets the PE step only when the template authors PE content. The template
  // is read for allied health ONLY: every other role's memo keeps the inputs it had (`undefined` here).
  const alliedTemplateHasPe =
    toClinicalRole(encounterRole) === 'allied-health' ? stepHasContent(dsTemplateQuestionnaire, 'pe') : undefined;
  const navSteps = useMemo(
    () =>
      resolveSteps({
        modality: 'in-person',
        role: toClinicalRole(encounterRole),
        hiddenSteps,
        ...(alliedTemplateHasPe !== undefined ? { templateHasPeContent: alliedTemplateHasPe } : {}),
      }),
    [encounterRole, hiddenSteps, alliedTemplateHasPe],
  );
  const [currentStep, setCurrentStep] = useCurrentStep(navSteps);
  // OFF (the step-by-step frame): main's rule, the step you leave is ticked (`handleStepChange`). Frame v2 derives its ✓ instead.
  const [completedSteps, setCompletedSteps] = useState<PaStepKey[]>([]);
  const [visitedSteps, setVisitedSteps] = useState<ReadonlySet<PaStepKey>>(() => new Set());
  const [response, setResponse] = useState<QuestionnaireResponse>({
    resourceType: 'QuestionnaireResponse', status: 'in-progress', answers: [],
  });
  // PROD-2700: a dirty GENERATION, not a boolean. Every edit bumps it (`markDirty`); a save records
  // the generation it snapshotted and, on success, clears dirty only if nothing newer was edited
  // while it was in flight (`markSavedThrough`). See `lib/dirty-generation.ts`.
  const { dirty, generation: dirtyGeneration, markDirty, markSavedThrough } = useDirtyGeneration();

  // FHIR-native QuestionnaireResponse persist (PROD-1159 / PROD-1354c). Persists the WHOLE shared
  // encounter QR (HPI/ROS/PE/Risk): first save POSTs (create); once the document id is known —
  // from the reload GET below or a prior save's response — every save PUTs the SAME row (QB
  // tenant-scoped upsert). The legacy CreateChiefComplaint dual-write is REMOVED (PROD-1354c
  // supersedes it — see the CLAUDE.md divergence register "HPI/QR persistence" row).
  const saveQR = useSaveEncounterQuestionnaire();
  // D-73 (amended, 6 Oct) · frame v2 only tonight: leaving with an unsaved, saving or failed note save asks first
  // (the browser's leave-page prompt on tab close / reload). OFF unchanged.
  useLeaveGuard(frameV2On && (dirty || saveQR.isPending || saveQR.isError));
  // PROD-2547 (B5): QuestionBank refused a save because the note is signed (a save that was in
  // flight when the sign landed, or this tab's sign state is stale — e.g. signed in another tab).
  // LATCHED until the page reloads: the refused content must not be re-sent, and in particular must
  // not be re-sent as an amendment once this tab catches up with the sign — that would land exactly
  // the late write the server just refused. Shown in the red channel, never swallowed.
  // Scoped to the encounter it happened on: the page remounts per appointment today, but if it is ever
  // kept alive across encounters the latch must not follow the clinician into the next one.
  const [signLockRefusalFor, setSignLockRefusalFor] = useState<{
    encounterId: string | null;
    refusal: SignLockRefusal;
    // Whether a stored revision existed when the save was refused. Without one there is nothing to
    // amend yet (PROD-2580), so "reload and amend" would be the wrong instruction.
    hadRevision: boolean;
    // PROD-2810: the step(s) whose edits the refused save carried (the open step, or the open frame-v2
    // section). They are not shown as completed while the refusal stands.
    steps: readonly PaStepKey[];
  } | null>(null);
  // QR reload (PROD-1354c): `savedQrQuery` is declared above the template read (PROD-2608) — the
  // saved note's pin decides WHICH template renders.
  // The stored QR document id — drives PUT-vs-POST. Adopted from the save response; the reload
  // GET's id is ALSO read directly (effectiveQrId below) so a save in the settled-but-not-yet-
  // hydrated window still PUTs instead of POSTing a duplicate (Codex MAJOR — the hydrate effect
  // commits a render later than the query data lands). No cross-encounter reset needed:
  // encounterId only ever transitions null→id here (adoption + create both gate on !encounterId;
  // a different appointment remounts the page), so a stale id from a PRIOR encounter is unreachable.
  const [qrId, setQrId] = useState<string | null>(null);
  const effectiveQrId = qrId ?? savedQrQuery.data?.id ?? null;
  // PROD-2668: the newest stored revision this tab knows of: the last save's response, else the
  // reload GET. Its server-owned creator decides whether "Amend note" is offered (isNoteAuthor).
  const [lastStoredQr, setLastStoredQr] = useState<QuestionnaireResponseResource | null>(null);
  const currentRevision = lastStoredQr ?? savedQrQuery.data ?? null;
  // The version the next PUT is conditional on (If-Match): the last save's, a 409's `currentVersion`, else the reload's.
  // None known → none sent (the server accepts that). A save after a sign may land on a new row; its response carries both.
  const [qrVersionOverride, setQrVersionOverride] = useState<string | null>(null);
  // Another writer changed the stored note (409): the answers stay, autosave stops, and the clinician chooses "Save again".
  // Tied to its encounter, like `signLockRefusalFor`: another encounter opened in this page autosaves as usual.
  // `reading`: the stored note is being read again for its row and version ("Save again" waits); `readFailed`: that read
  // failed, so "Save again" reads it again first.
  const [qrConflictFor, setQrConflictFor] = useState<{ encounterId: string | null; reading: boolean; readFailed: boolean } | null>(null);
  const qrConflict = qrConflictFor && qrConflictFor.encounterId === (encounterId ?? null) ? qrConflictFor : null;
  // The encounter on screen NOW, for a read that resolves later: a row and version read for one encounter never reach another.
  const encounterIdNowRef = useRef(encounterId ?? null);
  encounterIdNowRef.current = encounterId ?? null;
  // Read the stored note again after a conflict and adopt its row and version; the answers on screen stay as they are.
  function rereadStoredNote() {
    const forEncounter = encounterId ?? null;
    const settle = (readFailed: boolean) =>
      setQrConflictFor((c) => (c && c.encounterId === forEncounter ? { ...c, reading: false, readFailed } : c));
    setQrConflictFor({ encounterId: forEncounter, reading: true, readFailed: false });
    savedQrQuery
      .refetch()
      .then(({ data: head, isError }) => {
        if (encounterIdNowRef.current !== forEncounter) return; // another encounter is open now: its row is not this one
        if (isError || !head?.id || !head.meta?.versionId) return settle(true);
        setQrId(head.id);
        setQrVersionOverride(head.meta.versionId);
        settle(false);
      })
      .catch(() => settle(true));
  }
  const qrVersionId = qrVersionOverride ?? currentRevision?.meta?.versionId ?? null;
  const signLockRefusal =
    signLockRefusalFor && signLockRefusalFor.encounterId === (encounterId ?? null) ? signLockRefusalFor : null;
  const qrHydratedForRef = useRef<string | null>(null);
  // Per-item Questionnaire metadata for QR wire fidelity (PROD-1354c): valueCoding for coded
  // answers, decimal-vs-integer by item type, repeats → string[] shape on reload.
  const qrItemMeta = useMemo(
    () => buildQrItemMeta(templateData?.questionnaire?.item),
    [templateData],
  );
  // Hydrate ONCE per encounter from the saved QR, so a reopened encounter shows its prior
  // answers. Gated on BOTH reads settling: the QR GET (data !== undefined) and the template read
  // (`repeats`/coding meta must be ready — a repeats single-selection hydrated as a scalar would
  // read as unselected in the DS renderer). Never clobbers an in-session edit: if the user typed
  // before the GET settled (`dirty`), their draft wins and the next save PUTs over the server row.
  //
  // PROD-2608: hydrate only what the form on screen can show. A saved answer whose question is not in
  // this template — or, once the template is known to differ from the one the note was started on,
  // whose question can no longer hold its value — is an ORPHAN: kept out of the form (so nothing is
  // misplaced into a changed structure), shown read-only under "Additional documentation", and
  // written back verbatim on every save (`orphans` → `preserved` in handleSave).
  const [orphans, setOrphans] = useState<OrphanedAnswer[]>([]);
  // Mirrors `qrHydratedForRef` as state, so render can hold the step shell until the one-shot
  // hydrate has run (the ref alone does not re-render).
  const [hydratedFor, setHydratedFor] = useState<string | null>(null);
  // G11 — WITHOUT A TEMPLATE, THE ENCOUNTER STAYS UP. A template that could not be read keeps the shell
  // mounted with no template (see the step-shell guard below), and so does every re-read after that
  // failure (Retry, a focus or reconnect refetch): the shell is already on screen, so it is HELD —
  // never swapped back for the empty first-load slot — until the template is back and the saved answers are
  // hydrated into it. A swap would unmount an open note editor and drop its draft. Declared here,
  // ahead of every early return, because the sign handlers read it too.
  const templateHeld =
    templateFailedBefore &&
    !isError &&
    (isLoading || !templateData || (drift === 'pending' && hydratedFor !== encounterId));
  const templateUnavailable = isError || templateHeld;
  useEffect(() => {
    if (!encounterId || savedQrQuery.data === undefined || isLoading || !templateData) return;
    // Whether the template changed decides how strictly answers are matched to it: wait for it.
    if (drift === 'pending') return;
    if (qrHydratedForRef.current === encounterId) return;
    qrHydratedForRef.current = encounterId;
    setHydratedFor(encounterId);
    const saved = savedQrQuery.data;
    if (!saved) return; // fresh encounter — nothing persisted yet
    if (saved.id) setQrId(saved.id);
    const strict = drift === 'changed' || drift === 'unavailable';
    const { hydratable, orphans: found } = partitionSavedResponse(saved, qrItemMeta, strict);
    // Orphans are set even over a dirty draft: they are not in the form, so the draft cannot hold
    // them, and the next save must still carry them.
    setOrphans(found);
    if (!dirty) {
      setResponse(fromQuestionnaireResponseResource(hydratable, qrItemMeta));
      // PROD-2961: the lines' pieces come back with their text (a stale Delta is ignored where it is read).
      if (frameV2On) setNoteDeltas(noteDeltasFromResource(hydratable));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encounterId, savedQrQuery.data, isLoading, templateData, drift]);

  // Gender-scope the RENDERED questionnaire only (PROD-1497): opposite-sex top-level groups
  // (the `-male`/`-female` linkId convention) drop from what the view/renderer sees; the QR
  // serialization meta stays on the FULL template so answers saved under a filtered group still
  // round-trip untouched, and mergeSectionResponse preserves them on save.
  // DECLARED HERE, not beside its only use in the step-shell return below: that site sits AFTER
  // the `showRail` early return and the isLoading/isError guards, so a hook there would break the
  // rules of hooks — and the Risk bridge just below needs it, so it sits up with the QR hydrate.
  // PROD-3042: the template's OWN sex / age conditions, with the patient as launch context — its hidden context
  // items (`%patient.gender` …) and the answer-based enableWhen on them, and the SDC enableWhenExpression /
  // answerOptionsToggleExpression forms. Nothing is hidden that the template does not mark; no sex or birth date
  // on record hides nothing; a disabled item's answer is not saved (lib/patient-context.ts).
  // BOTH frames, deliberately: it is driven by template content only (a template with no condition comes back
  // as the same reference, so switch OFF is unchanged today), and a template that carries hidden context items
  // would otherwise break OFF too — the DS renderer cannot read `%patient`, so it would draw the empty context
  // item and hide every item gated on it, for every patient.
  // PROD-3040 · frame v2 only, and AFTER the patient scope (which reads the `ctx-` items' calculatedExpression):
  // an item the template fills from the chart (a launch-context calculatedExpression + readOnly, the carrier
  // QuestionBank stores) is an ordinary item carrying its population expression — filled read-only when the
  // record holds the value, asked (and saved as an answer) when it does not. OFF unchanged.
  // The ONE scoping order (lib/visit-questionnaire.ts), shared with the live-dev story.
  const patientScope = useMemo(
    () =>
      dsTemplateQuestionnaire
        ? visitQuestionnaire(dsTemplateQuestionnaire, templateData?.questionnaire, { sex: demographics?.sex, birthDateIso: detailHeader.dobIso }, frameV2On)
        : undefined,
    [dsTemplateQuestionnaire, templateData?.questionnaire, demographics?.sex, detailHeader.dobIso, frameV2On],
  );
  const dsQuestionnaire = patientScope?.questionnaire;
  const patientExclusions = patientScope?.exclusions ?? NO_EXCLUSIONS;
  // PROD-2816 (G3, design-desk D-45) · frame v2 ONLY: a READOUT (read-only, value-box) is drawn as
  // one line of text — its value, or "<label> — not available in this view yet" — never as an
  // empty disabled box, and never hidden. Applied to what the VIEW renders only; the Risk bridge
  // and the QR serialisation keep the gender-scoped questionnaire above. Memoised on the readouts'
  // values, so ordinary keystrokes keep the questionnaire identity the DS renderer memoises on.
  // OFF → the same `dsQuestionnaire` reference as before.
  const readoutKey = frameV2On ? readoutValuesKey(dsQuestionnaire, response) : '';
  const renderedQuestionnaire = useMemo(
    () =>
      frameV2On && dsQuestionnaire
        ? toReadoutLines(dsQuestionnaire, parseReadoutValuesKey(readoutKey))
        : dsQuestionnaire,
    [frameV2On, dsQuestionnaire, readoutKey],
  );

  // RISK (PROD-2577; closes PROD-2354 — Ahmed 2026-09-24). The Risk step renders the template's
  // Fall / Pain groups (the DS controls keep their names, `fall-assessment` / `wong-baker-pain`; since the
  // 7 Oct 2026 ruling the current tools are CDC STEADI and NRS 0–10, and Morse / Wong-Baker answers are an
  // old record's read-only history) through the same DS renderer as every other step,
  // so their answers sit in `response` while on screen — but they persist to EncounterMetaData
  // Fall/Pain (the store Flutter reads), never to the QR: `mirror` forwards them from
  // `handleResponseChange`, `handleSave` strips them. DECLARED AFTER the QR hydrate effect above
  // on purpose: its hydrate merges onto the response that one REPLACES, and effects run in order.
  // PROD-3128 (Andrew, answer (b)) · frame v2: on a template with a "Fall risk present" item and no CDC STEADI, the
  // STEADI follow-up opens under Yes (never Morse; never for a minor). Its answers live in the Fall record only.
  const steadiFollowUp = useMemo(
    () => (frameV2On ? steadiFollowUpFor(dsQuestionnaire?.item, { birthDateIso: detailHeader.dobIso }) : undefined),
    [frameV2On, dsQuestionnaire, detailHeader.dobIso],
  );
  // `?x=nurse-note-card` (Andrew, 4 Nov): the pre-visit's "Notes from nurse" card, read-only, from the visit's Fall / Pain reads.
  const [nurseNoteOn] = useState(() => isEncounterFlagOn('nurseNoteCard'));
  const riskBridge = useRiskBridge({
    encounterId,
    items: dsQuestionnaire?.item,
    steadiFollowUp,
    // The page's own hydrate flag, set in the same batch as its whole-response `setResponse` — so
    // the Risk merge can never run before it (PROD-2608 also holds that hydrate for the template
    // drift comparison, which a "saved QR loaded" check would miss).
    responseHydrated: !!encounterId && hydratedFor === encounterId,
    setResponse,
    // PROD-2947 · frame v2 (Andrew's walk, 7 Oct): Fall / Pain save on a pause (B5); the pain scale is a QR answer.
    frameV2: frameV2On,
  });
  // PROD-3142 (design desk D-78): a Fall / Pain list someone changed after this visit was opened. While it stands: the
  // sentence above the canvas, that list frozen, and no sign (doctor or nurse) — a signature would attest a list this
  // clinician has not seen.
  const riskConflictSentence = riskChangedElsewhereSentence(riskBridge.saveConflict);
  const riskSignReason = riskChangedSignReason(riskBridge.saveConflict);

  // ── PROD-1654 failed-load LATCH ──────────────────────────────────────────────
  // A genuine reload failure (5xx/network — the hook maps the contract's "none saved yet" 404
  // to data:null, isError:false) must stay blocking until SERVER TRUTH is re-established — not
  // merely until the query stops erroring. Without the latch, a later successful background
  // refetch (e.g. window-focus) delivers the row id, isError clears, and autosave would PUT a
  // blank-started draft over the full unseen answers — right after the strip promised the
  // opposite (Codex P1, pass 2).
  //   SET: any reload error while NO document id is known — covers the first-load failure
  //   (data undefined) AND a refetch failure over a cached "none" (data null): both mean the
  //   server row state is UNKNOWN, so a blind POST could duplicate/shadow a row another
  //   tab/device created meanwhile (Codex P2, pass 3). A refetch error over a cached ROW keeps
  //   its known id and stays writable — same-row PUTs are safe.
  //   CLEAR (only once the query is no longer erroring):
  //   · data === null  → the server AUTHORITATIVELY confirmed no row exists; a create is as
  //     safe as any fresh encounter, dirty or not — holding here would strand the clinician's
  //     draft behind a reload that discards it (Codex P1, pass 3);
  //   · data is a row AND the draft is clean → the one-shot hydrate effect above has seeded
  //     the draft from the server row (reconciled).
  //   · data is a row AND the draft is DIRTY → hold: the strip keeps saying reload, and a
  //     reload rehydrates from the server before any save can fire.
  const [qrLoadFailed, setQrLoadFailed] = useState(false);
  useEffect(() => {
    if (savedQrQuery.isError && !effectiveQrId) {
      setQrLoadFailed(true);
      return;
    }
    if (!savedQrQuery.isError && qrLoadFailed && savedQrQuery.data !== undefined) {
      if (savedQrQuery.data === null || !dirty) setQrLoadFailed(false);
    }
  }, [savedQrQuery.isError, savedQrQuery.data, dirty, qrLoadFailed, effectiveQrId]);

  // ── Assessment & Plan (PROD-1297) ──────────────────────────────────────────
  // The A/P canvas is template-INDEPENDENT (gate-relax) — it mounts even with no HPI
  // template. providerId = the practitioner placing orders (performer); gender narrows
  // sex-specific lab panels. Both feed the coded order pickers the view owns.
  const providerId = user?.practitionerId ?? user?.id;
  const postHistoryReviewEvent = useL360HistoryReviewEvent();
  const postLifestyleIntakeEvent = useL360LifestyleIntakeEvent();
  const postCareModelInputEvent = useL360CareModelInputEvent();
  const apGender = demographics?.sex ?? undefined;

  // Working canvas state — the lossy DS shape (DxCard[]) + the two app-side side-maps that
  // carry what the canvas drops (order payloads, Dx coding) + the document identifier that
  // drives POST-vs-PUT. Seeded from the loaded assessment; mutated locally by the callbacks.
  const assessmentQuery = useEncounterAssessment(patientId, encounterId);
  const [diagnoses, setDiagnoses] = useState<DxCard[]>([]);
  /**
   * The diagnosis list as the last COMMITTED update left it.
   *
   * Kept because a decision and its write have to read the same value. `diagnoses` above is the
   * render's captured array, and Codex found the case where that lies: 19 on the canvas and a
   * twentieth landing while an ambient diagnosis awaits terminology resolution — the captured read
   * says there is room, the updater sees 20 and refuses, and the panel then hides a finding the
   * chart never received.
   *
   * A ref, not an await. Two earlier attempts took the answer out of the updater asynchronously: a
   * `setTimeout(0)` flush did not catch it, and resolving a promise from inside the updater
   * DEADLOCKED — `await handleAddDx(...)` waits on a React render that is waiting on the accept
   * handler to yield. A mirror is written synchronously by whichever updater ran last, so the
   * decision reads current state with no scheduling involved at all.
   */
  const diagnosesRef = useRef<DxCard[]>([]);
  /**
   * The ONLY way the diagnosis list is written.
   *
   * Wraps `setDiagnoses` so the mirror cannot drift: a call site that forgot to update it would
   * reintroduce exactly the stale-read defect the mirror exists to remove, and there are seven
   * write paths. Accepts the same updater-or-value shape as `setDiagnoses`, so it is a drop-in.
   */
  const writeDiagnoses = useCallback(
    (next: DxCard[] | ((prev: DxCard[]) => DxCard[])) => {
      setDiagnoses((prev) => {
        const value = typeof next === 'function' ? (next as (p: DxCard[]) => DxCard[])(prev) : next;
        diagnosesRef.current = value;
        return value;
      });
    },
    [],
  );
  // v2 (PROD-1422): orders are a TOP-LEVEL list (not nested in DxCard), each linking 1..N diagnoses
  // via `dxIds`. Kept as a peer of `diagnoses`; the payloads/dxCoding side-maps are unchanged.
  const [orders, setOrders] = useState<PlanOrder[]>([]);
  /** The orders as of the latest render, for a check made later than the render that closed over them. */
  const ordersRef = useRef<PlanOrder[]>([]);
  ordersRef.current = orders;
  const [payloads, setPayloads] = useState<OrderPayloadMap>(() => new Map());
  /**
   * PROD-3182. The plan as the ambient panel reads it: every diagnosis and every order, so a
   * suggestion already on the plan says "Already added" and offers no Accept.
   */
  const ambientPlanItems = useMemo(() => toPlanItems(diagnoses, orders, payloads), [diagnoses, orders, payloads]);
  const [dxCoding, setDxCoding] = useState<DxCodingMap>(() => new Map());
  // Patient-education articles (PROD-1454) — TOP-LEVEL, Dx-less (assessmentPlanArticles).
  const [articles, setArticles] = useState<EducationArticle[]>([]);
  // Feedback for a refused diagnosis add (duplicate, or the 20 cap) — Flutter uses a toast; this app
  // surfaces it as the canvas notice, which is the same affordance the "add a diagnosis first" hint uses.
  const [dxNotice, setDxNotice] = useState<string | null>(null);
  /**
   * PROD-3182. An order a clinician's add found already on the plan, held for "Order anyway". A second
   * order of the same drug can be right (a split dose), so the refusal is the default and never final:
   * the canvas notice says what is already there and offers the override, as order sets do.
   */
  const [heldDuplicate, setHeldDuplicate] = useState<
    {
      dxIds: string[];
      planOrder: PlanOrder;
      payload: CodedOrderPayload;
      existing: string;
      /** The plan the refusal was made against. A plan that has changed since makes it stale. */
      ordersAt: PlanOrder[];
      diagnosesAt: DxCard[];
    } | null
  >(null);
  // The A/P SECTION narrative (PatientAssessment.notes/notePlainText — Flutter's commentButton).
  // Hydrated once from the GET like every other canvas slice; MUST be fed into every save, because the
  // save mapper used to write null here and destroy a note authored in Flutter (PROD-1801).
  const [assessmentNote, setAssessmentNote] = useState('');
  /** The same narrative as a Quill Delta (the `notes` wire field). Held ALONGSIDE the plain text
   *  so formatting survives a round-trip and so the save writes a Delta into `notes` — a bare
   *  plaintext string there renders blank in the Flutter client (src/dashboard/quill-delta.ts). */
  const [assessmentNoteDelta, setAssessmentNoteDelta] = useState<string | undefined>(undefined);
  const [assessmentIdentifier, setAssessmentIdentifier] = useState<string | null>(null);
  const [medQuery, setMedQuery] = useState('');

  // ORDER SETS (PROD-2712): apply a set from the A/P rail. Behind `VITE_ORDER_SETS_APPLY` (default
  // off) AND physician-only (`mayOrder`, the same gate the Settings › Order sets tab uses). With
  // either off the hook fetches nothing and every handler below that calls it is a no-op, so the
  // page — Bundles included — behaves exactly as before. Set orders go through `handleAttachOrder`,
  // the single-order attach path; see use-order-set-apply.ts.
  const orderSetsActive = canApplyOrderSets(env.orderSetsApplyEnabled, mayOrder);
  const signedRef = useRef(false);
  /**
   * A PROVIDER sign, current at the write (see `providerSigned`). NOT `signedRef`: that is also true
   * after a nurse-only sign, which does not lock the note — the physician documents after it.
   */
  const providerSignedRef = useRef(false);
  /**
   * Whether an ambient accept may write to the A&P canvas RIGHT NOW: not while the canvas is
   * read-only (the load has not settled, or a sign is promoting), not once the encounter is signed,
   * and not while a sign-lock request is in flight. A ref, written at render next to `signedRef`,
   * because the ambient diagnosis accept awaits a Master lookup before it writes: the value it must
   * read is the one current when it writes, not the one captured when the click arrived.
   */
  const apWriteBlockedRef = useRef<'loading' | 'signing' | 'signed' | null>(null);
  /**
   * A&P rows an ambient accept added that no save has carried to the server yet, each with the
   * resolver of the `persisted` promise the accept handed the panel. The panel records the decision
   * as Written only when it resolves true — see `settlePersisted` and `AcceptReceipt.persisted`.
   */
  const persistWaitersRef = useRef<Map<string, Array<(persisted: boolean) => void>>>(new Map());
  /** Client-minted diagnosis row ids a successful A&P save has carried. Loaded rows are in `serverIdsRef`. */
  const savedDxIdsRef = useRef<Set<string>>(new Set());
  /** A promise that settles when this row reaches the server (true) or is removed first (false). */
  function whenPersisted(rowId: string): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const waiting = persistWaitersRef.current.get(rowId) ?? [];
      persistWaitersRef.current.set(rowId, [...waiting, resolve]);
    });
  }
  /** Settles every waiter on these rows, and forgets them. */
  function settleWaiters(dxIds: Iterable<string>, persisted: boolean) {
    for (const id of dxIds) {
      const waiting = persistWaitersRef.current.get(id);
      if (!waiting) continue;
      persistWaitersRef.current.delete(id);
      waiting.forEach((resolve) => resolve(persisted));
    }
  }
  /**
   * Assessment-text lines an ambient accept appended (rule 5, `append-assessment`) that no save has
   * carried yet, each with the resolver of its `persisted` promise. The same rule as a diagnosis
   * row: "written to chart" only once an A&P save whose narrative contains the line succeeded.
   *
   * Lines are matched EXACTLY (`noteHasLine`), never as substrings: a longer, edited line is not
   * the line the accept wrote.
   *
   * `inserted` says whether THIS accept put the line there. False when the clinician's own unsaved
   * narrative already held it, exactly: the accept appended nothing, so the line is hers, and
   * Discard must not take it out (#539 follow-up, Codex P1). Settled FALSE by Discard (an inserted
   * line, which Discard also removes), or as soon as the narrative stops holding the line exactly
   * (she deleted or edited it): then no save will ever carry it, and the card goes back to review.
   */
  const noteWaitersRef = useRef<
    Array<{ line: string; inserted: boolean; resolve: (persisted: boolean) => void }>
  >([]);
  /** The narrative as the server last held it: seeded from the loaded document, then each successful save. */
  const savedNoteRef = useRef('');
  function whenLineSaved(line: string, inserted: boolean): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      noteWaitersRef.current = [...noteWaitersRef.current, { line, inserted, resolve }];
    });
  }
  /**
   * A save of the A&P just succeeded with these diagnosis rows and this narrative in its body:
   * every waiting accept whose row, or whose appended line, was in it is now in the chart.
   */
  function settlePersisted(savedDxIds: readonly string[], savedNote?: string) {
    savedDxIds.forEach((id) => savedDxIdsRef.current.add(id));
    settleWaiters(savedDxIds, true);
    if (savedNote === undefined) return;
    savedNoteRef.current = savedNote;
    const carried = noteWaitersRef.current.filter((w) => noteHasLine(savedNote, w.line));
    if (carried.length === 0) return;
    noteWaitersRef.current = noteWaitersRef.current.filter((w) => !carried.includes(w));
    carried.forEach((w) => w.resolve(true));
  }
  /**
   * A waiting line that is no longer one of the narrative's lines (she deleted or edited it before
   * any save carried it) will never be carried: settle it NOT written now, so the card goes back to
   * review. Waiting for a save would wait for ever when the deletion restores the saved narrative,
   * because then nothing is dirty and no save fires (#539 follow-up, Codex P2). Only resolvers run
   * here, never a state update; the panel's own update follows the promise.
   */
  useEffect(() => {
    const gone = noteWaitersRef.current.filter((w) => !noteHasLine(assessmentNote, w.line));
    if (gone.length === 0) return;
    noteWaitersRef.current = noteWaitersRef.current.filter((w) => !gone.includes(w));
    gone.forEach((w) => w.resolve(false));
  }, [assessmentNote]);
  const orderSetApply = useOrderSetApply({
    enabled: orderSetsActive,
    encounterId,
    patientId,
    diagnoses,
    orders,
    payloads,
    articles,
    attachOrder: handleAttachOrder,
    discardOrder: (orderId) => {
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
      setPayloads((prev) => removeOrderPayload(prev, orderId));
    },
    addArticle: handleAddArticle,
    removeArticleByDisplay: (display) => {
      const key = display.trim().toLowerCase();
      setArticles((prev) => prev.filter((a) => a.display.trim().toLowerCase() !== key));
    },
    addDx: handleAddDx,
    isSigned: () => signedRef.current,
    setVidalAlerts: (orderId, alerts) =>
      setPayloads((prev) => {
        const p = prev.get(orderId);
        return p?.kind === 'medication' ? setOrderPayload(prev, orderId, { ...p, vidalAlerts: alerts }) : prev;
      }),
  });

  const saveAssessment = useSaveAssessment();

  // Wrap-Up status header (PROD-1410): order-sent + count derived from the fetched assessment.
  const orderStatus = assessmentOrderStatus(assessmentQuery.data, frameV2On ? { distinct: true } : undefined);
  // Optimistic "sent" latch: the fetched flag only flips true after the send-save's refetch returns,
  // so a content edit in that window would autosave with orderSent=false and UN-send. This ref makes
  // "sent" sticky from the moment Send succeeds; it's OR'd with the fetched flag everywhere. Reset per
  // encounter (below) so a new encounter starts un-sent.
  const sentOnceRef = useRef(false);
  useEffect(() => {
    sentOnceRef.current = false;
  }, [encounterId]);
  const orderSent = orderStatus.orderSent || sentOnceRef.current;
  // PROD-1690: true for the WHOLE sign-promotion sequence (order-sign A&P save → sign-lock settled).
  // Declared here (not at the sign handler) because it also gates the canvas read-only below —
  // an edit made while the promotion PUT is in flight would otherwise be finalized UNPERSISTED:
  // the autosave can only flush it after isPending clears, i.e. after (or racing) the lock (Codex P1).
  const [signPromoting, setSignPromoting] = useState(false);
  /**
   * DISCARD THIS NOTE — Flutter's third footer button (`showDiscardNoteDialog`,
   * `generated_notes_widget.dart:502-514` → `discardEncounter()`, `patient_encounter_bloc.dart
   * :2595-2598` → `DiscardAppointmentEvent`, `:1749`-family handler).
   *
   * Flutter does NOT delete anything. It CLEARS each section by writing an empty one:
   *   createPatientComplaintsUseCase(data: null)   complaints
   *   createPeUseCase(items: [])                   PE
   *   createRosUseCase(items: [])                  ROS
   *   createApUseCase(empty PatientAssessment)     A/P
   *   createWrapUpUseCase(...)                     wrap-up
   * Vitals are NOT touched — and "do as flutter" (Andrew, 2026-08-27) settles that they stay.
   *
   * WHY THE SHAPE DIFFERS HERE. Flutter's per-section writes have no React counterpart: HPI, ROS,
   * PE, Risk and Docs are ONE `QuestionnaireResponse` in this app (the PROD-1234 divergence), so
   * three of Flutter's five calls collapse into clearing that one document. A consequence worth
   * naming: clearing it also clears DOCS, which Flutter's discard leaves alone because it has no
   * write for them. That is forced by the data model, not chosen. RISK is no longer in that
   * document (PROD-2577 — it persists to EncounterMetaData), so it is left alone, as in Flutter.
   *
   * NO SECOND PERSIST SHAPE. The QR and A/P both autosave off their own state (the debounced
   * handleSave, and the apSnapshot autosave), so this clears STATE and lets the proven paths write
   * it — exactly how every other edit persists. Only the chief complaint and wrap-up are written
   * directly, because neither has a local-state autosave: CC persists through `persistCc` on
   * change, and wrap-up is server-state driven.
   *
   * NOT gated on `signed`, matching Flutter: its footer swaps only the FIRST slot for the "Signed"
   * pill (`:376-411`) — View PVS and Discard render unconditionally. Flagged to Andrew as a
   * clinical oddity (discarding a signed note); parity is his call and this follows it.
   */
  const [pvsOpen, setPvsOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  function handleDiscardNote() {
    // 1. Chief complaint — replace-all with empty (the PROD-1588 seam, same write a removal uses).
    persistCc([]);
    // 2. The questionnaire: HPI + ROS + PE + Docs live in this one document. The Risk answers are
    //    KEPT: they are EncounterMetaData's (PROD-2577), which Flutter's discard never writes —
    //    clearing them here would blank the Risk step over a stored entry the next Risk edit
    //    would then overwrite from nothing. The free NRS / STEADI answers are kept with them: they are
    //    projected onto the same entries (PROD-2791), which this discard does not write either.
    setResponse((prev) => ({ ...prev, answers: riskStepAnswersOf(prev, riskBridge.scope, riskBridge.free) }));
    markDirty(); // let the debounced QR autosave persist the cleared document
    // 3. A/P — the canvas autosave picks the emptied snapshot up. Through `writeDiagnoses`, the one
    //    writer that keeps the mirror true: a bare `setDiagnoses([])` left `diagnosesRef` holding the
    //    discarded rows, so the next ambient accept judged itself a duplicate of a row that was gone.
    //    Any ambient accept still waiting on a save never reached the chart.
    settleWaiters([...persistWaitersRef.current.keys()], false);
    // An appended assessment line no save carried is settled NOT written, so the line goes too —
    // left in the narrative, the next autosave would save the "Suspected: X" the record says was
    // never written (#539 re-review). Only that line, only while it is still verbatim: an edited
    // line is the clinician's and stays.
    // ONLY A LINE THIS ACCEPT INSERTED: a line the clinician had already written is hers, and the
    // accept that found it there appended nothing, so it owns nothing to take back. That receipt
    // keeps waiting: her line stays in the narrative, and the save that carries it settles it.
    {
      const inserted = noteWaitersRef.current.filter((w) => w.inserted);
      let plain = assessmentNote;
      let delta = assessmentNoteDelta;
      for (const w of inserted) {
        const taken = removeLineFromNotePair(plain, delta, w.line);
        if (taken) ({ plain, delta } = taken);
      }
      if (plain !== assessmentNote) setAssessmentNote(plain);
      if (delta !== assessmentNoteDelta) setAssessmentNoteDelta(delta);
      noteWaitersRef.current = noteWaitersRef.current.filter((w) => !w.inserted);
      inserted.forEach((w) => w.resolve(false));
    }
    writeDiagnoses([]);
    setOrders([]);
    /*
     * 4. Wrap-up instructions — server-state driven, so written explicitly.
     *
     * GUARDED ON `wrapUpQuery.isFetched` (PROD-1549, 2026-09-09). `wrapUpQuery` is enabled only
     * while `wrapUpEnabled` (`wrapActive && !isNurse`), so on the PRE-ENCOUNTER surface — where
     * the note now carries a Discard pill for the first time — it never runs and
     * `wrapUpQuery.data` is `undefined`. `buildWrapUpInstructionsRequest` with no `existing`
     * builds a CREATE, so discarding before Start would `POST /api/Patients/CreateWrapUp` for an
     * encounter that has not begun, and a DUPLICATE row on a resumed one.
     *
     * Flutter DOES post CreateWrapUp on discard (`patient_encounter_bloc.dart:833-871`), so this
     * is a deliberate NARROWING, not parity: we decline to write a wrap-up we never read rather
     * than create one blind. Pending Andrew's ruling on Discard-before-Start (see FINDINGS §7).
     * The other three discard writes are unaffected — they all target records this surface owns.
     */
    if (patientId && encounterId && wrapUpQuery.isFetched) {
      saveWrapUp.mutate(
        buildWrapUpInstructionsRequest('', { patientId, encounterId, existing: wrapUpQuery.data }),
        { onError: (err) => showServerError('Discarding the note', err) },
      );
    }
    setDiscardOpen(false);
  }

  // Sign confirmation (Flutter asks before signing; React fired on one unguarded click).
  const [signConfirmOpen, setSignConfirmOpen] = useState(false);

  // ── Interaction gate (Flutter-faithful) ────────────────────────────────────
  // The canvas is INTERACTIVE once the initial load has settled — and STAYS interactive during a save
  // (Flutter parity — see apReadOnly below). The pre-seed window is still closed (read-only until the
  // load settles, so no edit precedes the seed). Edits made DURING a save are safe: they mutate local
  // state and the autosave effect's `apBusy` guard serializes the actual PUTs (one follow-up save after
  // the in-flight one settles — never a concurrent PUT).
  const apReady = assessmentQuery.isSuccess; // initial load settled
  const apBusy = saveAssessment.isPending; // a save is in flight
  // Read-only ONLY until the initial load settles — NOT while a save is in flight. Flutter never
  // locks the A/P (nor hides the search rail) during a save: a Dx edit is local (`updateDiagnosis`),
  // and the PUT fires later on nav/step/sign, gated on `isModified` — the surface stays interactive.
  // Gating on `apBusy` here unmounted the left order rail (assessment-plan-view.tsx renders it only
  // when !readOnly) for the whole save; with the slow BE PUT that read as "the left side vanished"
  // (PROD-1429). Overlapping saves are already prevented by the autosave effect's own `apBusy` guard
  // below, so read-only does not need it. A subtle "Saving…" notice still signals the in-flight save.
  // PROD-1690 exception to the "never read-only during a save" rule (PROD-1429): the SIGN-promotion
  // sequence freezes the canvas. The clinician has clicked Sign & Lock — the encounter is finalizing —
  // and an edit accepted mid-sequence could not be persisted before the lock (Codex P1). This is
  // Flutter-faithful too (the bloc's sign path runs under a blocking isProcessing spinner).
  const apReadOnly = !apReady || signPromoting;

  // Content snapshot for dirty-detection — deliberately ID-INDEPENDENT. It hashes the CLINICAL
  // content (each Dx's code/display in list order; each order's type/label + its coded payload), but
  // NOT the node ids. So adopting server-assigned ids after a save (client id → server id, same
  // clinical content) does NOT read as a change and cannot re-fire the autosave (loop-proof), while
  // a genuine edit (add/remove/reorder a Dx or order, change a payload) still flips it.
  function apContentSnapshot(
    d: DxCard[],
    o: PlanOrder[],
    p: OrderPayloadMap,
    c: DxCodingMap,
    a: EducationArticle[],
    note: string,
    noteDelta: string | undefined,
  ): string {
    return JSON.stringify({
      // The A/P narrative, BOTH halves (A3 finding 12). It was left out, so a narrative-only edit
      // never read as dirty: it saved only when something else changed, and a Sign on already-sent
      // orders skipped the A/P save and left it unsaved. The delta is in too, so a formatting-only
      // edit persists. Rule 5 depends on this — the ambient `append-assessment` writes ONLY here.
      narrative: { note, delta: noteDelta ?? null },
      // Education articles (Dx-less) — by display+url so add/remove flips the hash → autosave fires.
      articles: a.map((art) => ({ display: art.display, articleUrl: art.articleUrl ?? null })),
      // Dx clinical content + the one-primary flag (v2 PROD-1422: isPrimary is authoritative, not
      // position — so a primary toggle re-fires the autosave). ID-independent (see the effect notes).
      diagnoses: d.map((card) => ({
        code: c.get(card.id)?.code ?? card.code ?? null,
        display: c.get(card.id)?.display ?? card.display ?? null,
        isPrimary: card.isPrimary === true,
      })),
      // Each order's clinical content + its Dx links (by the linked Dx codes, so the hash is
      // id-independent and a re-link flips it). Orders are top-level now.
      orders: o.map((order) => ({
        type: order.type,
        label: order.label,
        dxCodes: order.dxIds.map((id) => c.get(id)?.code ?? id),
        payload: p.get(order.id) ?? null,
      })),
    });
  }

  // Tracks the last-persisted (or freshly-hydrated) content so the autosave only fires on a
  // genuine local change — not on the post-save re-hydration, and not on initial hydration.
  const lastSavedSnapshotRef = useRef<string | null>(null);
  // The content snapshot whose save FAILED. Without this, a failed save left the content "dirty"
  // forever, so every isPending true→false cycle re-fired the IDENTICAL body — an infinite retry
  // that flipped readOnly/"Saving…" each pass (visible flicker) and hammered the endpoint
  // (PROD-1334). We refuse to auto-resend this exact content; a further edit changes the snapshot →
  // the guard releases → the save retries. Cleared on a successful save.
  const lastFailedSnapshotRef = useRef<string | null>(null);
  const [apSaveError, setApSaveError] = useState(false);
  // The Dx/order ids the SERVER has assigned (populated from the loaded/re-hydrated doc). These are
  // echoed back as node identifiers on the next save so a PUT updates child rows in place instead of
  // duplicating them. Client-only ids (just-added, never persisted) are absent → they save with a
  // null identifier so the BE creates them; the post-save re-hydration then adopts the server ids.
  const serverIdsRef = useRef<Set<string>>(new Set());

  // Collect every server-assigned node id (Dx + orders) from a fetched/saved canvas state.
  // Client-synthesized fallback ids (isSyntheticId) are NOT server-assigned — exclude them so the
  // save mapper doesn't echo a never-persisted order's fallback id back as a server identifier.
  function collectServerIds(diags: DxCard[], ords: PlanOrder[]): Set<string> {
    const ids = new Set<string>();
    for (const d of diags) if (d.id && !isSyntheticId(d.id)) ids.add(d.id);
    for (const o of ords) if (o.id && !isSyntheticId(o.id)) ids.add(o.id);
    return ids;
  }

  // ── Seed once, on the first successful load ─────────────────────────────────
  // Seed ONCE, on the first successful load. The canvas is read-only until apReady, so no local
  // edit can precede the seed → no clobber. The seed adopts the server doc's ids into serverIdsRef,
  // so editing an EXISTING (loaded) assessment PUTs with server child ids (rows update in place).
  // After a brand-new CREATE the local canvas keeps its client ids until the next reload re-seeds —
  // a mid-session residual (the BE's child-dedup behavior is un-verified here); on reload it
  // reconciles. We do NOT re-hydrate from the post-save query refetch: that re-entrant adopt looped
  // the autosave, and the save response is only the id anyway.
  const seededRef = useRef(false);
  useEffect(() => {
    if (!assessmentQuery.isSuccess || seededRef.current) return;
    const seeded = patientAssessmentToCanvas(assessmentQuery.data);
    writeDiagnoses(seeded.diagnoses);
    setOrders(seeded.orders);
    setPayloads(seeded.payloads);
    setDxCoding(seeded.dxCoding);
    setArticles(seeded.articles);
    setAssessmentNote(seeded.assessmentNote);
    savedNoteRef.current = seeded.assessmentNote ?? '';
    setAssessmentNoteDelta(seeded.assessmentNoteDelta);
    setAssessmentIdentifier(seeded.identifier);
    serverIdsRef.current = collectServerIds(seeded.diagnoses, seeded.orders);
    seededRef.current = true;
    // A freshly-hydrated document is NOT dirty — mark its content as the last-saved baseline so
    // the autosave doesn't immediately re-save the doc we just loaded.
    lastSavedSnapshotRef.current = apContentSnapshot(
      seeded.diagnoses,
      seeded.orders,
      seeded.payloads,
      seeded.dxCoding,
      seeded.articles,
      seeded.assessmentNote,
      seeded.assessmentNoteDelta,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentQuery.isSuccess, assessmentQuery.data]);

  // ── Canvas callbacks (immutable updates; mark dirty implicitly via state change) ──────
  /**
   * Flutter's cap (AppConstants.maxDiagnosisInAssessment = 20, app_constants.dart:257) with its toast
   * "Max 20 diagnosis can be added." (assessment_plan_screen.dart:410-415). React had NO cap, so it
   * would build an assessment larger than the Flutter reader and the BE assume.
   */
  const MAX_DIAGNOSES = 20;

  /**
   * What adding `dx` to `prev` WOULD do. Pure, and the single decision site for both the write
   * and the answer the caller gets back.
   *
   * Extracted because the ambient accept path has to report whether a tick reached the chart, and
   * `handleAddDx` used to make that decision inside its `setDiagnoses` updater — where the answer
   * is unreachable to the caller. The ambient branch therefore reported `'written'` on the 20-cap
   * refusal too, which is the SAME defect the accept-outcome change exists to fix, on the one
   * branch that change claimed to have made honest. Found by adversarial review, not by a test.
   */
  function decideAddDx(prev: DxCard[], dx: CodeSearchResult): 'added' | 'duplicate' | 'capped' {
    const id = dx.id || dx.code || `dx-${dx.display}`;
    const codeKey = dx.code?.trim().toLowerCase();
    const duplicate = prev.some(
      (d) => d.id === id || (!!codeKey && d.code?.trim().toLowerCase() === codeKey),
    );
    if (duplicate) return 'duplicate';
    if (prev.length >= MAX_DIAGNOSES) return 'capped';
    return 'added';
  }

  /**
   * Adds a diagnosis to the canvas and RETURNS WHETHER IT DID.
   *
   * The returned verdict is computed against `diagnoses` as of this render, while the write is
   * decided inside the `setDiagnoses` updater against the very latest state — deliberately, so the
   * dedupe and the cap keep the exact guarantee they had before, which a captured-array read would
   * weaken. The two can only disagree if another add lands in the same tick, in which case the
   * WRITE is authoritative and the verdict is one render stale.
   *
   * That residual is stated rather than hidden, and it is a far smaller problem than the
   * unconditional `'written'` it replaces: stale-by-one-render can misreport a double-click, where
   * unconditional could file a capped diagnosis as recorded.
   */
  function handleAddDx(dx: CodeSearchResult): 'added' | 'duplicate' | 'capped' {
    // THE MIRROR, not the captured array — see `diagnosesRef`.
    const verdict = decideAddDx(diagnosesRef.current, dx);
    const id = dx.id || dx.code || `dx-${dx.display}`;
    // Dedupe by id AND by normalized code (four-moments M2 / Codex: a reloaded visit dx carries
    // the AssessmentPlan child id while the picker's on-list result carries the chart Condition
    // id — same ICD-10 code, different ids; "same record, never a duplicate" is a code-level
    // contract on day 1).
    writeDiagnoses((prev) => {
      // SAME function as the verdict above, so the write and the report cannot drift apart.
      const outcome = decideAddDx(prev, dx);
      // SAY SO. Flutter toasts "<title> is already added." (assessment_plan_screen.dart:407-419);
      // React deduped SILENTLY, so a re-click looked like a broken app and users re-clicked again.
      if (outcome === 'duplicate') {
        setDxNotice(`${dx.display} is already added.`);
        return prev;
      }
      if (outcome === 'capped') {
        setDxNotice(`Max ${MAX_DIAGNOSES} diagnoses can be added.`);
        return prev;
      }
      setDxNotice(null);
      return [
            ...prev,
            {
              id,
              ...(dx.code ? { code: dx.code } : {}),
              display: dx.display,
              // v2 one-primary: the FIRST Dx added is primary; later ones are secondary until the
              // user promotes them (onSetPrimary). Matches Flutter's default-primary-first.
              isPrimary: prev.length === 0,
              // Flutter stamps every added diagnosis with `now` (new_ap_bloc.dart:711). Only a NEW Dx
              // gets one; a loaded Dx keeps the server's, which is what the mapper now preserves.
              onSetDate: new Date().toISOString(),
            },
      ];
    });
    // NOTE: on a code-deduped add this writes an orphan coding entry keyed by the unused id —
    // harmless (the save mapper iterates `diagnoses`, never the coding map) and self-limiting.
    setDxCoding((prev) =>
      setDxCodingEntry(prev, id, {
        ...(dx.code ? { code: dx.code } : {}),
        display: dx.display,
        ...(dx.system ? { system: dx.system } : {}),
      }),
    );
    return verdict;
  }

  /**
   * "Add to note" on an Active Problems row in the chart column (the started long page): the problem joins the
   * Assessment through `handleAddDx` (same dedup, cap, primary rule, onset stamp and write as picking it in the A/P's
   * "Patient's active problems" group), then the note moves to the Assessment. A duplicate or capped add says so
   * through the A/P's own notice (`dxNotice`), which sits in that section.
   */
  const [assessmentJump, setAssessmentJump] = useState(0);
  function handleAddProblemToNote(dx: CodeSearchResult) {
    // The same gate the rail's action is offered under (`mayAddProblemToNote`): never a write the A&P itself would refuse.
    if (!mayAddProblemToNote) return;
    handleAddDx(dx);
    setAssessmentJump((n) => n + 1);
  }

  /**
   * Ticking an ambient proposal (PROD-1972).
   *
   * The panel renders proposals; this decides what accepting one DOES. Before this existed the
   * panel's accept props were never passed, so `decideAccept` — fully written and 16-tests
   * covered — was reachable from nothing, and every checkbox in the review UI was enabled and
   * inert.
   *
   * Policy lives in `decideAccept`, not here. This function only gathers the context it needs,
   * asks it, and dispatches the answer onto surfaces that already exist. Read `accept-rules.ts`
   * before changing any of the four branches: the diagnosis one is safety-critical, because
   * Master's ICD codes are GENERATED by an LLM rather than retrieved, so a proposed code is a
   * lookup hint and never a fact.
   */
  async function handleAcceptProposal(proposal: Proposal): Promise<AcceptResult> {
    // Resolve the model's code against Master FIRST. `decideAccept` needs the candidates to
    // decide whether an auto-add is safe; with none it falls through to a seeded search, which
    // is the expected path and not a failure.
    //
    // RULE 5: an UNCONFIRMED diagnosis is decided by its certainty alone (`append-assessment`), and
    // no candidate list can change that — so it takes no Master round trip. That is also what keeps
    // its write honest: with no `await` before the switch, the read-only check below reads the
    // render the click came from, not one a Sign started while the lookup was in flight (Codex P1).
    //
    // PROD-3184: and only for a record the server never looked up. When Master matched the diagnosis
    // (or looked and found no single record), `decideAccept` uses ITS answer, so the model's own
    // `icdCode` is not looked up again (`needsModelCodeLookup`).
    const withoutCodes = decideAccept(proposal, { dxCount: diagnoses.length, resolved: [] });
    let resolved: ConceptRef[] = [];
    if (proposal.kind === 'diagnosis' && withoutCodes.action !== 'append-assessment' && needsModelCodeLookup(proposal)) {
      try {
        resolved = await resolveDiagnosisCode(proposal.source.icdCode);
      } catch {
        // A Master outage must NOT block the clinician. Falling through with `resolved: []`
        // makes `decideAccept` open a seeded search, which is exactly the right degradation:
        // they can still find and add the diagnosis by hand. Swallowed rather than surfaced
        // because the seeded search IS the user-visible outcome.
        resolved = [];
      }
    }

    // PROD-1977: `autoAddEnabled` is deliberately NOT passed, so a diagnosis is never written
    // without the clinician seeing it. QA found confirmed-disease codes assigned to unconfirmed
    // conditions in 3 of 4 scripts (E72.11 for a mildly raised homocysteine, R73.01 for a normal
    // HbA1c, G47.33 for an unscored OSA screen). All three are real codes that resolve cleanly,
    // so the exact-match guard would have passed them straight through. Restoring auto-add means
    // adding `autoAddEnabled: true` here -- do it only once the coding step stops over-asserting.
    const decision = decideAccept(proposal, { dxCount: diagnoses.length, resolved });

    switch (decision.action) {
      case 'resolve-schedule-contest':
        /**
         * PROD-2018 — THE REFUSAL, HONOURED. The transcript contradicts the structured schedule and
         * the clinician has not answered it, so `decideAccept` declined to produce a writing
         * decision. Nothing is seeded, nothing is opened, nothing is written, and `searchSeed` is
         * left alone so no dose dialog can inherit the contested frequency.
         *
         * `'awaiting-clinician'` is literally true, and it is what keeps the proposal in front of
         * her with the contest still on it.
         *
         * This case exists even though the panel renders no accept control on a contested card, and
         * that is the point of putting the gate in the decision rather than the card: ANY caller
         * reaching this function with an unresolved contest — a keyboard path, a future surface, a
         * test — gets the same refusal. That is the difference between a gate and a convention.
         */
        return 'awaiting-clinician';

      case 'add-diagnosis': {
        /**
         * REFUSED WHILE THE A&P CANNOT TAKE THE ROW — the rule `prepareAmbientRetract` already
         * applies to Undo. Before the load settles the seed overwrites the canvas, so the row would
         * vanish; while a sign is promoting or a sign-lock is in flight it could not be saved before
         * the lock; once signed the record is closed. Answering `'written'` on any of them filed a
         * decision "written to chart" for a row the chart never kept. Read from the ref, not from
         * `apReadOnly`: the Master lookup above is awaited, and a Sign can start during it.
         */
        const blocked = apWriteBlockedRef.current;
        if (blocked) {
          setDxNotice(
            blocked === 'signed'
              ? `This encounter is signed, so ${decision.concept.display} was not added to the Assessment & Plan.`
              : `The Assessment & Plan cannot be edited right now, so ${decision.concept.display} was not added. Try again in a moment.`,
          );
          return 'awaiting-clinician';
        }
        // Master's concept, never the model's wording — `decideAccept` guarantees this branch
        // only fires on an exact code match against a single result.
        // PROD-2650: a fresh GUID as the canvas row id, so the decision can name the row this accept
        // created (`WrittenEntityId` is a Guid) and Undo can take exactly that row back out. The
        // id is client-only: it is not in `serverIdsRef`, so it saves with a null identifier like
        // every other just-added row, and dedupe still runs on the code.
        const rowId = crypto.randomUUID();
        const verdict = handleAddDx({
          id: rowId,
          ...(decision.concept.code ? { code: decision.concept.code } : {}),
          display: decision.concept.display,
          ...(decision.concept.system ? { system: decision.concept.system } : {}),
        } as CodeSearchResult);
        /**
         * The only branch that can write — and it does NOT always. `handleAddDx` refuses on two
         * paths, each with its own notice:
         *
         *   - `capped`    — 20 diagnoses already on the canvas. Nothing is added. Reporting
         *                   `'written'` here would file the finding as recorded when the chart
         *                   never received it, which is the defect this whole change exists to
         *                   remove. It must stay in review.
         *   - `duplicate` — the diagnosis is ALREADY in the chart. Nothing was written by this
         *                   click, but the finding IS recorded, so the proposal is genuinely
         *                   discharged and keeping it in review would ask the clinician to chase
         *                   something already done.
         *
         * So the two refusals resolve differently on purpose: the question the panel is asking is
         * "is this finding in the chart", not "did my click perform an insert".
         */
        if (verdict === 'capped') return 'awaiting-clinician';
        // A duplicate is in the chart but this click did not put it there, so there is no row of
        // its own for an Undo to remove: `'written'` with no receipt.
        if (verdict === 'duplicate') {
          /**
           * The finding is on the canvas, but that row may itself be unsaved — added by hand a
           * moment ago, its autosave still pending or failed. Durable only when it came from the
           * server (`serverIdsRef`) or a save has carried it (`savedDxIdsRef`); otherwise the
           * record waits for it like any other local write. No `written`: this click made no row.
           */
          const existing = diagnosesRef.current.find(
            (d) => d.code?.trim().toLowerCase() === decision.concept.code?.trim().toLowerCase(),
          );
          if (!existing || serverIdsRef.current.has(existing.id) || savedDxIdsRef.current.has(existing.id)) {
            return 'written';
          }
          return { outcome: 'written', persisted: whenPersisted(existing.id) };
        }
        /**
         * WRITTEN TO THE CANVAS IS NOT YET WRITTEN TO THE CHART. The row is in local state now; the
         * autosave carries it to the server a moment later, and that save can fail (the canvas then
         * says "Couldn't save the assessment") or never happen (the clinician leaves first). The
         * card discharges now, because the row is on the canvas in front of her; the decision
         * record's "written to chart" waits for `persisted`, which resolves true when a save that
         * contained this row succeeds and false if the row is removed before any did.
         */
        const persisted = whenPersisted(rowId);
        return {
          outcome: 'written',
          written: { entityType: AP_DIAGNOSIS_ENTITY, entityId: rowId },
          persisted,
        };
      }

      case 'append-assessment':
        /**
         * RULE 5 (dev interim A6). A diagnosis the consultation did not confirm goes into the A&P
         * assessment TEXT, never the diagnosis block — our HL7 sends every encounter diagnosis as
         * Final. APPEND ONLY (I5): each half of the narrative pair is extended by a functional
         * updater, so the clinician's own text stays exactly as it was and a concurrent edit or a
         * second accept cannot be overwritten by a captured value. The canvas autosave persists it:
         * the narrative is part of the A&P content snapshot.
         *
         * Refused while the canvas is read-only, for the reason `prepareAmbientRetract` refuses:
         * before the load settles the seed would overwrite the line, and during the sign sequence
         * it could not be saved before the lock. Refused after a PROVIDER sign too
         * (`providerSignedRef`, current at the write rather than captured) — the note is locked —
         * but NOT after a nurse-only sign, which locks nothing: nurse intake, nurse sign, then the
         * physician's "Add to assessment" is the ordinary flow and must write. And refused while a
         * sign-lock request is in flight: a Sign on already-sent orders skips the promotion, so
         * `signPromoting` never rises and the autosave would race the lock (Codex P1). `'written'`
         * would then be a lie. EVERY refusal SAYS SO (`setDxNotice`, as add-diagnosis does): the
         * proposal stays pending, and silence would read as a dead button.
         *
         * "WRITTEN" FOLLOWS THE PERSISTED RULE (#534, F5): the line is local until an A&P save carries
         * it, so the answer carries `persisted` (`whenLineSaved`) and the panel records Written only
         * once a save whose narrative contains the line succeeded. No `written` receipt: there is no
         * structured row for an Undo to take out; the clinician edits or deletes the line in the
         * narrative, like any other text there. A line already in the narrative is not added twice
         * (the helpers return it unchanged): durable at once when a save has already carried it
         * (`savedNoteRef`), otherwise it waits for that save like a new line.
         */
        // A PROVIDER sign on the record, or one THIS page just made: `useCreateSignLock` re-fetches the
        // sign-lock read without waiting, so between its success and that read landing
        // `providerSigned` still says unsigned (Codex). `lock: true` is the provider's request.
        if (providerSignedRef.current || (createSignLock.isSuccess && createSignLock.variables?.lock === true)) {
          setDxNotice('This note is signed, so the line was not added to the assessment.');
          return 'awaiting-clinician';
        }
        if (!apReady) {
          setDxNotice('The assessment is still loading, so the line was not added. Try again in a moment.');
          return 'awaiting-clinician';
        }
        if (apReadOnly || createSignLock.isPending) {
          setDxNotice('The note is being signed, so the line was not added to the assessment.');
          return 'awaiting-clinician';
        }
        setDxNotice(null);
        {
          // EXACT lines, never substrings: "Suspected: X — likely" is not "Suspected: X".
          const alreadyThere = noteHasLine(assessmentNote, decision.line);
          if (alreadyThere && noteHasLine(savedNoteRef.current, decision.line)) return 'written';
          setAssessmentNote((prev) => appendLineToNote(prev, decision.line));
          setAssessmentNoteDelta((prev) => appendLineToNoteDelta(prev, decision.line));
          // `inserted` is false when the line was already hers: the append above changes nothing,
          // and Discard must not treat the line as this accept's to remove.
          return { outcome: 'written', persisted: whenLineSaved(decision.line, !alreadyThere) };
        }

      case 'seed-diagnosis-search':
        // PROD-2946 (Andrew's review): the drawer this opens is the Add Order drawer, which only the
        // physician allowlist may reach. Off it, nothing opens: the proposal stays in review for a
        // physician, and the notice says so.
        if (!ordersOffered) {
          setDxNotice(`A physician must confirm ${decision.query} from the diagnosis list. It stays in review.`);
          return 'awaiting-clinician';
        }
        // The unresolved path, and the COMMON one. Opens the same drawer the clinician already
        // uses, scoped to diagnoses and pre-filled, so confirming against real terminology is
        // one look rather than a retype.
        setSearchSeed({ category: 'diagnosis', query: decision.query });
        setAddOrderOpen(true);
        // A drawer opened. Nothing is in the chart, and the clinician can close it.
        return 'awaiting-clinician';

      case 'seed-order-search':
        // PROD-2946: ordering is physician-only (the ambient panel already withholds order verbs off
        // the allowlist; this is the host's own check, so no other caller can open the drawer either).
        if (!ordersOffered) {
          setDxNotice(`Only a physician can order ${decision.query}. It stays in review.`);
          return 'awaiting-clinician';
        }
        // PROD-1977. Medications and vaccinations arrive from ambient with NO code, so the dose
        // dialog would open with empty route / unit-of-measurement / strength lists -- a form that
        // looks ready and cannot be completed. Send the clinician to the search instead, scoped to
        // the right catalog and pre-filled: they pick the real coded record, the drawer hands it to
        // the A/P, and the dialog then opens populated. `OrderType` is a subset of
        // `ApSearchCategory`, so the kind is the category.
        setSearchSeed({
          category: decision.kind,
          query: decision.query,
          ...(decision.dictatedDose ? { dictatedDose: decision.dictatedDose } : {}),
        });
        setAddOrderOpen(true);
        /**
         * Every medication and vaccination lands here. This is the path that lost paracetamol, so
         * neither answer below is `'written'` — a catalogue opened, and the clinician can close it.
         *
         * The two are still distinguished (PROD-2018). A `contradiction` on the dictated dose is set
         * by exactly one thing, `asDictatedSchedule`, and it exists only when the clinician answered
         * a contest with `'use-dictated'` — so its presence is the host's read of "the structured
         * schedule was cleared on purpose", taken from the decision rather than re-derived from the
         * proposal. The form that follows opens with no frequency BY INSTRUCTION, which is not the
         * same fact as an ordinary seeded search, and the outcome should not say it is.
         */
        return decision.dictatedDose?.contradiction
          ? 'awaiting-clinician-without-schedule'
          : 'awaiting-clinician';

      case 'add-order':
      case 'open-order-dialog': {
        // Both land on the A/P per-type dialog, which is where dose/route/frequency are
        // supplied — the same handover the Add-Order drawer performs. `add-order` differs only
        // in that `decideAccept` has already established there is exactly one diagnosis to
        // link to; the dialog reads that from the canvas itself, so the two are dispatched
        // identically here rather than duplicating the link logic.
        // PROD-1975. ADAPT the source — do not hand the raw ProposalSource over. `pendingSeed.item`
        // is `unknown`, so a shape mismatch compiles silently and fails in the dialog: ambient's
        // `route` is a string ("PO") where the dose dialog expects `{display, code}[]`, and
        // `(medication.route ?? []).map(...)` threw, taking the encounter page down.
        const seed = toOrderSeed(decision.kind, decision.seed);
        // Nameless proposal: nothing to seed, and an empty dialog helps no one. Nothing was
        // written either, so the card stays.
        if (!seed) return 'awaiting-clinician';
        setPendingSeed({ item: seed, category: decision.kind as DsOrderType });
        setCurrentStep('ap');
        // A dose dialog opened. Dose, route and frequency are still unentered.
        return 'awaiting-clinician';
      }
    }
  }

  /**
   * PROD-2650. Undo on an ambient diagnosis accept takes the row that accept wrote out of the A&P,
   * through THE SAME delete the clinician's own remove button runs (`handleRemoveDx`: the row goes,
   * orders linked only to it go with it, a primary is re-promoted, and the canvas autosave persists
   * the document). There is no second delete path.
   *
   * Asked before the undo is recorded. It refuses while the canvas is read-only (still loading, or
   * frozen for signing), because a removal made then would be lost to the seed or to the lock.
   * The row is looked up again at commit time, from the mirror, so a reorder or reload in between
   * cannot point the delete at the wrong row. A row that is already gone needs no removal: the
   * chart already agrees with the undo.
   */
  function prepareAmbientRetract(target: RetractTarget): RetractPlan {
    if (target.entityType !== AP_DIAGNOSIS_ENTITY) {
      return { ok: false, message: `${target.label} cannot be removed from here, so the undo was not recorded.` };
    }
    if (apReadOnly) {
      return {
        ok: false,
        message: `The Assessment & Plan cannot be edited right now, so ${target.label} was not removed and the undo was not recorded. Try again in a moment.`,
      };
    }
    /**
     * UNDO TAKES BACK WHAT THE ACCEPT WROTE, AND NOTHING ELSE. The accept wrote one diagnosis row.
     * `handleRemoveDx` also removes every order linked only to that row and unlinks the rest, and
     * those links were made by the clinician afterwards — so an undo from the decision record would
     * silently delete hand-entered orders. Refused instead, with the reason; the clinician can still
     * remove the diagnosis from the A&P by hand, which is a decision about those orders she makes
     * herself.
     *
     * Checked twice. Here, before the undo is recorded, so the ordinary case is refused outright.
     * And again at commit, after the server stored the undo: the request (and a Retry from the error
     * strip) can take any length of time, and the clinician can link an order or start signing
     * meanwhile. Then the row is LEFT and the commit says so, because the undo is already recorded and
     * deleting her orders to make the two agree would be the worse disagreement.
     */
    const written = findWrittenDiagnosis(diagnosesRef.current, target);
    const linkedOrders = written ? orders.filter((o) => o.dxIds.includes(written.id)).length : 0;
    if (linkedOrders > 0) {
      return {
        ok: false,
        message:
          `${target.label} has ${linkedOrders === 1 ? 'an order' : `${linkedOrders} orders`} linked to it, ` +
          'so it was not removed and the undo was not recorded. Removing it would also remove those orders, ' +
          'which this accept did not add. Remove the diagnosis from the Assessment & Plan yourself if you mean to.',
      };
    }
    return {
      ok: true,
      commit: () => {
        const row = findWrittenDiagnosis(diagnosesRef.current, target);
        if (!row) return undefined;
        const left = (why: string) =>
          `Undo recorded. ${target.label} was left in the Assessment & Plan, ${why}. Remove it there if it should not be in the note.`;
        if (apWriteBlockedRef.current) return left('because the Assessment & Plan could not be edited at that moment');
        if (ordersRef.current.some((o) => o.dxIds.includes(row.id))) {
          return left('because an order was linked to it after the accept, and removing it would remove that order too');
        }
        handleRemoveDx(row.id);
        return undefined;
      },
    };
  }

  function handleReorder(orderedDxIds: string[]) {
    writeDiagnoses((prev) => {
      const byId = new Map(prev.map((d) => [d.id, d]));
      const next = orderedDxIds.map((id) => byId.get(id)).filter((d): d is DxCard => d != null);
      // Append any Dx not named in the order (defensive — keep nothing lost).
      for (const d of prev) if (!orderedDxIds.includes(d.id)) next.push(d);
      return next;
    });
  }

  // Promote one Dx to primary; demote every other (one-primary rule — the canvas relies on the host
  // to enforce it). v2 PROD-1422.
  function handleSetPrimary(dxId: string) {
    writeDiagnoses((prev) => {
      const flagged = prev.map((d) => ({ ...d, isPrimary: d.id === dxId }));
      // PRIMARY SORTS TO THE FRONT and the cards renumber — Flutter does this on every promotion
      // (assessment_plan_screen.dart:686-688). Without it the primary could render as card 3 while a
      // secondary is card 1, so the numerals carried no clinical meaning.
      return [...flagged].sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0));
    });
  }

  // Change which diagnoses an order justifies (min 1). v2 PROD-1422.
  function handleLinkOrderDx(orderId: string, dxIds: string[]) {
    setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, dxIds } : o)));
    orderSetApply.onOrderEdited(orderId); // PROD-2712: a re-linked set order counts as edited
  }

  function handleRemoveDx(dxId: string) {
    // An ambient accept whose row is removed before any save carried it never reached the chart.
    settleWaiters([dxId], false);
    // Removing a Dx cascades: drop it from every order's dxIds, and remove orders left with 0 links.
    setOrders((prevOrders) => {
      const orphaned: string[] = [];
      const next = prevOrders
        .map((o) => (o.dxIds.includes(dxId) ? { ...o, dxIds: o.dxIds.filter((id) => id !== dxId) } : o))
        .filter((o) => {
          if (o.dxIds.length === 0) {
            orphaned.push(o.id);
            return false;
          }
          return true;
        });
      // Drop the payloads of the now-removed (0-link) orders.
      if (orphaned.length) setPayloads((p) => orphaned.reduce((m, id) => removeOrderPayload(m, id), p));
      return next;
    });
    // One-primary rule: deleting THE primary must promote another, else the save mapper (which
    // serializes the explicit isPrimary flag) would persist a no-primary doc (Codex #147 P2).
    writeDiagnoses((prev) => ensureOnePrimary(prev.filter((d) => d.id !== dxId)));
    setDxCoding((prev) => removeDxCoding(prev, dxId));
  }

  function handleRemoveOrder(orderId: string) {
    setOrders((prev) => prev.filter((o) => o.id !== orderId));
    setPayloads((prev) => removeOrderPayload(prev, orderId));
    orderSetApply.onOrderRemoved(orderId); // PROD-2712: removed_before_sign, for a set order only
  }

  // Education (PROD-1454): add a picked article (deduped by display, Flutter addEducation) / remove one.
  function handleAddArticle(article: EducationArticle) {
    setArticles((prev) => addArticleWithOnSet(prev, article));
  }
  function handleRemoveArticle(idOrIndex: string | number) {
    setArticles((prev) =>
      typeof idOrIndex === 'number'
        ? prev.filter((_, i) => i !== idOrIndex)
        : prev.filter((a) => a.id !== idOrIndex),
    );
  }

  /**
   * Append a coded order, linked to EVERY diagnosis the clinician ticked (PROD-1801).
   *
   * Was `dxIds: [dxId]` — a single link, because the order dialogs had no way to express more. They
   * do now (`DiagnosisMultiSelect`), and the save mapper fans an order out to every entry in `dxIds`,
   * so the whole set has to arrive here intact. A caller with one diagnosis simply passes one.
   *
   * Order matters: `dxIds[0]` is the nesting parent for the single-emit types (medication, referral),
   * which is why the multi-select emits in canvas order rather than click order.
   */
  function attachOrder(dxIds: string[], planOrder: PlanOrder, payload: CodedOrderPayload) {
    const linked = [...new Set(dxIds)].filter(Boolean);
    if (linked.length === 0) return; // no link = the save mapper would drop it; never append a ghost
    setOrders((prev) => [...prev, { ...planOrder, dxIds: linked }]);
    setPayloads((prev) => setOrderPayload(prev, planOrder.id, payload));
  }

  function handleAttachOrder(dxIds: string[], planOrder: PlanOrder, payload: CodedOrderPayload) {
    attachOrder(dxIds, planOrder, payload);
  }

  /**
   * PROD-3182. A clinician's single add (an order dialog, the "+ Med" fallback) REFUSES an order
   * that is already on the plan, and says so, as `handleAddDx` does for a diagnosis. Without it the
   * same drug could be added twice: once from the catalogue and once from the ambient card.
   *
   * "Already on the plan" uses the order sets' own rules (`findDuplicateOrder`), so a dialog and an
   * order set agree. Order sets do NOT come through here: they list a duplicate with "Order anyway"
   * and place it through `handleAttachOrder` only when the clinician chose that.
   */
  function handleAttachOrderUnlessDuplicate(dxIds: string[], planOrder: PlanOrder, payload: CodedOrderPayload) {
    const duplicateOf = findDuplicateOrder(ordersRef.current, payloads, payload);
    if (duplicateOf.length > 0) {
      setDxNotice(null);
      setHeldDuplicate({
        dxIds,
        planOrder,
        payload,
        existing: duplicateOf[0],
        ordersAt: ordersRef.current,
        diagnosesAt: diagnosesRef.current,
      });
      return;
    }
    setDxNotice(null);
    setHeldDuplicate(null);
    attachOrder(dxIds, planOrder, payload);
  }

  /** PROD-3182. The held duplicate, only while the plan is still the one it was refused against. */
  const heldDuplicateActive =
    heldDuplicate && heldDuplicate.ordersAt === orders && heldDuplicate.diagnosesAt === diagnoses ? heldDuplicate : null;

  /** PROD-3182. "Order anyway": the held order goes on the plan as a second order, with an id of its own. */
  function orderHeldDuplicateAnyway() {
    if (!heldDuplicate) return;
    const { dxIds, planOrder, payload } = heldDuplicate;
    const taken = ordersRef.current.some((o) => o.id === planOrder.id);
    attachOrder(dxIds, taken ? { ...planOrder, id: `${planOrder.id}-${medOrderSeqRef.current++}` } : planOrder, payload);
    setHeldDuplicate(null);
  }

  // Update an existing order in place (PROD-1433 click-to-edit): replace its label + payload, but KEEP
  // its identity — the original id (so the side-map key + any server id survive). The dialog builds a
  // fresh planOrder with a derived id; we override the id here. PROD-2886: the form's diagnosis
  // selection (`dxIds`) re-links it, like handleLinkOrderDx; none → its links are kept.
  function handleUpdateOrder(orderId: string, planOrder: PlanOrder, payload: CodedOrderPayload, dxIds?: string[]) {
    setOrders((prev) => replaceOrderKeepingLinks(prev, orderId, planOrder, dxIds));
    setPayloads((prev) => setOrderPayload(prev, orderId, payload));
    orderSetApply.onOrderEdited(orderId); // PROD-2712: edited_before_sign, for a set order only
  }

  // Monotonic suffix for stable medication order ids (same med, >1 Dx → distinct ids).
  const medOrderSeqRef = useRef(0);
  function handleAttachMedication(dxId: string, result: MedicationResult) {
    const orderId = `med-${result.id}-${medOrderSeqRef.current++}`;
    const planOrder = medicationResultToPlanOrder(result, orderId);
    const payload: CodedOrderPayload = {
      kind: 'medication',
      medicationCode: { code: result.code ?? '', displayName: result.name },
    };
    // Legacy single-Dx path (the quick "+ Med" fallback for hosts without the dose dialog) — one
    // link is correct here; the multi-select lives in the dialogs. PROD-3182: refused when the drug
    // is already on the plan.
    handleAttachOrderUnlessDuplicate([dxId], planOrder, payload);
  }

  /**
   * BOTH TIERS. Measured: 0 of 22 dictated drug names resolved through favourites alone.
   *
   * `/api/Provider/GetMedication` is the favourites endpoint by design (Andrew) — one column, one
   * substring, scoped to this provider — so a drug the clinician has never favourited returns
   * nothing however good the master search is. `SearchValueSets2` has searched `LongDescription`
   * since 2022 and that column carries the DDC scientific name, so brand<->generic already works.
   * Andrew's measurement: AZITHROMYCIN 0 -> 95 products, ROSUVASTATIN 0 -> 127, CETIRIZINE 0 -> 71,
   * with 8 of 35 generics returning ZERO on Display-only before favourites scoping applies.
   *
   * A routing fix, not a vocabulary one. Favourites still come first.
   */
  const medicationSearch = useMedicationSearchTiered(providerId, medQuery);

  // ── FOUR MOMENTS (PROD-1656 spec §10, mounting wave 2026-07-24) ─────────────────────────────
  // The visit ↔ problem-list connection: the patient's chart-detail conditions feed the A&P
  // patient-aware picker (M2), the EncounterDxCard identity chips + resolve action (M2b/M3), and
  // the Wrap-Up problem-list closing step (M4b). Same queryKeys the rail/chart already use — one
  // cache, no duplicate fetch.
  const activeConditionsQuery = usePatientConditions(patientId, CONDITION_STATUS.active);
  const resolvedConditionsQuery = usePatientConditions(patientId, CONDITION_STATUS.resolved);
  const patientProblems = useMemo(
    () => toPatientProblems(activeConditionsQuery.data),
    [activeConditionsQuery.data],
  );
  // PROD-3040 · L1: what the record holds, for template items filled from the chart (frame v2 reads it).
  const chartLaunchContext = useMemo<ChartLaunchContext>(
    () => ({
      patient: { birthDate: detailHeader.dobIso, gender: genderOf(demographics?.sex) },
      visitDate: nav?.appointmentStart ?? appointmentStatusQuery.data?.start ?? undefined,
      vitals: { bmi: Number.isFinite(bmiValue) && bmiValue > 0 ? bmiValue : undefined },
      problemCodes: patientProblems.map((p) => p.code),
    }),
    [detailHeader.dobIso, demographics?.sex, nav?.appointmentStart, appointmentStatusQuery.data?.start, bmiValue, patientProblems],
  );
  // The SAME condition write path the rail + chart use (PUT /api/Patients/condition/{id}).
  const saveProblemCondition = useSaveCondition(patientId ?? undefined);
  const currentUserName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || undefined;
  // [PROD-1653] The visit's day (the appointment's start, else today): only a reconciliation saved on it is this visit's.
  const previsitDay = useMemo(() => new Date(chartLaunchContext.visitDate ?? Date.now()), [chartLaunchContext.visitDate]);
  const dxCodeOf = (dxId: string): string | undefined =>
    (dxCoding.get(dxId)?.code ?? diagnoses.find((d) => d.id === dxId)?.code)?.trim() || undefined;

  // Stewardship opt-out state (ruling C, "Don't add to problem list"). SESSION-scoped — the wire
  // has no per-dx exclusion field (verified against Scrips.Patient AssessmentPlanConditionService
  // @origin/master AND the in-flight PROD-1682/1684 branch), so the opt-out cannot persist on the
  // assessment document. Mechanism (the honest FE-side implementation): the BE auto-promotion is
  // allowed to run (it re-runs on EVERY isOrderSign save, so excluding the dx from one payload
  // cannot work and would drop the dx from the visit/billing); once a kept-off dx's promoted chart
  // Condition appears — stamped with THIS encounter and absent from the pre-opt-out snapshot — the
  // reconciliation effect soft-deletes it (the same delete PUT the rail uses). Pre-existing
  // problems are structurally untouchable: both guards exclude them.
  interface KeptOffEntry {
    dxId: string;
    code: string;
    term: string;
    /** Undo window elapsed — reconciliation may act. */
    confirmed: boolean;
    /** Active condition identifiers at opt-out time — anything in here is NEVER deleted. */
    snapshotIds: string[];
  }
  const [keptOff, setKeptOff] = useState<ReadonlyMap<string, KeptOffEntry>>(new Map());
  const keptOffDxIds = useMemo(() => new Set(keptOff.keys()), [keptOff]);
  const [resolvedNowDxIds, setResolvedNowDxIds] = useState<ReadonlySet<string>>(new Set());
  // The 10s undo receipt (light UndoSnackbar) — one at a time, latest action wins (D-16).
  const [receipt, setReceipt] = useState<
    | { kind: 'keptOff'; dxId: string; message: string }
    | { kind: 'resolved'; dxId: string; message: string; original: PatientCondition }
    | null
  >(null);
  // Chart-condition identifiers with a stewardship delete IN FLIGHT or DONE. Added on success,
  // removed on error (Codex: adding before the PUT settles left a failed exclusion permanently
  // skipped while the UI claimed "Not added"); `pendingStewardshipRef` holds the one in flight.
  const stewardshipDeletedRef = useRef<Set<string>>(new Set());
  const pendingStewardshipRef = useRef<string | null>(null);

  // Replacing the single receipt must not strand the previous opt-out unconfirmed (Codex:
  // UndoSnackbar unmount never fires onDismiss) — a superseded keptOff receipt COMMITS.
  function confirmKeptOffEntry(dxId: string) {
    setKeptOff((prev) => {
      const entry = prev.get(dxId);
      if (!entry || entry.confirmed) return prev;
      const next = new Map(prev);
      next.set(dxId, { ...entry, confirmed: true });
      return next;
    });
  }
  function pushReceipt(next: NonNullable<typeof receipt>) {
    if (receipt?.kind === 'keptOff' && receipt.dxId !== next.dxId) confirmKeptOffEntry(receipt.dxId);
    setReceipt(next);
  }

  function handleKeepOffDx(dxId: string) {
    const dx = diagnoses.find((d) => d.id === dxId);
    const code = dxCodeOf(dxId);
    if (!dx || !code) return;
    // The snapshot guard is only meaningful over an AUTHORITATIVE conditions read (Codex CRITICAL:
    // an unsettled query would record an EMPTY snapshot, and the reconciliation could then delete
    // a pre-existing problem later stamped with this encounter). No successful read → no opt-out.
    if (!activeConditionsQuery.isSuccess) return;
    const snapshotIds = (activeConditionsQuery.data ?? [])
      .filter((c) => c.isDeleted !== true && !!c.identifier)
      .map((c) => c.identifier as string);
    setKeptOff((prev) => {
      const next = new Map(prev);
      next.set(dxId, { dxId, code, term: dx.display, confirmed: false, snapshotIds });
      return next;
    });
    pushReceipt({ kind: 'keptOff', dxId, message: `Not added to problem list — ${dx.display}` });
  }

  // One-tap resolve for an ON-LIST dx (M3): the SAME resolve write the rail's Active Problems row
  // fires (PROD-1489 builder, local-calendar today). Undo restores the ORIGINAL record via the
  // builder's edit action (Active + original onset — an undo, not the chart's unresolve-reset).
  function handleResolveDxWithVisit(dxId: string) {
    const dx = diagnoses.find((d) => d.id === dxId);
    const code = dxCodeOf(dxId);
    if (!dx || !code || !patientId || !user?.id) return;
    // One condition write at a time (Codex: an undo racing an in-flight resolve produced two
    // concurrent PUTs whose arrival order decided the server state).
    if (saveProblemCondition.isPending) return;
    const existing = (activeConditionsQuery.data ?? []).find(
      (c) => c.isDeleted !== true && !!c.identifier && c.code?.code?.trim().toLowerCase() === code.toLowerCase(),
    );
    if (!existing) return;
    const req = buildConditionRequest(
      { type: 'resolve', existing, endYyyymmdd: todayLocalYmd() },
      { patientId, userId: user.id, userName: currentUserName, encounterId },
    );
    if (!req) return;
    // Optimistic receipt; ROLLED BACK on failure (Codex: a failed resolve must not leave the card
    // claiming resolved). Success keeps the receipt — the undo window runs its course.
    setResolvedNowDxIds((prev) => new Set(prev).add(dxId));
    pushReceipt({ kind: 'resolved', dxId, message: `Resolved — ${dx.display}`, original: existing });
    saveProblemCondition.mutate(req, {
      onError: (err) => {
        setResolvedNowDxIds((prev) => {
          const next = new Set(prev);
          next.delete(dxId);
          return next;
        });
        setReceipt((r) => (r?.kind === 'resolved' && r.dxId === dxId ? null : r));
        showServerError('Resolving the problem', err);
      },
    });
  }

  function handleReceiptUndo() {
    if (!receipt) return;
    if (receipt.kind === 'keptOff') {
      setKeptOff((prev) => {
        const next = new Map(prev);
        next.delete(receipt.dxId);
        return next;
      });
    } else {
      // Walk the resolve back: restore Active + the original onset (buildUnresolveValue). If the
      // resolve PUT is still in flight, keep the receipt up — undo retries on the next click
      // (never two concurrent writes to the same condition; Codex race).
      if (saveProblemCondition.isPending) return;
      if (patientId && user?.id) {
        const req = buildConditionRequest(
          { type: 'edit', existing: receipt.original, value: buildUnresolveValue(receipt.original) },
          { patientId, userId: user.id, userName: currentUserName, encounterId },
        );
        if (req) {
          const dxId = receipt.dxId;
          saveProblemCondition.mutate(req, {
            onError: (err) => {
              // Server still resolved — restore the receipt state so the UI never lies (Codex).
              setResolvedNowDxIds((prev) => new Set(prev).add(dxId));
              showServerError('Undoing the resolve', err);
            },
          });
        }
      }
      setResolvedNowDxIds((prev) => {
        const next = new Set(prev);
        next.delete(receipt.dxId);
        return next;
      });
    }
    setReceipt(null);
  }

  function handleReceiptDismiss() {
    // Window elapsed — a keptOff opt-out COMMITS: reconciliation may now act on this entry.
    if (receipt?.kind === 'keptOff') confirmKeptOffEntry(receipt.dxId);
    setReceipt(null);
  }

  // Wrap-Up "Reactivate" (M4b): move a resolved-today problem back to Active, keeping its
  // original onset (undo semantics — the visit is still open, the resolve was today's).
  function handleReactivateResolved(existing: PatientCondition) {
    if (!patientId || !user?.id) return;
    const req = buildConditionRequest(
      { type: 'edit', existing, value: buildUnresolveValue(existing) },
      { patientId, userId: user.id, userName: currentUserName, encounterId },
    );
    if (!req) return;
    saveProblemCondition.mutate(req);
    // Clear any matching session receipt state so the A&P card's resolvedNow retires too.
    const code = existing.code?.code?.trim().toLowerCase();
    if (code) {
      setResolvedNowDxIds((prev) => {
        const next = new Set(prev);
        for (const dxId of prev) {
          if (dxCodeOf(dxId)?.toLowerCase() === code) next.delete(dxId);
        }
        return next;
      });
    }
  }

  // ── Stewardship reconciliation (the working FE-side "exclusion") ────────────────────────────
  // When a CONFIRMED kept-off dx's promoted chart Condition materializes — active, code match,
  // stamped with THIS encounter, absent from the opt-out snapshot — soft-delete it. Runs off the
  // conditions cache; the promotion paths below refetch it after every isOrderSign save. Both
  // guards (encounter stamp + snapshot) must pass, so a pre-existing problem can never be touched
  // (the PROD-1682 re-sign delete hazard class is exactly what these guards exclude).
  useEffect(() => {
    if (!patientId || !user?.id || !encounterId) return;
    if (!activeConditionsQuery.isSuccess) return; // only act over an authoritative read
    if (saveProblemCondition.isPending || pendingStewardshipRef.current) return; // one write at a time
    const conditions = activeConditionsQuery.data ?? [];
    for (const entry of keptOff.values()) {
      if (!entry.confirmed) continue;
      const target = conditions.find(
        (c) =>
          c.isDeleted !== true &&
          !!c.identifier &&
          !stewardshipDeletedRef.current.has(c.identifier as string) &&
          c.code?.code?.trim().toLowerCase() === entry.code.toLowerCase() &&
          c.encounter === encounterId &&
          !entry.snapshotIds.includes(c.identifier as string),
      );
      if (!target) continue;
      const req = buildConditionRequest(
        { type: 'delete', existing: target },
        { patientId, userId: user.id, userName: currentUserName, encounterId },
      );
      if (!req) continue;
      // Mark DONE only on success; a failed exclusion clears the pending latch and retries on the
      // next effect pass — and is surfaced, never silent (Codex: pre-marking left a failed delete
      // permanently skipped while the card claimed "Not added").
      const targetId = target.identifier as string;
      pendingStewardshipRef.current = targetId;
      saveProblemCondition.mutate(req, {
        onSuccess: () => {
          stewardshipDeletedRef.current.add(targetId);
        },
        onError: (err) => {
          showServerError('Keeping the diagnosis off the problem list', err);
        },
        onSettled: () => {
          pendingStewardshipRef.current = null;
        },
      });
      return; // one write per pass; the mutation settle re-runs the effect for the rest
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keptOff, activeConditionsQuery.data, activeConditionsQuery.isSuccess, saveProblemCondition.isPending, patientId, encounterId, user?.id]);

  // The undo receipt node the A&P view anchors at the bottom of the diagnosis column.
  const dxReceipt = receipt ? (
    <UndoSnackbar
      key={`${receipt.kind}-${receipt.dxId}`}
      message={receipt.message}
      onUndo={handleReceiptUndo}
      onDismiss={handleReceiptDismiss}
    />
  ) : undefined;

  // ── Controlled autosave ──────────────────────────────────────────────────────
  // After ANY mutation to diagnoses/payloads we persist the WHOLE document. The canvas is read-only
  // until apReady (so the seed's id is in hand — no duplicate CREATE), then editable including during
  // a save. Concurrency is guarded HERE, not by read-only:
  //   • only fire when there is ≥1 Dx (no empty-shell create) and the content actually changed;
  //   • the `apBusy` (isPending) guard is the SOLE overlapping-save guard — an edit made mid-save
  //     stays dirty and flushes as ONE follow-up save after the in-flight one settles (no concurrent PUT).
  // On success we do NOT re-hydrate the canvas from the response (CreateAssessment returns only the
  // id string, not the doc — re-hydrating from it wiped the just-added Dx). We adopt the returned id
  // (so the next save PUTs) and set lastSavedSnapshot to the sent content so the dirty effect does
  // not re-fire (no save loop). Server child ids reconcile on the next reload via the seed; the
  // id-independent snapshot keeps that churn from reading as dirty.
  // An image-only note has blank plain text (Quill gives an embed no text), so the Delta is read too.
  const noteDeltaHeld = useMemo(() => noteDeltaHasContent(assessmentNoteDelta), [assessmentNoteDelta]);
  const apSnapshot =
    // Fire when there's ≥1 Dx OR ≥1 education article — an education-only assessment (Dx-less) must
    // still persist (PROD-1454). Empty (no Dx, no articles) → null → no empty-shell create.
    // A NARRATIVE counts as content too (rule 5): a suspected condition lands in the assessment text
    // precisely when there may be no diagnosis yet, and without this the line would never save.
    // So does a narrative the SERVER may still hold (a saved document with a Delta): clearing the
    // narrative of a narrative-only assessment must save the empty note, or the deleted statement
    // comes back on reload (Codex P2). An untouched empty assessment still creates nothing.
    (diagnoses.length > 0 ||
      articles.length > 0 ||
      assessmentNote.trim() !== '' ||
      noteDeltaHeld ||
      (assessmentIdentifier !== null && assessmentNoteDelta !== undefined)) &&
    encounterId &&
    patientId &&
    providerId
      ? apContentSnapshot(diagnoses, orders, payloads, dxCoding, articles, assessmentNote, assessmentNoteDelta)
      : null;

  useEffect(() => {
    if (apSnapshot == null) return; // nothing to save (no Dx / ids unresolved)
    if (!apReady) return; // load not settled — canvas was read-only, nothing to flush
    if (apBusy) return; // a save is in flight — no overlapping saves
    if (apSnapshot === lastSavedSnapshotRef.current) return; // not dirty
    if (apSnapshot === lastFailedSnapshotRef.current) return; // already failed this exact content — don't loop-retry (PROD-1334)
    if (!encounterId || !patientId || !providerId) return;

    const body = toPatientAssessment(diagnoses, orders, payloads, dxCoding, {
      assessmentNote,
      assessmentNoteDelta,
      encounter: encounterId,
      subject: patientId,
      performer: providerId,
      identifier: assessmentIdentifier,
      serverIds: serverIdsRef.current,
      // Patient-education articles (PROD-1454) — emitted into assessmentPlanArticles (Dx-less).
      articles,
      // Preserve the order-sent flag on every autosave — else a post-send A/P edit would re-emit
      // false and silently un-send the orders (PROD-1410). `orderSent` = fetched flag OR the
      // optimistic latch, so it's correct even before the send-save's refetch lands.
      orderSent,
    });

    // Snapshot what we're sending NOW so the autosave doesn't re-fire on the id write-back, and so
    // the post-save refetch reconcile (the hydrate effect) recognises the canvas as clean.
    const sentSnapshot = apSnapshot;
    const sentDxIds = diagnoses.map((d) => d.id);
    const sentNote = assessmentNote;
    setApSaveError(false); // clear any prior failure notice as we (re)attempt
    saveAssessment.mutate(body, {
      onSuccess: (savedId) => {
        settlePersisted(sentDxIds, sentNote);
        // The save returns only the document identifier (string) — NOT the full doc — so we must
        // NOT rebuild the canvas from it (that wiped the just-added Dx until a reload, PROD-1297
        // live smoke). Adopt the id (so the next save PUTs, no duplicate CREATE) and keep the local
        // canvas. The invalidated assessment query refetches the full server doc, and the hydrate
        // effect above adopts the server-assigned child ids when the canvas is clean.
        if (savedId && savedId !== assessmentIdentifier) setAssessmentIdentifier(savedId);
        lastSavedSnapshotRef.current = sentSnapshot;
        lastFailedSnapshotRef.current = null; // succeeded — release any failure guard
      },
      onError: () => {
        // Record the failed content so the autosave doesn't immediately re-send the identical body
        // (the infinite retry → readOnly/"Saving…" flicker, PROD-1334). A further edit changes the
        // snapshot, releasing the guard so the next change retries. Surface a notice so it's not silent.
        // Known edge (Codex MEDIUM): if a CREATE failed client-side but actually persisted server-side,
        // `assessmentIdentifier` stays null, so the next distinct edit CREATEs again → a duplicate doc.
        // This is strictly better than the prior behaviour (the loop CREATEd many duplicates); the
        // durable fix is BE idempotency on CreateAssessment, tracked separately — not this guard.
        lastFailedSnapshotRef.current = sentSnapshot;
        setApSaveError(true);
      },
    });
    // saveAssessment is a stable mutation object; only content/gate transitions drive this. apBusy
    // (isPending) is a dep so a pending-then-settled save re-runs the effect and flushes any edit made
    // DURING the save once it settles. assessmentIdentifier is a dep so a post-create id adoption
    // re-runs the effect (debounced by the CONTENT-only snapshot — the id write-back alone doesn't
    // read as dirty).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apSnapshot, apBusy, apReady, assessmentIdentifier]);

  // The A/P props for the shell's 'ap' step. Template-independent — always supplied.
  const allowed: DsOrderType[] = toCanvasOrderTypes(allowedOrderTypes ?? [], { referrals: referralsOn });
  const assessmentPlan: AssessmentPlanViewProps = {
    diagnoses,
    orders,
    bundles: bundlesQuery.data,
    bundlesLoading: bundlesQuery.isLoading,
    // Apply a bundle's saved A&P to this canvas (Flutter `importBundle`). The merge itself is a pure
    // function so its rules — code-level dedupe, keep-the-clinician's-row, synthetic ids, the 20 cap —
    // are testable without a page; here we only commit the result and say what happened.
    onImportBundle: (bundle) => {
      // `withBundleConditionIds` FIRST — a bundle's conditions carry no identifier, and without one
      // the mapper collapses them all to id "" and every order is lost. See the helper for the trace.
      const incoming = patientAssessmentToCanvas(withBundleConditionIds(bundle.bundleDetails));
      const merged = importBundleIntoCanvas(
        { diagnoses, orders, payloads, dxCoding, articles },
        incoming,
        MAX_DIAGNOSES,
      );
      writeDiagnoses(merged.diagnoses);
      setOrders(merged.orders);
      setPayloads(merged.payloads);
      setDxCoding(merged.dxCoding);
      setArticles(merged.articles);
      // SAY SO either way — a bundle whose every row is already on the canvas would otherwise look
      // like a dead button (the same failure handleAddDx's notice exists to prevent).
      setDxNotice(
        merged.noop
          ? `${bundle.bundleName ?? 'That bundle'} is already on this assessment.`
          : merged.skippedForCap > 0
            ? `Added ${merged.addedDx}; ${merged.skippedForCap} skipped (max ${MAX_DIAGNOSES} diagnoses)` +
              // Name the dropped orders too. They were dropped because their only justifying dx hit
              // the cap, and a provider who built the bundle around them needs to know they are not
              // on the canvas (review, PR #292) — silence here is a UX mystery on import.
              (merged.droppedOrders > 0
                ? `, and ${merged.droppedOrders} order${merged.droppedOrders === 1 ? '' : 's'} could not be linked.`
                : '.')
            : null,
      );
    },
    // PROD-2722: the A/P ⋯ "Save as order set", on the same gate as applying one (flag + physician).
    saveAsOrderSet: orderSetsActive,
    // The A/P tab draws its OWN chrome, so the two shared handlers have to reach it directly —
    // wiring them only on EncounterSectionChromeHost left all three icons dead on A/P
    // (Andrew's screenshot 2026-08-10).
    // PROD-1972: a MANUAL open always starts empty. Clearing here rather than at each of the
    // drawer's four close paths makes "seeded" a property of how it was opened, so a pick that
    // closes the drawer without going through onClose cannot leave a stale pre-fill behind.
    onOpenAddOrder: ordersOffered
      ? () => {
          setSearchSeed(null);
          setAddOrderOpen(true);
        }
      : undefined,
    onAddToFavourites: ordersOffered ? () => setFavouritesOpen(true) : undefined,
    seedRequest: pendingSeed,
    onSeedConsumed: consumePendingSeed,
    // `frameV2` (1 Oct): an order picked in the A/P while the plan has no diagnosis is kept as the
    // pending seed rather than dropped, so it survives a step change and opens once a diagnosis
    // exists. OFF: not wired, and the view keeps today's hint-and-drop.
    ...(frameV2On ? { onHoldOrder: setPendingSeed, pendingSeedCount: pendingSeeds.length } : {}),
    // Archive (after the rail's confirm, PROD-2697), then refetch. `user?.id` again — the SAME id
    // the list was fetched with, so the right cache entry is invalidated (see use-bundles.ts for
    // why it is not `providerId`).
    onDeleteBundle: user?.id
      ? (bundle) => deleteBundle.mutate({ bundle, providerId: user.id as string })
      : undefined,
    // ORDER SETS (PROD-2712) — only with the flag on and a user who may order; absent otherwise.
    ...(orderSetsActive ? { orderSets: orderSetApply } : {}),
    // An archive is in flight → the row's archive affordance disables. Without this a double-tap
    // between the POST and the invalidation settling queues two deletes against the same id
    // (review, PR #292); invalidation is deliberately not optimistic, so that window is real.
    deletingBundle: deleteBundle.isPending,
    allowedOrderTypes: allowed,
    // Block canvas interaction only until the initial load settles (no edit before the seed). A save
    // in flight does NOT block — the surface stays interactive (Flutter parity, PROD-1429); overlapping
    // saves are serialized by the autosave effect's apBusy guard, not here.
    readOnly: apReadOnly,
    // Muted notice that makes the blocked state legible (DS tokens; no hardcoded hex). A failed
    // save surfaces here (PROD-1334) so it isn't silent — the change re-attempts on the next edit.
    // dxNotice FIRST: a refused add (duplicate / the 20 cap) is a direct response to something the
    // clinician just did, so it outranks the ambient loading and saving states.
    // PROD-3182: the held "already added" notice ranks BELOW loading and saving, and shows only while
    // the plan is the one it was refused against (a plan change or Dismiss closes it). A save that
    // settles does NOT close it: one already in flight when the duplicate was held would hide it.
    notice: dxNotice
      ? dxNotice
      : !apReady
        ? <QuietLoading label="Loading assessment…" />
        : apBusy
          ? 'Saving…'
          : heldDuplicateActive
            ? (
                <span className="inline-flex flex-wrap items-center gap-2">
                  <span>{heldDuplicateActive.existing} is already added.</span>
                  <Button variant="link" size="compact" onClick={orderHeldDuplicateAnyway}>
                    Order anyway
                  </Button>
                  <Button variant="link" size="compact" onClick={() => setHeldDuplicate(null)}>
                    Dismiss
                  </Button>
                </span>
              )
          : apSaveError
            ? 'Couldn’t save the assessment — your change will retry when you edit again.'
            : undefined,
    roleLabel,
    // AI "Suggest" in the A/P rail (design line 592). The button + the ranked suggested-Dx
    // rows were built but never reachable in the app: this flag was only ever set in a
    // Storybook story, so `AssessmentPlanView` defaulted it undefined and the control never
    // rendered. The endpoint is real and traced from Flutter — GET /api/AISearch/Diagnosis
    // (FetchAIDiagnosisCodeUseCase → common_data_source_impl.dart:1158) — and the same
    // /api/AISearch host already serves the live chief-complaint RAG on the pre-encounter
    // card, so the service is reachable from this app today.
    //
    // Enabled unconditionally rather than behind a config read: there is no feature-flag
    // mechanism in this repo (grepped — none), and inventing one to gate a shipped endpoint
    // would be scaffolding, not a gate. Suggestions are opt-in per use anyway (the button
    // must be pressed; nothing calls the API until then).
    aiDiagnosisEnabled: true,
    providerId,
    patientId: patientId ?? undefined,
    gender: apGender,
    onAddDx: handleAddDx,
    onReorder: handleReorder,
    onSetPrimary: handleSetPrimary,
    onLinkOrderDx: handleLinkOrderDx,
    onRemoveDx: handleRemoveDx,
    onRemoveOrder: handleRemoveOrder,
    // PROD-3182: a dialog's add is refused when the order is already on the plan.
    onAttachOrder: handleAttachOrderUnlessDuplicate,
    // ACTIVE MEDICATIONS under the order rows (design lines 725-765): this encounter's Rx orders
    // only, never the chart list, which used to draw every reported or continuing medication as an
    // order in this visit (Andrew's A&P audit; Flutter parity in lib/to-ap-active-medications.ts).
    // The chart list stays on the pre-encounter rail and the summary banner.
    // Read-only on purpose: the row's Change/Refill/Discontinue actions render only when their
    // handlers are supplied, and of the three only Discontinue has a hook today (its dialog is
    // hosted by the pre-encounter rail, not here). Passing a handler-less action would put three
    // live-looking buttons on a clinical row that do nothing.
    activeMedications: toApActiveMedications(rxOrdersQuery.data, orders, payloads),
    // `medActions`: the Active Medications row's Change / Refill / Discontinue (= today's Stop); Delete stays disabled. The view
    // already turns them off when read-only (after the sign).
    ...(medActionsOn && mayOrderMedications
      ? {
          onChangeMedication: (rowId: string) => activeMedicationToPlan(rowId),
          onRefillMedication: (rowId: string) => activeMedicationToPlan(rowId),
          ...(mayStopOrSuspend === true ? { onDiscontinueMedication: (rowId: string) => activeMedicationStop(rowId) } : {}),
        }
      : {}),
    assessmentNote,
    assessmentNoteDelta,
    noteChartReads,
    // TS-4: the phrase menu offers phrases for THIS visit's specialty and type (unscoped ones always). Frame v2
    // only (PROD-3031): with the switch OFF the A&P note's phrase list is main's (no scope on the request).
    ...(frameV2On
      ? {
          noteMacroScope: {
            specialtyId: templateSpecialty.specialtyId,
            visitType: templateVisitType.visitType,
            // Same hold as the template request: no phrase list until the visit facts settle.
            pending: templateSpecialty.pending || templateVisitType.pending,
          },
        }
      : {}),
    onAssessmentNoteChange: (note: string, delta: string) => {
      setAssessmentNote(note);
      setAssessmentNoteDelta(delta);
    },
    dxCoding,
    orderPayloads: payloads,
    onUpdateOrder: handleUpdateOrder,
    medicationResults: medicationSearch.data ?? [],
    medicationQuery: medQuery,
    onMedicationQueryChange: setMedQuery,
    onAttachMedication: handleAttachMedication,
    // Patient education (PROD-1454): the Dx-less article tab + canvas section.
    articles,
    onAddArticle: handleAddArticle,
    onRemoveArticle: handleRemoveArticle,
    // Four moments (spec §10): the patient-aware picker + EncounterDxCard identity/stewardship/
    // resolve wiring + the undo receipt. All fed from the chart-detail conditions above.
    patientProblems,
    // Samer, 1 Oct (A21): the per-diagnosis problem-list choice is in today's demo. The DS card
    // discloses "joins the problem list at sign" with "Don't add to problem list"; the opt-out is the
    // existing handleKeepOffDx reconciliation (it soft-deletes only the condition THIS encounter
    // promoted). Behind ?ef=frameV2 so OFF keeps Flutter parity (no counterpart in the Flutter A/P card).
    showProblemListDisclosure: frameV2On,
    keptOffDxIds,
    resolvedNowDxIds,
    onKeepOffDx: handleKeepOffDx,
    onResolveDxWithVisit: handleResolveDxWithVisit,
    dxReceipt,
  };

  // Read-only grouped summary of the A/P orders for the drawer Orders tab (PROD-1263).
  // Same working state the canvas edits → the summary always reflects current orders.
  const ordersSummary = toOrdersSummaryGroups(orders, payloads);
  // PROD-2849: wrap-up's order view adds each lab order's patient prep and the Patient's choice tests
  // (this session's orders, plus the signed encounter's requisitions). Read only while it is open.
  const labRequisitionsQuery = useLabRequisitions(patientId, encounterId, pvsOpen);
  const labPrintExtras = useMemo(
    () => toLabPrintExtras(orders, payloads, labRequisitionsQuery.data),
    [orders, payloads, labRequisitionsQuery.data],
  );

  // Vaccination administration requests for the middle column (PROD-1250). Read from the RAW
  // assessment — patientAssessmentToCanvas drops nested vaccinations, so the seeded canvas (diagnoses)
  // doesn't carry them. Display-only here; opening the lot-aware administration editor is the
  // PROD-1227 composition seam: the nurse "Give"/"Edit" opens the lot-aware administration editor.
  const vaccinationRequests = toVaccinationRequests(assessmentQuery.data);
  // Medication administration requests (PROD-1524): A/P meds with frequency=Now, flattened from the
  // raw assessment's per-condition childMedicalCode buckets (same source as vaccinations). Display-only
  // here; the "Give" write is a follow-up seam (like the vaccination administration editor).
  const medicationAdminRequests = toMedicationAdminRequests(
    (assessmentQuery.data?.assessmentPlanCondition ?? []).flatMap(
      (dx) => dx.childMedicalCode?.assessmentPlanMedication ?? [],
    ),
  );
  // The request view carries only a DDC coding; re-find the full nested entry to seed the editor
  // (pre-fill what the doctor requested) AND to preserve its identity (identifier/orderId) on save.
  const [administering, setAdministering] = useState<VaccineAdministrationRequestView | null>(null);
  // Form-level failure from the administration PUT. Held here (not in the editor) because the editor is
  // remounted per request via editorKey, and the message must survive that mount.
  const [administerError, setAdministerError] = useState<string | null>(null);
  const administeringEntry = administering ? findVaccinationEntry(assessmentQuery.data, administering.id) : null;
  function handleAdministerSave(draft: VaccinationDraft) {
    if (!administering || !assessmentQuery.data || !encounterId || !patientId || !providerId) return;
    // Serialize against the canvas autosave: both write the same doc via this one mutation, so never
    // fire a nurse PUT while a save is in flight (would race + overwrite). The editor's Save is also
    // disabled while `saving`, so this only guards a concurrent canvas autosave (rare cross-phase).
    if (saveAssessment.isPending) return;
    // Merge the administered entry into the loaded doc + PUT the whole thing (explicit, decoupled
    // from the canvas autosave). The vaccination round-trip keeps a later canvas save from wiping it.
    const merged = applyVaccinationAdministration(assessmentQuery.data, administering.id, draft, {
      encounter: encounterId,
      subject: patientId,
      performer: providerId,
      // The dialog's own mode: "Give" (a standing request) ⇒ the nurse is RECORDING the dose, so the
      // request is fulfilled and must be cleared. "Edit" ⇒ a doctor amending the plan; leave it standing.
      administered: administering.isAdministrationRequested === true,
    });
    if (!merged) {
      setAdministering(null); // request not locatable — no-op rather than risk a bad write
      return;
    }
    setAdministerError(null);

    // LAST OUTSTANDING REQUEST ⇒ SIGN, exactly as Flutter (Andrew, 31 Jul: "do exactly the same as
    // flutter"). pre_encounter_widget.dart:1505-1520 runs the A/P save then signNote(isVaccinationSign:
    // true); patient_encounter_bloc.dart:2771-2785 explains why the ORDER is load-bearing — the sign
    // must land only AFTER the A/P is persisted with IsOrderSign=1, so the Inventory deduction saga
    // (Scrips.Patient WrapUpService) finds the confirmed plan and publishes vaccine-administered.
    // Flutter got that ordering by awaiting createApUseCase, replacing an older 2s-delay race
    // (PROD-1227); we get it by chaining off this mutation's onSuccess.
    //
    // Flutter's nurse branch does NOT run the doctor-side sign validations (no chief-complaint or
    // ≥1-diagnosis gate) — `if (isNurse)` returns early — so this path doesn't either.
    const outstanding = vaccinationRequests.filter((r) => r.isAdministrationRequested === true).length;
    const isLastOutstanding = administering.isAdministrationRequested === true && outstanding <= 1;
    const body = isLastOutstanding
      ? { ...merged, isOrderSign: true, hasOrderSent: true }
      : merged;

    saveAssessment.mutate(body, {
      onSuccess: () => {
        setAdministering(null);
        if (!isLastOutstanding) return;
        // Same endpoint Flutter hits (POST /api/Patients/CreateSignLock) with the same SignAndLockModel
        // body, including isVaccinationSign — that flag is what marks this as the vaccination sign-lock
        // the deduction saga listens for.
        createSignLock.mutate(
          {
            subject: patientId,
            encounter: encounterId,
            performer: providerId,
            assessmentPlanId: assessmentIdentifier,
            isVaccinationSign: true,
            sign: true,
            lock: true,
          },
          {
            // The dose IS recorded at this point — only the sign failed. Say exactly that, so nobody
            // re-administers to "retry", and so a silent inventory drift is visible.
            onError: () =>
              setAdministerError(
                'The dose was recorded, but the sign did not complete — inventory may not have been deducted. Tell a supervisor.',
              ),
          },
        );
      },
      // A failed PUT used to close nothing and SAY nothing, so the nurse believed the dose was
      // recorded when it was not. Keep the editor open and surface it (PROD-1801).
      onError: () =>
        setAdministerError('Could not record this administration. Nothing was saved — please try again.'),
    });
  }

  // Map the aggregate summary → IPS sections ONCE (memoized), shared by the pre-encounter rail and
  // the in-encounter patient-summary rail.
  // The "Patient-reported" section (`?x=patient-reported`, off by default): the pre-visit questionnaires the patient
  // submitted, placed after Active Problems; nothing is read or shown with the switch off (lib/patient-reported.ts).
  const [patientReportedOn] = useState(() => isEncounterFlagOn('patientReported'));
  const [familyRelationOn] = useState(() => isEncounterFlagOn('railFixes') && isEncounterFlagOn('railV3'));
  const [previsitQuestionnaireIds] = useState(() => parseQuestionnaireIds(env.previsitQuestionnaireIds));
  const [patientReported, setPatientReported] = useState<PatientReportedState>(PATIENT_REPORTED_OFF);
  const [viewedReportId, setViewedReportId] = useState<string | null>(null);
  const viewedReport =
    patientReported.kind === 'ready' ? (patientReported.responses.find((r) => r.id === viewedReportId) ?? null) : null;
  const ipsSections = useMemo(() => {
    // `?x=rail-fixes` on the rail-v3 rail: family history reads the relative first (Samer, 3 Nov; to-ips-sections.ts).
    const base = patientSummaryToIpsSections(summaryQuery.data, { familyRelation: familyRelationOn });
    if (patientReported.kind === 'ready') return withPatientReportedSection(base, patientReportedSection(patientReported.responses, setViewedReportId));
    if (patientReported.kind === 'error') return withPatientReportedSection(base, patientReportedSection(null, setViewedReportId));
    return base;
  }, [summaryQuery.data, patientReported, familyRelationOn]);
  const patientReportedDialog = (
    <>
      {patientReportedOn ? (
        <PatientReportedReader patientId={patientId} questionnaireIds={previsitQuestionnaireIds} onState={setPatientReported} />
      ) : null}
      {viewedReport ? <PatientReportedAnswersDialog response={viewedReport} onClose={() => setViewedReportId(null)} /> : null}
    </>
  );
  // Raw flags for the rail's write path (PROD-1989). `ipsSections` drops `flagResponse.code`, and a
  // list-replace body without it is rejected by the backend. Stays `undefined` until the summary
  // resolves so the rail can tell "still loading" from "loaded, no flags" — the latter must remain
  // writable or the first flag could never be added.
  // PROD-2804: an OMITTED flags field is "not loaded", not "no flags" — the flag write is a
  // list-replace, so treating a missing list as [] would let the first added flag wipe the rest.
  const flagRecords = useMemo(
    () => (summaryQuery.data && Array.isArray(summaryQuery.data.flags) ? summaryQuery.data.flags : undefined),
    [summaryQuery.data],
  );
  // R6 (PROD-2804): sections whose field a SETTLED read did not deliver as a list say
  // "… unavailable", never "No … recorded". A pending read is the loading state instead.
  const summarySettledWithData = summaryQuery.isSuccess || (summaryQuery.isError && summaryQuery.data !== undefined);
  const unavailableSections = useMemo(
    () => (summarySettledWithData ? unavailableSummarySections(summaryQuery.data) : []),
    [summarySettledWithData, summaryQuery.data],
  );

  // In-encounter patient-summary (PROD-1329 / PROD-1396): the SAME PatientSummaryRail the pre-encounter
  // rail renders — full patient card + INTERACTIVE IPS (add/edit/delete/resolve) + occupation/education
  // — but in `layout="banner"` (card LEFT + 2-col IPS masonry RIGHT), composed by EncounterShellView as
  // a TOP banner above the canvas (Flutter renders patientSummaryWidget in landscape there, not a left
  // rail). Interactivity is live via the same patientId/currentUserId/encounterId gates.
  /*
   * Flutter's `isPatientInfoViewExpanded` (encounter_screen.dart:113) — ONE flag, TWO effects:
   * the band's height (180px / 70% of the frame, :1112-1136) and its content mode
   * (`ClinicalInfoViewType.view` -> `.editable`, :1449-1454). It lives HERE because the shell
   * owns the height and the rail owns the content, and a single chevron has to drive both.
   */
  const [summaryExpanded, setSummaryExpanded] = useState(false);

  const inEncounterSummaryRail = (
    <PatientSummaryRail
      expanded={summaryExpanded}
      layout="banner"
      patient={patient}
      demographics={demographics}
      ipsSections={ipsSections}
      flagRecords={flagRecords}
      occupation={toRailOccupation(detailQuery.data)}
      education={toRailEducation(detailQuery.data)}
      occupationEntity={detailQuery.data?.patientOccupation}
      educationEntity={detailQuery.data?.patientEducation}
      patientId={patientId ?? undefined}
      currentUserId={user?.id}
      currentUserName={[user?.firstName, user?.lastName].filter(Boolean).join(' ') || undefined}
      encounterId={encounterId ?? undefined}
      performerId={user?.practitionerId ?? user?.id}
      canStopMedication={mayStopOrSuspend === true}
      /* Tap-time nav first (already correct, no round trip); otherwise the fetched appointment.
         Without the fallback the status pill disappeared on every reload and deep-link, because
         `nav` is router state — see use-appointment-details.ts. */
      appointmentStatus={appointmentStatusCode}
      appointmentId={appointmentId}
      importance={detailQuery.data?.importance}
      contacts={toContacts(detailQuery.data?.emergencyContactResponse)}
      loading={summaryLoading || detailQuery.isLoading}
      loadError={summaryLoadError}
      onRetry={retrySummary}
      unavailableSections={unavailableSections}
    />
  );

  // Vitals editing (PROD-1245): needs the encounter (CreateVitals carries encounter). Edit affordance
  // opens a DS-atom dialog; save fires CreateVitals/updateVitals + invalidates the vitals read.
  const [vitalsDialogOpen, setVitalsDialogOpen] = useState(false);
  // Vitals "Historical" drawer (PROD-1535): the encounter-grouped read-only history
  // (GetEncounterVitalsHistory). Needs BOTH ids, so it's gated on `vitalsEditable`
  // (same gate as edit) — the affordance stays hidden until an encounter resolves.
  const [vitalsHistoryOpen, setVitalsHistoryOpen] = useState(false);
  // PROD-2810: an ENCOUNTER-RECORD save (vitals, chief complaint) refused because the note is signed
  // (409 `encounter-signed`) is LATCHED for this encounter, the way the Risk save latches
  // (`useRiskAssessments.saveRefused`, which never clears within an encounter): the refusal stays on
  // screen, and no later record write is sent (the server would refuse every one; these fields have
  // no amendment path). Other failures keep their behaviour.
  const [recordRefusedFor, setRecordRefusedFor] = useState<string | null>(null);
  const latchRecordRefusal = (error: unknown): boolean => {
    if (!encounterId || !isSignedNoteRefusal(error)) return false;
    setRecordRefusedFor(encounterId);
    return true;
  };
  const saveVitals = useSaveVitals(patientId ?? undefined, encounterId ?? undefined, (error) => {
    latchRecordRefusal(error);
  });
  const recordRefused = !!encounterId && recordRefusedFor === encounterId;
  /** Every vitals write goes through here: none is sent once a record save was refused as signed. */
  function sendVitals(req: VitalsRequest) {
    if (recordRefused) return;
    saveVitals.mutate(req);
  }
  // Do we have the ids the vitals surfaces need at all? This gates the READ affordances —
  // "View Vitals" and the history drawer — and must NOT gate on status: Andrew, 2026-08-25,
  // "I should have the option to view vitals". An earlier cut folded the arrived check in here and
  // took View Vitals with it.
  const vitalsEditable = !!patientId && !!encounterId;

  /**
   * The WRITE affordances — the "+", inline edit, the visual-acuity form.
   *
   * Flutter: `canEditVitals => isArrived` (appointment_model.dart:528), and the vitals header hides
   * its edit control when that is false (`showEdit`, pre_encounter_widget.dart:486). That is the "+"
   * Andrew flagged on a Booked appointment. Reading stays available either way, which is Flutter's
   * shape too — `showEdit` hides the pencil, not the panel.
   */
  const vitalsWritable = vitalsEditable && isArrivedStatus(appointmentStatusCode);
  function onSubmitVitals(form: VitalsFormValue, lmpFlags?: LmpFlags) {
    if (!patientId || !encounterId) return;
    const req = buildVitalsRequest(
      form,
      {
        patientId,
        encounterId,
        existing: vitalsQuery.data,
        now: new Date().toISOString(),
      },
      lmpFlags,
    );
    if ('error' in req) return; // dialog pre-validates with the same rules; keep open on the rare miss
    sendVitals(req);
    setVitalsDialogOpen(false);
  }
  // Inline variant: identical to onSubmitVitals minus the dialog close (the grid owns its own
  // edit mode). Kept as its own function rather than a parameter so the dialog path is untouched.
  function onSubmitVitalsInline(
    form: Record<string, string>,
    lmpFlags?: { isMenstrual: boolean; isBreastFeeding: boolean },
  ) {
    if (!patientId || !encounterId) return;
    const req = buildVitalsRequestFromCodes(
      form,
      { patientId, encounterId, existing: vitalsQuery.data, now: new Date().toISOString() },
      lmpFlags,
    );
    if ('error' in req) return; // the grid pre-validates with the same rules
    sendVitals(req);
    // Visual acuity rides its OWN request (it writes extraData, which the numeric body has no
    // slot for), so the inline form commits alongside the same Save press. Only when the draft
    // actually differs from what is stored - otherwise every vitals save would re-POST an
    // unchanged acuity row.
    const acuityChanged =
      JSON.stringify(acuityDraft) !== JSON.stringify(existingVisualAcuity ?? emptyVisualAcuity());
    if (acuityChanged && !isVisualAcuityEmpty(acuityDraft)) {
      const acuityReq = buildVisualAcuityRequest(acuityDraft, {
        patientId,
        encounterId,
        existing: vitalsQuery.data,
        now: new Date().toISOString(),
      });
      if (!('error' in acuityReq)) sendVitals(acuityReq);
    }
  }
  /**
   * PROD-3183 (vitals part). An accepted "Dictated vitals" suggestion: ONE vitals capture, MERGED with
   * the encounter's current readings (`buildDictatedVitalsRequest`), through the same save as the
   * vitals card. Every vitals save replaces the encounter's capture, so the current readings must be
   * loaded: until they are, nothing is written. Resolves true only once the server stored it, which
   * is when the panel records the accept.
   */
  async function handleAcceptDictatedVitals(values: readonly AcceptedVital[]): Promise<DictatedVitalsAcceptResult> {
    if (!patientId || !encounterId || !vitalsWritable || recordRefused || !vitalsQuery.isSuccess) return false;
    // What the panel showed: the clinician chose "keep" or "use dictated" against these values.
    const shown = vitalsQuery.data ?? [];
    // READ AGAIN BEFORE MERGING. The page's copy can be minutes old, and a nurse may have saved vitals
    // since. Merging into the stale copy would write a capture without her readings, and the save
    // replaces the capture, so they would be lost. A failed read writes nothing.
    let latest: PatientVital[] | undefined;
    try {
      const fresh = await vitalsQuery.refetch();
      if (!fresh.isSuccess) return false;
      latest = fresh.data ?? [];
    } catch {
      return false;
    }
    // A reading for an accepted code changed since the panel showed it: a dictated value would
    // silently replace one nobody chose to replace. Write nothing; the panel now shows the new
    // recorded value and asks the clinician to review.
    if (recordedVitalsChanged(shown, latest, values.map((v) => v.code))) return 'recorded-vitals-changed';
    const req = buildDictatedVitalsRequest(values, {
      patientId,
      encounterId,
      existing: latest,
      now: new Date().toISOString(),
    });
    if ('error' in req) return false;
    try {
      await saveVitals.mutateAsync(req);
      return true;
    } catch {
      return false;
    }
  }

  // Mark-reviewed (PROD-1536): re-save the current readings with status "reviewed" (Flutter
  // updateVitalReviewed → save with reviewed status). Reuses the vitals save transport.
  const vitalsReviewed = isVitalsReviewed(vitalsQuery.data);
  // "Vitals Reviewed" is actionable only when this encounter HAS recorded readings —
  // otherwise the strip renders it disabled (was a silent no-op: buildMarkReviewedRequest
  // returned {error} and nothing happened; Samer vitals review 2026-07-23).
  const hasVitalReadings = (vitalsQuery.data ?? []).some((v) => !v.isDeleted);
  function onMarkVitalsReviewed() {
    const req = buildMarkReviewedRequest(vitalsQuery.data);
    if ('error' in req) return; // nothing recorded yet → no-op
    sendVitals(req);
  }

  // Visual acuity (PROD-1537): the "+ Visual Acuity" affordance opens a dialog; on Done the R/L eye
  // acuity is persisted as the visual-acuity vital's extraData via the same vitals save path.
  const [visualAcuityOpen, setVisualAcuityOpen] = useState(false);
  // WAVE 2: the acuity entry point lives in the Edit dialog; the tile shows the current
  // OS/OD summary (same derivation the strip tile uses) or "Add…".
  const vaVitalRaw = (vitalsQuery.data ?? []).find(
    (v) => !v.isDeleted && v.code?.code === VISUAL_ACUITY_LOINC,
  );
  const visualAcuitySummary =
    (vaVitalRaw?.quantity?.value?.trim() || null) ??
    ((vaVitalRaw ? visualAcuityReading(vaVitalRaw)?.value : null) as string | null);
  const existingVisualAcuity = fromVisualAcuityJson(
    (vitalsQuery.data ?? []).find((v) => !v.isDeleted && v.code?.code === VISUAL_ACUITY_LOINC)?.extraData,
  );
  // Working copy for the INLINE acuity form (the dialog keeps its own). Seeded from the saved
  // reading; committed by the same buildVisualAcuityRequest path the dialog uses.
  const [acuityDraft, setAcuityDraft] = useState<VisualAcuityData>(() => emptyVisualAcuity());
  // Depend on the SERIALISED value, not the object. fromVisualAcuityJson() builds a fresh object
  // every render, so an identity dependency re-fired the effect on every pass - setState ->
  // rerender -> setState, which React kills with "Maximum update depth exceeded" (it surfaced
  // inside the DS SelectRoot the acuity form composes).
  const existingAcuityKey = JSON.stringify(existingVisualAcuity ?? null);
  useEffect(() => {
    setAcuityDraft(existingVisualAcuity ? { ...existingVisualAcuity } : emptyVisualAcuity());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the serialised value above
  }, [existingAcuityKey]);
  function onSubmitVisualAcuity(data: VisualAcuityData) {
    if (!patientId || !encounterId) return;
    const req = buildVisualAcuityRequest(data, {
      patientId,
      encounterId,
      existing: vitalsQuery.data,
      now: new Date().toISOString(),
    });
    if ('error' in req) return; // dialog pre-validates with the same rules
    sendVitals(req);
    setVisualAcuityOpen(false);
  }

  // ── Wrap-Up step (PROD-1295) ────────────────────────────────────────────────
  // Feed the shell's 'wrap-up' step with live data. Two sections wire to ready,
  // verified reads now; Services + Invoice are deferred (PROD-1309) — they need an
  // encounter→invoice resolution that no fetch exposes yet, and a populated
  // encounter to smoke (the bringup account has none). Gated to the step shell
  // (`!showRail`) so the rail phase doesn't fire these reads.
  const wrapActive = !showRail;
  /*
   * PROD-1549 notes-rail parity: the sign-lock read is NO LONGER scoped to the started shell.
   *
   * It was `wrapActive ? … : null`, and `wrapActive = !showRail` — so on the pre-encounter screen
   * the query never ran and that surface could not know whether the encounter was signed. Flutter
   * mounts ONE `GeneratedNotesWidget` beside both screens (`encounter_screen.dart:1657`) and its
   * footer reads `isSignAndLocked` on both, so the pre-encounter note now carries the same footer
   * and the same "Signed" attestation. Only THIS query is widened; `wrapActive` still gates the
   * billing/wrap-up calls below, which is what it was actually protecting.
   */
  const signLockQuery = useSignLock(encounterId, patientId);

  const createSignLock = useCreateSignLock(encounterId, patientId);
  // Follow-Up scheduler (PROD-1405): read the wrap-up record (prefill), upsert on change. Doctor-only
  // (Flutter gates followUpWidget on `!isNurse`). Gated to the step shell like the other wrap-up reads.
  // Doctor-only: the follow-up UI is gated on !isNurse, so don't even fetch the (doctor-only)
  // wrap-up record for nurses — avoids caching it + any 403/retry noise if the API role-gates it. (Codex P2)
  const wrapUpEnabled = wrapActive && !isNurse;
  const wrapUpQuery = useWrapUp(wrapUpEnabled ? encounterId : null, wrapUpEnabled ? patientId : null);

  // ── Wrap-Up INVOICE block ────────────────────────────────────────────────────────────────
  // Flutter gates the whole block on the Show_Billing_Summary VIEW permission AND !isNurse
  // (wrap_up_billing_screen.dart:680-686). Both gates ride into the fetch too, so a nurse or an
  // unpermitted user issues zero billing calls. Scoped to the wrap-up step (`wrapActive`) so
  // the rest of the encounter never pays for it.
  // The same gate drives the Pending orders list (PROD-2594) — see `lib/wrap-up-invoice-gate`.
  const canSeeInvoice = canSeeWrapUpInvoice({
    wrapActive,
    isNurse,
    systemPermissions: user?.systemPermissions,
  });
  const encounterInvoice = useEncounterInvoice({
    appointmentId,
    patientId,
    providerId,
    enabled: canSeeInvoice,
  });
  const invoiceSectionData = useMemo(
    () => toInvoiceSection(encounterInvoice.invoice, user?.organizationCurrency || 'AED'),
    [encounterInvoice.invoice, user?.organizationCurrency],
  );
  // ── ePrescription (eRx → DHPO) ───────────────────────────────────────────────────────────
  // Flutter `showRx` (wrap_up_billing_screen.dart:265-269): eRx configured for this provider AND
  // (an existing eRx that the plan has since changed, OR >=1 prescribed med, OR >=1 active med).
  // `isRxSynced` (:263-264): an eRx exists AND the plan has NOT changed since it was sent.
  const erxEnabledQuery = useEPrescriptionEnabled(
    wrapUpEnabled ? providerId : null,
    wrapUpEnabled ? user?.practiceId : null,
  );
  const erxPreviewQuery = useEPrescriptionPreview(encounterId, !!wrapUpEnabled && !!erxEnabledQuery.data);
  const submitEPrescription = useSubmitEPrescription();
  const [resendConfirmOpen, setResendConfirmOpen] = useState(false);
  const [erxPreviewOpen, setErxPreviewOpen] = useState(false);
  const erxRef = ePrescriptionId(erxPreviewQuery.data);
  const erxStale = !!assessmentQuery.data?.ePrescriptionChanged;
  const medCount =
    orders.filter((o) => o.type === 'medication').length +
    toRichActiveMedications(summaryQuery.data, rxOrdersQuery.data).length;
  // PROD-2946: sending an ePrescription is prescribing, so the cluster is offered only on the
  // physician allowlist (`ordersOffered`). Flutter's `showRx` had no role term, so a nurse at Wrap-up
  // on a visit with medications saw Send ePrescription.
  const showRx = ordersOffered && !!erxEnabledQuery.data && ((!!erxRef && erxStale) || medCount > 0);
  const isRxSynced = !!erxRef && !erxStale;
  function sendEPrescription() {
    if (!encounterId) return;
    // Flutter confirms before a RE-send because the backend cancels the previous eRx
    // (:358-373). A first send goes straight through.
    if (erxRef) setResendConfirmOpen(true);
    else submitEPrescription.mutate(encounterId);
  }

  // Invoice mutations. Both add and remove ride the SAME AddRemoveService contract Flutter uses
  // (wrap_up_billing_screen.dart:645-654 / :825-835).
  const invoiceServiceMutation = useAddRemoveService(encounterInvoice.invoiceId);
  // Row ×: an order line goes back to Pending orders (`Orders/revert`); other lines are removed.
  const invoiceLineRemove = useRemoveInvoiceLine({
    invoiceId: encounterInvoice.invoiceId,
    services: encounterInvoice.invoice?.services,
    removeService: (lineId) => invoiceServiceMutation.mutate({ removeServices: [lineId] }),
  });
  const [addServiceOpen, setAddServiceOpen] = useState(false);
  // Preview Invoice → the shipped print-preview path (usePrintInvoice, PROD-1001). Flutter opens
  // its own InvoicePopUp; that popup is not ported, but the underlying artefact — the rendered
  // invoice PDF — is the same one this hook previews, so the action is honest rather than dead.
  const printInvoice = usePrintInvoice();
  // Management discount — SELF-PAY only and only while the invoice is editable, matching the
  // Flutter tap gate (invoice_services_widget.dart:852-890 + the not-ISSUED check).
  const [discountOpen, setDiscountOpen] = useState(false);
  // Per-LINE discount (Flutter EditServicePopup) — the row pencil. Distinct from the
  // invoice-wide management discount above.
  const [editLineId, setEditLineId] = useState<string | null>(null);
  const updateServiceDiscount = useUpdateServiceDiscount(encounterInvoice.invoiceId);
  const addManagementDiscount = useAddManagementDiscount();
  // Sponsored + draft → the per-line coverage/auth-code editors are live (Flutter gates the
  // whole editable row set on `enableInvoiceEdit`, which the wrap-up passes as `status == Draft`).
  const invoiceLinesEditable =
    !!encounterInvoice.invoiceId && !!invoiceSectionData && invoiceSectionData.sponsored && invoiceSectionData.draft;
  const canEditManagementDiscount =
    !!encounterInvoice.invoiceId && !!invoiceSectionData && !invoiceSectionData.sponsored && invoiceSectionData.draft;
  const saveWrapUp = useSaveWrapUp(encounterId, patientId);
  const [followUpValue, setFollowUpValue] = useState<{ count: number; unit: FollowUpUnit }>({
    count: 0,
    unit: 'Weeks',
  });
  useEffect(() => {
    // Sync local follow-up state to the loaded record once the query SETTLES — incl. resetting to
    // the default when the (new) encounter has no follow-up, so a prior encounter's value can't
    // linger and get saved to this one (Codex P2). Skip while loading (`data === undefined`) to
    // avoid flashing 0 then the real value. After a save the invalidated refetch returns the value
    // we just persisted, so this never clobbers an in-progress edit (controls are disabled mid-save).
    if (wrapUpQuery.data === undefined) return;
    setFollowUpValue(followUpFromWrapUp(wrapUpQuery.data));
  }, [wrapUpQuery.data]);
  const pvsDetailsQuery = useEncounterClinicalDetails({
    encounterId: wrapActive ? encounterId ?? undefined : undefined,
    patientId: wrapActive ? patientId ?? undefined : undefined,
  });
  const pvsPrint = usePVSPrint();

  const signLockState = deriveSignLockState(signLockQuery.data);
  const postVisitSummary = toPostVisitSummary(pvsDetailsQuery.data);
  // Doctor Sign & Lock is gated on a diagnosis (Flutter bloc rule); the A/P canvas
  // state lives in this page, so the gate is the live Dx count. Nurse sign isn't gated.
  /**
   * The persisted chief-complaint document, and the selection derived from it.
   *
   * HOISTED here, and the read is now UNCONDITIONAL — it used to sit with the rest of the CC block
   * and be gated on `ccActive` (`showRail || currentStep === 'hpi'`). Both changes exist to make
   * Flutter's doctor sign-gate correct: signing happens on WRAP-UP, where `ccActive` is false, so the
   * complaint was never fetched there and any gate reading it would have blocked a doctor who HAD
   * entered a complaint. It also has to be declared before the sign gates below, which the old
   * position was not.
   *
   * Cost, stated: one small GET per encounter on surfaces that previously skipped it (the A/P and
   * Wrap-Up steps). The suggestions query stays gated on `ccActive` — that one is per-keystroke and
   * has no business running outside the editor.
   */
  // PROD-2947 · frame v2 only: `mappedOnly` (Scrips.Patient #1040) — an unmapped complaint brings no attribute
  // groups instead of the whole 119-option catalogue. OFF sends exactly today's request.
  const complaintQuery = usePatientComplaint(patientId, encounterId, frameV2On ? { mappedOnly: true } : undefined);
  // Optimistic selection while the save is in flight; server truth otherwise.
  const [ccPending, setCcPending] = useState<string[] | null>(null);
  const ccServerSelected = useMemo(() => complaintsToSelected(complaintQuery.data), [complaintQuery.data]);
  const ccSelected = ccPending ?? ccServerSelected;
  // PROD-2136 — the optimistic complaint DOCUMENT while a chip toggle's replace-all save is in
  // flight (the HPI block's chips would otherwise snap back to the stale server list for one round
  // trip, exactly the added-removed-added flicker `ccPending` cures for the complaint chips). Held
  // until the post-save refetch lands, then handed back to server truth in the same onSettled.
  const [ccHpiPending, setCcHpiPending] = useState<PatientComplaintModel | null>(null);
  const ccDocument = ccHpiPending ?? complaintQuery.data;
  // PROD-2789 — the complaints as {code, display} (`coding.code` / `coding.display`), read off the
  // SAME document the HPI block draws. No new fetch. They only suggest which ROS / exam systems
  // open first.
  const ccComplaintRefs = useMemo(() => complaintRefs(ccDocument), [ccDocument]);
  // PROD-2947 Part 2 · frame v2 · G8: in every section line, the phrases the libraries' starter maps list for the
  // visit's chief complaint come FIRST in the phrase menu, in that order — suggested, never inserted. The order is
  // the complaint map's `phrasesFirst` (pcp-adult-v1 for a physician, the nurse library for a nurse), for a patient
  // 18 or older on the visit date (the libraries' scope). Read off what the page already holds; no new request.
  const suggestPhrasesFor = useMemo(
    () => phraseSuggester(ccComplaintRefs, toClinicalRole(encounterRole), chartLaunchContext.patient.birthDate, chartLaunchContext.visitDate),
    [encounterRole, chartLaunchContext.patient.birthDate, chartLaunchContext.visitDate, ccComplaintRefs],
  );
  // One array per complaint / author / age, so the A&P line's phrase menu is not rebuilt on every page render.
  const apSuggested = useMemo(() => suggestPhrasesFor(AP_NARRATIVE_EDITORS), [suggestPhrasesFor]);
  // PROD-3055 (b, d) · frame v2: the visit's booked specialty NAME is an allied clinician's discipline — for her phrase
  // editors and the plan's framework. (Nothing read it before: the plan said "not recorded yet" to every allied
  // clinician.)
  const bookedSpecialtyName = appointmentStatusQuery.data?.specialtyName ?? undefined;
  const alliedDiscipline = frameV2On && encounterRole === 'allied-health' ? alliedDisciplineOf(bookedSpecialtyName) : undefined;
  const alliedPlanSpeciality = frameV2On && encounterRole === 'allied-health' ? planSpecialityOf(bookedSpecialtyName) : undefined;

  // Flutter: `ccData?.title.trim().isEmpty` — at least one complaint on the record.
  const hasChiefComplaint = ccSelected.length > 0;
  /*
   * The visit's chief complaint as CONTEXT in the A&P (frame v2; the view gates it on the switch, so
   * OFF renders nothing new). Dev test walk, 1 Oct: "chief complaint is not moving to assessment" — the A&P
   * read nothing from the complaint document. This is the SAME list the HPI block and the sign gate
   * read (`ccSelected`), so the two steps cannot disagree; a failed read stays a failed read, never
   * "no complaint". A readout only: it adds and pre-fills nothing (authority §4 rules 3 and 4).
   */
  const apVisitComplaint = useMemo<VisitComplaintState>(
    () =>
      complaintQuery.isError
        ? { kind: 'error' }
        : complaintQuery.isSuccess
          ? { kind: 'ready', complaints: ccSelected }
          : { kind: 'loading' },
    [complaintQuery.isError, complaintQuery.isSuccess, ccSelected],
  );
  // The A&P view's props: frame v2 adds the visit's complaint; OFF hands main's object unchanged.
  const apViewBase: AssessmentPlanViewProps = frameV2On
    ? { ...assessmentPlan, visitComplaint: apVisitComplaint, noteSuggested: apSuggested }
    : assessmentPlan;

  /*
   * The note's CHIEF COMPLAINT rows + its Notes row, from the SAME `complaintQuery` record the HPI
   * step reads. Until 2026-09-09 the note derived its CC section from the QuestionnaireResponse
   * alone, so an encounter with saved complaints and an untouched HPI questionnaire printed
   * "No CC data selected." — the note contradicting the chart. Flutter reads the complaints
   * (`chief_complaint_note_component_widget.dart.dart:52-53`).
   *
   * One memo, consumed by BOTH hosts, so the pre-encounter note and the drawer cannot disagree.
   */
  const noteComplaints = useMemo(() => toNoteComplaints(complaintQuery.data), [complaintQuery.data]);
  /*
   * The A/P order row's second line. `PlanOrder` cannot carry it (`{id,type,label,dxIds}`), so the
   * detail is looked up in the host's live payload map — see `note-column/to-note-order-detail.ts`.
   */
  const noteOrderDetailFn = useMemo(() => noteOrderDetail(payloads), [payloads]);
  const hasDiagnosis = diagnoses.length > 0;
  // `doSign`'s two clinical gates (chief complaint, then a diagnosis for anyone but allied health; never the nurse), as the
  // reason a Sign click would get now. The Sign still refuses on click; this only lets the floating bar say so first.
  const clinicalGateReason = isNurse
    ? undefined
    : !hasChiefComplaint
      ? 'Please enter chief complaint'
      : !hasDiagnosis && encounterRole !== 'allied-health'
        ? 'Please add at-least one diagnosis before signing encounter'
        : undefined;
  // Sign step done = the encounter is signed (doctor → locked; nurse → nurse-signed). Drives the
  // status header's Sign ✓ + the progress value (PROD-1410).
  const signed = signLockState === 'locked' || signLockState === 'nurse-signed';
  signedRef.current = signed;
  // A PROVIDER sign freezes the encounter records (A&P, wrap-up, vitals, chief complaint) but never the note's answers
  // (Flutter sign model, 14 Oct 2026): QuestionBank takes a snapshot per sign and the edits go on as ordinary saves.
  // A nurse-only sign freezes nothing. See `isProviderSigned` for why this is not `signed`.
  const providerSigned = isProviderSigned(signLockQuery.data);
  providerSignedRef.current = providerSigned;
  // Who signed, for the closing card's footer: the sign record names the performer by id only, so a name is shown only when that
  // performer is the signed-in practitioner. Anyone else (or no performer) → the footer leaves the name out, as main names no
  // signer: the visit's booked provider is not necessarily who signed.
  const providerSignRecord = (signLockQuery.data ?? []).find((r) => r.isSignedByNurse !== true);
  const signerIsMe = !!providerSignRecord?.performer && [user?.practitionerId, user?.id].includes(providerSignRecord.performer);
  const signerName = (signerIsMe ? [user?.firstName, user?.lastName].filter(Boolean).join(' ') : '') || undefined;
  // PROD-2810 / rule 38 (PROD-3031 item 3, switch ON and OFF — the gap is on main too, and Andrew asked for it
  // ungated): after a PROVIDER sign the A&P is read-only. Scrips.Patient refuses an in-place
  // A&P change after the provider sign ([SignedAssessmentPlanWrite], API/Controllers/PatientsController.cs:1897
  // and :1942; AssessmentPlanPostSignRule.IsPermittedAfterSign, Application/Common/AssessmentPlanPostSignRule.cs
  // :44) and the A&P has no amendment path. Applied to the A&P VIEW only: `apReadOnly` (the page's own
  // gates) is unchanged, so the post-sign paths the server still accepts (Send orders, a vaccine administration)
  // keep working. A nurse-only sign freezes nothing.
  const apFrozen = providerSigned;
  // "Add to note" on an Active Problems row (frame v2 chart column) writes a diagnosis, so it is offered only to the A&P's own
  // writer: the role whose steps carry the A&P ('ap', `resolveSteps`: the physician; never a nurse or allied health, Andrew's
  // ruling), once the A&P has loaded (`apReady` inside `apReadOnly`, so the first-load seed cannot overwrite the add) and while
  // the A&P is editable (not mid-sign, not frozen by the provider sign). Everyone else keeps main's "+ Note".
  const mayAddProblemToNote = started && navSteps.includes('ap') && !apReadOnly && !apFrozen;
  // Andrew's walk, 7 Oct (B10 / D3; ON and OFF): Discard This Note is a provider's act on an UNSIGNED note — gone
  // after the provider's sign (rule 38), and never offered to a nurse.
  const mayDiscard = !isNurse && !providerSigned;
  // The view runs the added templates only once the visit has an encounter and the template list is read.
  // Dev only (decision A): off on any other host, so no answer sets are read, "/" offers no template and no block shows.
  const [insertOn] = useState(() => isDevHost());
  const insertedTemplates = useMemo<InsertedTemplatesConfig | undefined>(
    () =>
      insertOn && encounterId && patientId && templateData
        ? { encounterId, patientId, templates: templateData.all, mainTemplateId: templateData.questionnaire?.id ?? null }
        : undefined,
    [insertOn, encounterId, patientId, templateData],
  );
  // Frame v2 (Andrew's walk, 7 Oct, B12; rule 38): after the PROVIDER's sign the pre-visit vitals are read-only — the
  // inline editor, its "+" and the acuity form. OFF unchanged.
  const preVisitVitalsWritable = vitalsWritable && !(frameV2On && providerSigned);
  // Rule 38 in Wrap-up (the independent review of #634, item B; switch ON and OFF, for the same reason): the
  // patient instructions and the follow-up are ONE wrap-up record (CreateWrapUp / UpdateWrapUp), which
  // Scrips.Patient refuses after the PROVIDER sign ([SignedEncounterWrite], API/Controllers/PatientsController.cs
  // :1959 and :1991, 409 `encounter-signed`) and which no amendment covers. So, as for the A&P, both are
  // read-only after the provider sign — and once any record save was refused as signed (the
  // latch), since the server would refuse this one too.
  const wrapUpFrozen = providerSigned || recordRefused;
  // [PROD-2949] One plan-save path for the two non-prescriber plans, both behind `?ef=planSave`: the
  // allied-health Plan step (MS-1) and the nurse's plan step (`nursing-plan`, D-66). OFF → no read, no
  // prop: the allied Plan step keeps its placeholder and the nurse's plan step is hidden (`stepHides`).
  // Chosen ONCE per mount (the switch is read once above), so the hook order never changes; OFF runs
  // the inert draft, which calls no query hook at all.
  const usePlanDraft = planSaveOn ? useNonPrescriberPlanDraft : useNoNonPrescriberPlanDraft;
  const alliedPlanDraft = usePlanDraft({
    patientId, encounterId, audience: 'allied-health', readOnly: providerSigned, mayWrite: mayOrder !== true,
    enabled: planSaveOn && encounterRole === 'allied-health',
  });
  const nursingPlanDraft = usePlanDraft({
    patientId, encounterId, audience: 'nurse', readOnly: providerSigned, mayWrite: mayOrder !== true,
    enabled: planSaveOn && encounterRole === 'nurse',
  });
  /**
   * WHAT LOCKS AMBIENT: A PROVIDER SIGN, NOT ANY SIGN (encounter owner ruling, 2026-09-30).
   *
   * Every ambient gate on this page used `signed`, which is true after a nurse-only sign too. The
   * nurse branch neither freezes the note nor completes the encounter, and the flow it serves is
   * nurse intake, nurse sign, then the physician's visit WITH ambient — so the physician met an
   * inert panel (no verbs, no Decline, no Undo, no chief complaint, no narratives) on a note the
   * server still accepts her work on. The server's own ambient refusals already key on a provider
   * sign (`RefuseIfSignedAsync` reads provider sign rows only), so this is the same line.
   *
   * Used by the panel's `encounterLocked`, the A&P write guard below, the narrative and sink
   * merges, and the recoveries hook. `signed` keeps its other jobs (the Sign pill, progress).
   */
  // Or a provider sign THIS page just made: `useCreateSignLock` re-fetches the sign-lock read without
  // waiting, so between its success and that read landing `providerSigned` still says unsigned. The
  // same window rule 5's append refuses on (Codex, #532); `lock: true` is the provider's request.
  const ambientLocked =
    providerSigned || (createSignLock.isSuccess && createSignLock.variables?.lock === true);
  apWriteBlockedRef.current = ambientLocked
    ? 'signed'
    : signPromoting || createSignLock.isPending
      ? 'signing'
      : !apReady
        ? 'loading'
        : null;

  // Send Orders (PROD-1410 / Flutter SendOrderEvent): re-save the A/P with the order-sent flag set —
  // NOT a separate transmission API. Gated like Flutter's `isDiagnosisAdded` + one-shot (no re-send
  // once sent, no overlap with an in-flight save). The optimistic latch makes it stick before refetch.
  function handleSendOrders() {
    if (!encounterId || !patientId || !providerId) return;
    if (orderSent || saveAssessment.isPending || !hasDiagnosis) return;
    const body = toPatientAssessment(diagnoses, orders, payloads, dxCoding, {
      assessmentNote,
      assessmentNoteDelta,
      encounter: encounterId,
      subject: patientId,
      performer: providerId,
      identifier: assessmentIdentifier,
      serverIds: serverIdsRef.current,
      // Include education articles here too (PROD-1454/Codex) — this whole-document Send-Orders save
      // must NOT re-emit assessmentPlanArticles: [] and wipe the list added on the autosave path.
      articles,
      orderSent: true,
    });
    const sentDxIds = diagnoses.map((d) => d.id);
    const sentNote = assessmentNote;
    saveAssessment.mutate(body, {
      onSuccess: (savedId) => {
        settlePersisted(sentDxIds, sentNote);
        sentOnceRef.current = true; // latch: keep it sent through the refetch + future autosaves
        if (savedId && savedId !== assessmentIdentifier) setAssessmentIdentifier(savedId);
        // Four moments: the isOrderSign save just ran the BE Condition promotion — refresh the
        // chart-detail conditions so identity chips flip and stewardship reconciliation can act.
        void activeConditionsQuery.refetch();
      },
      // The Assessment save can return 500 AFTER the write persists (post-write pubsub timeout —
      // PROD-1152 family; confirmed on the live Send-Orders smoke). useSaveAssessment invalidates
      // onSuccess ONLY, so on that spurious 500 the header wouldn't refresh. Re-read: if the flag
      // persisted (the common case) orderStatus flips sent; if it GENUINELY failed it stays unsent —
      // correct either way. Do NOT latch here (a real failure must not read as sent).
      onError: () => {
        void assessmentQuery.refetch();
      },
    });
  }

  // PROD-1690: signing must ALSO promote the visit's coded diagnoses to the chart problem list.
  // Flutter truth (patient_encounter_bloc.dart ~1112–1200): the sign path FIRST saves the A&P with
  // isOrderSent=true (wire key `isOrderSign` — the flag AssessmentPlanConditionService keys the
  // Condition promotion on, via EnsureConditionForAssessmentPlanAsync), and fires the sign event
  // only AFTER that save succeeds. React previously rode the flag only on Send Orders, so
  // sign-without-send left the problem list un-promoted (continuity bug). `signPromoting` (declared
  // with the orderSent latch above — it also freezes the canvas) surfaces the promotion save as part
  // of the "signing" busy state (createSignLock.isPending alone misses it).
  // Promotion is needed when the order-sign write never happened this encounter and there is
  // something to promote. Keyed on the PERSISTED `isOrderSign` (the exact flag the BE promotion
  // keys on) OR the successful-send latch — NOT the merged `orderSent` display flag: a legacy doc
  // with hasOrderSent:true but isOrderSign:false reads as "sent" for the header, yet its Dx were
  // never promoted, and signing it must still promote (Codex P2). When the write DID happen, the
  // BE already promoted — re-sending would be a redundant write (BE dedupes by patient+code, so a
  // double call is safe, just wasteful).
  const signNeedsOrderSign = !(assessmentQuery.data?.isOrderSign || sentOnceRef.current) && hasDiagnosis;

  // Flutter confirms BEFORE signing, and gates that confirmation on `isOrderSent` ALONE
  // (`getSignLockDialog`, patient_encounter_bloc.dart:3032-3042) — not on whether a diagnosis
  // exists. So this is deliberately NOT `signNeedsOrderSign`, which also requires hasDiagnosis:
  // that flag decides whether to PROMOTE the orders, this one decides whether to ASK.
  //
  // INVARIANT (asked on review): can orders be promoted WITHOUT the confirm gate running?
  // No, by construction — the two share their first term, so
  //     signNeedsOrderSign === signNeedsConfirm && hasDiagnosis
  // which makes promotion a strict subset of confirmation: signNeedsOrderSign ⇒ signNeedsConfirm.
  // Pinned by "never promotes orders without having asked first" in the sign-confirm test, because
  // editing either term independently would break the implication silently.
  //
  // FULL FLUTTER PARITY on the dialog itself (Andrew's call, PROD-2012). An earlier version
  // suppressed the orders sentence on notes with no orders, on the grounds that it states something
  // which will not happen. That was raised for review and the answer was full parity, so the
  // sentence is UNCONDITIONAL, matching Flutter. Button order, left alignment and non-dismissibility
  // follow Flutter too — see the header of sign-note-confirm-dialog.tsx for the per-property source
  // lines. Knowing trade: strict parity over literal accuracy on that one sentence.
  const signNeedsConfirm = !(assessmentQuery.data?.isOrderSign || sentOnceRef.current);

  // `?x=sign-reason` (off by default): a refused Sign also says why in a line next to the Sign button; the toast stays.
  // Every refusal on the sign path goes through `refuseSign`. The line clears when Sign is pressed again, and as soon as
  // anything a refusal can rest on changes (a chief complaint or diagnosis added, an answer edited, a save settling or
  // failing, the template or a Fall/Pain list reloaded): a "Not signed" alert never outlives its reason. Derived on
  // render from the values at refusal time, so no effect has to clear it.
  const [signReasonOn] = useState(() => isEncounterFlagOn('signReason'));
  // The answers by CONTENT, not by object: a save echo or re-hydration that hands back an equal response must not clear
  // the line; an edited answer must. Serialised only when the response object changes.
  const answersKey = useMemo(() => JSON.stringify(response.answers ?? null), [response]);
  const signBlockDeps: readonly unknown[] = [
    hasChiefComplaint,
    hasDiagnosis,
    answersKey,
    templateUnavailable,
    riskSignReason,
    providerId,
    encounterId,
    patientId,
    saveWrapUp.isPending,
    saveWrapUp.isError,
    saveAssessment.isPending,
    riskBridge.saveFailed,
  ];
  // The values a refusal rests on are read from a ref kept at the LATEST commit, never from the closure's render: a
  // refusal fired later (the held sign's `onStopped`, after its write failed) must record the state that refused it.
  const signBlockDepsRef = useRef(signBlockDeps);
  useLayoutEffect(() => {
    signBlockDepsRef.current = signBlockDeps;
  });
  const [signRefusal, setSignRefusal] = useState<{ message: string; deps: readonly unknown[] } | null>(null);
  const signRefusalShown =
    signRefusal && signRefusal.deps.every((d, i) => Object.is(d, signBlockDeps[i])) ? signRefusal.message : null;
  function refuseSign(message: string, options?: Parameters<typeof showWarning>[1]): void {
    showWarning(message, options);
    if (signReasonOn) setSignRefusal({ message, deps: signBlockDepsRef.current });
  }

  /**
   * G11: no sign while the encounter template is unavailable. The note's saved answers were never
   * loaded into the form (there was no template to load them against), so the clinician has not seen
   * what they would be signing, and the template-change comparison could not run. Refused on click
   * with the reason, like the clinical gates; every role. Retry on the banner clears it.
   */
  function templateBlocksSign(): boolean {
    if (!templateUnavailable) return false;
    refuseSign('The encounter template hasn’t loaded — use Retry before signing.');
    return true;
  }

  function handleSign() {
    setSignRefusal(null);
    if (templateBlocksSign()) return;
    // PROD-3142 (D-78): the drawer's Sign has no disabled state, so it is refused here with the header's reason.
    if (riskSignReason) {
      refuseSign(riskSignReason);
      return;
    }
    // Frame v2: text still held by an inline line is written BEFORE the sign, never after it (rule 38).
    if (holdSignForHeldText()) return;
    // ORDER SETS (PROD-2712): orders still waiting for a diagnosis are asked about FIRST, and only
    // they; once resolved the sign continues through the LATEST handler (never a stale closure).
    if (orderSetApply.interceptSign(() => handleSignRef.current?.())) return;
    // THE TWO CLINICAL GATES FIRE HERE, ON CLICK, AS TOASTS — not as a pre-disabled button.
    // That is Flutter's shape: `signNote()` (patient_encounter_bloc.dart:2790-2805) checks the
    // chief complaint first, then the diagnosis, and each failure goes out as
    // `SendErrorMessageEvent(Failure(...))` while the icon itself stays live (its only disable is
    // `IgnorePointer(ignoring: state.isProcessing)`, wrap_up_billing_screen.dart:493-494).
    //
    // Moved here 2026-09-07 on Ahmed's instruction ("Sign note is disabled, please make it same as
    // the deployed version"). They previously sat inside `canDoctorSign`, which greyed the button
    // out and put the reason in a tooltip — the reason was Flutter's verbatim string, but a
    // clinician could not click and be told, they had to hover to find out. Order and wording are
    // Flutter's, including its "at-least" spelling.
    //
    // NOTE what did NOT move: the three save-in-flight gates stay as DISABLE conditions. They are
    // ours, not Flutter's (Codex P2s / PROD-1690) — they stop a sign racing a wrap-up or assessment
    // write, which Flutter's synchronous bloc cannot hit. Turning those into toasts would let the
    // click through and reintroduce the race.
    // Signing is irreversible AND outward-facing — it auto-sends the orders to the patient. React
    // fired it on a single unguarded click; Flutter asks first (generated_notes_widget.dart:452-466).
    if (signNeedsConfirm) {
      setSignConfirmOpen(true);
      return;
    }
    doSign();
  }

  const handleSignRef = useRef<(() => void) | null>(null);
  handleSignRef.current = handleSign;

  function doSign() {
    // Clinical-finalization write — user-initiated only (never auto-fired). Everyone signs;
    // only a signer with the POSITIVE lock capability also locks (toSignLockRequest sets
    // lock = canLock, from canLockEncounter → isDoctor). Locking is irreversible, so a user we
    // cannot positively identify as a doctor — a degraded profile, an allied-health persona —
    // signs WITHOUT locking rather than finalising the encounter by accident. For doctors and
    // nurses this is the same behaviour as the old `lock = !isNurse`.
    // Smoke test 21 Oct: every refusal on this path is said; a sign that stops without a word reads as a dead button.
    if (!encounterId || !patientId) {
      refuseSign(SIGN_BLOCKED.notLoaded);
      return;
    }
    if (!providerId) {
      refuseSign(SIGN_BLOCKED.noPractitioner);
      return;
    }
    // The confirm dialog lands here directly, so the template gate is checked again (G11).
    if (templateBlocksSign()) return;

    // THE TWO CLINICAL GATES — chief complaint, then a diagnosis — fire HERE, as toasts, and the
    // sign icon itself stays live. That is Flutter's shape: `signNote()`
    // (patient_encounter_bloc.dart:2790-2805) refuses with `SendErrorMessageEvent(Failure(...))`
    // while the icon's only disable is `IgnorePointer(ignoring: state.isProcessing)`
    // (wrap_up_billing_screen.dart:493-494). Order and wording are Flutter's, "at-least" included.
    //
    // Moved out of `canDoctorSign` on 2026-09-07 (Ahmed: "Sign note is disabled, please make it
    // same as the deployed version"). They used to grey the button and put the reason in a tooltip,
    // so a clinician had to hover to learn why rather than click and be told.
    //
    // AFTER the confirm, not before, because that is where Flutter's own confirm path puts them:
    // the note drawer runs `signAndLockButtonPress()` → confirm dialog → `sign()` → `signNote()`
    // → these gates (generated_notes_widget.dart:452-466). The wrap-up icon has no confirm at all,
    // so it lands straight here. Putting them ahead of the confirm would also have inverted three
    // PROD-2012 tests that deliberately drive a no-diagnosis encounter to prove the dialog's copy.
    if (!isNurse) {
      // Kept for allied health: the server's provider-sign branch refuses without a chief
      // complaint (Scrips.Patient WrapUpService.cs CreateSignLockAsync), and allied health takes it.
      if (!hasChiefComplaint) {
        refuseSign('Please enter chief complaint');
        return;
      }
      // Physician only. Allied health documents a plan and never a medical diagnosis (encounter
      // authority §2), so a diagnosis cannot be required to sign; the server has no such gate.
      if (!hasDiagnosis && encounterRole !== 'allied-health') {
        refuseSign('Please add at-least one diagnosis before signing encounter');
        return;
      }
    }

    // THE THIRD GATE (C10.2): the required items this signer can see and has not answered, named,
    // same toast shape. Counts the nav this signer has (`navSteps`) over the questionnaire the view
    // renders (`dsQuestionnaire`); see lib/required-items.ts for what "visible" and "answered" mean.
    // Physician and allied health only. The nurse is left alone: no template scopes an item to a
    // persona, so her own items cannot be told apart from the physician's still to come.
    // Behind VITE_REQUIRED_ITEMS_SIGN_GATE (default off everywhere): dev templates mark many items
    // required, so the gate would block nearly every sign at once.
    if (!isNurse && env.requiredItemsSignGateEnabled) {
      const missingRequired = unansweredRequiredItemGaps(dsQuestionnaire, response, navSteps);
      if (missingRequired.length > 0) {
        // Each item named with its section; held for 10 s (desk advisories on #622).
        refuseSign(requiredItemsWarning(missingRequired, stepTitlesOf(templateData?.questionnaire)), {
          duration: REQUIRED_ITEMS_TOAST_MS,
        });
        return;
      }
    }

    // Defensive: never sign while a follow-up save is pending (the button is also disabled) — the
    // BE schedules the follow-up on sign by reading the persisted wrap-up (Codex P2).
    // The Wrap-Up button is disabled in this state, but the note drawer's Sign Note is not, so the reason is said here.
    if (saveWrapUp.isPending) {
      refuseSign(SIGN_BLOCKED.followUpSaving);
      return;
    }
    if (saveWrapUp.isError) {
      refuseSign(SIGN_BLOCKED.followUpFailed);
      return;
    }
    const fireSignLock = () =>
      createSignLock.mutate(
        toSignLockRequest({
          encounterId,
          patientId,
          performerId: providerId,
          canLock,
          assessmentPlanId: assessmentIdentifier,
        }),
        // Andrew's walk, 7 Oct (C3): a refused sign said nothing. Every refusal is said.
        { onError: (err) => showServerError('Signing the note', err) },
      );
    // C3 (Andrew's walk, 7 Oct; ON and OFF): a NURSE sign goes straight to the nurse sign lock, as Flutter
    // (`signNote`, patient_encounter_bloc.dart:2771 → UpdateSignByNurseEvent). The A&P save with the order flags is
    // the PROVIDER's (and the vaccination sign's, which has its own path above): from a nurse the ordering gate
    // refuses it (403) and no sign was made.
    if (!signNeedsOrderSign || isNurse) {
      // Orders already sent (or nothing to promote) — sign directly, no redundant A&P write.
      // KNOWN EDGE (rule 5): Accept then an immediate Sign can lock before the A&P autosave lands; the A&P is not server-frozen, so the line still saves, but anything read at sign time can miss it. Fix pending (sign waits for the save).
      fireSignLock();
      return;
    }
    // Serialize with the canvas autosave: this page's discipline is ONE in-flight A&P PUT at a time
    // (the autosave effect's apBusy guard; handleSendOrders bails the same way). The wrap-up sign
    // button also disables for this window (canDoctorSign below), so this is a defensive no-op.
    if (saveAssessment.isPending) {
      refuseSign(SIGN_BLOCKED.assessmentSaving);
      return;
    }
    const body = toPatientAssessment(diagnoses, orders, payloads, dxCoding, {
      assessmentNote,
      assessmentNoteDelta,
      encounter: encounterId,
      subject: patientId,
      performer: providerId,
      identifier: assessmentIdentifier,
      serverIds: serverIdsRef.current,
      // Whole-document save — must not re-emit assessmentPlanArticles: [] and wipe the education
      // list added on the autosave path (PROD-1454 rule, same as Send Orders).
      articles,
      orderSent: true,
    });
    setSignPromoting(true);
    const sentDxIds = diagnoses.map((d) => d.id);
    const sentNote = assessmentNote;
    saveAssessment.mutate(body, {
      onSuccess: (savedId) => {
        settlePersisted(sentDxIds, sentNote);
        sentOnceRef.current = true; // latch: keep "sent" through the refetch + future autosaves (PROD-1410)
        if (savedId && savedId !== assessmentIdentifier) setAssessmentIdentifier(savedId);
        // Flutter ordering: the sign-lock fires only after the promotion save commits — never
        // sign an encounter whose Dx promotion write failed. signPromoting stays true (canvas
        // frozen, footer disabled) until the sign-lock itself settles — the sequence is atomic
        // from the user's side (Codex P1).
        // Four moments: the promotion just ran BE-side — refresh the chart-detail conditions so
        // the stewardship reconciliation (kept-off deletes) and identity chips see the new rows.
        // NOTE (documented limitation): the opt-out does NOT alter this promotion payload — the
        // wire has no per-dx exclusion field, and dropping the dx from the body would (a) remove
        // it from the visit/billing and (b) not stick (every later isOrderSign save re-promotes).
        // The working FE-side exclusion is the guarded post-promotion delete above.
        void activeConditionsQuery.refetch();
        // `savedId` first: on a first save the state update above has not reached this closure,
        // and the sign row must name the A&P that was just promoted.
        createSignLock.mutate(
          toSignLockRequest({
            encounterId,
            patientId,
            performerId: providerId,
            canLock,
            assessmentPlanId: savedId || assessmentIdentifier,
          }),
          {
            onSettled: () => setSignPromoting(false),
            // The orders were promoted but the note is not signed: say so (same as the direct path).
            onError: (err) => showServerError('Signing the note', err),
          },
        );
      },
      onError: (err) => {
        setSignPromoting(false);
        // C3: the note is NOT signed — say so, never a silent stop.
        showServerError('Saving the plan before signing', err);
        // Same spurious-500 handling as Send Orders (PROD-1152 family): the write can persist and
        // still return 500 (post-write pubsub timeout). Re-read: if the flag persisted, orderSent
        // flips true and the NEXT sign click signs directly; if it genuinely failed it stays unsent
        // and the next click retries the promotion. Never proceed to sign-lock on error (Flutter
        // also bails on the A&P-save error).
        void assessmentQuery.refetch();
      },
    });
  }

  // ── M4b — the Wrap-Up "Problem list" closing step (spec §10.5, RATIFIED; never gates sign) ──
  // JOINING = the visit's coded diagnoses not already on the active list (ruling-C auto-promotion
  // made visible; kept-off rows stay listed as receipts). RESOLVED TODAY = chart conditions whose
  // resolution date is the local-calendar today (covers both the A&P one-tap resolve and the
  // rail's Resolve — one source of truth, the refetched chart detail). CHRONIC-STILL-DUE is
  // DATA-GATED: the chronic/DUE columns land with the PROD-1682/1684 backend (in flight) — the
  // group renders nothing until real data exists (no fabricated chips).
  const wrapUpProblems: WrapUpProblemsProps | undefined =
    patientId && encounterId
      ? {
          joining: diagnoses
            .filter((d) => {
              const code = dxCodeOf(d.id);
              return code && !matchProblem(patientProblems, code) && !resolvedNowDxIds.has(d.id);
            })
            .map((d) => ({
              dxId: d.id,
              term: d.display,
              code: dxCodeOf(d.id),
              keptOff: keptOffDxIds.has(d.id),
              onKeepOff: () => handleKeepOffDx(d.id),
            })),
          resolvedToday: (resolvedConditionsQuery.data ?? [])
            .filter((c) => c.isDeleted !== true && !!c.identifier && isTodayLocal(c.endDate))
            .map((c) => ({
              id: c.identifier as string,
              term: c.code?.display?.trim() || 'Problem',
              code: c.code?.code ?? undefined,
              resolvedOn: new Date().toLocaleDateString('en', { month: 'short', year: 'numeric' }),
              onReactivate: user?.id ? () => handleReactivateResolved(c) : undefined,
            })),
        }
      : undefined;

  const wrapUp: WrapUpViewProps = {
    // Frame v2 only (chair ruling, 30 Sep): the note-level actions that left the right column —
    // the generated note, Clinical edits, Discard This Note — sit in the Wrap-up action bar beside
    // Save / Sign. Discard opens the SAME confirm the note footer's pill opened.
    ...(frameV2On
      ? {
          noteActions: {
            // A centre panel takes the note's place: an open, filled phrase is asked about first (#689 follow-up).
            onViewNote: () => confirmNoteViewChange(() => setV2Panel('note')),
            onClinicalEdit: () => confirmNoteViewChange(() => setV2Panel('clinical-edit')),
            onViewPvs: () => setPvsOpen(true),
            ...(mayDiscard ? { onDiscard: () => setDiscardOpen(true) } : {}),
            // The SAME gate the note footer put on its Discard pill (Codex P1): never while a sign
            // or its order-sign promotion is in flight, nor mid assessment save on that path.
            discardDisabled:
              createSignLock.isPending ||
              signPromoting ||
              pvsPrint.isPending ||
              (signNeedsOrderSign && saveAssessment.isPending),
          },
        }
      : {}),
    problems: wrapUpProblems,
    // Physician only (the sender of a medical referral); the rows are the encounter's saved referrals (existing A&P save).
    ...(referralHandoffOn && encounterRole === 'doctor'
      ? {
          referrals: sentReferralHandoffs(assessmentQuery.data?.referrals, {
            fromName: currentUserName,
            fromRole: 'Physician',
            signed,
            viewerId: providerId,
            assessmentPerformer: assessmentQuery.data?.performer,
          }),
        }
      : {}),
    // Status header (PROD-1410): progress via Send-Orders + Sign steps. Gated on the resolved ids
    // (like the other slices); orderSent/count from the fetched assessment, signed from sign-lock.
    status:
      patientId && encounterId
        ? {
            orderSent,
            orderCount: orderStatus.orderCount,
            canSendOrders: hasDiagnosis,
            onSendOrders: handleSendOrders,
            sendingOrders: saveAssessment.isPending,
            signed,
            // Preview orders → the encounter's Post-Visit Summary. Flutter OPENS A POPUP —
            // `PopUpUtils.showPostVisitSummaryDialog(PostVisitSummaryStandalonePage(...))`
            // (wrap_up_billing_screen.dart:411-427) — and the PDF is a Print button INSIDE that
            // popup, not the action itself.
            //
            // FIXED 2026-09-07. This fired `pvsPrint` directly, so the eye produced a file in a new
            // browser tab instead of showing the summary — a different action from the one the
            // clinician asked for. The note footer had exactly this bug and it was fixed there on
            // its own (see the comment at the `onViewPvs` handler below), but the wrap-up was left
            // behind, and the note it left behind — "React renders the same artefact through the
            // shipped PVS print path" — described the workaround as if it were parity.
            // `PostVisitSummaryDialog` carries its own Print, so nothing is lost: this relocates
            // the print INTO the popup, exactly as Flutter has it.
            // Frame v2 (Andrew's walk, 7 Oct): "Preview orders" shows the ORDERS — the Plan's order list — and the
            // post-visit summary is its own named action in the bar (noteActions.onViewPvs). OFF keeps Flutter's PVS.
            onPreviewOrders: frameV2On ? () => handleStepChange('ap') : () => setPvsOpen(true),
            // Opening a popup is synchronous — there is no request to wait on, and Flutter's eye
            // is never disabled (`iconInkWell`, :409-427).
            previewingOrders: false,
          }
        : undefined,
    signLock: {
      state: signLockState,
      isNurse,
      // Also block the (doctor-only) sign while a follow-up save is in flight: the backend schedules
      // the follow-up DURING sign/lock by reading the persisted wrap-up, so signing before the save
      // commits would drop the just-entered follow-up (Codex P2). Once the mutation resolves the
      // record is committed, so gating on isPending is sufficient.
      // Also block on a FAILED follow-up save: isPending clears but the optimistic value still shows
      // while nothing was persisted — signing would finalize from the stale/absent record (Codex P2).
      // Re-adjusting the stepper re-fires the mutation (clearing isError) to retry.
      // PROD-1690: also block while an A&P save is in flight AND the sign would need the order-sign
      // promotion save — the promotion serializes with the canvas autosave (one in-flight PUT), so
      // the button communicates the wait instead of a silently-swallowed click.
      // Flutter's doctor sign has TWO clinical gates, in THIS order (`signNote`,
      // patient_encounter_bloc.dart:2790-2805): a chief complaint must exist, then at least one
      // diagnosis. Ours had only the diagnosis. Neither gate applies to the NURSE in Flutter, and
      // nor do they here — `canDoctorSign` is doctor-only by name and by use.
      // The two save-in-flight gates after them are ours, not Flutter's: they stop a sign racing a
      // wrap-up/assessment write (Codex P2s), which Flutter's synchronous bloc cannot hit.
      // THE TWO CLINICAL TERMS ARE GONE FROM HERE — they now fire on click as toasts in
      // `handleSign`, which is Flutter's shape; see the note there. What remains are the three
      // save-in-flight gates, which are OURS and must stay a disable: they exist to stop a sign
      // racing an in-flight write, and a toast would let the click through.
      canDoctorSign:
        !saveWrapUp.isPending &&
        !saveWrapUp.isError &&
        !(signNeedsOrderSign && saveAssessment.isPending),
      signDisabledReason: saveWrapUp.isPending
        ? SIGN_BLOCKED.followUpSaving
        : saveWrapUp.isError
          ? SIGN_BLOCKED.followUpFailed
          : signNeedsOrderSign && saveAssessment.isPending
            ? SIGN_BLOCKED.assessmentSaving
            : undefined,
      // SAVE — Flutter's `b.saveEncounter(isExist: false)` (wrap_up_billing_screen.dart:487), which
      // is LIVE and unconditional there: no gate, no disabled state, no confirm.
      //
      // Wired 2026-09-07 on Ahmed's instruction. It shipped as a permanently-disabled
      // "Save note (coming soon)" under the anti-fiction rule, on the reading that React had no
      // manual-save path — but it does: the questionnaire content (HPI/ROS/PE/Risk) sits behind a
      // debounced autosave whose flush is already built for step-change and unmount, and the
      // wrap-up's own fields (instructions, follow-up) persist immediately on change. Flushing that
      // debounce IS "commit what I have typed now", which is what the button means.
      //
      // Flutter early-returns when nothing is dirty (`!isDataModified()`,
      // patient_encounter_bloc.dart:960-961) and raises no toast on success. `flushAutosaveRef`
      // has exactly that shape — it no-ops unless there is something to save — so the quiet
      // no-feedback behaviour matches rather than being an omission.
      onSaveNote: () => flushAutosaveRef.current(),
      // Rule 38 (B10): after the PROVIDER's sign nothing is saved in place.
      saveLocked: providerSigned,
      loadError: signLockQuery.isError,
      // Signing spans BOTH writes on the promotion path: the order-sign A&P save + the sign-lock.
      signing: createSignLock.isPending || signPromoting,
      // ROLE-NEUTRAL pending gate (Codex P2): the nurse sign ignores canDoctorSign, but handleSign
      // no-ops in this window too — surface it so the nurse's click is never silently swallowed.
      saveInFlight: signNeedsOrderSign && saveAssessment.isPending,
      // PROD-3142 (D-78): every sign waits for a reload while a Fall / Pain list changed elsewhere.
      ...(riskSignReason ? { blockedReason: riskSignReason } : {}),
      ...(signRefusalShown ? { refusedReason: signRefusalShown } : {}),
      // The two clinical gates of `doSign`, read up front (same order and words) for the floating sign bar only.
      ...(clinicalGateReason ? { clinicalGateReason } : {}),
      onSign: handleSign,
    },
    // PATIENT INSTRUCTIONS card (PROD-1588 S7): same upsert path as the follow-up scheduler;
    // builder preserves follow-up fields so an instructions save can't clobber the scheduler.
    instructions:
      patientId && encounterId && !isNurse
        ? {
            value: wrapUpQuery.data?.instructions ?? '',
            // Frozen (rule 38): no onChange → the DS panel is read-only.
            ...(wrapUpFrozen
              ? {}
              : {
                  onChange: (value: string) =>
                    saveWrapUp.mutate(
                      buildWrapUpInstructionsRequest(value, { patientId, encounterId, existing: wrapUpQuery.data }),
                      {
                        // A refusal as signed is latched and said in the banner ("Not saved — this note is
                        // signed."), like vitals; nothing is resent. Any other failure: the server error, as before.
                        onError: (err) => {
                          if (latchRecordRefusal(err)) return;
                          showServerError('Saving patient instructions', err);
                        },
                      },
                    ),
                }),
            saving: saveWrapUp.isPending,
            // The freeze is explicit, the same contract as the follow-up (the independent kit's W38): frozen
            // → disabled AND no onChange.
            disabled: wrapUpFrozen || wrapUpQuery.data === undefined,
          }
        : undefined,
    summary: {
      summary: postVisitSummary,
      printing: pvsPrint.isPending,
      onPrint: () => pvsPrint.mutate({ encounterId: encounterId ?? undefined, appointmentId }),
    },
    // Follow-Up scheduler (PROD-1405) — doctor-only (Flutter `if !isNurse`). Upserts CreateWrapUp/
    // UpdateWrapUp on change; controls disable while saving (guards a duplicate-create race). The
    // backend schedules the next visit on sign/lock.
    // NOTE: `!isNurse` here is a UI gate matching Flutter's port — it is NOT a security boundary.
    // Server-side role enforcement on the CreateWrapUp/UpdateWrapUp endpoints is the backend's
    // responsibility (Scrips.Patient / Andrew); do not assume it exists. (#work-reviews #4)
    followUp:
      !isNurse && patientId && encounterId
        ? {
            count: followUpValue.count,
            unit: followUpValue.unit,
            saving: saveWrapUp.isPending,
            // Disable until the wrap-up GET has SUCCESSFULLY settled AND is not refetching:
            //  • `data === undefined` covers initial load AND error (on error isLoading is false but
            //    data stays undefined).
            //  • `isFetching` covers the post-save refetch window: after the first edit POSTs a new
            //    record, data is still null until the invalidated GET returns the new id — a second
            //    edit in that gap would omit the id and POST a DUPLICATE. react-query flips
            //    isFetching true inside onSuccess's invalidate before isPending clears, so there is
            //    no enabling gap. Once the refetch returns the record (with id) → enabled → PUT.
            // (All four Codex P2s in this family — encounter-change, loading, error, create-race.)
            disabled: wrapUpFrozen || wrapUpQuery.data === undefined || wrapUpQuery.isFetching,
            onChange: (next) => {
              if (wrapUpFrozen) return;
              setFollowUpValue(next);
              // A refusal as signed is no longer silent (item B, ON and OFF): it is latched — the banner says "Not
              // saved — this note is signed." and no wrap-up write is resent. Any other failure shows the server
              // error in frame v2 only; OFF keeps main's silence for those (Codex on e2e0eae: OFF changes only
              // for rule 38).
              saveWrapUp.mutate(buildWrapUpFollowUpRequest(next, { patientId, encounterId, existing: wrapUpQuery.data }), {
                onError: (err) => {
                  if (!latchRecordRefusal(err) && frameV2On) showServerError('Saving the follow-up', err);
                },
              });
            },
          }
        : undefined,
    // INVOICE BLOCK (Flutter renders it inside the wrap-up step; the old
    // "deferred — no encounter→invoice fetch yet" note is retired now that
    // useEncounterInvoice resolves it). Gates are Flutter's own
    // (wrap_up_billing_screen.dart:680-686): Show_Billing_Summary (view) AND !isNurse.
    // `undefined` when either gate fails → the section doesn't render at all.
    ePrescription: {
      visible: showRx,
      synced: isRxSynced,
      reference: erxRef,
      onSend: encounterId ? sendEPrescription : undefined,
      sending: submitEPrescription.isPending,
      onPreview: encounterId ? () => setErxPreviewOpen(true) : undefined,
    },
    invoiceSection: canSeeInvoice
      ? {
          data: invoiceSectionData,
          onAddService: encounterInvoice.invoiceId ? () => setAddServiceOpen(true) : undefined,
          onPreviewInvoice: encounterInvoice.invoiceId
            ? () => printInvoice.mutate(encounterInvoice.invoiceId!)
            : undefined,
          onRemoveService: encounterInvoice.invoiceId ? invoiceLineRemove.remove : undefined,
          // Row pencil → Flutter's EditServicePopup, which edits the line's DISCOUNT
          // (amount + reason). Self-pay only, matching the source's row actions.
          onEditService: invoiceSectionData?.draft ? (lineId: string) => setEditLineId(lineId) : undefined,
          onEditManagementDiscount: canEditManagementDiscount
            ? () => setDiscountOpen(true)
            : undefined,
          // Per-line COVERAGE + AUTH CODE edits, offered only on a sponsored DRAFT invoice —
          // Flutter's own `enableInvoiceEdit = invoiceStatus == Draft` gate
          // (wrap_up_billing_screen.dart:824). Each maps to its own AddRemoveService array.
          onCoverageChange: invoiceLinesEditable
            ? (lineId: string, coverage: string) =>
                invoiceServiceMutation.mutate(
                  coverage === 'Excluded'
                    ? { excludedServices: [lineId] }
                    : coverage === 'Needs approval'
                      ? { needsApprovalServices: [lineId] }
                      : { includedServices: [lineId] },
                )
            : undefined,
          onAuthCodeChange: invoiceLinesEditable
            ? (lineId: string, authCode: string) =>
                invoiceServiceMutation.mutate({ updateAuthCode: [{ id: lineId, authCode }] })
            : undefined,
          onServiceDateChange: invoiceLinesEditable
            ? (lineId: string, serviceAddedDate: string) =>
                invoiceServiceMutation.mutate({
                  updateServiceDate: [{ id: lineId, serviceAddedDate }],
                })
            : undefined,
          onValidityChange: invoiceLinesEditable
            ? (lineId: string, validityStartDate: string) =>
                invoiceServiceMutation.mutate({
                  // Flutter defaults the END to the START when only a start is picked
                  // (wrap_up_billing_screen.dart:925-931) — same rule here, so a single-date
                  // pick posts a valid one-day window rather than a half-open one.
                  updateValidity: [
                    {
                      id: lineId,
                      validityStartDate,
                      validityEndDate:
                        invoiceSectionData?.rows.find((r) => r.id === lineId)?.validityEnd ??
                        validityStartDate,
                    },
                  ],
                })
            : undefined,
          busy:
            encounterInvoice.isLoading ||
            invoiceServiceMutation.isPending ||
            invoiceLineRemove.isPending ||
            printInvoice.isPending ||
            addManagementDiscount.isPending ||
            updateServiceDiscount.isPending,
        }
      : undefined,
    // PROD-2594: pending orders under the invoice panel. Same gate as the panel; its actions add
    // only the invoice id and the server's Draft-only `canEdit` (no separate edit permission).
    pendingOrders: canSeeInvoice
      ? {
          invoiceId: encounterInvoice.invoiceId,
          canSeeInvoice,
          currency: invoiceSectionData?.currencyCode || user?.organizationCurrency || 'AED',
        }
      : undefined,
  };

  // `useCallback` with an EMPTY dep list — both setters are React state setters and are stable for
  // the component's lifetime, so this handler never needs to change identity. It is passed straight
  // down as `onResponseChange`, and the view builds the renderer's `onChange` from it: a new
  // function here on every render propagated a new `onChange` into the DS renderer on every
  // keystroke. (Declared as a const rather than the previous hoisted `function` — its only
  // reference is the JSX prop far below, so nothing relied on hoisting.)
  //
  // PROD-2577: the previous response is read from `responseRef` (synced after every commit, and
  // advanced here so two emits before a commit still diff correctly) so `mirrorRisk` can forward a
  // changed Risk slice to EncounterMetaData. `mirrorRisk` is stable, so this identity still is.
  const responseRef = useRef(response);
  useEffect(() => {
    responseRef.current = response;
  }, [response]);
  /**
   * PROD-3183 (ROS, PE and risk part): accepted findings waiting for a note save that carries them.
   * Settled by `handleSave` (success / failure) and by the effect below (the answer left the note).
   */
  const qrWaitersRef = useRef(createQrSaveWaiters());
  useEffect(() => {
    qrWaitersRef.current.current(response);
  }, [response]);
  const { mirror: mirrorRisk } = riskBridge;
  // PROD-2445 / PROD-2447: what ambient produced that has no proposal card — contested narratives and
  // recovered orphan lines — and the two writes a clinician can ask of them. Refuses while signed.
  const ambientRecoveries = useAmbientRecoveries({ signed: ambientLocked, setResponse, markDirty });
  const handleResponseChange = useCallback((r: QuestionnaireResponse) => {
    // DS QuestionnaireRenderer (>= 1.12.1, scrips-signal-ds#99) emits onChange from the event
    // phase, so applying our setState synchronously here is safe — no render-phase warning.
    const prev = responseRef.current;
    responseRef.current = r;
    setResponse(r);
    // A Risk-only edit does not dirty the QR — the QR never carries those answers, and a dirty QR
    // on a signed encounter saves an amendment of unchanged content.
    if (mirrorRisk(prev, r)) markDirty();
  }, [mirrorRisk, markDirty]);
  /**
   * PROD-3183 (ROS, PE and risk part) — the host side of the finding suggestions.
   *
   * WHO AND WHEN. A section is offered to whoever this page shows it to (`navSteps`: ROS and PE for
   * the medical team, Risk for every clinical role), the same people who can tick it by hand: these
   * are documentation, not orders. Nothing is offered until the saved note has hydrated (an earlier
   * write would be overwritten by the hydrate), nor while the note cannot be saved (failed load, a
   * sign-lock refusal) or is provider-signed.
   *
   * WHAT IT WRITES. The Risk bridge's Fall / Pain answers and the free screens persist elsewhere, so a
   * finding never ticks them (`excluded`); everything it can write lives in the QuestionnaireResponse.
   */
  const findingsExcluded = useMemo(
    () => new Set([...riskBridge.scope.linkIds, ...riskBridge.free.linkIds, ...riskBridge.qrExcluded]),
    [riskBridge.scope.linkIds, riskBridge.free.linkIds, riskBridge.qrExcluded],
  );
  const findingsScope = useMemo(
    () => ({ excluded: findingsExcluded, patientExcluded: patientExclusions }),
    [findingsExcluded, patientExclusions],
  );
  const findingsWritable =
    !!encounterId &&
    !!patientId &&
    hydratedFor === encounterId &&
    !!templateData?.questionnaire?.id &&
    !qrLoadFailed &&
    !signLockRefusal &&
    !ambientLocked;
  // Left-rail target §7.2: the rail's "Add to note" puts ONE chart piece at the end of a LINE of the current encounter note — HPI
  // into the HPI section's narrative line, ROS into the ROS section's (lib/rail-note-landing.ts) — through the same two writes a
  // completed phrase makes in that line (its `cps-note-delta`, then its text). Assessment text has no landing until the
  // Assessment free-text box exists (§4d); the A&P narrative is never used in its place. Every reason it cannot is said, never a
  // fallback to another place.
  const railLandingFacts = (landing: RailNoteLanding): RailLandingFacts => {
    const linkId = railLandingLinkId(dsQuestionnaire, landing);
    return {
      started,
      linePieces: frameV2On,
      steps: navSteps,
      linkId,
      signed: ambientLocked || !!signLockRefusal,
      writable: findingsWritable,
      value: linkId ? responseRef.current.answers.find((a) => a.linkId === linkId)?.value : undefined,
      delta: linkId ? noteDeltas[linkId] : undefined,
    };
  };
  const railNotePieces: RailNotePieceHost = {
    state: (landing, sourceId) => railLandingState(landing, railLandingFacts(landing), sourceId),
    insert: (landing, piece, typedText) => {
      const facts = railLandingFacts(landing);
      const sourceId = piece.snapshot?.results[0]?.sourceId;
      if (railLandingState(landing, facts, sourceId).kind !== 'ready' || !facts.linkId) return;
      const linkId = facts.linkId;
      const prev = responseRef.current;
      const current = prev.answers.find((a) => a.linkId === linkId)?.value;
      const next = appendPieceToLine(typeof current === 'string' ? current : '', facts.delta, piece, typedText);
      handleNoteDeltaChange(linkId, next.delta);
      handleResponseChange({ ...prev, answers: [...prev.answers.filter((a) => a.linkId !== linkId), { linkId, value: next.value }] });
    },
  };
  const findingSections = useMemo<ReadonlySet<FindingSection>>(() => {
    if (!findingsWritable) return new Set();
    const out = new Set<FindingSection>();
    if (navSteps.includes('ros')) out.add('ros');
    if (navSteps.includes('pe')) out.add('exam');
    if (navSteps.includes('risk')) out.add('risk');
    return out;
  }, [findingsWritable, navSteps]);
  /**
   * One accepted finding: the tick or the notes line goes into the note through `handleResponseChange`
   * (the same path a click on the form takes), and the note's autosave persists it. Resolves true once
   * a save that carried it succeeded, which is when the panel records the accept.
   */
  async function handleAcceptFinding(placement: Exclude<FindingPlacement, { kind: 'nowhere' }>): Promise<FindingAcceptResult> {
    if (!findingSections.size) return false;
    const prev = responseRef.current;
    // RE-READ AT THE CLICK. An option the clinician answered differently since the suggestion was
    // shown is hers: the tick would replace it. Write nothing.
    if (placement.kind === 'tick' && !placement.repeats) {
      const now = prev.answers.find((a) => a.linkId === placement.linkId)?.value;
      if (now !== undefined && now !== '' && String(now) !== String(placement.value)) return 'changed';
    }
    const next = applyPlacement(prev, placement);
    // Already in the note, nothing unsaved and no save in flight: the stored note holds it.
    if (next === prev && !dirty && !saveQR.isPending) return true;
    const stored = qrWaitersRef.current.whenCarried((r) => responseCarries(r, placement));
    if (next !== prev) handleResponseChange(next);
    return stored;
  }
  /**
   * PROD-3195: the dictated HPI (and any other narrative slot) is an offer the physician accepts. The
   * hook keeps the offers and makes the one write, on the Accept click (`use-ambient-narrative-host.ts`).
   */
  const ambientNarratives = useAmbientNarrativeHost({
    locked: ambientLocked,
    template: templateData?.questionnaire ?? undefined,
    response,
    responseRef,
    writable: findingsWritable,
    noteCollisions: ambientRecoveries.noteCollisions,
    applyResponse: handleResponseChange,
    markDirty,
    whenStored: (carries) => qrWaitersRef.current.whenCarried(carries),
  });
  const findingsHost: FindingsHost = {
    questionnaire: dsQuestionnaire,
    response,
    scope: findingsScope,
    canAccept: findingSections,
    onAccept: handleAcceptFinding,
  };
  /**
   * Everything ambient recovered that has no card and no template slot, for the encounter narrative sink.
   *
   * WHY THE SINK AND NOT A TEMPLATE SLOT. Samer reported that a normal blood pressure and a normal
   * physical examination "was never captuered". They were measured, and the analyzer returned them
   * — but the note is derived from a QuestionnaireResponse against a Template-Studio template, and
   * a template that does not declare a slot has nowhere to put them. The sink is the one section
   * `generated-note.ts` renders from the response itself, so it reaches the note whatever template
   * the clinician chose.
   *
   * PROD-3195 (product owner, 15 Oct 2026: "nothing fill by themselves, the owner should add them by
   * himself"): each line is an OFFER under "Additional notes". Nothing is appended until the physician
   * clicks Accept on that line (`use-ambient-sink-host.ts`). `findingsWritable` carries the B5 lock and
   * the hydration check (`hydratedFor === encounterId`), so no Accept writes before the saved note loaded.
   */
  const ambientSink = useAmbientSinkHost({
    locked: ambientLocked,
    response,
    writable: findingsWritable,
    // A functional update: the line is appended to the LATEST note, so a fast second Accept or a
    // typed edit made while an Accept runs is not lost (PR #729 review).
    updateResponse: setResponse,
    markDirty,
    whenStored: (carries) => qrWaitersRef.current.whenCarried(carries),
  });

  function handleStepChange(step: PaStepKey) {
    // Frame v2, one section at a time: a step in another section replaces the open one, so an open, filled phrase is
    // asked about first (#689 follow-up). One scroll keeps every section on screen: a step change only scrolls.
    if (frameV2On && noteMode === 'sections' && !sectionStepsFor(navSteps, currentStep).includes(step)) {
      confirmNoteViewChange(() => changeStep(step));
      return;
    }
    changeStep(step);
  }
  function changeStep(step: PaStepKey) {
    // D8 AUTOSAVE: leaving a step FLUSHES any debounced pending edit immediately — a
    // step switch must never leave the departed step's answers unsaved for the debounce
    // window (the burst timer is superseded by this synchronous flush).
    if (currentStep !== step) {
      flushAutosaveRef.current();
      // Risk's encounter-level save — the Risk panel's unmount used to fire it on leaving the step.
      riskBridge.flush();
    }
    // Frame v2 (TS-3): a section's ✓ is its DOCUMENTED state, computed by the view from what the
    // note holds (structured-note/section-completion.ts) — never a visit (authority rule 8). In one
    // continuous scroll every section is on screen, so leaving one would tick them all. Nothing is
    // marked here; OFF keeps its leave-a-step rule exactly as before.
    if (!frameV2On && currentStep !== step && !completedSteps.includes(currentStep)) {
      setCompletedSteps((prev) => [...prev, currentStep]);
    }
    setCurrentStep(step);
    setVisitedSteps((prev) => (prev.has(step) ? prev : new Set(prev).add(step)));
  }
  // (rule C, 16 Oct; frame v2 only) a step's ✓ is derived from the note (`lib/step-completion.ts`), never from leaving a step.
  /**
   * Every save (debounce, step-change flush, unmount flush, the wrap-up "Save note" flush). Flutter sign model
   * (Andrew, 14 Oct 2026): a sign never locks the note, so a save after a sign is the same PUT as any other.
   */
  function handleSave({ keepalive = false }: { keepalive?: boolean } = {}) {
    // MEDIUM 2 (PR #9): gate on the RESOLVED subject, not nav.createContext — on a
    // deep-link / hard refresh nav state is null but the subject resolves from the
    // GET-by-appointment, so Save must still fire (was silently no-op'ing → data loss).
    if (!templateData?.questionnaire || !encounterId || !patientId) return;

    // CANONICAL persist (PROD-1159 / PROD-1354c): the WHOLE shared QuestionnaireResponse (all
    // sections) FHIR-native. With a known document id (reload GET or a prior save) → PUT the SAME
    // row (QB upsert — edit → re-save is idempotent, no duplicate); without → POST creates and the
    // returned id is adopted so every later save PUTs. dirty-clear gates on the REAL 2xx (no false
    // "saved"); the Save button is disabled while pending, so a double-click can't double-write.
    // The legacy CreateChiefComplaint dual-write is REMOVED (superseded — divergence register).
    const questionnaireId = templateData.questionnaire.id ?? undefined;
    if (!questionnaireId) return;
    // Don't CREATE while the reload GET is still resolving — it may be about to deliver the
    // existing row's id, and a POST now would write a duplicate row. (The Save button is also
    // disabled through the `dirty` prop below; this is defense-in-depth.)
    if (!effectiveQrId && savedQrQuery.isFetching) return;
    // PROD-1654: don't save while the reload is in the FAILED-LOAD latch (genuine 5xx/network —
    // the hook maps the contract's "none saved yet" 404 to null, so this is never a fresh
    // encounter). Server row state is UNKNOWN or unreconciled: a POST could shadow, and a PUT
    // could overwrite, the clinician's earlier full answers with a blank-started draft (QB
    // serves latest-by-UpdatedOn). Surfaced in the red channel below.
    if (qrLoadFailed) return;
    // PROD-2547: after a sign-lock refusal nothing is re-sent until a reload (see the latch above).
    if (signLockRefusal) return;
    // PROD-2577 — NO DUAL WRITE: the Risk answers persist to EncounterMetaData (`riskBridge`), so
    // they are stripped here and never reach the QuestionnaireResponse row.
    // PROD-3042: an item the template disables for this patient carries no answer (FHIR enableWhen semantics).
    const resource = toQuestionnaireResponseResource(
      // PROD-3128: the STEADI follow-up's answers live in the Fall record only (`qrExcluded`), never in the QR.
      withoutPatientExcluded(withoutRiskAnswers(response, riskBridge.scope, riskBridge.qrExcluded), patientExclusions),
      {
        questionnaireId,
        patientId,
        encounterId,
        ...(effectiveQrId ? { id: effectiveQrId } : {}),
        // PROD-2608: answers the form has no place for go back exactly as they were stored.
        ...(orphans.length ? { preserved: preservedItems(orphans) } : {}),
        // PROD-2961 (frame v2): the lines' Deltas with pieces, beside their text. OFF sends none.
        ...(frameV2On ? { noteDeltas } : {}),
      },
      qrItemMeta,
    );
    // PROD-2700: the generation of the answers in `resource` — read in the same render as `response`.
    const sentGeneration = dirtyGeneration;
    // #690: the generation the save now in flight carries — a newer edit supersedes it, the same one never re-sends.
    const sentRecord = { rowId: effectiveQrId ?? null, generation: sentGeneration };
    inFlightGenerationRef.current = sentRecord;
    // PROD-2810: the step(s) these edits came from — a flush on leaving a step runs BEFORE the step
    // changes, so this is the step (or frame-v2 section) being left.
    // Integration (#606 + #626): in one scroll every section is on screen and the ONE live response
    // carries edits from any of them, so a refused save holds back every questionnaire step of the note,
    // not only the section in view (the safer reading; sections mode keeps #626's section-being-left rule).
    const sentSteps: readonly PaStepKey[] = frameV2On
      ? noteMode === 'scroll'
        ? navSteps.filter(isQuestionnaireStep)
        : sectionStepsFor(navSteps, currentStep)
      : [currentStep];
    saveQR.mutate(
      {
        resource,
        // A save after a sign may land on a NEW row (copy-on-write): the version adopted below is the head's.
        ...(qrVersionId ? { versionId: qrVersionId } : {}),
        ...(keepalive ? { keepalive: true } : {}),
      },
      {
        onSuccess: (stored) => {
          if (inFlightGenerationRef.current === sentRecord) inFlightGenerationRef.current = null;
          if (stored?.id) setQrId(stored.id);
          if (stored) {
            setLastStoredQr(stored);
            setQrVersionOverride(null);
          }
          // PROD-2608: the first save of a fresh note returns the pin QuestionBank just stamped.
          // Adopt it, so the template stays put for the rest of this session.
          if (!notePin) {
            const stamped = readTemplatePin(stored);
            if (stamped) setSessionPin(stamped);
          }
          // PROD-2700: clean only if no edit landed while this save was in flight. A newer edit keeps
          // the page dirty, so the autosave (re-armed as isPending clears) sends it as the next save.
          markSavedThrough(sentGeneration);
          // PROD-2447: a recovered line is `Written` only once a save carrying it has resolved.
          ambientRecoveries.notePersisted(response.answers);
          // PROD-3183: an accepted finding is Written once a save carrying it has resolved.
          qrWaitersRef.current.saved(response);
          // L360 I2: tell Workflows the history was reviewed. Fire-and-forget; never blocks or fails this save.
          // L360 I2: reviewerId is the PA user id (user.id), as agreed with the Workflows I2 handler; not practitionerId.
          postHistoryReviewEvent({ questionnaire: templateData?.questionnaire, stored, patientId, encounterId, reviewerId: user?.id });
          // L360 P1: tell Workflows the T03 lifestyle assessment was saved (fire-and-forget, flag-gated).
          postLifestyleIntakeEvent({ questionnaire: templateData?.questionnaire, stored, patientId, encounterId });
          // L360: tell Workflows a care-model input template (plan proposal, clearance, CPET review) was saved (same rules).
          postCareModelInputEvent({ questionnaire: templateData?.questionnaire, stored, patientId, encounterId, reviewerId: user?.id });
        },
        onError: (error) => {
          if (inFlightGenerationRef.current === sentRecord) inFlightGenerationRef.current = null;
          // Another writer moved the row on (a second tab, or a save we cancelled that the server still took): adopt its
          // version and keep these answers. Autosave stops until the clinician chooses "Save again" — it never
          // overwrites the other writer's changes silently. `not-current-revision` means the head is now another row (a
          // save after a sign makes one), and a refusal may carry no version: both read the stored note again for its row
          // and version.
          const conflict = readVersionConflict(error);
          if (conflict) {
            if (conflict.code === 'version-conflict' && conflict.currentVersion) {
              setQrVersionOverride(conflict.currentVersion);
              setQrConflictFor({ encounterId: encounterId ?? null, reading: false, readFailed: false });
            } else {
              rereadStoredNote();
            }
          }
          // #690: replaced by a newer save — not a failure. No banner, no refusal latch; dirty stays as it is, and the
          // newer save clears it.
          if (isSupersededSave(error)) return;
          // PROD-3183: the finding waiting on this save is not stored. Its item says so and can be tried again.
          qrWaitersRef.current.failed();
          const refusal = readSignLockRefusal(error);
          if (refusal) {
            setSignLockRefusalFor({ encounterId: encounterId ?? null, refusal, hadRevision: !!effectiveQrId, steps: sentSteps });
          }
        },
      },
    );
  }

  // ── D8 AUTOSAVE (Samer decision strip 2026-07-22, PROD-1588) ────────────────
  // Debounced per-step persistence REPLACES the dirty-gated Save button: ~1.8s after the
  // last questionnaire edit the page fires the SAME handleSave (the proven QR upsert path,
  // reused 1:1 — no second persist shape), plus a synchronous flush on step-change
  // (handleStepChange above) and on unmount. Guards:
  //   • dirty only — a clean response never saves (no idle re-PUTs);
  //   • a sign never stops it (Flutter sign model, 14 Oct 2026): editing continues after a sign and saves as before;
  //   • the same duplicate-create guards handleSave enforces (template id, resolved ids,
  //     and the effectiveQrId-unresolved fetch window) so a debounce can't POST a dup row;
  //   • saveQR.isPending — no overlapping saves; the effect re-runs when the in-flight
  //     save settles, so an edit made DURING a save flushes as ONE follow-up save.
  const AUTOSAVE_DEBOUNCE_MS = 1800;
  const canAutosave =
    dirty &&
    !!templateData?.questionnaire?.id &&
    !!encounterId &&
    !!patientId &&
    !(effectiveQrId === null && savedQrQuery.isFetching) &&
    // PROD-1654: same failed-load latch as handleSave — a debounced autosave must not shadow
    // or overwrite unseen server answers either (incl. AFTER a background refetch recovers:
    // the latch, not the transient isError, is what gates — see the latch effect above).
    !qrLoadFailed &&
    // PROD-2547: a sign-lock refusal stops autosave until reload (no 1.8 s retry loop of a refused write).
    !signLockRefusal &&
    // A 409 conflict stops every autosave and flush until "Save again" (the answers stay as they are).
    !qrConflict;
  // Refs so the flush paths (step-change handler + unmount cleanup) always see the CURRENT
  // gate + save fn without re-subscribing effects (no stale-closure save of an old response).
  // #690 (Andrew, 10 Oct 2026): a newer edit does not wait for the save in flight — the new PUT cancels it
  // (`useSaveEncounterQuestionnaire`) and goes now, on the debounce and on the hide flush alike, with keepalive on hide.
  // Only when the edit is newer than what is in flight (never the same body twice, so a slow server is not a cancel
  // loop) and only for a PUT of an existing row: a first save still CREATING the row is never cancelled and never
  // doubled — a second POST would write a duplicate row — so edits made during it wait, as before.
  // Keyed by row (#690 review): the save in flight and the generation it carries. Only a newer edit of the SAME row
  // supersedes it (a row whose id arrived since — the create's — never cancels anything).
  const inFlightGenerationRef = useRef<{ rowId: string | null; generation: number } | null>(null);
  const supersedesInFlight = () => {
    const sent = inFlightGenerationRef.current;
    return !!effectiveQrId && sent !== null && sent.rowId === effectiveQrId && dirtyGeneration > sent.generation;
  };
  // In flight from the moment it is sent — `isPending` only turns true on the next render, and a flush in between must
  // not send the same body again.
  const qrSaveInFlight = () => saveQR.isPending || inFlightGenerationRef.current !== null;
  // "Save again" after a conflict: clear it and send the clinician's answers on the version adopted from the refusal.
  // `handleSave` has no conflict guard of its own (only the autosave gate does), so it goes out at once.
  // A failed read of the stored note is read again first; "Save again" stays disabled while a read is running.
  const handleSaveAgain = () => {
    if (!qrConflict || qrConflict.reading) return;
    if (qrConflict.readFailed) {
      rereadStoredNote();
      return;
    }
    setQrConflictFor(null);
    handleSave();
  };
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flushAutosaveRef = useRef<(opts?: { keepalive?: boolean }) => void>(() => {});
  flushAutosaveRef.current = (opts = {}) => {
    // A flush SUPERSEDES the pending debounce — cancel it so the flushed content can't
    // save a second time when the burst timer would have fired.
    if (autosaveTimerRef.current !== null) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    if (!canAutosave) return;
    if (qrSaveInFlight() && !supersedesInFlight()) return;
    handleSaveRef.current(opts);
  };
  useEffect(() => {
    if (!canAutosave || (qrSaveInFlight() && !supersedesInFlight())) return;
    // One timer per render of `response` — every keystroke re-runs this effect and the
    // cleanup clears the previous timer, so a typing burst collapses to ONE trailing save.
    const t = setTimeout(() => {
      autosaveTimerRef.current = null;
      handleSaveRef.current();
    }, AUTOSAVE_DEBOUNCE_MS);
    autosaveTimerRef.current = t;
    return () => {
      clearTimeout(t);
      if (autosaveTimerRef.current === t) autosaveTimerRef.current = null;
    };
  }, [response, canAutosave, saveQR.isPending]);
  // Unmount flush: navigating away from the encounter persists a pending dirty edit.
  useEffect(() => () => flushAutosaveRef.current(), []);
  // Page-hide flush: a refresh or a closed tab never unmounts React, so the debounce window used to be lost — text typed
  // in the last 1.8 s before a reload never reached the server (dev repro, 10 Oct 2026). Hidden or unloaded, the pending
  // edit goes NOW, with `keepalive` so the browser lets the write outlive the page. Same guards as every autosave
  // (`canAutosave`: signed, refused, failed-load, Discard via the note session's write gate).
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'visible') return;
      flushAutosaveRef.current({ keepalive: true });
    };
    const onPageHide = () => flushAutosaveRef.current({ keepalive: true });
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, []);
  // D-73 (amended, 6 Oct) · frame v2 only: the in-app leave guard (encounter-leave-guard.tsx) reads the note's save
  // state from here and flushes the note through here before deciding. OFF publishes nothing.
  const riskFlushRef = useRef(riskBridge.flush);
  riskFlushRef.current = riskBridge.flush;
  // (The guard's save-state publication sits below `saveCc`: it waits on the chief-complaint and wrap-up writes too.)
  useEffect(() => {
    if (!frameV2On) return;
    const unregister = registerNoteFlush(() => {
      flushAutosaveRef.current();
      riskFlushRef.current();
    });
    return () => {
      unregister();
      useUnsavedNote.getState().publish({ active: false, dirty: false, saving: false, failed: false, canSave: false });
    };
  }, [frameV2On]);

  // Genuine errors only → the red error channel. Key off the PRIMARY QR save (PROD-1159); the
  // legacy chief-complaint bridge is fire-and-forget and must not block the canonical persist.
  // PROD-1654 (QA: silent-failure): a FAILED saved-responses reload used to render as a fresh
  // encounter — blank sections, no signal. That's never fake-empty now: it takes the red channel
  // (the designed "none saved yet" 404 is mapped to null in the hook and stays a true, quiet
  // empty). Keyed on the SAME latch the save guards gate on (qrLoadFailed) so the copy never
  // lies (Codex P2): a background-REFETCH error with a cached/adopted id still PUTs the same
  // row fine — no message, no false "paused". A real failed save outranks it.
  // PROD-2810 / rule 38: a save refused because the note is signed (409 `encounter-signed`) — the QR
  // (QuestionBank) or the Risk Fall / Pain record (Scrips.Patient). Never silent: the one sentence in
  // the save channel (so the step never reads "Saved") and the DS Banner above the canvas.
  const qrSignedRefusal = signLockRefusal?.refusal.code === 'encounter-signed' ? signLockRefusal : null;
  // The sentence depends on what was refused and who is looking (design desk, 6 Oct; the one source is
  // lib/signed-refusal.ts): an encounter-record refusal (vitals, chief complaint, Fall / Pain — never
  // amendable) and a note-answer refusal say the same plain sentence (there is no amendment route).
  const refusalSentence = signedRefusalSentence({
    answers: !!qrSignedRefusal,
    records: riskBridge.saveRefused || recordRefused,
  });
  // PROD-3142: a Fall / Pain save refused as stale says so in the same channel (banner + header), and Risk is not done.
  const noticeSentence = refusalSentence ?? riskConflictSentence;
  const riskNotSaved = riskBridge.saveRefused || !!riskConflictSentence;
  const refusedSteps: readonly PaStepKey[] = [...(qrSignedRefusal?.steps ?? []), ...(riskNotSaved ? (['risk'] as const) : [])];
  // Rule C (Antonin, 16 Oct): the default frame's ✓ is derived from the note every render (reload-proof), and a step with blank
  // required items shows a needs-attention marker once visited or answered. Frame v2 keeps its own section completion.
  const stepCompletionInput = {
    navSteps,
    questionnaire: templateUnavailable ? undefined : renderedQuestionnaire,
    response,
    plan: {
      diagnoses: apViewBase.diagnoses,
      orders: apViewBase.orders,
      articles: apViewBase.articles,
      assessmentNote: apViewBase.assessmentNote,
    },
    chiefComplaintPresent: ccSelected.length > 0,
    riskAnswered: !!riskBridge.heads?.fall || !!riskBridge.heads?.pain,
    diagnosisRequired: encounterRole !== 'allied-health',
    fallAnswered: !!riskBridge.heads?.fall,
    painAnswered: !!riskBridge.heads?.pain,
    providerSigned,
  };
  // Passes ("Nothing to record"): display only, and only while their step holds no answer; an answer ends the pass for good.
  const answeredSteps = new Set(stepRulesActive ? navSteps.filter((s) => stepHoldsAnswer(s, stepCompletionInput)) : []);
  const passedSteps = effectivePasses(stepPasses.stored, answeredSteps);
  const stalePasses = [...stepPasses.stored].filter((s) => answeredSteps.has(s));
  const staleKey = stalePasses.join(',');
  useEffect(() => {
    if (staleKey) stepPasses.drop(staleKey.split(',') as PaStepKey[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staleKey]);
  const stepPass: StepPass | undefined = stepRulesActive
    ? { passed: new Set([...passedSteps].filter((s) => PASSABLE_STEPS.includes(s))), onToggle: stepPasses.toggle }
    : undefined;
  // Rule C (`lib/step-completion.ts`) drives the ✓ on the long page (frame v2) only. OFF keeps main's leave-a-step ticks
  // (`completedSteps`, set in `handleStepChange`) and draws no needs-attention marker: no clinical rule change on the default path.
  const doneSteps = frameV2On ? derivedCompletedSteps(stepCompletionInput) : completedSteps;
  const shownCompletedSteps = refusedSteps.length > 0 ? doneSteps.filter((s) => !refusedSteps.includes(s)) : doneSteps;
  const attentionSteps = frameV2On
    ? stepsNeedingAttention({ ...stepCompletionInput, visited: new Set([...visitedSteps, currentStep]), requiredMarkers: encounterRole !== 'nurse' })
    : undefined;
  const saveError = noticeSentence
    ? noticeSentence
    : signLockRefusal
    ? SIGNED_NOT_SAVED
    : saveQR.isError
    ? 'Save failed — please retry.'
    : qrLoadFailed
      ? "Couldn't load saved responses — reload to retry. Saving paused to protect earlier answers."
      : createEncounter.isError
        ? "Couldn't start the encounter — try again."
        : undefined;
  // Start gate (PROD-1473 + PROD-1475). The arrived-gate blocks a known non-arrived
  // appointment (only when there's no encounter yet). Creation happens on the Start CLICK
  // (handleStart), so a null encounterId pre-Start is NORMAL — Start is ENABLED and clicking
  // it creates. While the create POST is in flight, show a transient disabled reason.
  const startActionResolved = startActionFor(resolvedStatus);

  /**
   * The status gate blocks STARTING a new encounter — never resuming one (PROD-2029).
   *
   * Samer, 2026-08-25: "The status gate blocks *Start*. It must not block Continue or Edit: a note
   * that exists is a clinical record already begun, and cancelling the appointment doesn't un-write
   * it." So it applies only to the `'start'` action; `'continue'` / `'edit'` / `'retry'` pass through.
   *
   * DELIBERATE DIVERGENCE FROM FLUTTER, and it has a register row. Flutter greys the button whatever
   * its label, because its switch keys off `canEditVitals` alone and never looks at the label
   * (pre_encounter_widget.dart:293-312) — so a Flutter practitioner mid-draft on a cancelled
   * appointment is locked out of their own note. We do not copy that.
   *
   * What must NOT change back: the gate is no longer nested inside `!encounterId &&
   * nav?.createContext`. That nesting was the bug — an appointment that already had an encounter
   * skipped the gate entirely and Start stayed enabled on a Booked appointment (measured on dev
   * 2026-08-25). Keying on the ACTION is what separates "don't create" from "don't resume";
   * reverting to the nested form would silently restore the hole.
   *
   * `handleStart` keeps its own refusal for the create path (defence in depth) — and it sits after
   * the `encounterId` resume return, so it cannot block a resume either.
   *
   * "Preparing encounter…" remains the second reason: no encounter AND no create context
   * (deep-link/reload) means we can neither create nor resume, and entering an encounter-less shell
   * would make Save a no-op.
   */
  const startBlock =
    (startActionResolved === 'start'
      ? appointmentStartBlockReason(appointmentStatusCode)
      : undefined) ??
    (!encounterId && !nav?.createContext ? 'Preparing encounter…' : undefined);
  const startDisabledReason =
    startBlock ?? (createEncounter.isPending ? 'Starting encounter…' : undefined);

  // Start is DISABLED for a non-startable status, never hidden (Andrew, 2026-08-25: "I told you to
  // disable start in case of booked not hide it"). That is Flutter's behaviour too — it renders the
  // button greyed and toasts the reason on tap (pre_encounter_widget.dart:415-425), rather than
  // removing it. `startDisabledReason` above already carries the reason; nothing else is needed.

  // Start the encounter — Flutter `bloc.startEncounter()`.
  //
  // Two shapes, matching Flutter's two branches: if the open phase already created the row this
  // UPGRADES it Initial→Draft (see below); if there is no row at all — a status we didn't create
  // on open for, or a failed open-create — it creates one directly as Draft, which is the older
  // create-on-click path and still the fallback. The arrived-gate is enforced via
  // `startDisabledReason`, so this only fires when starting is allowed.
  function handleStart() {
    if (encounterId) {
      /**
       * START PHASE — upgrade the open-phase row from `Initial` to `Draft`.
       *
       * FLUTTER (`startEncounter()`, patient_encounter_bloc.dart:2555-2578):
       *
       *     else if (state.encounter?.status == AppConstants.encounterStatusInitial)
       *       createEncounter(status: AppConstants.encounterStatusDraft);
       *     else if (encounterState == created || fetched)
       *       add(UpdateView(...));            // already Draft → just move the view
       *
       * so the re-POST happens ONLY while the row is still Initial. It is the same row, not a
       * second encounter: Flutter re-sends the model it already holds, whose `identifier` is set,
       * and CreateEncounter upserts on that key. We pass `identifier: encounterId` for exactly
       * that reason — omitting it would INSERT a duplicate encounter for the appointment.
       *
       * A resume (status already Draft, or unknown because it came from `nav.encounterId`) falls
       * through to the plain view move, matching Flutter's second branch.
       */
      if (
        encounterStatus === ENCOUNTER_STATUS_INITIAL &&
        nav?.createContext &&
        !createFiredRef.current &&
        !createEncounter.isPending
      ) {
        createFiredRef.current = true;
        createEncounter.mutate(
          {
            ...nav.createContext,
            status: ENCOUNTER_STATUS_DRAFT,
            identifier: encounterId, // UPDATE this row — never INSERT a second one
          },
          {
            onSuccess: () => {
              setEncounterStatus(ENCOUNTER_STATUS_DRAFT);
              setStarted(true);
            },
            onError: () => {
              // The row EXISTS and the clinical work can proceed — it is only the Draft marker
              // that failed to persist, so blocking entry here would cost more than it protects
              // (Flutter moves the view regardless: `UpdateView` runs before the create resolves).
              // Reset so the banner doesn't claim "Couldn't start the encounter" about an
              // encounter that is open on screen, and clear the latch so a later Start can retry
              // the upgrade.
              createFiredRef.current = false;
              createEncounter.reset();
              setStarted(true);
            },
          },
        );
        return;
      }
      // Resume: an already-resolved encounter just enters the steps.
      setStarted(true);
      return;
    }
    // No create context (deep-link/reload with no nav) AND no encounter: we can neither create
    // nor resume — do NOT enter an encounter-less shell (Save would no-op). The Start button is
    // already disabled here (startDisabledReason = 'Preparing encounter…').
    if (!nav?.createContext) return;
    // Defense-in-depth arrived-gate: the Start button is already disabled for a non-arrived
    // appointment (startDisabledReason), but never create one from here either (PROD-1473).
    if (appointmentStartBlockReason(appointmentStatusCode)) return;
    // Guard a double-fire (an accidental re-click while the POST is in flight or done).
    if (createFiredRef.current || createEncounter.isPending) return;
    createFiredRef.current = true;
    createEncounter.mutate(nav.createContext, {
      onSuccess: (id) => {
        setEncounterId(id);
        // Created straight to Draft (the mutation's default status) — no upgrade is owed, so
        // record that or a later Start would try to "upgrade" an already-Draft row.
        setEncounterStatus(ENCOUNTER_STATUS_DRAFT);
        setStarted(true);
      },
      // PROD-1152: a 500 can arrive AFTER the row is written (Dapr publish timeout). Recover
      // by re-fetching by appointment; if that resolves the row, adopt it and enter. Otherwise
      // clear the guard so the user can retry Start (no auto-retry loop).
      onError: () => {
        void encounterQuery
          .refetch()
          .then((r) => {
            const created = r.data?.identifier;
            if (created) {
              // Recovered: clear the settled create error so the step shell doesn't show a
              // stale "Couldn't start the encounter" while the recovered encounter is active.
              createEncounter.reset();
              setEncounterId(created);
              setEncounterStatus(r.data?.status ?? null);
              setStarted(true);
            } else {
              createFiredRef.current = false;
            }
          })
          .catch(() => {
            createFiredRef.current = false;
          });
      },
    });
  }
  // `medActions` (Samer's Loom, 3 Nov): a chart medication into the A&P Plan. Its FULL record seeds today's medication dose dialog
  // (the drug and its schedule), which the clinician saves as today's plan order. Continue, Refill (R1: a new order for the same drug
  // and dose) and Change open the same dialog; Change is where the clinician alters it.
  // Left-rail target §3: each intent reaches its own route (`medicationPlanRoute`). Stop is the A&P's own Discontinue (#906
  // `ApMedStopHost`, an immediate PostStopMedication), so the rail does NOT offer it (rail-v3-actions.ts); Refill and Edit dose open
  // the dose dialog pre-filled with the recorded dose, where the drug-interaction gate (X2) runs before anything is ordered.
  async function medicationToPlan(identifier: string, intent: MedicationPlanIntent = 'continue') {
    const route = medicationPlanRoute(intent);
    if (route.kind === 'stop') {
      const m = (summaryQuery.data?.medication ?? []).find((x) => x.identifier === identifier && !x.isDeleted);
      if (!m?.identifier) {
        showWarning('This medication is not on the patient\'s medication list, so it cannot be stopped here.');
        return;
      }
      setApMedStop({ identifier: m.identifier, display: m.code?.display ?? '' });
      return;
    }
    try {
      const seed = medicationRecordToSeed(await getMedicationDetails(identifier));
      // A record with no drug name or code has nothing to order: say so rather than a dead click (#906 review).
      if (!seed) {
        showWarning('This medication has no drug name or code, so it cannot be sent to the plan.');
        return;
      }
      setPendingSeed({ item: seed.item, category: 'medication', dictatedDose: seed.dictatedDose });
      setCurrentStep('ap');
    } catch (err) {
      showServerError('Load medication', err);
    }
  }
  /** The A&P Active Medications row (an Rx order) as its chart medication, else say why nothing opens. */
  function activeMedicationRecord(rowId: string) {
    const m = patientMedicationForOrder(summaryQuery.data?.medication, rxOrdersQuery.data, rowId);
    if (!m) showWarning('This order is not on the patient\'s medication list, so it cannot be continued or stopped here.');
    return m;
  }
  function activeMedicationToPlan(rowId: string) {
    const m = activeMedicationRecord(rowId);
    if (m) void medicationToPlan(m.identifier);
  }
  function activeMedicationStop(rowId: string) {
    const m = activeMedicationRecord(rowId);
    if (m) setApMedStop(m);
  }
  // Transient loading is a NEUTRAL status, not an error (PR #9 review POLISH,
  // PROD-1160) — routed through the separate `status` channel below.
  const status = !encounterId ? 'Preparing encounter…' : undefined;

  // Generated-note sections for the drawer's Note tab (PROD-1260). Derived from the live
  // response; NON-BLOCKING (ternary → [] when the template hasn't resolved) so it respects
  // the rail's template-independence (see the showRail note below). Empty pre-Start (no
  // answers yet) — the Note tab then shows "No responses yet."; it fills as the encounter
  // questionnaires are answered.
  // Grouped by STEP and headed by Flutter's fixed tab titles — "Chief Complaint",
  // "Review of Systems", "Physical Exam", "Risk Assessment" (PROD-2011). Before this the heading
  // was whatever the template author named the group, and a step spread across several authored
  // groups produced several differently-titled sections instead of one per step.
  // EVERY step gets a section, answered or not, each empty one carrying Flutter's own "No X yet."
  // line (Andrew, 2026-08-21: "you should show all tabs sections even if they are empty in the
  // notes"). `groupNoteByStep` defaults to all steps, so there is nothing to pass. PROD-2011.
  // PROD-2136: where the HPI section authors the chief-complaint block, the HPI note rows are the
  // per-complaint selections ("Back Pain (General)" → "Onset: 1 day ago, Sudden onset"), read from
  // the same complaint document the block edits, ahead of any groups authored beside the block.
  // A template without the marker takes the unchanged path.
  //
  // The note DOES depend on the live response — that is the point of it — but only the cheap
  // `responseToNote`/`groupNoteByStep` pass re-runs per keystroke now, not the conversion (declared
  // with the QR hydrate, above — the Risk bridge needs the rendered template that early).
  //
  // PROD-2786: a note PROVIDER-signed before NOTE_WORDING_CUTOFF_UTC (or with no readable sign time)
  // renders with the pre-PROD-2786 formatter ("Fever: No"), byte for byte. Every other note gets the
  // clinical wording ("Denies Fever"): unsigned, nurse-only signed, and provider-signed at or after
  // the cutoff, so a note signed today never flips on sign. Read from the same sign-lock records as
  // `providerSigned`. Removed once PROD-2810 freezes the narrative at sign.
  const legacyNoteWording = signedUnderLegacyNoteWording(signLockQuery.data);
  const questionnaireNoteSections = useMemo(() => {
    if (!dsTemplateQuestionnaire) return [];
    const grouped = groupNoteByStep(
      responseToNote(dsTemplateQuestionnaire, response, { legacyWording: legacyNoteWording }),
    );
    return hasChiefComplaintHpiBlock(dsTemplateQuestionnaire)
      ? withChiefComplaintHpiNote(grouped, complaintHpiNoteCategories(ccDocument?.complaints))
      : grouped;
  }, [dsTemplateQuestionnaire, response, ccDocument?.complaints, legacyNoteWording]);
  const noteSections = useMemo(
    () => [
      // Flutter's note leads with the vitals (inside reasonForVisit, generated_notes_widget.dart:344)
      // and the PATIENT SUMMARY table (:429) BEFORE the step sections — see to-note-preamble.ts.
      // Gender/age drive Flutter's two CONDITIONAL rows (LMP, Head Circumference) and `reviewed`
      // picks its review line — see vitalsNoteSection.
      ...vitalsNoteSection(vitalsQuery.data, {
        sex: demographics?.sex,
        ageYears: demographics?.age,
        reviewed: vitalsReviewed,
      }),
      ...patientSummaryNoteSection(ipsSections),
      ...questionnaireNoteSections,
      // PROD-2608: saved answers the current template has no question for — carried by the note too.
      ...orphanedAnswersNoteSection(orphans),
    ],
    [vitalsQuery.data, demographics?.sex, demographics?.age, vitalsReviewed, ipsSections, questionnaireNoteSections, orphans],
  );

  // Modality-driven telemedicine (PROD-1276): a floating video panel for Virtual
  // visits. locationName comes from the persisted encounter (survives reload),
  // falling back to tap-time nav. ProviderId = the clinician on the call (Flutter:
  // nurse → practitionerId, else user id). Renders nothing for non-virtual visits.
  // Virtual-visit call state (Flutter StartVideoCallState). Connect is offered ONLY when
  // telemedicine is configured for the environment — VITE_VIDEO_BASE_URL empty means the embed
  // can't form a session URL, so the advisory renders without a dead action.
  const [videoInCall, setVideoInCall] = useState(false);
  const televisitConfigured = !!env.videoBaseUrl && isVirtualLocation(
    encounterQuery.data?.locationName ?? nav?.createContext?.locationName,
  );
  // Flutter `canStartCall` (lib/video-join-window.ts): Connect only from 10 minutes before the start until 2 hours after
  // the end, never once Fulfilled. Outside it the notice says why instead ("Video opens at 2:50 PM"); Leave is never
  // gated. Nav wins for the times, as for the note stamp; the fetched appointment covers a reload or a deep link.
  const videoJoin = useVideoJoinWindow(
    nav?.appointmentStart ?? appointmentStatusQuery.data?.start,
    nav?.appointmentEnd ?? appointmentStatusQuery.data?.end,
    appointmentStatusCode,
  );
  const videoCanConnect = televisitConfigured && videoJoin.state === 'open';
  const videoJoinReasonText = televisitConfigured ? videoJoinReason(videoJoin) : undefined;
  const televisit = (
    <EncounterTelevisit
      locationName={encounterQuery.data?.locationName ?? nav?.createContext?.locationName}
      appointmentId={appointmentId}
      providerId={televisitProviderId({ isNurse, id: user?.id, practitionerId: user?.practitionerId })}
      patientName={patient.name}
    />
  );

  // ── Chief complaint quick-capture (PROD-1588 — Samer live-review verdict 2026-07-23) ────
  // The pre-encounter CC card is LIVE end-to-end: typing feeds the CC RAG (GET /api/AISearch/
  // Search — Scrips.Master vector+SQL complaint index, the same source the Flutter SearchPicker
  // used); toggling a chip / "+ Create new" persists the FULL list via POST /api/Patients/
  // CreateChiefComplaint (replace-all upsert per encounter+subject); hydration + re-read via
  // GET /api/Patients/GetPatientComplaint. All three live-verified 200 on dev-api this session.
  // This resolves the PROD-1159 open product question (pre-encounter quick-CC survives —
  // Samer's verdict); the in-encounter HPI QR persistence (PROD-1354c divergence) is untouched.
  // Selected chips are SERVER TRUTH (the query re-hydrates after every save settles), with the
  // optimistic list shown while a save is in flight so chips don't lag the click.
  const [ccText, setCcText] = useState('');
  const ccDebounced = useDebounce(ccText, 300);
  // TWO surfaces share this one seam (2026-08-06): the pre-encounter card, and the in-encounter HPI
  // step the design puts it on (EncounterScreen.dc.html:349-371). Same state, same replace-all
  // persist — deliberately NOT a second write path, which is the whole point of widening the gate
  // rather than standing up a parallel one. Everything downstream (`ccReady` hydration gate,
  // single-flight queue) therefore protects both surfaces unchanged.
  const ccActive = showRail || currentStep === 'hpi';
  const ccSuggestQuery = useChiefComplaintSuggestions(ccActive ? ccDebounced : '');
  /* `complaintQuery` is NOT declared here. It is hoisted far above and read UNCONDITIONALLY — see
     its docblock. main's copy of this line gated it on `ccActive`, which is correct for the
     suggestions query above (per-keystroke, no business running outside the editor) but wrong for
     the persisted document: the doctor sign gate reads it on WRAP-UP, where `ccActive` is false, so
     gating it there blocks a doctor who HAS entered a complaint. Resolved this way when main was
     merged in (2026-08-28); re-gating it would silently revert the PROD-2032 sign gate. */
  const saveCc = useSaveChiefComplaint();
  // D-73 (amended) · frame v2 only: the leave guard's view of the note's saves. Integration (Codex P1): a held inline
  // line (History notes, patient instructions) commits through the chief-complaint and wrap-up writes, not the QR, so
  // the guard waits on those too, and asks when one failed — a clean QR alone no longer lets the clinician leave
  // while that text is still in flight. OFF publishes nothing.
  // Frame v2 (B5): a Fall / Pain save in flight counts too — the header never says "Saved" over it, and a sign waits.
  const noteSaving = saveQR.isPending || saveCc.isPending || saveWrapUp.isPending || (frameV2On && riskBridge.saving);
  // Frame v2 (Codex P1 on the walk fixes): a failed Fall / Pain save is a failed note save — the guard asks, the sign stops.
  const riskSaveFailed = frameV2On && riskBridge.saveFailed;
  const noteFailed = saveQR.isError || saveCc.isError || saveWrapUp.isError || riskSaveFailed;
  useEffect(() => {
    if (!frameV2On) return;
    useUnsavedNote.getState().publish({ active: true, dirty, saving: noteSaving, failed: noteFailed, canSave: canAutosave });
  }, [frameV2On, dirty, noteSaving, noteFailed, canAutosave]);
  // Integration (Codex P1): a sign never races a held line — held text is committed first, and the sign waits for
  // those writes (services/unsaved-note.ts useSignAfterFlush). Frame v2 only.
  // Frame v2 (B5): Fall / Pain edits not yet saved are sent BEFORE a sign (and before the leave guard decides), and the
  // sign waits for that write — a provider's sign would otherwise freeze them unsaved (the server refuses after it).
  useInlineDraftFlush('risk-assessments', () => (frameV2On ? riskBridge.flush() : false));
  const holdSignForHeldText = useSignAfterFlush({
    enabled: frameV2On,
    busy: noteSaving,
    failed: saveCc.isError || saveWrapUp.isError || riskSaveFailed,
    proceed: () => handleSignRef.current?.(),
    onStopped: () => refuseSign(SIGN_BLOCKED.heldWriteFailed),
  });
  // display → category side-map from every suggestion batch seen this session (the wire wants
  // complaintCategory alongside the display; Flutter carried categoryName the same way).
  // Merged in an effect (never during render); read only in the persist handler.
  const ccCategoryMapRef = useRef(new Map<string, string>());
  useEffect(() => {
    for (const row of ccSuggestQuery.data ?? []) {
      if (row.ComplaintName && row.CategoryName) ccCategoryMapRef.current.set(row.ComplaintName, row.CategoryName);
    }
  }, [ccSuggestQuery.data]);
  // Chips render only from `suggestions` — union the AI rows with the selected set so
  // persisted complaints stay visible (and de-selectable) when the query text changes.
  // (The HPI step's own typeahead-dropdown rows used to be derived here too, minus what was
  // already picked, for the retired PA-drawn field's `searchOptions`. PROD-2577 part B moved
  // that search inside the DS block via `resolveChiefComplaint`'s `onSearch`, which does its own
  // imperative fetch — see `fetchChiefComplaintSuggestions` — rather than reading this reactive
  // query, so no derivation of that shape is needed here any more.)
  const ccSuggestions = useMemo(() => {
    const names = (ccSuggestQuery.data ?? [])
      .map((r) => r.ComplaintName)
      .filter((n): n is string => !!n);
    return [...new Set([...ccSelected, ...names])];
  }, [ccSuggestQuery.data, ccSelected]);
  // Editor unlocks only once BOTH ids resolve AND the existing document has hydrated —
  // a replace-all POST fired before hydration would carry only the new pick and soft-delete
  // every existing complaint (+ its patientHpi children). (Codex P1, 2026-07-23.)
  const ccReady = !!patientId && !!encounterId && complaintQuery.isSuccess;
  // Replace-all saves must NEVER overlap: two in-flight bodies ([A] then [A,B]) can land
  // out of order and the server keeps the earlier one. While a save is pending, the latest
  // desired list parks in the queue ref and fires as ONE follow-up save on settle — same
  // coalescing discipline as the QR/A-P autosaves. (Codex P1, 2026-07-23.)
  // `existing` rides along (PROD-2136): a chip toggle's save carries the optimistic document its
  // `patientHpi` edit was applied to, so the queued follow-up re-posts THAT, not the stale server
  // copy — otherwise a second tap while the first save is in flight would drop the first.
  const ccQueueRef = useRef<{ selected: string[]; existing?: PatientComplaintModel } | null>(null);
  function fireCcSave(next: string[], comment?: string, notePlainText?: string, existing?: PatientComplaintModel) {
    // PROD-2810: the chief complaint is an encounter record; once a record save was refused as
    // signed, nothing more is sent (and no optimistic list is shown for a write that never goes out).
    if (recordRefused) return;
    setCcPending(next);
    // The optimistic document (a pending HPI edit) outranks the server copy: an add/remove of
    // a complaint while a chip save is settling must not re-post the pre-toggle answers.
    const sentDoc = existing ?? ccHpiPending ?? complaintQuery.data;
    saveCc.mutate(
      buildPatientComplaint({
        encounterId: encounterId as string,
        patientId: patientId as string,
        selected: next,
        categoryByName: ccCategoryMapRef.current,
        existing: sentDoc,
        comment,
        notePlainText,
      }),
      {
        onSettled: async () => {
          const queued = ccQueueRef.current;
          ccQueueRef.current = null;
          if (queued) {
            fireCcSave(queued.selected, undefined, undefined, queued.existing);
            return;
          }
          // HOLD the optimistic list until the server document is actually in hand. This used to
          // `setCcPending(null)` straight away and let the chips fall back to `complaintQuery.data`
          // — but the save hook only FIRED an invalidation, so that data was still the pre-save
          // document. The chip therefore appeared (optimistic), vanished (stale server list), then
          // reappeared when the refetch landed; removing with the x did the same in reverse
          // (Andrew, dev 2026-08-25). Awaiting the refetch removes the stale window entirely.
          //
          // `await` on a refetch cannot reject here (react-query resolves with an error result), so
          // the handover happens either way and the chips can never latch on a stale optimistic
          // list. onError still surfaces the failure.
          await complaintQuery.refetch();
          setCcPending(null);
          // PROD-3043: only the document THIS save sent. A newer edit started during the refetch (a
          // History notes pause) keeps its optimistic document, or its text would flash back to the old
          // value and an open draft would take that flash for a removal.
          setCcHpiPending((current) => (current === sentDoc ? null : current));
        },
        // A signed-note refusal latches and shows the record sentence (no toast on top); any other
        // failure keeps its toast.
        onError: (e) => {
          if (!latchRecordRefusal(e)) showServerError('Could not save the chief complaint', e);
        },
      },
    );
  }
  function persistCc(next: string[], existing?: PatientComplaintModel) {
    if (!ccReady) return;
    if (saveCc.isPending) {
      ccQueueRef.current = { selected: next, existing };
      setCcPending(next); // chips reflect the latest intent immediately
      return;
    }
    fireCcSave(next, undefined, undefined, existing);
  }
  // PROD-2136 — one HPI chip tapped on one complaint's card. Flutter NormalTag semantics live in
  // `toggleHpiSelection` (append a 'yes' entry / drop it / single-choice replaces in-group); the
  // result is the WHOLE complaint list re-posted through the same replace-all as adding a complaint
  // (kept complaints echoed verbatim, `templateItem` stripped). Same single-flight queue, so two
  // quick taps coalesce into one follow-up save carrying both.
  function handleCcHpiToggle(display: string, group: ComplaintHpiGroup, option: ComplaintHpiOption) {
    if (!ccReady || !ccDocument || !encounterId || !patientId) return;
    const target = findComplaintByDisplay(ccDocument, display);
    if (!target) return;
    const nextHpi = toggleHpiSelection(target.patientHpi, group, option, { encounterId, patientId });
    const nextDoc = applyComplaintHpi(ccDocument, display, nextHpi);
    setCcHpiPending(nextDoc);
    persistCc(ccSelected, nextDoc);
  }
  /**
   * PROD-3043 (D-73 amendment, frame v2): "History notes" — the document's `comment` (Quill Delta) +
   * `notePlainText`, the pair Flutter's Chief Complaint "Add notes" writes. Applied to the optimistic
   * document and posted through the SAME replace-all and single-flight queue as a chip edit, so a save in
   * flight, a queued follow-up and a later complaint edit all carry it (`buildPatientComplaint` reads the
   * comment off `existing`).
   */
  function handleCcComment(comment: string, notePlainText: string) {
    if (!ccReady || !encounterId || !patientId) return;
    const base: PatientComplaintModel = ccDocument ?? { encounter: encounterId, subject: patientId, complaints: [] };
    const nextDoc: PatientComplaintModel = { ...base, comment, notePlainText };
    setCcHpiPending(nextDoc);
    persistCc(ccSelected, nextDoc);
  }
  /** "Clear" on a category card — every selected option in that ONE group, in a single write.
   *
   *  Deliberately NOT expressed as N calls to `handleCcHpiToggle`: that handler reads `ccDocument`
   *  from the render closure, and a synchronous loop never re-renders between calls, so all N
   *  compute from the same document and only the last removal survives — the reported bug, where
   *  three selected chips lost exactly one (Ahmed 2026-09-13). `clearHpiGroup` drops them all in
   *  one pass, so there is one next document, one state update and one PUT. */
  function handleCcHpiClearGroup(display: string, group: ComplaintHpiGroup) {
    if (!ccReady || !ccDocument || !encounterId || !patientId) return;
    const target = findComplaintByDisplay(ccDocument, display);
    if (!target) return;
    const nextDoc = applyComplaintHpi(ccDocument, display, clearHpiGroup(target.patientHpi, group));
    setCcHpiPending(nextDoc);
    persistCc(ccSelected, nextDoc);
  }
  /**
   * PROD-2577 part B — the host half of the PE vitals block.
   *
   * `vitals-block` is a Studio POSITION marker like `chief-complaint-hpi`: the DS renderer calls
   * this for the group carrying itemControl `vitals-block` and the resolved props win over any
   * authored `blockProps`. The readings themselves are NOT questionnaire answers — same
   * `vitalsRow` (`patientVitalsToRow`) the pre-encounter rail and the retired `peVitals` strip
   * both read, so this is a new PRESENTATION shape for an existing feed, not a new fetch.
   *
   * Edit/Historical/Mark-reviewed are wired only where a real handler exists — `vitalsEditable`
   * gates all three, same as the retired `peVitals` prop did — and "mark reviewed" only renders
   * at all when there is something on this encounter to review (`hasVitalReadings`): the new
   * `VitalsBlock` pills layout has no "disabled with a reason" state for that control, so a host
   * with nothing to review omits the control rather than rendering a dead one.
   */
  const physicianVitalsReadOnly = physicianReadsVitalsOnly(clinicalRole);
  const resolveVitals = useCallback(
    (): VitalsBlockProps => ({
      layout: 'pills',
      // PROD-2962 (OL-1308 F2): frame v2 colours no % change, and shows none for temperature or SpO2.
      items: vitalsRowToBlockItems(vitalsRow, vitalsPatientType, frameV2On ? { frameV2: true } : {}),
      reviewed: vitalsReviewed,
      // D-74 (design desk, 6 Oct) · frame v2 only: with no readings for the visit the tiles keep their em
      // dashes (D-36) and the header's action is "Add" (the same Edit dialog and save path). OFF unchanged.
      // `?x=physician-vitals-readonly` (dev default, Antonin 3 Nov): the physician reads the nurse's vitals here, no Edit / Add.
      ...vitalsBlockActions({
        editable: vitalsEditable,
        hasReadings: hasVitalReadings,
        frameV2On,
        readOnly: physicianVitalsReadOnly,
        onReviewed: onMarkVitalsReviewed,
        onHistorical: () => setVitalsHistoryOpen(true),
        onEdit: () => setVitalsDialogOpen(true),
      }),
    }),
    [vitalsRow, vitalsPatientType, vitalsReviewed, vitalsEditable, hasVitalReadings, frameV2On, physicianVitalsReadOnly],
  );
  /**
   * PROD-2136 / Q2 — the host half of the chief-complaint HPI block.
   *
   * The block is a Signal DS component now, and the DS renderer draws it wherever the template
   * authored the `chief-complaint-hpi` marker. The marker says WHERE; this says WHAT, because the
   * complaints and their attribute history live in the COMPLAINT document, not in the
   * Questionnaire the renderer owns.
   *
   * The DTO -> presentation mapping itself is `toChiefComplaintHpiView`, in the lib beside the
   * document it maps, so this page and its tests cannot drift into two mappings of one datum.
   */
  const resolveChiefComplaint = useCallback((): ChiefComplaintHpiBlockProps => {
    const { complaints, findGroup } = toChiefComplaintHpiView(ccDocument?.complaints, frameV2On ? { keepSavedGroups: true } : undefined);
    return {
      complaints,
      // `ccReady` gates BOTH writes, exactly as it did before the block graduated. It is
      // load-bearing, not defensive: the chief-complaint write is a replace-all, so one fired
      // before hydration soft-deletes every complaint on the encounter.
      onToggle: ccReady
        ? (complaint, category, option) => {
            const group = findGroup(complaint.id, category.id);
            const opt = group?.options.find((o) => o.itemId === option.id);
            if (!group || !opt) return;
            handleCcHpiToggle(complaint.id, group, opt);
          }
        : undefined,
      // The SINGLE-WRITE clear. N toggles would lose all but the last — the calls never re-render
      // between each other, so each recomputes from the same captured document (Ahmed 2026-09-13).
      onClearCategory: ccReady
        ? (complaint, category) => {
            const group = findGroup(complaint.id, category.id);
            if (group) handleCcHpiClearGroup(complaint.id, group);
          }
        : undefined,
      loading: !complaintQuery.isSuccess && !complaintQuery.isError,
      // The PA brand spinner stays in the PA — the DS ships none, and takes this as a slot rather
      // than growing one.
      loadingIndicator: <QuietLoading label="Loading chief-complaint history…" />,
      error: complaintQuery.isError ? 'Couldn’t load the chief-complaint history.' : undefined,
      onRetry: complaintQuery.isError ? () => void complaintQuery.refetch() : undefined,
      saving: saveCc.isPending,
      // PROD-2577 part B (Ahmed 2026-09-24): "the HPI tab renders entirely from the template, so
      // the practitioner app no longer composes the field beside it" — the picker moves INSIDE
      // the block. Same seam as the retired `chiefComplaintSurface` field used
      // (`handleCcCreate`/`persistCc`, PROD-1588's AISearch RAG + CreateChiefComplaint
      // replace-all): not a second write path, the same one reached through a different prop.
      onAdd: ccReady ? handleCcCreate : undefined,
      onSearch: ccReady
        ? async (query: string) => {
            const rows = await fetchChiefComplaintSuggestions(query);
            for (const row of rows) {
              if (row.ComplaintName && row.CategoryName) {
                ccCategoryMapRef.current.set(row.ComplaintName, row.CategoryName);
              }
            }
            const picked = new Set(ccSelected);
            return [...new Set(rows.map((r) => r.ComplaintName).filter((n): n is string => !!n))].filter(
              (n) => !picked.has(n),
            );
          }
        : undefined,
      onRemove: ccReady ? (complaint) => persistCc(ccSelected.filter((name) => name !== complaint.id)) : undefined,
      pickerLabel: 'Chief Complaint',
      // Frame v2: once a complaint is recorded the empty field above its card is for ANOTHER one.
      // Reading "Enter Chief Complaint" there made a charted complaint look unrecorded (dev test
      // walk, 1 Oct). OFF keeps today's words.
      pickerPlaceholder: chiefComplaintPickerPlaceholder(complaints.length, frameV2On),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ccDocument, ccReady, complaintQuery, saveCc.isPending, ccSelected, frameV2On]);

  // Committing a SUGGESTION must clear the search box, like the typed "+ Create" path below
  // (PROD-1989). Flutter clears the controller the moment a pick is committed — SearchPicker's
  // onPointerDown calls `_textEditController?.clear()` BEFORE onChangedInternal
  // (scrips_core/lib/widgets/general/field_and_label.dart:3318-3321) — so the query never lingers
  // behind the new chip. Gated on the selection GROWING because this same callback also fires for
  // de-selection, and Flutter clears on commit only, never on removal.
  function selectCc(next: string[]) {
    const grew = next.length > ccSelected.length;
    persistCc(next);
    if (grew) setCcText('');
  }
  function handleCcCreate(value: string) {
    const v = value.trim();
    if (!v || ccSelected.includes(v)) return;
    persistCc([...ccSelected, v]);
    setCcText('');
  }
  // NOTE (merge of origin/main, 2026-08-08): the HPI step's chief-complaint block is now
  // main's `chiefComplaint` prop (PROD-1835 ChiefComplaintCards) — ONE implementation, so this
  // branch's competing `hpiChiefComplaint`/`hpiSectionNote` derivations are gone.
  // CAPABILITY GAP carried by that choice: the HPI SECTION NOTE persistence went with them.
  // It wrote the note into the complaint document's own `comment`/`notePlainText` via the same
  // CreateChiefComplaint replace-all (what Flutter's ChiefComplaintScreen posts) — no new
  // endpoint, no new store. Main's shell view exposes no host for it, so re-adding it is a
  // deliberate follow-up, not a merge fix. See git history for `hpiSectionNote`.
  /**
   * The vitals overlays — Edit dialog, history drawer, visual-acuity dialog — as ONE node that
   * BOTH returns render (the `growthPrintDialog` pattern).
   *
   * They used to be mounted only inside the `showRail` branch, which returns early. After Start,
   * `resolveVitals` still wired the PE vitals block's Edit and Historical to `setVitalsDialogOpen`
   * / `setVitalsHistoryOpen`, so both buttons set state that nothing rendered. One definition
   * means the two branches cannot disagree on props or gating: the mounts stay gated on
   * `vitalsEditable` (ids resolved), exactly as before; the WRITE gate (`vitalsWritable`) still
   * lives on the controls that open them, unchanged.
   */
  const vitalsOverlays = vitalsEditable ? (
    <>
      <VitalsEditDialog
        open={vitalsDialogOpen}
        existing={vitalsQuery.data}
        saving={saveVitals.isPending}
        onSubmit={onSubmitVitals}
        // PROD-3130: the same vitals, in the same order and names, as the PE vitals cards.
        patientType={vitalsPatientType}
        onClose={() => setVitalsDialogOpen(false)}
        // WAVE 2 (Samer 2026-07-23): acuity is part of the vitals — capture hands off
        // from the Edit flow (the standalone header link is retired). Edit stays OPEN
        // underneath (unsaved edits survive; closing VA returns to Edit — Codex P1).
        onVisualAcuity={() => setVisualAcuityOpen(true)}
        visualAcuitySummary={visualAcuitySummary}
      />
      <VitalsHistorySlot
        open={vitalsHistoryOpen}
        onOpenChange={setVitalsHistoryOpen}
        patientId={patientId}
        encounterId={encounterId}
        patientName={patient.name}
        female={demographics?.sex === 'F'}
        // The empty state's "Record Vitals" opens the same Edit dialog, only where this user may record vitals.
        onRecord={preVisitVitalsWritable && !physicianVitalsReadOnly ? () => setVitalsDialogOpen(true) : undefined}
      />
      <VisualAcuityDialog
        open={visualAcuityOpen}
        existing={existingVisualAcuity}
        saving={saveVitals.isPending}
        onSubmit={onSubmitVisualAcuity}
        onClose={() => setVisualAcuityOpen(false)}
      />
    </>
  ) : null;

  // The pre-encounter rail does NOT consume the template (it's gated on patientId, which
  // may still be resolving) — render it before the template guards so a pending/disabled
  // template query can't block the rail. Template loading/error gate the STEP shell only.
  // PROD-2804: the patient could not be resolved from the route — every source settled without one
  // (no encounter, no router state, and the appointment read failed or named no patient). Say so,
  // with a way back, rather than mounting a chart with nothing behind it. After every hook above.
  if (patientState === 'failed') {
    return (
      <PatientUnresolvedState
        onBackToToday={(home) => navigate(home)}
        onRetry={() => {
          void appointmentStatusQuery.refetch();
          if (!encounterId) void encounterQuery.refetch();
        }}
      />
    );
  }

  // `?x=previsit-intel`: the pre-visit's right column is the encounter's own Intelligence rail and cards (the same mount
  // as `intelligenceRail` below), phase "Pre-visit". No ambient session exists before Start, so no ambient node here.
  // Engine off or no patient: no rail at all, so the pre-visit keeps Care pathway / Note (an empty rail would replace them).
  // The pre-visit's ONE orb state, for the rail header and its orb button (PROD-3388): no ambient panel is mounted before Start,
  // so nothing is running, whatever a session last reported (the encounter's rule when it has no panel: Codex P2, #569).
  const previsitOrbState: OrbState = 'idle';
  // `?x=intel-always-open`: the rail follows the workspace tab. On Patient chart, Timeline and Documents its phase is the tab's
  // name and Explain is operable only where chip-routes.ts routes "Explain a body system" for that screen (it routes it on
  // none of them today, so the chip is drawn disabled there). The cards are the same patient's.
  const intelTab = intelFollowsTab && topTab !== 'encounter' ? topTab : undefined;
  const explainRouted = !intelTab || routeFor(TAB_SCREEN[intelTab], RAIL_WORDS.explainChip).kind === 'explain';
  const previsitIntelligence =
    frameV2On && env.intelligenceEngineEnabled && patientId ? (
      // `?x=intel-rail-catalog`: Antonin's catalog rail, the same panel pre-visit and in the encounter (catalog-rail.tsx).
      catalogRailOn ? (
        <CatalogIntelligenceRail
          // The rail serves the header tab showing (SAMER RULINGS, 4 Nov): Terrain, Chart, Timeline, Documents or Pre-visit.
          tab={workspaceTab}
          phase={intelligencePhase({ started: false, signed: false })}
          patientId={patientId}
          encounterId={encounterId ?? null}
          engineOn
          explainRequest={explainRequest}
          onExplain={askExplain}
          onOpenResults={handleOpenResults}
          // Ask (#910) stays behind its own `ask` switch, as on today's rail.
          onAsk={askOn ? askQuestion : undefined}
          askRequest={askRequest}
          appointmentId={appointmentId ?? null}
        />
      ) : (
      <IntelligenceRail
        orbState={previsitOrbState}
        phase={intelligencePhase({ started: false, signed: false, tab: intelTab })}
        footer={<IntelligenceRailFooter onExplain={explainRouted ? askExplain : undefined} onAsk={askOn ? askQuestion : undefined} />}
      >
        <IntelligenceCards
          patientId={patientId}
          encounterId={encounterId ?? null}
          appointmentId={appointmentId ?? null}
          explainRequest={explainRequest}
          onOpenResults={handleOpenResults}
          askRequest={askRequest}
        />
      </IntelligenceRail>
      )
    ) : undefined;

  if (showRail) {
    return (
      <>
      {/* THE CONSULT MOUNTS AT THE SAME TREE POSITION IN BOTH BRANCHES, AND THAT IS LOAD-BEARING.
          `showRail` flips between this branch and the started-encounter one below, and React
          reconciles a returned tree BY POSITION: while the consult lived inside
          the rail branch's wrapper div here and as a bare fragment child there, pressing Continue
          mid-call unmounted and remounted `EncounterTelevisit` — which reset its `phase` to
          'prejoin', tore down the live iframe, and (once the gate became a real modal) threw a
          scrim over the encounter the clinician had just opened. Measured 2026-09-08:
          iframe present -> absent, gate and scrim re-opened.
          Keeping it as fragment child 0 in BOTH returns is what preserves the element across the
          flip, so a call carries from the pre-encounter into the encounter the way Flutter's does
          (`isVideoCallStarted` is bloc state there, not widget state). Do not nest it back inside
          a branch wrapper without moving its state up to this page. */}
      {videoInCall ? televisit : null}
      {/* The rail branch's wrapper is layout only (a bounded flex column). It used to carry a
          `pa-encounter-canvas` class that re-pointed the surface-background and radius tokens
          from a PA stylesheet; that stylesheet is deleted (PA-Z, 2026-09-23) and both branches
          paint from the Signal DS tokens directly, so there is no class to share across Start. */}
      <div
        className="h-full flex flex-col min-h-0"
        // PROD-1549 (2026-09-05): the published Flutter page scrolls each COLUMN on its own — the
        // person card and the care-pathway header never leave the viewport. That needs a bounded
        // height here: the shell's <main> is the app scroller, and an auto-height wrapper made the
        // rail view's `height: 100%` resolve to its content (8.5k px), so the whole page scrolled
        // as one. The rail view fills the column. Scoped to this branch — the started-encounter
        // view bounds itself. (This once read "lets the televisit embed above take its height";
        // that is no longer true and was the defect — the pre-join gate is now portalled and the
        // in-call panel is absolute, so neither takes layout height from this column.)
      >
        <PreEncounterRailView
          patient={patient}
          deviceData={<DeviceTrendsSlot patientId={patientId} variant="card" />}
          ambientCard={
            // `ambientWindow` B1: the same card, in the same place, on the pre-visit: it starts the encounter, then listening.
            ambientWindowOn && env.ambientBaseUrl && accessToken ? (
              <AmbientStartWithEncounterCard
                disabledReason={startDisabledReason}
                onStart={() => {
                  listenAfterStartDone.current = false;
                  setListenAfterStart(true);
                  handleStart();
                }}
              />
            ) : undefined
          }
          // [PROD-2643] Unreviewed results filed on or before the visit day; only the physician marks them reviewed.
          previsitDiagnostics={
            <Suspense fallback={null}>
              <PrevisitDiagnosticsBlock patientId={patientId} visitDay={previsitDay} canReview={encounterRole === 'doctor'} />
            </Suspense>
          }
          // The nurse's pre-visit is Antonin's page (nurse-previsit-template.tsx), the medication reconciliation included [PROD-1653]:
          // the nurse records what the patient takes; the physician decides in the encounter. The reason is read from the booking.
          nursePrevisit={
            encounterRole === 'nurse'
              ? {
                  appointmentId,
                  navReason: nav?.createContext?.reasonForVisitName,
                  navAppointmentTypeCode: nav?.appointmentTypeCode,
                  encounterId,
                  visitDay: previsitDay,
                  userName: currentUserName,
                  userRoleLabel: roleLabel,
                  performerId: user?.practitionerId ?? user?.id,
                }
              : undefined
          }
          nurseNotesNode={
            nurseNoteOn ? (
              <Suspense fallback={null}>
                <NurseNotesBlock encounterId={encounterId} nurse={isNurse} />
              </Suspense>
            ) : undefined
          }
          demographics={demographics}
          role={role}
          // [PROD-1395] The widened role, only for the allied-health Referral card (flag `referrals`).
          encounterRole={encounterRole}
          // Top context chrome (overlay-gate iteration 2): the Encounter|Chart tab strip +
          // room/time/RFV/status block per the E2 pre-encounter frame. Every cell is
          // data-gated (anti-fiction): `room` has NO feed (list endpoints ship only roomId —
          // same gap as the in-encounter VisitInfoStrip) so it is omitted; time = tap-time
          // appointment start; reason = the fetched encounter's RFV; status = the appointment
          // status. The Chart tab swaps this body for the chart IN PLACE (PROD-1986) once the
          // patient id resolves; Back returns to the previous surface (worklist/calendar).
          chart={chartNode}
          documents={documentsNode}
          timeline={timelineNode}
          timelineLabel={carePlanTabOn && patientId ? 'Care plan' : 'Timeline'}
          terrain={terrainNode}
          intelligence={previsitIntelligence}
          intelligenceStrip={<IntelligenceStrip orbState={previsitOrbState} />}
          // No summary band above the Chart tab (Antonin, 16 Oct: "remove top section horizontal, it's useless"): the chart starts
          // under the page header. `chartBand` is simply not passed.
          visitContext={{
            startISO: nav?.appointmentStart,
            reason: encounterQuery.data?.reasonForVisitName ?? undefined,
            appointmentStatus: appointmentStatusCode,
            onBack: () => navigate(-1),
            activeTab: topTab,
            onTabChange: onTopTabChange,
            // D9 search — HONEST wiring: routes to the shipped /patients patient-search
            // surface (the list page's live search field), never a dead glyph.
            // Malaffi HIE tab — undefined for a non-Malaffi org / unresolved MRN / unconfigured
            // environment, so the tab simply doesn't render (Flutter gates the same third tab on
            // `resourceBasedIntegration.integrationType`).
            onOpenMalaffi: openMalaffi,
          }}
          reason={{
            // reasonForVisitName is populated on the fetched encounter (the PA appointment
            // list item doesn't carry reasonCode, so createContext omits it).
            reason: encounterQuery.data?.reasonForVisitName ?? undefined,
            // Visit-type chip + comment banner (Flutter reads both off the appointment).
            // Neither is on the encounter nor any single-appointment GET, so both ride the
            // tap-time nav context — absent on reload/deep-link, where the chip and banner
            // simply don't render (same degradation as Reschedule).
            appointmentType: visitTypeFor(nav?.appointmentTypeCode),
            comment: nav?.appointmentComment,
            // Modality glyph (PROD-1531) — same locationName that drives the televisit panel
            // ("Office"/"Home"/"Virtual"); unknown → glyph hidden.
            locationType: locationModality(
              encounterQuery.data?.locationName ?? nav?.createContext?.locationName,
            ),
          }}
          startAction={startActionResolved}
          startDisabledReason={startDisabledReason}
          onStart={() => {
            // `ambientWindow` B1: the normal Start never starts listening.
            setListenAfterStart(false);
            handleStart();
          }}
          // Activate IPS Flags interactivity (PROD-1174): patientId turns on the add/delete
          // affordance; currentUserId stamps CreateFlag. Backend confirmed reachable (the
          // PROD-1252 "gap" was a practice-scope artifact, not a write→read failure).
          // Intentionally NOT gated on encounterId: flags are PATIENT-level (CreateFlag carries
          // `subject`, no encounter — see to-flag-request.ts), so reviewing/updating them is a
          // valid pre-Start action on the rail. When patientId is unresolved (deep-link before
          // the subject resolves) it's `undefined` → the rail keeps Flags display-only.
          patientId={patientId ?? undefined}
          currentUserId={user?.id}
          // Allergy `recorder` / condition `asserter` are stamped from currentUserId; the
          // condition CREATE performer sentinel carries the user's display name (PROD-1265).
          currentUserName={[user?.firstName, user?.lastName].filter(Boolean).join(' ') || undefined}
          // Medication add (PROD-1257) needs the encounter id + the practitioner id (performer).
          encounterId={encounterId ?? undefined}
          performerId={user?.practitionerId ?? user?.id}
          canStopMedication={mayStopOrSuspend === true}
          // Patient-card extras in the DS card (PROD-1329) — same feed as the in-encounter rail so
          // the card matches on both surfaces (status, VIP/importance code badge, relative contacts).
          /* Tap-time nav first, then the fetched appointment — the same fallback the in-encounter
             rail has had. Without it the PRE-encounter card showed no status on reload/deep-link,
             where `nav` (router state) is gone. Both surfaces now read one source. */
          appointmentStatus={appointmentStatusCode}
          appointmentId={appointmentId}
          importance={detailQuery.data?.importance}
          contacts={toContacts(detailQuery.data?.emergencyContactResponse)}
          // Reschedule shows only when we have the slot to seed the dialog (tap-time nav).
          // Chief complaint quick-capture — LIVE (Samer live-review verdict 2026-07-23 resolves
          // the PROD-1159 open question: the pre-encounter quick-CC entry SURVIVES). RAG
          // suggestions + replace-all persist + re-read; see the CC block above the rail render
          // and use-chief-complaint.ts. The in-encounter HPI QR persistence (PROD-1354c) is
          // untouched — this writes only the complaint list, never questionnaire answers.
          chiefComplaint={ccText}
          onChiefComplaintChange={setCcText}
          ccSuggestions={ccSuggestions}
          ccSelected={ccSelected}
          onCcSelectionChange={selectCc}
          onCcCreate={handleCcCreate}
          ccSaving={saveCc.isPending}
          // Deferred until ids + hydration resolve. KNOWN GATE (matches vitals editing): a
          // brand-new appointment has no encounter row until Start creates one, so on that
          // flow the quick-CC stays inert pre-Start (Flutter's widget was likewise disabled
          // for un-arrived appointments; arrived/checked-in appointments resolve an encounter
          // via GET-by-appointment and unlock). Eager encounter creation on rail load would
          // be a side-effecting product change — deliberately NOT done here.
          chiefComplaintDeferred={!ccReady}
          /* Flutter locks the field for Booked/Pending/Proposed — a different rule from the arrived
             gate above, which is why it has its own helper. See isChiefComplaintLocked. */
          chiefComplaintLocked={isChiefComplaintLocked(appointmentStatusCode)}
          vitals={vitalsRow}
          onVitalsEdit={preVisitVitalsWritable ? () => setVitalsDialogOpen(true) : undefined}
          onVitalsHistorical={vitalsEditable ? () => setVitalsHistoryOpen(true) : undefined}
          // WAVE 2: the "+ Visual Acuity" header link is retired — acuity capture lives in
          // the Edit dialog (see VitalsEditDialog onVisualAcuity below).
          // "Vitals Reviewed" (PROD-1536): green-check state from the vital status + a mark-reviewed
          // write (re-save with status "reviewed"). Enabled only when vitals editing is available.
          vitalsReviewed={vitalsReviewed}
          // Inline vitals edit — the grid edits in place through the SAME buildVitalsRequest +
          // useSaveVitals path the Edit dialog uses. No second write path, no new endpoint.
          onVitalsSubmit={preVisitVitalsWritable ? onSubmitVitalsInline : undefined}
          vitalsExisting={vitalsQuery.data}
          vitalsSaving={saveVitals.isPending}
          // Frame v2 (B12): a vitals save refused as signed is said HERE, in place, not only in the note's banner.
          vitalsNotice={frameV2On && recordRefused ? SIGNED_NOT_SAVED : undefined}
          // Visual acuity captures through its OWN request (extraData), so the form is mounted
          // by the page and slotted into the grid rather than folded into the numeric form.
          visualAcuity={
            preVisitVitalsWritable ? (
              <VisualAcuityForm value={acuityDraft} onChange={setAcuityDraft} />
            ) : undefined
          }
          // Cancel on the acuity panel reverts to the last SAVED acuity, so a discarded edit
          // leaves nothing behind for the next Save to pick up.
          onAcuityCancel={() => setAcuityDraft(existingVisualAcuity ?? emptyVisualAcuity())}
          videoInCall={videoInCall}
          onVideoConnect={videoCanConnect ? () => setVideoInCall(true) : undefined}
          onVideoLeave={televisitConfigured ? () => setVideoInCall(false) : undefined}
          videoJoinReason={videoJoinReasonText}
          patientGender={demographics?.sex === 'F' ? 'female' : demographics?.sex === 'M' ? 'male' : null}
          patientAge={demographics?.age ?? null}
          onVitalsReviewed={vitalsEditable ? onMarkVitalsReviewed : undefined}
          canReviewVitals={hasVitalReadings}
          // Growth chart (PROD-1538) — same age/sex-gated node the in-encounter shell mounts;
          // undefined for adults (gate closed) → the middle column renders nothing.
          growthChart={growthChartNode}
          submittedForm={toSubmittedForm(submittedFormQuery.data)}
          submittedFormError={submittedFormQuery.isError}
          patientType={vitalsPatientType}
          ipsSections={ipsSections}
          flagRecords={flagRecords}
          occupation={toRailOccupation(detailQuery.data)}
          education={toRailEducation(detailQuery.data)}
          occupationEntity={detailQuery.data?.patientOccupation}
          educationEntity={detailQuery.data?.patientEducation}
          activeMedications={toRichActiveMedications(summaryQuery.data, rxOrdersQuery.data)}
          vaccinationRequests={vaccinationRequests}
          onAdministerVaccination={setAdministering}
          // Medication administration requests (PROD-1524) — display-only for now; the Give write
          // is a follow-up seam. Previous Notes (PROD-1523): fed only under `?x=previous-notes`; empty → hidden.
          previousNotes={previousNotes}
          onViewPreviousNote={setViewingPreviousNote}
          medicationAdminRequests={medicationAdminRequests}
          // PROD-1988: the pre-encounter's right rail now serves its Note tab. This is the SAME
          // `noteSections` the in-encounter drawer renders — one generated note, two hosts, no
          // second derivation to drift. Pre-Start it is legitimately empty (no answers yet) and
          // the DS organism says so itself, which is why passing it early is honest rather than a
          // fake panel. Flutter mounts GeneratedNotesWidget beside this screen too
          // (`sidePanel()`, encounter_screen.dart:1657), so the tab was always meant to work; the
          // PROD-1588 wave-S2 note below it referred to the 4-tab DRAWER, which still belongs to
          // the in-encounter shell alone.
          note={
            // PROD-1999: the SAME `GeneratedNoteBody` the in-encounter drawer renders, so the two
            // surfaces cannot show a different note — and cumulative by construction (every
            // questionnaire section plus A/P). `onGoToStep` is deliberately omitted: there are no
            // steps to jump to before Start, so the per-section links would be dead.
            /* NO `ambient` here, deliberately. Andrew, 2026-08-24: "no keep it inside the
               encounter." The ambient scribe (PROD-1944) records a consultation, so it belongs to
               the started encounter and not to the screen you are still deciding whether to open.
               This is a PRODUCT call, not an oversight or an unfed seam — it was raised as a
               difference between the two note hosts and answered. Do not "fix" it by passing
               `ambient` through here; everything else about this note IS shared with the drawer
               (sections, A/P, header stamp, inset), which is what makes the omission look like a
               gap. Flutter cannot settle it either way: it has no ambient scribe at all. */
            /* PROD-1549 notes-rail parity: the pre-encounter note is now the WHOLE note —
               body + attestation + footer — because Flutter mounts ONE `GeneratedNotesWidget`
               beside both screens and its `footer()` is inside the same padded ListView
               (`generated_notes_widget.dart:113`), suppressed only for the claim workbench. The
               pre-encounter previously rendered the body alone, so Sign Note / View PVS / Discard
               existed after Start and vanished before it. */
            <>
              <GeneratedNoteBody
                noteSections={noteSections}
                apNoteSections={toApNoteSections({ diagnoses, orders, assessmentNote, orderDetail: noteOrderDetailFn })}
                /* The identity stamp Flutter's note carries wherever it renders — the SAME object the
                   in-encounter drawer gets, so the two headers cannot say different things. */
                patient={noteHeaderPatient}
                /* Flutter's DOCS section, on this surface too (`:192`). The ids, not the rows:
                   the fetch mounts inside the note (see `note-column/note-docs-feed.tsx`) so the
                   page keeps no `useQuery` that its twenty mocked page suites would have to
                   provide a QueryClient for. */
                docsPatientId={patientId}
                docsEncounterId={encounterId}
                /* The saved complaints — same record the HPI step reads. */
                chiefComplaints={noteComplaints}
                /*
                 * `enableAction` — `notes_component_widget.dart:130`. This is the ONE surface where
                 * Flutter's empty-state CTA is live: its own gate is the appointment being
                 * startable, which is exactly what `startDisabledReason` already encodes here
                 * (status gate + "Preparing encounter…" + the in-flight create). Reusing it means
                 * the CTA and the Start pill can never disagree about whether starting is allowed.
                 *
                 * NOT VERIFIED against Flutter's second condition: its gate also requires valid
                 * vitals (`canEditVitals`), and the PA has no vitals-validity predicate to read.
                 * Recorded as owed in FINDINGS rather than approximated.
                 */
                canStartEncounter={!startDisabledReason}
                /* Pre-Start, so the CTA keeps Flutter's "Start Appointment and " prefix (`:132`). */
                isAppointmentStarted={false}
                /* Flutter's CTA STARTS the appointment and then lands on the step. Same two
                   actions, same handlers the Start pill and the step nav already use. */
                onGoToSection={(key: NoteSectionKey) => {
                  setListenAfterStart(false);
                  handleStart();
                  /*
                   * `setCurrentStep`, NOT `handleStepChange` (PROD-1549, 2026-09-09).
                   * (before rule C) `handleStepChange` pushed the step you are LEAVING into `completedSteps`
                   * (`:2782-2784`) — correct when a clinician finishes a step and moves on, wrong
                   * here: `currentStep` is still its initial 'hpi', so "Start Appointment and Go
                   * to Docs" landed with HPI already ticked in the step nav and the progress bar,
                   * marking work nobody had done.
                   *
                   * The autosave flush `handleStepChange` also performs is a no-op on this path:
                   * this CTA only exists BEFORE Start, so there is no debounced questionnaire
                   * edit in flight to lose.
                   */
                  setCurrentStep(key);
                }}
              /* Scroll-follow (PROD-1999 follow-up): the note brings the active step's section to
                 the top, as Flutter's note does. Wrap-up is skipped inside the note body, matching
                 Flutter's own exclusion. */
              activeStep={currentStep}
                reasonForVisit={encounterQuery.data?.reasonForVisitName ?? undefined}
              />
              <SignedAttestation signed={signed} signedAt={signLockSignedAt(signLockQuery.data) ?? undefined} />
              {/*
                * The SAME `NoteFooter` the drawer mounts, with the SAME handlers — Flutter's
                * `footer()` is one widget on one screen (`generated_notes_widget.dart:368-427`),
                * so a second set of behaviours here would be a second sign path.
                * `Discard This Note` is not gated on `signed`: Flutter renders it
                * unconditionally and only the sign SLOT swaps for the Signed pill.
                */}
              {patientId && encounterId && (
                <NoteFooter
                  onSignLock={!signed ? handleSign : undefined}
                  onViewPvs={() => setPvsOpen(true)}
                  signLockLabel="Sign Note"
                  onDiscard={mayDiscard ? () => setDiscardOpen(true) : undefined}
                  disabled={
                    createSignLock.isPending ||
                    signPromoting ||
                    pvsPrint.isPending ||
                    (signNeedsOrderSign && saveAssessment.isPending)
                  }
                />
              )}
            </>
          }
          loading={summaryLoading || detailQuery.isLoading || vitalsQuery.isLoading}
          loadError={summaryLoadError}
          onRetry={retrySummary}
          unavailableSections={unavailableSections}
        />
        {previousNotesOn && (
          // Follow-up only, as Flutter: the visit type rides the tap-time nav context, so a reload or deep link shows no
          // section (same as the visit-type chip). Id = user.id (Flutter providerId()).
          <PreviousNotesLoader
            userId={user?.id}
            patientId={patientId}
            reasonForVisitCode={encounterQuery.data?.reasonForVisit}
            reasonForVisitName={encounterQuery.data?.reasonForVisitName}
            currentEncounterId={encounterQuery.data?.identifier ?? encounterId}
            isFollowUp={visitTypeFor(nav?.appointmentTypeCode) === 'follow-up'}
            onNotes={setPreviousNotes}
          />
        )}
        {previousNotesOn && (
          <ClinicalNotePanel
            open={viewingPreviousNote !== null}
            onOpenChange={(open) => {
              if (!open) setViewingPreviousNote(null);
            }}
            encounterId={viewingPreviousNote?.id}
            patientId={patientId ?? undefined}
            centred={isEncounterFlagOn('layoutFixes')}
          />
        )}
        {vitalsOverlays}
        {administering && (
          <VaccinationAdminDialog
            open
            editorKey={administering.id}
            vaccine={vaccinationEntryToValueSet(administering.vaccine, administeringEntry, administering.title)}
            // "Give" (isAdministrationRequested) ⇒ nurse recording the dose (lot mandatory);
            // "Edit" ⇒ doctor amending the plan request.
            isAdministration={administering.isAdministrationRequested}
            initial={
              administeringEntry
                ? vaccinationEntryToInitial(administeringEntry)
                : { isAdministrationRequested: administering.isAdministrationRequested }
            }
            saving={saveAssessment.isPending || createSignLock.isPending}
            saveError={administerError ?? undefined}
            // Flutter switches the button to "Save & Sign" on the LAST outstanding request
            // (pre_encounter_widget.dart:1456-1459, 1473-1480), because that save also signs.
            saveLabel={
              administering.isAdministrationRequested === true &&
              vaccinationRequests.filter((r) => r.isAdministrationRequested === true).length <= 1
                ? 'Save & Sign'
                : undefined
            }
            onSave={handleAdministerSave}
            onClose={() => {
              setAdministerError(null);
              setAdministering(null);
            }}
          />
        )}
        {growthPrintDialog}
      </div>
      {/*
        THE NOTE FOOTER'S TWO DIALOGS, MOUNTED ON THIS BRANCH TOO (PROD-1549, 2026-09-09).

        `showRail` returns EARLY, and both `DiscardNoteConfirmDialog` and `PostVisitSummaryDialog`
        live at the bottom of the started-encounter return. So when the pre-encounter note gained
        Flutter's footer, its Discard and View PVS pills set state that nothing on this branch
        rendered — two live-looking buttons that did nothing. Found by a test that drove the real
        click instead of asserting the prop; the page-level suites stayed green throughout, because
        the node existed and only its dialog was unreachable.

        Same components, same handlers as the other branch — not a second implementation.
      */}
      {patientReportedDialog}
      <NeedsDxSignPrompt state={orderSetApply} diagnoses={diagnoses} />
      <SignNoteConfirmDialog
        open={signConfirmOpen}
        forNurse={isNurse}
        signing={createSignLock.isPending || signPromoting}
        onConfirm={() => {
          setSignConfirmOpen(false);
          doSign();
        }}
        onCancel={() => setSignConfirmOpen(false)}
      />
      <DiscardNoteConfirmDialog
        open={discardOpen}
        discarding={saveWrapUp.isPending}
        onConfirm={handleDiscardNote}
        onCancel={() => setDiscardOpen(false)}
      />
      {pvsOpen && (
        <Suspense fallback={<QuietLoading label="Loading visit summary" />}>
          <PostVisitSummaryDialog
            open
            onOpenChange={(next) => {
              if (!next) setPvsOpen(false);
            }}
            encounterId={encounterId ?? undefined}
            appointmentId={appointmentId ?? ''}
            labExtras={labPrintExtras}
          />
        </Suspense>
      )}
      {/* The served-template marker, on the pre-encounter too (ENC-L0+, D-4983): the dev feature
          check reads it BEFORE Continue, because Continue writes the encounter. Same element as the
          started branch. LAST child on purpose: the televisit must stay fragment child 0. */}
      <span
        hidden
        data-served-template-id={templateData?.questionnaire?.id ?? ''}
        data-served-template-title={templateData?.questionnaire?.title ?? ''}
        data-served-template-source={templateData?.source ?? 'axes'}
      />
      </>
    );
  }

  // Step shell: needs the template. (Rail already returned above.)
  // PROD-2608: a pinned note also waits for the template comparison before its FIRST hydrate — an
  // editable form with the saved answers not yet in it would let a keystroke autosave over them.
  if (!templateHeld && (isLoading || (drift === 'pending' && hydratedFor !== encounterId))) {
    // No spinner (2026-10-12): the slot holds its height and stays empty, and the shell arrives through its own
    // page-load motion (visit strip → step nav → note rail, then the step fade) once the template is in.
    return <QuietLoading className="h-full" label="Loading encounter template…" />;
  }
  // G11: a template that could not be READ no longer takes the whole encounter down. The shell stays
  // up with no template (`templateUnavailable`): A/P and Wrap-up work, HPI · Risk · ROS · PE say the
  // template failed to load, and the banner above the canvas carries Retry. Nothing the template owns
  // can be lost meanwhile — the saved note is not hydrated (no template to hydrate against), the
  // questionnaire form is not drawn and its autosave and Save need the template, the ambient scribe is
  // held (its narrative writes would land before the saved answers do), and signing waits for the
  // template (see `templateBlocksSign`).
  // The template query is DISABLED (no resolved patient) — nothing to retry.
  if (!templateData && !templateUnavailable) {
    return <FullMessage text="Couldn't load the encounter template. Please retry." />;
  }

  // No Type-A template matched the axes (BFF returned 200 []). Do NOT block the encounter:
  // A/P + Wrap-Up are template-independent, so the shell still mounts — only the HPI step
  // degrades to an in-pane "configure in Template Studio" notice (template-gate-relax).
  // (Authoring the encounter template is a PM/Template-Studio task.)
  // `dsQuestionnaire` — the gender-scoped questionnaire the view renders — is MEMOISED far above,
  // beside the one shared `fhirToDsQuestionnaire` conversion. It cannot be declared here: this
  // point is past the `showRail` early return and the two guards above it, and a hook after a
  // conditional return breaks the rules of hooks.

  // AUTHOR-CONTROLLED STEP TITLES: the active template's `cps-layout` root extension carries a
  // `stepTitles` map (all seven `EncounterStepKey`s: hpi, risk, ros, pe, docs, ap, wrap-up) that
  // renames the nav tabs. Read from the FHIR resource — the DS `Questionnaire` the view renders has
  // no `extension` field, so this is the one place the layout document is reachable. `stepTitlesOf`
  // never throws: a missing extension, malformed JSON or a wrong-typed value yields {} and every
  // tab keeps its built-in label. `hiddenSteps` (the sibling `hiddenSteps` array, hpi/risk/ros/pe
  // only) was already computed above, alongside `templateData`, because the initial-step useState
  // needs it too.
  const stepTitles = stepTitlesOf(templateData?.questionnaire);

  // PROD-1944 · the ambient scribe node, built ONCE and placed by the frame. Default frame: inside
  // the drawer's Note tab, above the generated note (unchanged — the same element as before). Frame
  // v2: it IS the right column (Scrips Intelligence), and the generated note leaves that column.
  // Held while the template is unavailable (G11): its sink and recovery writes would edit the note
  // before the saved answers are loaded, and the first save after Retry would then overwrite them.
  // PROD-3195. The slot shows its placeholder from the first render. While sign-in was still being
  // restored it used to render NOTHING, so the panel appeared late and the page jumped. Same placeholder,
  // same slot in both frames; the panel replaces it when the token arrives. Only while sign-in is
  // pending: with no session at all there is nothing coming, and the slot stays empty as before.
  // `ambientOverlay`: a nurse has no ambient (ambientPlace.hidden); with the switch off this is never true.
  const ambientNode = ambientPlace.hidden ? undefined :
    env.ambientBaseUrl && !accessToken && authPending && !templateUnavailable ? (
      <QuietLoading label="Loading ambient scribe" />
    ) : env.ambientBaseUrl && accessToken && !templateUnavailable ? (
      // The panel is one block inside a scrolling rail, so the fallback stays
      // small and in-flow — the brand mark at rail scale, not a skeleton block.
      <Suspense fallback={<QuietLoading label="Loading ambient scribe" />}>
        {/* PROD-3195: the patient's recording consent is checked in the background when the encounter
            opens and gates Start (UX only: Video still enforces it). */}
        <RecordingConsentHost patientId={patientId} encounterId={encounterId}>
        {(recordingConsentPort) => (
        <>
        {ambientWindowOn ? <AmbientConsentReporter status={recordingConsentPort.status} onChange={setAmbientConsent} /> : null}
        <AmbientScribePanel
          /* Frame v2: the panel reports its status so the header Orb tells the truth (#569). OFF:
             not passed, so the panel is handed exactly today's props. */
          {...(frameV2On ? { onStatusChange: setAmbientStatus, onRecordingControl: setRecordingControl, quietEmptyDecisionRecord: true } : {})}
          streamBaseUrl={`${env.ambientBaseUrl.replace(/^http/, 'ws').replace(/\/$/, '')}/AmbientScribe/Stream`}
          accessToken={accessToken}
          // The three inputs the panel needs to LABEL each accept control before it is
          // pressed. All required, none defaulted: `acceptVerb` withholds prescribing
          // verbs from a nurse and every verb from a signed encounter, and a defaulted
          // value would silently offer a write the record or the role does not allow.
          //
          // The same `role` and lock the rest of this page gates on — the panel must
          // not derive its own answer to a question the shell has already answered, or
          // the drawer and the canvas will eventually disagree about who is looking.
          role={ambientRole}
          // WHICH CHART, AND — as of `encounter-from-transcript/2` — WHICH CLINIC.
          //
          // This prop carried a standing "do not pass until Scrips.VideoApplication#204
          // is deployed" hold, because a server without it treated any text frame as
          // end-of-stream and sent no `ContextAck`, so the binding would sit at
          // `pending` and the fail-closed accept guard would block the whole
          // consultation. #204 is deployed; the hold is discharged, and passing it is
          // what turns the encounter-binding guard on for every encounter.
          //
          // It now also does a second job: the server resolves the DISCIPLINE and
          // SPECIALTY from this context rather than defaulting to primary care, which is
          // what selects the clinical prompt the consultation is analysed with. Sending
          // nothing means the panel silently gets the generalist prompt in a specialist
          // clinic. `templateId` is deliberately omitted — the encounter template is
          // chosen on this screen and can change mid-encounter, and a stale one on the
          // session would be worse than none.
          //
          // THE ENCOUNTER AND THE PROVIDER, NEVER THE PATIENT. `AmbientSessionContext`
          // accepts a `patientId` and nothing downstream needs one: the binding ack is
          // compared on `encounterId`, and the discipline/specialty resolution reads the
          // practitioner's claims. The narrowest identity that does the job is the one
          // that should cross the boundary, and `outbound-privacy-guard.test.tsx` is the
          // standing rule this follows.
          sessionContext={
            encounterId ? { encounterId, providerId } : undefined
          }
          // A PROVIDER sign locks ambient, not a nurse-only one — see `ambientLocked`.
          encounterLocked={ambientLocked}
          // The A/P canvas's own diagnosis count. It decides whether accepting an order
          // can auto-link to a sole diagnosis ("Add order") or has to open the dialog.
          dxCount={diagnoses.length}
          // PROD-3182: the plan's diagnoses and orders, so a suggestion already on it says
          // "Already added" and the order a suggestion opened marks it accepted once saved.
          planItems={ambientPlanItems}
          // PROD-1972. Omitting this was the bug: the panel rendered a full review UI
          // whose checkboxes were bound to `undefined`, so accepting a proposal did
          // nothing and looked like it should work. The prop is required now.
          // main's form (#358): the host RETURNS what it did, so the panel records an
          // acceptance only on a real write. Keep it — the `void` wrapper here was the
          // pre-#358 shape and would discard the outcome.
          onAcceptProposal={handleAcceptProposal}
          // PROD-3195: keeps each dictated narrative as an OFFER (`to-narrative-answers.ts`); the
          // note is written only when the physician accepts it (`narratives` below). Offers nothing
          // for a template that does not declare a slot.
          onNarratives={ambientNarratives.onNarratives}
          // PROD-3195: keeps the "Additional notes" lines as OFFERS; one line is appended only when the
          // physician accepts it (`sinkLines` below).
          onSinkEntries={ambientSink.onSinkEntries}
          sinkLines={ambientSink.host}
          // PROD-2532 (B7): a reported / continuing medication is charted through the
          // rail's own CreateMedication write (isOrder:false), with the rail's performer.
          medicationList={
            patientId && (user?.practitionerId ?? user?.id)
              ? { patientId, performerId: (user?.practitionerId ?? user?.id) as string }
              : undefined
          }
          narrativeContests={ambientRecoveries.narrativeContests}
          onResolveNarrativeContest={ambientRecoveries.resolveContest}
          recoveredNotes={ambientRecoveries.recoveredNotes}
          onUndoRecoveredNote={ambientRecoveries.undoNote}
          // PROD-2000 is live (Scrips.Patient #967/#969), so decisions now persist: the
          // Decline control renders, a written accept is recorded, Undo appends a
          // reversal, the dictated chief complaint can be charted without typing it, and
          // the decision record is read back from the server (PROD-2596 / PROD-2533).
          decisions={ambientDecisions}
          // PROD-2598: Acknowledge on a clinical alert is recorded server-side (audit only).
          alertAcknowledgements={ambientAlertAcks}
          // PROD-1994: transcript edits persist (who/when/before/after recorded server-side).
          transcriptStore={ambientTranscript}
          // PROD-3195: suggestions come back after a reload.
          analysisStore={ambientAnalysis}
          // PROD-3181: New recording asks Keep both / Replace first; recordings are listed.
          recordings={ambientRecordings}
          // PROD-3195: Start is disabled until the patient's recording consent is confirmed.
          recordingConsent={recordingConsentPort}
          // PROD-2650: Undo of a written diagnosis accept removes the A&P row it wrote.
          onPrepareRetract={prepareAmbientRetract}
          // PROD-3183 (vitals part): dictated vitals become one "Dictated vitals" suggestion. Anyone who
          // may enter vitals here may accept it (the vitals card's own gate); the write merges with the
          // recorded vitals, which must be loaded first.
          dictatedVitals={{
            current: vitalsQuery.isSuccess ? (vitalsQuery.data ?? []) : undefined,
            canAccept: vitalsWritable && !recordRefused,
            onAccept: handleAcceptDictatedVitals,
          }}
          // PROD-3183 (ROS, PE and risk part): each dictated finding is a suggestion for its section:
          // tick the matching option, else add the line to the section's notes, only on Accept.
          findings={findingsHost}
          narratives={ambientNarratives.host}
        />
        </>
        )}
        </RecordingConsentHost>
      </Suspense>
    ) : undefined;

  // ── PROD-2743 · frame v2 (DARK) — built only when the switch is on; OFF passes nothing new ────
  // The RULED structure (C1a/C1c as amended 30 Sep + D-31): chart on the leading edge for the whole
  // encounter; Encounter · Patient chart · Timeline over the centre + right column; the centre is
  // the existing step canvas under the S·O·A·P ribbon; the right column is Scrips Intelligence only.
  const frameV2: EncounterFrameV2 | undefined = frameV2On
    ? {
        // LEADING: the pre-visit chart's data and handlers — the same `PatientSummaryRail` with
        // the SAME props the pre-encounter rail hands it (pre-encounter-rail-view.tsx, from the
        // values this page passes that view) — laid out as the approved chart column
        // (`layout="chart"`, chart-column-v2.tsx). Never `band`: the frame does not render one in
        // this mode, and the page does not pass one either (EncounterShellView drops it).
        // R6 (PROD-2804) as on the pre-visit rail: a failed read says so with a retry, and a
        // section whose field was not delivered says "unavailable", never "No … recorded".
        leading: (
          <PatientSummaryRail
            layout="chart"
            // The visit has started: the column leaves out the visit card and the MRN row (Antonin, 1 Nov).
            started
            // The visit has started: its status is the design's chip (the live state solid, D-49; the
            // field's own words, so it reads as the pre-visit pill does, PROD-3045 N5).
            hideStatus
            patient={patient}
            demographics={demographics}
            ipsSections={ipsSections}
            flagRecords={flagRecords}
            occupation={toRailOccupation(detailQuery.data)}
            education={toRailEducation(detailQuery.data)}
            occupationEntity={detailQuery.data?.patientOccupation}
            educationEntity={detailQuery.data?.patientEducation}
            patientId={patientId ?? undefined}
            currentUserId={user?.id}
            currentUserName={[user?.firstName, user?.lastName].filter(Boolean).join(' ') || undefined}
            encounterId={encounterId ?? undefined}
            performerId={user?.practitionerId ?? user?.id}
            canStopMedication={mayStopOrSuspend === true}
            {...(medActionsOn && mayOrderMedications && !providerSigned ? { onMedicationToPlan: medicationToPlan } : {})}
            appointmentStatus={appointmentStatusCode}
            appointmentId={appointmentId}
            importance={detailQuery.data?.importance}
            contacts={toContacts(detailQuery.data?.emergencyContactResponse)}
            loading={summaryQuery.isLoading || detailQuery.isLoading || vitalsQuery.isLoading}
            loadError={summaryLoadError}
            onRetry={retrySummary}
            unavailableSections={unavailableSections}
            // "Add to note" only for the A&P's own writer, once the A&P is loaded and editable (`mayAddProblemToNote`). Everyone else
            // (a nurse, allied health), before Start, while the A&P loads, or after the provider sign keeps main's "+ Note".
            {...(mayAddProblemToNote ? { onAddProblemToNote: handleAddProblemToNote } : {})}
            // Left-rail target §4: "Add to note" writes ONE chart piece into this encounter's note (or says why it cannot).
            notePieces={railNotePieces}
          />
        ),
        // The collapsed strip (below 1384px by default, or by the practitioner's chevron): the
        // initials and the allergy dot, which stays visible whenever the chart is collapsed (D-41).
        leadingStrip: (
          <ChartColumnStrip
            patient={patient}
            allergies={ipsSections.find((s) => s.id === 'allergies')}
            loading={summaryLoading}
            loadError={summaryLoadError}
            unavailable={unavailableSections.includes('allergies')}
          />
        ),
        // PROD-2962: the Intelligence rail is collapsible by the clinician's chevron (it always starts open,
        // D-67); collapsed, its 56px strip keeps the ONE live Orb and what the ambient scribe is doing.
        intelligenceStrip: (
          <IntelligenceStrip
            orbState={ambientNode ? orbStateForAmbient(ambientStatus) : 'idle'}
            statusWord={ambientStatusWord(ambientStatus, !!ambientNode)}
          />
        ),
        workspaceTabs: (
          <EncounterWorkspaceTabStrip
            activeTab={workspaceTab === 'chart' ? 'chart' : 'encounter'}
            onTabChange={handleWorkspaceTabChange}
            chartAvailable={!!chartNode}
            // Same meaning as the in-encounter strip's back: return to the pre-encounter. That unmounts the note, so an
            // open, filled phrase is asked about first, as a leave is (#689 follow-up, Andrew 10 Oct).
            // The long page has no pre-encounter to go back to (the visit opens on it): no Back.
            // Malaffi HIE SSO, as in the old frame's started visit (same gated opener).
            onOpenMalaffi={openMalaffi}
            // D-68 (design desk, 6 Oct): the full name, DOB with age and MRN, drawn here only while the
            // chart card is not visible (collapsed, or below 1384px), so identity is on screen at every width.
            endSlot={
              <>
                <VisitStatusChip beforeStart={false} providerSigned={providerSigned} appointmentStatus={appointmentStatusCode} />
                <FrameV2PatientIdentity patient={patient} demographics={demographics} />
              </>
            }
          />
        ),
        activeTab: workspaceTab,
        // Orders staged but not sent — the page's existing order status; nothing is fetched for it.
        pendingOrderCount: orderSent ? 0 : orderStatus.orderCount,
        // TS-3: the note's layout, remembered per browser.
        noteMode,
        // "Add to note" in the chart column: each press moves the note to the Assessment (a counter, like `seedRequest`).
        assessmentJump,
        // PROD-2961: the lines' pieces, kept beside their text and saved with it.
        noteDeltas,
        onNoteDeltaChange: handleNoteDeltaChange,
        // The structured note's phrase library (PROD-2743). "Create macro from current data" opens
        // the existing Settings macro dialog pre-filled with the narrative; nothing saves until the
        // clinician names it and presses Save there.
        // PROD-3040 · L1: what the record holds, for template items filled from the chart (SDC initialExpression /
        // code binding): the birth date and registered sex (patient detail), the visit's date, the latest BMI in this
        // encounter's vitals, and the ACTIVE problem list's ICD-10 codes. The SAME reads the page already holds.
        chartContext: chartLaunchContext,
        // D-74 / D-45: the vitals read state for the Vitals block the note draws first in Objective when the
        // template has no vitals-block marker — the SAME read the pre-visit vitals use (no new request).
        vitals: vitalsReadState({
          idsResolved: vitalsEditable,
          isError: vitalsQuery.isError,
          isPending: vitalsQuery.isPending,
          refetch: vitalsQuery.refetch,
          latestAt: (vitalsQuery.data ?? []).reduce<string | undefined>((m, v) => {
            const t = v.effectiveDateTime ?? v.createdDate ?? undefined;
            return t && (!m || t > m) ? t : m;
          }, undefined),
        }),
        phrases: {
          macros: noteMacros,
          onCreateFromText: authUser?.id && authUser?.organizationId ? (text) => setMacroDraft(text) : undefined,
          // PROD-2824: piece phrases (filled before they land) and the chart reads their chart pieces quote
          // — the SAME reads the A&P note's chart pieces use.
          pieces: phrasePiecesOn,
          chartReads: noteChartReads,
          suggestFor: suggestPhrasesFor,
          // PROD-3055 (b): an allied clinician's lines answer to her discipline's phrase editor.
          ...(alliedDiscipline ? { discipline: alliedDiscipline } : {}),
        },
        // PROD-3043 (D-73 amendment): "History notes" in the HPI block, the chief-complaint document's comment.
        // 7 Oct 2026 ruling: Flutter's managed CDC STEADI / NRS lines in the Fall / Pain record, read back (read-only).
        riskManaged: managedRiskLines(riskBridge.heads.fall, riskBridge.heads.pain),
        // PROD-3128: the STEADI follow-up under "Fall risk present" (absent → none).
        ...(steadiFollowUp ? { riskSteadi: steadiFollowUp } : {}),
        // PROD-3142 (D-78): the list refused as stale is frozen (read as text) until a reload; the other keeps saving.
        ...(riskConflictSentence ? { riskFrozen: riskBridge.saveConflict } : {}),
        historyNote: {
          comment: ccDocument?.comment,
          notePlainText: ccDocument?.notePlainText,
          onChange: handleCcComment,
          owner: { patientId: patientId || undefined, encounterId: encounterId || undefined },
          ready: ccReady,
          macroScope: {
            specialtyId: templateSpecialty.specialtyId,
            visitType: templateVisitType.visitType,
            pending: templateSpecialty.pending || templateVisitType.pending,
          },
        },
        // Flutter sign model (Andrew, 14 Oct 2026): a sign never locks the note's answers; editing continues and a re-sign
        // is allowed. The encounter-record writes (chief complaint, Fall / Pain) stay frozen after the provider sign:
        // the server refuses them (409) and they are not QuestionBank records.
        signedFreeze: { answers: false, records: providerSigned },
        // PROD-2810 x TS-3: the steps whose save was refused as signed — their sections show no ✓.
        ...(refusedSteps.length > 0 ? { refusedSteps } : {}),
        // FV2-38: a signed note's header says when it was signed (the record's own time, never "now").
        signedAt: signLockSignedAt(signLockQuery.data),
        signedBy: signerName,
        centrePanel:
          v2Panel === 'note' ? (
            <CentrePanel title="Generated note" closeLabel="Close generated note" onClose={() => setV2Panel(null)}>
              {/* The SAME note body and attestation the drawer's Note tab renders, with the same
                  inputs — minus the ambient panel, which stays in the right column, and minus the
                  footer, whose actions are in the Wrap-up bar now. */}
              <div className="flex flex-col gap-4">
                <GeneratedNoteBody
                  patient={noteHeaderPatient}
                  reasonForVisit={encounterQuery.data?.reasonForVisitName ?? undefined}
                  noteSections={noteSections}
                  apNoteSections={toApNoteSections({ diagnoses, orders, assessmentNote, orderDetail: noteOrderDetailFn })}
                  docsPatientId={patientId}
                  docsEncounterId={encounterId}
                  chiefComplaints={noteComplaints}
                  isAppointmentStarted
                />
                <SignedAttestation signed={signed} signedAt={signLockSignedAt(signLockQuery.data) ?? undefined} />
              </div>
            </CentrePanel>
          ) : v2Panel === 'clinical-edit' ? (
            <CentrePanel title="Clinical edits" closeLabel="Close clinical edits" onClose={() => setV2Panel(null)}>
              <ClinicalEditTab encounterId={encounterId ?? undefined} />
            </CentrePanel>
          ) : undefined,
      }
    : undefined;
  // PROD-3041: Scrips Intelligence diagnosis suggestions (the A&P's former "Suggest"; the A&P passes
  // `aiDiagnosisEnabled: true`, unconditionally), physician only. Add records through the A&P's own add
  // (`handleAddDx`); nothing is added after the provider sign (rule 38). Reads the same values the A&P props
  // are built from, not the props object (the React Compiler lint treats that object as mutable).
  const intelligenceDxSuggestions =
    frameV2On && mayOrder === true ? (
      <IntelligenceDxSuggestions
        complaints={ccSelected}
        existingCodes={diagnoses}
        onAdd={handleAddDx}
        readOnly={apFrozen || apReadOnly}
        // Round 18r: a card in the rail's column, and no bare "Record a chief complaint" line without a complaint.
        quietWithoutComplaint
        className={R.card}
      />
    ) : null;
  // Frame v2's right column: Scrips Intelligence ONLY (D-31) — the ambient panel + its proposals.
  const intelligenceRail = !frameV2On ? undefined : catalogRailOn ? (
    // `?x=intel-rail-catalog`: Antonin's catalog rail with the same engine hooks. Ambient and the diagnosis suggestions are
    // OUT of the rail (SAMER RULINGS, 4 Nov): the ambient session lives in its window (ambientWindowOn above). The rail
    // serves the header tab showing (Terrain, Chart, Timeline, Documents, or the encounter's phase).
    <CatalogIntelligenceRail
      tab={workspaceTab}
      phase={intelligencePhase({ started: true, signed: providerSigned, step: currentStep })}
      patientId={patientId ?? null}
      encounterId={encounterId ?? null}
      engineOn={env.intelligenceEngineEnabled}
      explainRequest={explainRequest}
      onExplain={env.intelligenceEngineEnabled && patientId ? askExplain : undefined}
      onOpenResults={handleOpenResults}
      onAsk={env.intelligenceEngineEnabled && patientId && askOn ? askQuestion : undefined}
      askRequest={askRequest}
      appointmentId={appointmentId ?? null}
      // The ONE live Orb follows the ambient scribe (no panel mounted: nothing is running), as on today's rail.
      orbState={ambientNode ? orbStateForAmbient(ambientStatus) : 'idle'}
    />
  ) : (
    /* No panel mounted (no ambient URL or token) → nothing is running, whatever it last reported
       (Codex P2: a stale "thinking" must not survive the panel). */
    // Round 18r design: header (orb · "Scrips Intelligence" · "· <phase>" · More), a scrolling column of cards, and the
    // footer pinned at the bottom. This rail is the STARTED encounter's column, so the phase is Visit or Wrap-up.
    <IntelligenceRail
      orbState={ambientNode ? orbStateForAmbient(ambientStatus) : 'idle'}
      phase={intelligencePhase({ started: true, signed: providerSigned, step: currentStep, tab: intelTab })}
      footer={<IntelligenceRailFooter onExplain={env.intelligenceEngineEnabled && patientId && explainRouted ? askExplain : undefined} onAsk={env.intelligenceEngineEnabled && patientId && askOn ? askQuestion : undefined} />}
    >
      {/* The DoH §3.4 gate, then Next best action (first) and the Explain card, loading by themselves: the SAME
          mount the classic drawer uses (intelligence-cards.tsx), in its rail mode. */}
      {env.intelligenceEngineEnabled && patientId ? (
        <IntelligenceCards
          patientId={patientId}
          encounterId={encounterId ?? null}
          appointmentId={appointmentId ?? null}
          explainRequest={explainRequest}
          onOpenResults={handleOpenResults}
          askRequest={askRequest}
        />
      ) : null}
      {intelligenceDxSuggestions}
      {/* The ambient scribe's live session (transcript, proposals, alerts); its idle "Record encounter" card is
          not drawn here: the recording control is in the page header. With `ambientWindow` it is in the floating window. */}
      {ambientWindowOn ? null : ambientNode}
    </IntelligenceRail>
  );
  // `ambientWindow`: the Start listening card (above the note) and the window that holds today's panel. The panel is mounted in
  // the window even while it is closed, so its recording control (which drives the card) and a running session stay alive.
  // Pre-visit placement (Andrew's call, pending): pass `ambientCardNode` to the pre-visit view too.
  const ambientCardNode =
    ambientWindowOn && recordingControl && ambientNode ? (
      <AmbientStartCard control={recordingControl} consent={ambientConsent} onOpen={() => setAmbientWindowOpen(true)} />
    ) : undefined;
  const ambientWindowNode =
    ambientWindowOn && ambientNode ? (
      <AmbientWindow
        open={ambientWindowOpen || (listenAfterStart && started)}
        onClose={() => {
          setAmbientWindowOpen(false);
          setListenAfterStart(false);
        }}
        status={recordingControl?.status === 'recording' ? `Listening · ${clockOf(recordingControl.elapsed)}` : undefined}
      >
        {ambientNode}
      </AmbientWindow>
    ) : null;

  return (
    <>
      {/* Fragment child 0, same as the pre-encounter branch — see the long note there. This is
          also now GATED on `videoInCall` like that branch, which it never used to be: for a
          Virtual visit the pre-join gate mounted the moment the step shell did, so the clinician
          was greeted by a camera-permission card they had not asked for — and once that card
          became a real modal (2026-09-08) it became a scrim over the whole encounter. Flutter
          never does this: the consult is gated on `isVideoCallStarted` and offered by the banner
          (encounter_screen.dart:919-931, :973). The banner now rides this shell too, below. */}
      {videoInCall ? televisit : null}
      <EncounterShellView
        // PROD-2577: the Risk renderer mounts only once its stored EncounterMetaData answers are
        // merged into `response` (see `useRiskBridge`).
        // Templates added from the "/" menu: each its own answer set, run by the view (never by this page's save).
        insertedTemplates={insertedTemplates}
        riskReady={riskBridge.ready}
        riskReadFailed={riskBridge.readFailed}
        onRiskDone={riskBridge.onRiskDone}
        // PROD-2608: the template-changed banner + the read-only "Additional documentation" list.
        ambientCard={ambientCardNode}
        templateNotice={
          <>
            {/* PROD-2810: a save refused because the note is signed — above the canvas on both paths. */}
            <SignedSaveRefusedNotice sentence={noticeSentence} />
            {/* Another writer changed the note: the answers stay, and a save waits for "Save again". */}
            <NoteConflictNotice conflict={!!qrConflict} busy={!!qrConflict?.reading} onSaveAgain={handleSaveAgain} />
            {/* G11: how the template loaded — the failure + Retry, and a pick made without the
                visit's specialty because its lookup failed. */}
            <TemplateLoadNotice
              failed={templateUnavailable}
              onRetry={retryTemplate}
              retrying={retryingTemplate || templateHeld}
              chosenWithoutSpecialty={
                !!templateSpecialty.lookupFailed && !!templateData && templateData.source !== 'pinned'
              }
              chosenWithoutVisitType={
                !!templateVisitType.lookupFailed && !!templateData && templateData.source !== 'pinned'
              }
            />
            <TemplateDriftNotice
              drift={drift}
              pin={notePin}
              templateTitle={templateData?.questionnaire?.title}
              orphans={orphans}
            />
          </>
        }
        templateUnavailable={templateUnavailable}
        // The Connect / Leave banner, in-encounter. Flutter renders `videoStartWidget` on BOTH
        // surfaces (encounter_screen.dart:973 for the started encounter, :906 pre-encounter); the
        // PA had it on the pre-encounter rail only, which is why gating the consult here needed
        // this in the same change — otherwise a deep-link straight into a started Virtual
        // encounter would have no way to join at all. Same three props the rail is given, so the
        // two surfaces cannot drift.
        virtualVisitNotice={
          televisitConfigured ? (
            <VideoJoinNotice
              inCall={videoInCall}
              onConnect={videoCanConnect ? () => setVideoInCall(true) : undefined}
              onLeave={() => setVideoInCall(false)}
              reason={videoJoinReasonText}
            />
          ) : undefined
        }
        // D9 NAVFIRST (Samer 2026-07-22): the SAME top bar the pre-encounter rail mounts —
        // back + Encounter|Chart + search — now on the in-encounter step shell too. Wiring
        // mirrors the pre-encounter visitContext 1:1: back = history, Chart = the in-place chart
        // tab (only when the patient id resolves — PROD-1986), search = the shipped /patients
        // patient-search surface (honest — a real route, not a dead glyph).
        topBar={
          // Frame v2 (the long page) has ONE page header (photo, name, facts, the tabs, "Checked In"): this strip, with
          // the prototype's tabs, in place of the workspace tabs and the patient card's own header.
          (
            <PreEncounterTabStrip
              activeTab={frameV2 ? workspaceTab : topTab}
              onTabChange={frameV2 ? (tab) => handleWorkspaceTabChange(tab) : onTopTabChange}
              documentsAvailable={!!documentsNode}
              timelineAvailable={!!timelineNode}
              timelineLabel={carePlanTabOn && patientId ? 'Care plan' : 'Timeline'}
              terrainAvailable={!!terrainNode}
              patient={patient}
              demographics={demographics}
              importance={detailQuery.data?.importance}
              /* Back inside a STARTED encounter returns to the pre-encounter screen — it does not
                 leave the encounter. Flutter, `onBackButtonPress` (encounter_screen.dart:1624-1629):

                     if (state.viewState == EncounterPageViewState.appointment) {
                       bloc?.add(UpdateView(EncounterPageViewState.initial));
                     }

                 i.e. the started view steps back to `initial`, which IS the pre-encounter. Only from
                 `initial` does back pop the route (the pre-encounter's own onBack, above, keeps
                 `navigate(-1)`). This was `navigate(-1)` here too, so back left the encounter
                 entirely and the pre-encounter was unreachable once started (Andrew, 2026-08-24).
                 `started` is the same client flag the rail's Continue sets, so this is the exact
                 inverse of that transition: no route change and no refetch. (On the long page the started view does unmount; see below.) */
              // On the long page Back swaps the started view for the pre-visit view; the note's answers (`response`) and its dirty
              // generation are this page's state, so a typed answer survives Back and Continue (tested in long-page-start).
              // The long page (frame v2) has its pre-visit too (the `!started` branch), so Back returns there as well, behind
              // `?x=previsit-back` (Andrew, 3 Nov: "I can't go back from encounter to the pre-encounter screen"; it was left out on
              // 19 Oct, when frame v2 had no pre-encounter). Switch OFF (prod): the long page keeps no Back, as before.
              onBack={frameV2 && !previsitBackOn ? undefined : () => setStarted(false)}
              {...(frameV2
                ? {
                    oneLook: true,
                    // Round 18r: the ambient recording control sits in the page header, by the name; the status follows it (or, with
                    // `?x=rail-fixes`, opens the subline).
                    // `ambientWindow`: the Start listening card above the note replaces this mic.
                    startedTag: recordingControl && ambientNode && ambientPlace.headerMic ? <RecordingHeaderControl control={recordingControl} /> : undefined,
                    status: <VisitStatusChip beforeStart={false} providerSigned={providerSigned} appointmentStatus={appointmentStatusCode} />,
                    // With rail-v3 the started rail draws no patient card under `?x=rail-fixes`: its importance and language come here.
                    cardMoved: isEncounterFlagOn('railV3'),
                  }
                : {})}
              // Malaffi HIE SSO — Flutter carries the same third tab into the STARTED encounter
              // (encounter_screen.dart tabViewInitial + the tabController listener), so it mounts
              // on both surfaces from the one gated opener.
              onOpenMalaffi={openMalaffi}
              // Staff chat (SR-14): message a colleague about this patient. Only with `?x=staff-chat` and
              // `?x=staff-chat-patient` (patient threads wait for Samer's call). Nothing when off.
              headerAction={isStaffChatPatientOn() ? <MessageColleagueAction patientId={patientId} /> : undefined}
            />
          )
        }
        // PROD-1986: Chart swaps the step chrome + canvas only — the summary banner above and the
        // Note/Orders drawer beside it stay mounted (Flutter encounterView parity).
        activeTab={topTab}
        chart={chartNode}
        documents={documentsNode}
        timeline={timelineNode}
        terrain={terrainNode}
        // PROD-2743 frame v2 — spread ONLY when this browser turned the switch on, so the OFF
        // render hands the view no new prop at all (not even an undefined one).
        {...(frameV2 ? { frameV2 } : {})}
        patient={patient}
        demographics={demographics}
        role={encounterRole}
        currentStep={currentStep}
        // Wrap-up has neither icon in the live app, so it is handed no handler and the
        // cluster renders them disabled — the same shape as every other un-wired action.
        //
        // PROD-2040 adds the allied-health PLAN step to the same withholding. Belt and braces: the
        // plan step does not mount the section chrome that renders the ✦ Add-Order icon in the
        // first place, so no affordance reaches the screen either way — but allied health has no
        // prescribing scope, so it must not be handed an order handler it could later grow a
        // surface for. The 'wrap-up' arm is unchanged.
        //
        // Frame v2 (Wrap-up is now the last BLOCK of A&P / P, so it is on screen while `currentStep`
        // is 'ap' or 'allied-plan'): still correct, and deliberately still keyed on the step. These
        // two handlers reach ONLY the questionnaire section chrome (hpi · risk · ros · pe), and a
        // section holding Wrap-up holds no questionnaire step — A&P is ap + wrap-up, P is
        // allied-plan + wrap-up or wrap-up alone — so no block that could draw the icons ever
        // shares a section with Wrap-up. Nothing on the Wrap-up block itself (sign, send/preview
        // orders, pending orders, the post-visit summary) is gated on `currentStep`: `wrapUp` below
        // is built for the whole step shell (`wrapActive`), so it works wherever the block shows.
        // PROD-2946: and only on the physician allowlist (`ordersOffered`), whatever the step.
        onAddOrder={
          !ordersOffered || currentStep === 'wrap-up' || currentStep === ALLIED_PLAN_STEP || currentStep === NURSING_PLAN_STEP
            ? undefined
            : () => {
                setSearchSeed(null); // manual open — see onOpenAddOrder
                setAddOrderOpen(true);
              }
        }
        onAddToFavourites={
          !ordersOffered || currentStep === 'wrap-up' || currentStep === ALLIED_PLAN_STEP || currentStep === NURSING_PLAN_STEP
            ? undefined
            : () => setFavouritesOpen(true)
        }
        // PROD-2810: a step whose save was refused as signed is not shown as completed.
        completedSteps={shownCompletedSteps}
        {...(attentionSteps ? { stepAttention: attentionSteps } : {})}
        {...(stepPass ? { stepPass } : {})}
        {...(frameV2On
          ? {
              ruleC: true,
              onStepsVisited: (steps: readonly PaStepKey[]) =>
                setVisitedSteps((prev) => (steps.every((x) => prev.has(x)) ? prev : new Set([...prev, ...steps]))),
            }
          : {})}
        onStepChange={handleStepChange}
        // The long page: the section ribbon slides in once, when the visit starts (never on a later step change).
        {...(frameV2On ? { stepBarReveal, onStepBarRevealed: () => setStepBarReveal(false) } : {})}
        // G11: while the template is unavailable (or held through a re-read, until the saved
        // answers are hydrated) no form is drawn — an editable form ahead of the hydrate could be
        // autosaved over the saved answers. Main's frame-v2 readout lines ride on the same gate.
        questionnaire={templateUnavailable ? undefined : renderedQuestionnaire}
        stepTitles={stepTitles}
        hiddenSteps={hiddenSteps}
        // D7 template pill (Samer 2026-07-22): the picked template's REAL name from the
        // 5-axis selection (FHIR Questionnaire.title — the only name field the BFF wire
        // carries) — omitted when the wire serves none (no fabricated "Default").
        templateName={templateData?.questionnaire?.title?.trim() || undefined}
        visitType={visitTypeFor(nav?.appointmentTypeCode)}
        response={response}
        onResponseChange={handleResponseChange}
        saving={saveQR.isPending}
        // D8 saved-state indicator: quiet "Saved" only when the content is CLEAN and a
        // persisted row actually exists (effectiveQrId) — never a claimed save that didn't happen.
        saved={!dirty && !!effectiveQrId}
        saveError={saveError}
        status={status}
        // Frame v2 only: OFF passes the very same props as before (no `visitComplaint` key at all).
        assessmentPlan={
          // Rule 38 (PROD-3031 item 3, ON and OFF): the A&P stays read-only after the provider sign (Scrips.Patient refuses it).
          apFrozen ? { ...apViewBase, readOnly: true } : apViewBase
        }
        // [PROD-2949] spread only when switched on, so the OFF render hands the view no new prop.
        {...(alliedPlanDraft.active
          ? {
              alliedPlan: {
                ...alliedPlanProps(alliedPlanDraft),
                ...(alliedPlanSpeciality ? { speciality: alliedPlanSpeciality } : {}),
                // [PROD-3376] The template draws the nutrition care process (LOINC 75305-3) → no duplicate card.
                frameworkInTemplate: questionnaireHasItemCode(renderedQuestionnaire, 'http://loinc.org', '75305-3'),
                templateOwnsPlan: (alliedSoapLayout(renderedQuestionnaire, 'allied-health').groups.plan?.length ?? 0) > 0,
              },
            }
          : {})}
        {...(nursingPlanDraft.active ? { nursingPlan: nursingPlanProps(nursingPlanDraft) } : {})}
        wrapUp={wrapUp}
        // NO vitals props (regression cured 2026-07-23): the step shell renders no vitals
        // strip — the E2 step frames carry none. Vitals capture/review/history stays on the
        // PRE-ENCOUNTER surface above (EncounterWorkflow branch, preencounter-frame anchor).
        visitInfo={
          // Frame v2 (PROD-3045 N4): one header for every role and path — see `visitFacts` above.
          frameV2On
            ? visitFacts
            : {
                // Header strip (Flutter roomInfo + visitReasonInfo). time+reason are on the page; ROOM
                // comes from the appointment-detail fetch above — the nested `room.name`, which is the
                // only place a room NAME exists (list endpoints ship a bare roomId). Still optional: an
                // unroomed appointment leaves it undefined and the strip falls back to a time-only cell
                // under the neutral "Time" caption, never a fabricated room.
                room: appointmentStatusQuery.data?.room?.name?.trim() || undefined,
                startISO: nav?.appointmentStart,
                reason: encounterQuery.data?.reasonForVisitName ?? undefined,
              }
        }
        // PROD-2577 part B: the HPI chief-complaint field + cards this `chiefComplaint` prop used
        // to feed are retired (Ahmed 2026-09-24) — the SAME quick-CC seam (`ccReady`/`ccText`/
        // `handleCcCreate`/`persistCc`/`ccSelected`) now reaches the DS `ChiefComplaintHpiBlock`
        // through `resolveChiefComplaint`'s `onAdd`/`onSearch`/`onRemove` above, not this prop.
        // PROD-2136 / Q2 — the chief-complaint HPI block's feed, now a RESOLVER rather than a
        // prop bag. The DS renderer calls it for a group carrying the `chief-complaint-hpi`
        // itemControl, so the Studio's authored position decides where the block draws and this
        // page no longer does. Always passed; a template without a marker never reaches it.
        // See `resolveChiefComplaint` for the DTO boundary and the `ccReady` gate.
        resolveChiefComplaint={resolveChiefComplaint}
        // PROD-2789 — the complaint suggests which ROS / exam systems open first (A9: it never
        // selects, hides or answers). The view turns these ids into the renderer's `systemOrder`.
        chiefComplaints={ccComplaintRefs}
        // PROD-2577 part B — vitals on PE, now a RESOLVER like `resolveChiefComplaint` above:
        // the DS renderer calls it for the group carrying itemControl `vitals-block`, so the
        // Studio's authored position decides where the block draws. Same readings + the same
        // three writes the pre-encounter strip uses — a second surface onto one source, not a
        // second source. The retired `peVitals` prop bag is gone; see `resolveVitals`.
        resolveVitals={resolveVitals}
        // GROWTH CHART IS PRE-ENCOUNTER ONLY (Ahmed, 2026-09-07: "the Growth Chart only exists in
        // the pre-encounter, but when I start an encounter step I see it there").
        //
        // Flutter renders it in ONE widget — `pre_encounter_widget.dart`; a grep for GrowthChart
        // across the whole shared encounter module finds no other view (the remaining hits are
        // bloc/event/state and the scheduling vitals tab). The deployed app agrees: no chart on any
        // encounter step. Mounted here it also sat OUTSIDE the step canvas's scroll region, so it
        // was pinned above hpi/risk/ros/pe/docs/ap/wrap-up and had to be collapsed by hand to reach
        // the step — the same shape as the "strip above every step canvas" regression that
        // encounter-shell-view.tsx:759-762 records as cured (PROD-1588).
        //
        // ⚠️ CONTRADICTS A TICKET: the mount came from PR #120 / PROD-1324, titled "In-encounter
        // pediatric GrowthChart", so this is a ticketed feature being un-mounted on parity grounds,
        // NOT drift being cleaned up. PROD-1324 needs amending, or this line needs restoring.
        // The `growthChart` slot on EncounterShellView is deliberately LEFT IN PLACE so restoring
        // is exactly this one line. The pre-encounter mount (PROD-1538/PROD-2044, line ~3292) is
        // untouched and is where the chart still renders.
        // Frame v2: the chart leads instead — no band is handed to the shell in that mode.
        patientSummary={frameV2 ? undefined : inEncounterSummaryRail}
        summaryExpanded={summaryExpanded}
        onSummaryExpandedChange={setSummaryExpanded}
        docs={
          // Docs step (Contract §4 Docs block): the shipped patient-documents surface — list +
          // Status/Filters + Add/upload (NewFilePopup). ENCOUNTER-scoped (PROD-1238): the fetch
          // and new uploads carry the encounter id, mirroring Flutter's encounter Docs tab
          // (`FetchPatientEncounterDocument`, `patient_encounter_bloc.dart:2452`); handles an
          // unresolved patient with its own "save the patient first" empty state. Gated on the
          // RESOLVED encounter id (Flutter guards the fetch the same way) — while it resolves,
          // an ungated mount would fire a transient PATIENT-wide fetch and flash the exact
          // cross-encounter list this scoping removes.
          encounterId ? (
            // `listScope` and `surface` are two different jobs and both are the step's, not the
            // chart's: WHICH documents (this encounter's only) and WHICH anatomy (the deployed
            // `encounter_docs_screen.dart` — its own header over a bare list, no Status segment,
            // no Filters, no pager). Both props are documented in full on `DocumentsChartTabProps`.
            // `onAddOrder` is Flutter's `moreOptionsButton` sparkle (`:154-161`, "Add Order"),
            // pointed at the same handler A/P's sparkle uses.
            <DocumentsChartTab
              patientId={patientId ?? undefined}
              encounterId={encounterId}
              listScope="encounter"
              surface="encounter-step"
              // Frame v2 (Andrew's walk, 7 Oct; K8): orders are added in Plan, on the one "Add order" line — not
              // from the Documents toolbar. OFF keeps Flutter's sparkle here.
              onAddOrder={frameV2On ? undefined : assessmentPlan.onOpenAddOrder}
            />
          ) : undefined
        }
        drawer={
          frameV2 ? (
            // Frame v2: the right column is Scrips Intelligence (the ambient panel and its
            // proposals). The generated note, Orders, Timeline and Edit tabs leave it.
            intelligenceRail
          ) : (
            // RIGHT column (Contract §4 right-drawer): one column of collapsible sections (Scrips
            // Intelligence, Orders, Clinical edits, Care pathway, Note; encounter-drawer.tsx).
            <EncounterDrawer
              /* Flutter's panel is `Expanded` — the remainder after the main column takes
                 max(969, 70%) — not a design constant (PROD-2011). The DS ContextRail expresses
                 that as `width="fill"`; below 1384px the popup fills the same way. */
              width="fill"
              noteSections={noteSections}
              // PROD-1944. The ambient scribe, above the generated note in the Note tab.
              //
              // Rendered only when BOTH the host and a token are present. Missing config means no
              // panel rather than a broken one: the endpoint is authenticated, so a panel without
              // a token can only produce a 401 the provider cannot act on, and one without a host
              // would stream a consultation nowhere.
              ambient={ambientWindowOn ? undefined : ambientNode}
              // I3 deep links: "Go To <section>" in the note rail rides the SAME step-navigation
              // handler EncounterStepNav fires (autosave flush + completed-step marking included).
              onGoToStep={handleStepChange}
              encounterId={encounterId ?? undefined}
              // The Care pathway section reads the same /history feed the pre-encounter column reads
              // (usually a cache hit). The Timeline tab it replaces was never fed (PROD-1261).
              appointmentId={appointmentId}
              orders={ordersSummary}
              // Note stamp + RFV (design lines 797-818). Straight off the identity the card already
              // resolved, so the rail and the band can never disagree. Sub-fields the record doesn't
              // carry stay undefined and the DS organism omits their row.
              //
              // PROD-2011: Flutter's note header is three lines — patient identity, then
              // "location • date • time", then "Created by <avatar> <provider>"
              // (generated_notes_widget.dart:120-163). `visitType` carries the LOCATION, which is the
              // DS slot that sits on that line; `date` carries the appointment start, which the DS
              // formats. `createdBy` stays UNSET: Flutter names the appointment's PROVIDER, and no
              // payload we read carries one — the logged-in user is not the same thing (a nurse can
              // open a physician's encounter), so naming them would put the wrong clinician on a
              // clinical record. Omitted until the appointment provider is plumbed through.
              patient={noteHeaderPatient}
              reasonForVisit={encounterQuery.data?.reasonForVisitName ?? undefined}
              // On the A/P step the rail restates what the canvas holds (Diagnosis + the order
              // groups) instead of the questionnaire note — derived from the SAME diagnoses/orders
              // the canvas renders, so the two cannot drift. Empty A/P → [] → the questionnaire note
              // stays. `orderDetail` is the row's secondary line; PlanOrder has no code/sig of its
              // own, so it is left unsupplied until the order payloads expose one, and rows render
              // name-only rather than showing a fabricated code.
              /* PROD-1999: NOT gated on the active step. Flutter appends the A/P section to the
                 note unconditionally (`generated_notes_widget.dart:194` —
                 `generatedNotesComponent(EncounterTab.ap, widget.apBloc)` sits in the same list as
                 HPI/ROS/PE with no tab condition), so the diagnoses and orders stay in the note
                 wherever the clinician is standing. Gating on `currentStep === 'ap'` made the A/P
                 block appear only while on that step — half of the "every tab alone" defect; the
                 other half was the drawer dropping the questionnaire note when A/P had content. */
              apNoteSections={toApNoteSections({ diagnoses, orders, assessmentNote, orderDetail: noteOrderDetailFn })}
              // Flutter's DOCS section of the note (`generated_notes_widget.dart:192`), from the
              // SAME encounter-scoped query the Docs step renders.
              docsPatientId={patientId}
              docsEncounterId={encounterId}
              /* The saved complaints — same record the HPI step reads. */
              chiefComplaints={noteComplaints}
              // `isAppointmentStarted` (`notes_component_widget.dart:132`) — true here by
              // definition; it only drops the "Start Appointment and " prefix from an empty
              // section's CTA. `canStartEncounter` stays FALSE (default): Flutter's own gate is the
              // appointment being startable, which it no longer is once the encounter is running,
              // so the CTA is absent in-encounter exactly as Andrew observed.
              isAppointmentStarted
              // "Signed <when>" attestation — the createdDate of the record that produced the current
              // sign state. Null for a draft, and null for a signed encounter whose record carries no
              // date, in which case no attestation renders rather than an invented timestamp.
              signedAt={signLockSignedAt(signLockQuery.data) ?? undefined}
              // Flutter shows the "Signed" pill whenever the encounter IS signed, with the date line
              // empty when the record carries no `createdDate` (`date != null ? … : ''`,
              // generated_notes_widget.dart:376-400). Ours keyed the whole pill off the timestamp, so
              // a signed encounter without one showed nothing at all — which is what happened on the
              // encounter used to verify this.
              signed={signed}
              // D4 NOTE FOOTER (Samer 2026-07-22): the DS NoteFooter under the generated note,
              // wired to the EXISTING handlers 1:1 — no second sign/print path:
              //   • Sign & Lock → handleSign (the same createSignLock write the Wrap-Up sign
              //     button fires, same gates: not already signed; doctor needs a diagnosis and
              //     no pending/failed follow-up save — the WrapUpView canDoctorSign rule).
              //     Already-signed / gated → the pill is omitted (DS contract: a callback-less
              //     pill does not render — never a dead button).
              //   • View PVS → the same pvsPrint the Wrap-Up summary Print uses.
              //   • Discard This Note → OMITTED (honest): no whole-note discard/clear handler
              //     exists in the app (the note derives live from the QuestionnaireResponse;
              //     only per-section Clear exists) — so the destructive pill is not rendered.
              // `disabled` rides the in-flight sign-lock request per the DS contract.
              noteFooter={
                patientId && encounterId ? (
                  <NoteFooter
                    // Same clinical gates as the Wrap-Up sign, so the two entry points agree on WHEN
                    // signing is allowed: Flutter requires a chief complaint AND a diagnosis of the
                    // DOCTOR, and neither of the nurse (patient_encounter_bloc.dart:2771-2805).
                    //
                    // Known deviation, unchanged here: this pill is HIDDEN when gated (the DS omits a
                    // callback-less action) where Flutter shows the button and toasts the reason on
                    // click. Wrap-Up is the surface that explains it — it renders the button disabled
                    // with `signDisabledReason` as its tooltip. Worth revisiting as its own change if
                    // the note footer should explain itself too.
                    /*
                     * PROD-1549 notes-rail parity: the pill now renders whenever the encounter is
                     * NOT SIGNED. The two clinical gates — chief complaint, then a diagnosis — are
                     * no longer a VISIBILITY gate here; they already fire inside `doSign()` as
                     * Flutter's own error toasts ("Please enter chief complaint" / "Please add
                     * at-least one diagnosis before signing encounter",
                     * `patient_encounter_bloc.dart:2790-2805`), which is where Flutter puts them:
                     * `footer()` swaps the Sign button for the Signed pill on `isSignAndLocked`
                     * alone (`generated_notes_widget.dart:376-400`) and gates nothing else.
                     *
                     * Hiding the pill made the note's only sign affordance disappear with no reason
                     * given — the same defect the wrap-up header fixed on 2026-09-07. The
                     * save-in-flight gates stay, but on `disabled` (below) where they belong.
                     */
                    onSignLock={!signed ? handleSign : undefined}
                    // Flutter's View PVS OPENS A POPUP — `showPostVisitSummary()` →
                    // `PopUpUtils.showPostVisitSummaryDialog(PostVisitSummaryStandalonePage(...))`
                    // (generated_notes_widget.dart:516-526). Ours fired a PRINT instead, which is a
                    // different action: the clinician asked to look at the summary, not to produce a
                    // file. The print is not lost — `PostVisitSummaryDialog` carries its own
                    // Print/Download, so this relocates it INTO the popup exactly as Flutter does.
                    // The Wrap-Up summary's own Print keeps using `pvsPrint` directly.
                    onViewPvs={() => setPvsOpen(true)}
                    // Flutter's label is "Sign Note" (`S.signNote`, intl_en.arb:303), not
                    // "Sign & Lock" — the DS default. The write is unchanged.
                    signLockLabel="Sign Note"
                    // Discard This Note — no longer omitted. Flutter renders it unconditionally
                    // (only the sign slot swaps for the Signed pill), so it is not gated on `signed`.
                    onDiscard={mayDiscard ? () => setDiscardOpen(true) : undefined}
                    // PROD-1690: signPromoting = the sign-path order-sign A&P save + lock sequence —
                    // keep the footer disabled through it, not just the lock write. Also disable while
                    // an unsent encounter's ordinary A&P autosave is in flight (signNeedsOrderSign &&
                    // isPending): handleSign no-ops in that window (one in-flight PUT discipline), so
                    // an enabled pill would silently swallow the click (Codex P2 — mirrors the wrap-up
                    // button's canDoctorSign gate).
                    disabled={
                      createSignLock.isPending ||
                      signPromoting ||
                      pvsPrint.isPending ||
                      (signNeedsOrderSign && saveAssessment.isPending)
                    }
                  />
                ) : undefined
              }
            />
          )
        }
      />
      {/* THE TWO SHARED HEADER SURFACES. Mounted once at page level: the Add-Order drawer's icon
          exists on every encounter tab except Wrap-up, so a per-step mount would be seven copies
          of the same surface. Both are no-ops until their icon is pressed. */}
      <EncounterSearchDrawer
        open={addOrderOpen}
        title="Add Order"
        onClose={() => {
          setAddOrderOpen(false);
          // Clear the ambient pre-fill on close so the NEXT manual "Add Order" opens empty.
          // The rail seeds from useState initialisers at mount, so a stale seed left here
          // would silently pre-fill a search the clinician opened for something else.
          setSearchSeed(null);
        }}
        categories={searchCategories}
        // PROD-1972 — pre-filled only when the ambient accept path opened this drawer.
        {...(searchSeed ? { seed: searchSeed } : {})}
        providerId={providerId}
        aiDiagnosisEnabled
        bundles={bundlesQuery.data}
        bundlesLoading={bundlesQuery.isLoading}
        onImportBundle={
          ordersOffered
            ? (b) => {
                assessmentPlan.onImportBundle?.(b);
                setAddOrderOpen(false);
              }
            : undefined
        }
        onDeleteBundle={assessmentPlan.onDeleteBundle}
        deletingBundle={deleteBundle.isPending}
        // ORDER SETS (PROD-2723): with the flag on and a user who may order, the drawer offers
        // Order sets where it offered Bundles, applied through the A/P's own apply (PROD-2712);
        // the drawer then closes on the A/P, where the orders and the Undo receipt land.
        {...(orderSetsActive
          ? {
              orderSets: orderSetsForDrawer(orderSetApply.apply, orderSetApply.applyingId, diagnoses, () => {
                setCurrentStep('ap');
                setAddOrderOpen(false);
              }),
            }
          : {})}
        /* Add Order applies straight to the encounter — no Save/Cancel, and the Bundles tab IS
           offered here (unlike the Create-Bundle drawer). */
        onActivateItem={(item, category, queryAtPick) => {
          // PROD-2946: a pick off the physician allowlist writes nothing (the drawer does not open for
          // that user; this is the handler's own check).
          if (!ordersOffered) {
            setAddOrderOpen(false);
            return;
          }
          // DIAGNOSIS adds straight through the page's own handler (dedupe + 20-cap + notice all
          // live there), which is the full behaviour.
          if (category === 'diagnosis') {
            handleAddDx(item.item as CodeSearchResult);
            setAddOrderOpen(false);
            return;
          }
          // ORDER TYPES: setup is a per-type dialog owned by the A/P view. Rather than lift that
          // dialog to the page for one caller, hand the pick over and switch to the step that owns
          // it — the drawer is reachable from every tab, so the jump is what makes the flow work
          // from HPI/ROS/PE at all.
          if (category === 'education') return;
          /**
           * The dictated sig, handed on ONLY when the picked medication IS the dictated one.
           *
           * Matching on the CATEGORY alone was not enough, and cross-model review (Codex,
           * gpt-5.6-sol) named the failure: every medication result carries
           * `category === 'medication'`, so a clinician who lands in the seeded paracetamol search,
           * retypes, and picks ibuprofen inherited **paracetamol's 500 mg three-times-daily sig on
           * the ibuprofen order**. A wrong dose on the right-looking drug is the worst outcome this
           * whole feature can produce.
           *
           * So the seed is bound to the drug it came from: the picked record's name must still
           * contain the dictated name. Substring rather than equality because the catalogue name is
           * the DDC registered one — dictated "paracetamol" against "Paracetamol 500mg Tablet" — and
           * that is exactly the direction we need. Anything else drops the sig and the clinician
           * types the dose, which is the safe failure.
           *
           * With `frameV2` on, a row picked from the UNEDITED seeded search also carries it: a DDC
           * catalogue row is named by its brand ("ADOL 500MG …"), so the name rule alone dropped
           * the sig for nearly every catalogue pick (1 Oct). Every carried sig is then marked, and
           * the dose form asks the physician to check it. See `dictated-dose-for-pick.ts`.
           */
          const dictated = dictatedDoseForPick({
            seed: searchSeed,
            category,
            pickedItem: item.item,
            queryAtPick,
            switchOn: frameV2On,
          });
          setPendingSeed({
            item: item.item,
            category: category as DsOrderType,
            ...(dictated ? { dictatedDose: dictated } : {}),
          });
          setCurrentStep('ap');
          setAddOrderOpen(false);
        }}
      />
      <AddToFavouritesDialog
        open={favouritesOpen}
        onClose={() => setFavouritesOpen(false)}
        providerId={providerId}
        categories={searchCategories}
      />
      {/* NO in-encounter vitals dialogs: with the step-shell vitals strip removed (regression
          cured 2026-07-23) nothing in-encounter opens Edit/History/Visual-Acuity — the dialogs
          stay mounted on the PRE-ENCOUNTER branch, where the vitals strip (and its wave-2
          Edit-dialog acuity handoff + reviewed status chip) lawfully lives. */}
      {/* Wrap-Up invoice "+" → add a billable service (the PM Billing tab's picker). Mounted only
          when the invoice block is
          (permission + role) visible, so no billing UI exists for a nurse/unpermitted user.
          `hasDiagnosis` carries Flutter's "Please add diagnosis first." rule (:613-619). */}
      {canSeeInvoice && (
        <InvoiceAddServiceDialog
          open={addServiceOpen}
          onClose={() => setAddServiceOpen(false)}
          existingServiceIds={(encounterInvoice.invoice?.services ?? [])
            .map((s) => s.serviceId)
            .filter((id): id is string => !!id)}
          hasDiagnosis={(invoiceSectionData?.dx.length ?? 0) > 0}
          onAdd={(serviceId) => invoiceServiceMutation.mutate({ addedServices: [serviceId] })}
          busy={invoiceServiceMutation.isPending}
          providerId={encounterInvoice.invoice?.providerId ?? encounterInvoice.invoice?.provider?.id}
          locationId={encounterInvoice.invoice?.locationId}
          patientId={encounterInvoice.invoice?.patientId ?? encounterInvoice.invoice?.patient?.id}
          currency={invoiceSectionData?.currencyCode || user?.organizationCurrency || 'AED'}
          // Also while the invoice is absent: `isLoading` is false while its query is disabled.
          scopeLoading={encounterInvoice.isLoading || !encounterInvoice.invoice}
        />
      )}
      <EPrescriptionPreviewDialog
        open={erxPreviewOpen}
        onClose={() => setErxPreviewOpen(false)}
        preview={erxPreviewQuery.data}
        loading={erxPreviewQuery.isLoading}
        error={erxPreviewQuery.isError}
      />
      <ModalYesNo
        open={resendConfirmOpen}
        title="Confirm Resend"
        message="e-Prescription is generated already, would you like to generate again, the old eRx will be cancelled"
        confirmLabel="Yes"
        cancelLabel="Cancel"
        onConfirm={() => {
          setResendConfirmOpen(false);
          if (encounterId) submitEPrescription.mutate(encounterId);
        }}
        onCancel={() => setResendConfirmOpen(false)}
      />
      {canSeeInvoice && (
        <InvoiceEditServiceDialog
          open={!!editLineId}
          onClose={() => setEditLineId(null)}
          line={(() => {
            const r = invoiceSectionData?.rows.find((x) => x.id === editLineId);
            return r
              ? { id: r.id, label: r.label, netAmount: r.net, discountAmount: r.discount, discountReason: r.discountReason }
              : null;
          })()}
          currencyCode={user?.organizationCurrency || 'AED'}
          onSave={(v) => updateServiceDiscount.mutate(v)}
          busy={updateServiceDiscount.isPending}
        />
      )}
      {canEditManagementDiscount && (
        <InvoiceManagementDiscountDialog
          open={discountOpen}
          onClose={() => setDiscountOpen(false)}
          currencyCode={user?.organizationCurrency || 'AED'}
          onApply={({ type, amount, reason }) =>
            addManagementDiscount.mutate({
              invoiceId: encounterInvoice.invoiceId!,
              type,
              amount,
              reason,
            })
          }
          busy={addManagementDiscount.isPending}
        />
      )}

      {/* Sign confirmation. Rendered from the page (not the wrap-up header) because BOTH sign entry
          points route through handleSign — the Wrap-Up action and the Note drawer's Sign & Lock —
          so one dialog here covers both rather than each growing its own. */}
      {patientReportedDialog}
      <NeedsDxSignPrompt state={orderSetApply} diagnoses={diagnoses} />
      <SignNoteConfirmDialog
        open={signConfirmOpen}
        forNurse={isNurse}
        signing={createSignLock.isPending || signPromoting}
        onConfirm={() => {
          setSignConfirmOpen(false);
          doSign();
        }}
        onCancel={() => setSignConfirmOpen(false)}
      />
      <DiscardNoteConfirmDialog
        open={discardOpen}
        discarding={saveWrapUp.isPending}
        onConfirm={handleDiscardNote}
        onCancel={() => setDiscardOpen(false)}
      />
      {/* Growth-chart print sheet - the ONE instance, shared with the pre-encounter branch above.
          Flutter's print icon opens the same preview (`growth_chart.dart:1319`). */}
      {growthPrintDialog}

      {/* The PE vitals block's Edit / Historical / visual-acuity targets (PROD-2577 part B) —
          the SAME `vitalsOverlays` node the pre-encounter branch renders. See its definition. */}
      {vitalsOverlays}

      {/* The View-PVS target for BOTH entry points — the note footer's View PVS and the wrap-up
          header's Preview-orders eye — and the SAME dialog the calendar and the chart's notes tab
          mount, so there is one PVS render in the app rather than a third.
          `encounterId` is passed alongside the appointment id because Flutter's wrap-up preview
          resolves the summary BY ENCOUNTER (`PostVisitSummaryStandalonePage(encounterId: …)`,
          wrap_up_billing_screen.dart:414). `buildPvsQuery` ships only the truthy keys, the same
          conditional inclusion Flutter uses, so sending both narrows the lookup rather than
          conflicting. */}
      {pvsOpen && (
        <Suspense fallback={<QuietLoading label="Loading visit summary" />}>
          <PostVisitSummaryDialog
            open
            onOpenChange={(next) => {
              if (!next) setPvsOpen(false);
            }}
            encounterId={encounterId ?? undefined}
            appointmentId={appointmentId ?? ''}
            labExtras={labPrintExtras}
          />
        </Suspense>
      )}
      {frameV2On && (
        <NoteMacroLibrary
          onLoad={setNoteMacros}
          // F5 (live dev, 6 Oct): the note lines' phrase list sent NO scope keys, so it could never narrow by the
          // visit's specialty or type. Same scope, and the same hold, as the A&P note's (TS-4).
          scope={{
            specialtyId: templateSpecialty.specialtyId,
            visitType: templateVisitType.visitType,
            pending: templateSpecialty.pending || templateVisitType.pending,
          }}
        />
      )}
      {frameV2On && macroDraft !== null && authUser?.id && authUser?.organizationId && (
        <MacroEditDialog
          open
          onOpenChange={(next) => {
            if (!next) setMacroDraft(null);
          }}
          initial={macroDraftShell(macroDraft, authUser.id, authUser.organizationId)}
          addContext={{ ownerId: authUser.id, organizationId: authUser.organizationId }}
          providerId={authUser.id}
        />
      )}
      {/* `ambientWindow`: the floating ambient window (a portal; today's panel inside it). */}
      {ambientWindowNode}
      {/* `medActions`: the A&P Active Medications "Discontinue" is today's Stop (the rail's confirm, the same save). */}
      {apMedStop && (
        <ApMedStopHost
          key={apMedStop.identifier}
          patientId={patientId ?? undefined}
          identifier={apMedStop.identifier}
          display={apMedStop.display}
          onClose={() => setApMedStop(null)}
        />
      )}
      {/* Which template this visit renders, for the feature check on dev (ENC-L0+, D-4983): the check
          must judge the content actually served, not a copy of the pick logic. Hidden, no layout. LAST child on purpose: the
          televisit must stay fragment child 0 so it is not remounted. */}
      <span
        hidden
        data-served-template-id={templateData?.questionnaire?.id ?? ''}
        data-served-template-title={templateData?.questionnaire?.title ?? ''}
        data-served-template-source={templateData?.source ?? 'axes'}
      />
    </>
  );
}

/** Frame v2 only: reads the phrase library (`useNoteMacros`, GET /api/Doctor/GetMacros) into the page.
 *  A component rather than a page-level hook so the OFF page issues no query, exactly as before. */
function NoteMacroLibrary({ onLoad, scope }: { onLoad: (macros: NoteMacro[]) => void; scope?: NoteMacroScope }) {
  const { data } = useNoteMacros(scope);
  useEffect(() => {
    // L8 / F5: each phrase once (an exact duplicate by id, or by name and body, is shown once). No data (the scope is
    // still resolving, or a new scope is loading) → an empty list, never the previous scope's phrases (Codex P2).
    onLoad(data ? dedupeMacros(data) : []);
  }, [data, onLoad]);
  return null;
}

/**
 * The Settings macro dialog's own ADD shell (macro-edit-dialog.tsx builds the same one when `initial`
 * is null), with the narrative as its body. `id: ''` keeps the dialog in Add mode (`isEdit = !!id`),
 * so its save is the existing create (CreateUpdateMacro with no id).
 */
function macroDraftShell(text: string, ownerId: string, organizationId: string): MacroData {
  return {
    id: '',
    organizationId,
    ownerId,
    ownerName: '',
    ownerPhoto: null,
    macroKey: '',
    macroValue: JSON.stringify([{ insert: `${text}\n` }]),
    macroValueFormatted: text,
    createdDate: '',
    createdBy: '',
    updatedDate: '',
    updatedBy: '',
    isGlobal: false,
    isDeleted: false,
    macroHistories: [],
    macroCollaborators: [],
  };
}

/** PROD-2804: the encounter's patient could not be resolved. Never an empty chart in its place. */
function PatientUnresolvedState({ onRetry, onBackToToday }: { onRetry: () => void; onBackToToday: (home: string) => void }) {
  // Home is the /dashboard route for everyone (home-path.ts); the readyList switch only decides what it draws.
  const home = useHomePath();
  return (
    <div role="alert" className="flex flex-col items-start gap-3 p-10 font-sans">
      <p className="m-0 text-size-base font-semibold text-text-primary">Couldn't load this patient</p>
      <p className="m-0 text-size-sm text-text-secondary">
        The appointment in this link didn't return a patient, so no chart is shown. Open the visit from Today, or try again.
      </p>
      <div className="flex items-center gap-2">
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Try again
        </Button>
        {/* A real link (middle-click / copy work); a plain click routes in-app. `home` is a route in
            `app/router.tsx`; the router has no basename. */}
        <a
          href={home}
          className="text-size-sm font-medium text-brand-primary underline"
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            e.preventDefault();
            onBackToToday(home);
          }}
        >
          Back to Today
        </a>
      </div>
    </div>
  );
}

function FullMessage({ text }: { text: string }) {
  return (
    <div className="p-10 font-sans text-text-secondary">
      {text}
    </div>
  );
}
